import { Encodings, Font, FontNames } from "@pdf-lib/standard-fonts";
import pako from "pako";
import UPNGModule from "@pdf-lib/upng";
import ColorParser from "color";
import { NodeType, parse } from "node-html-better-parser";
//#region src/utils/base64.ts
var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
var lookup = /* @__PURE__ */ new Uint8Array(256);
for (let i = 0; i < 64; i++) lookup[chars.charCodeAt(i)] = i;
var encodeToBase64 = (bytes) => {
	let base64 = "";
	const len = bytes.length;
	for (let i = 0; i < len; i += 3) {
		base64 += chars[bytes[i] >> 2];
		base64 += chars[(bytes[i] & 3) << 4 | bytes[i + 1] >> 4];
		base64 += chars[(bytes[i + 1] & 15) << 2 | bytes[i + 2] >> 6];
		base64 += chars[bytes[i + 2] & 63];
	}
	if (len % 3 === 2) base64 = base64.substring(0, base64.length - 1) + "=";
	else if (len % 3 === 1) base64 = base64.substring(0, base64.length - 2) + "==";
	return base64;
};
var decodeFromBase64 = (base64) => {
	let bufferLength = base64.length * .75;
	const len = base64.length;
	let i;
	let p = 0;
	let encoded1;
	let encoded2;
	let encoded3;
	let encoded4;
	if (base64[base64.length - 1] === "=") {
		bufferLength--;
		if (base64[base64.length - 2] === "=") bufferLength--;
	}
	const bytes = new Uint8Array(bufferLength);
	for (i = 0; i < len; i += 4) {
		encoded1 = lookup[base64.charCodeAt(i)];
		encoded2 = lookup[base64.charCodeAt(i + 1)];
		encoded3 = lookup[base64.charCodeAt(i + 2)];
		encoded4 = lookup[base64.charCodeAt(i + 3)];
		bytes[p++] = encoded1 << 2 | encoded2 >> 4;
		bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
		bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
	}
	return bytes;
};
var DATA_URI_PREFIX_REGEX = /^(data)?:?([\w/+]+)?;?(charset=[\w-]+|base64)?.*,/i;
/**
* If the `dataUri` input is a data URI, then the data URI prefix must not be
* longer than 100 characters, or this function will fail to decode it.
*
* @param dataUri a base64 data URI or plain base64 string
* @returns a Uint8Array containing the decoded input
*/
var decodeFromBase64DataUri = (dataUri) => {
	const trimmedUri = dataUri.trim();
	const res = trimmedUri.substring(0, 100).match(DATA_URI_PREFIX_REGEX);
	if (!res) return decodeFromBase64(trimmedUri);
	const [fullMatch] = res;
	return decodeFromBase64(trimmedUri.substring(fullMatch.length));
};
//#endregion
//#region src/utils/strings.ts
var toCharCode = (character) => character.charCodeAt(0);
var toCodePoint = (character) => character.codePointAt(0);
var toHexStringOfMinLength = (num, minLength) => padStart(num.toString(16), minLength, "0").toUpperCase();
var toHexString = (num) => toHexStringOfMinLength(num, 2);
var charFromCode = (code) => String.fromCharCode(code);
var charFromHexCode = (hex) => charFromCode(parseInt(hex, 16));
var padStart = (value, length, padChar) => {
	let padding = "";
	for (let idx = 0, len = length - value.length; idx < len; idx++) padding += padChar;
	return padding + value;
};
var stringAsByteArray = (str) => {
	const buffer = new Uint8Array(str.length);
	copyStringIntoBuffer(str, buffer, 0);
	return buffer;
};
var copyStringIntoBuffer = (str, buffer, offset) => {
	const length = str.length;
	for (let idx = 0; idx < length; idx++) buffer[offset++] = str.charCodeAt(idx);
	return length;
};
var addRandomSuffix = (prefix, suffixLength = 4) => `${prefix}-${Math.floor(Math.random() * 10 ** suffixLength)}`;
var escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
var cleanText = (text) => text.replace(/\t|\u0085|\u2028|\u2029/g, "    ").replace(/[\b\v]/g, "");
var escapedNewlineChars = [
	"\\n",
	"\\f",
	"\\r",
	"\\v"
];
var newlineChars = [
	"\n",
	"\f",
	"\r",
	"\v"
];
var isNewlineChar = (text) => /^[\n\f\r\v]$/.test(text);
var lineSplit = (text) => text.split(/[\n\f\r\v]/);
var mergeLines = (text) => text.replace(/[\n\f\r\v]/g, " ");
var charAtIndex = (text, index) => {
	const cuFirst = text.charCodeAt(index);
	let cuSecond;
	const nextIndex = index + 1;
	let length = 1;
	if (cuFirst >= 55296 && cuFirst <= 56319 && text.length > nextIndex) {
		cuSecond = text.charCodeAt(nextIndex);
		if (cuSecond >= 56320 && cuSecond <= 57343) length = 2;
	}
	return [text.slice(index, index + length), length];
};
var charSplit = (text) => {
	const chars = [];
	for (let idx = 0, len = text.length; idx < len;) {
		const [c, cLen] = charAtIndex(text, idx);
		chars.push(c);
		idx += cLen;
	}
	return chars;
};
var buildWordBreakRegex = (wordBreaks) => {
	const newlineCharUnion = escapedNewlineChars.join("|");
	const escapedRules = ["$"];
	for (let idx = 0, len = wordBreaks.length; idx < len; idx++) {
		const wordBreak = wordBreaks[idx];
		if (isNewlineChar(wordBreak)) throw new TypeError(`\`wordBreak\` must not include ${newlineCharUnion}`);
		escapedRules.push(wordBreak === "" ? "." : escapeRegExp(wordBreak));
	}
	const breakRules = escapedRules.join("|");
	return new RegExp(`(${newlineCharUnion})|((.*?)(${breakRules}))`, "gm");
};
var breakTextIntoLines = (text, wordBreaks, maxWidth, computeWidthOfText) => {
	const regex = buildWordBreakRegex(wordBreaks);
	const words = cleanText(text).match(regex);
	let currLine = "";
	let currWidth = 0;
	const lines = [];
	const pushCurrLine = () => {
		if (currLine !== "") lines.push(currLine);
		currLine = "";
		currWidth = 0;
	};
	for (let idx = 0, len = words.length; idx < len; idx++) {
		const word = words[idx];
		if (isNewlineChar(word)) pushCurrLine();
		else {
			const width = computeWidthOfText(word);
			if (currWidth + width > maxWidth) pushCurrLine();
			currLine += word;
			currWidth += width;
		}
	}
	pushCurrLine();
	return lines;
};
var dateRegex = /^D:(\d\d\d\d)(\d\d)?(\d\d)?(\d\d)?(\d\d)?(\d\d)?([+\-Z])?(\d\d)?'?(\d\d)?'?$/;
var parseDate = (dateStr) => {
	const match = dateStr.match(dateRegex);
	if (!match) return void 0;
	const [, year, month = "01", day = "01", hours = "00", mins = "00", secs = "00", offsetSign = "Z", offsetHours = "00", offsetMins = "00"] = match;
	const tzOffset = offsetSign === "Z" ? "Z" : `${offsetSign}${offsetHours}:${offsetMins}`;
	return /* @__PURE__ */ new Date(`${year}-${month}-${day}T${hours}:${mins}:${secs}${tzOffset}`);
};
var findLastMatch = (value, regex) => {
	if (value.length > 1e4) return {
		match: void 0,
		pos: 0
	};
	let position = 0;
	let lastMatch;
	let iterations = 0;
	const MAX_ITERATIONS = 1e3;
	while (position < value.length) {
		if (++iterations > MAX_ITERATIONS) return {
			match: lastMatch,
			pos: position
		};
		const match = value.substring(position).match(regex);
		if (!match) return {
			match: lastMatch,
			pos: position
		};
		lastMatch = match;
		position += (match.index ?? 0) + match[0].length;
	}
	return {
		match: lastMatch,
		pos: position
	};
};
//#endregion
//#region src/utils/arrays.ts
var last = (array) => array[array.length - 1];
var typedArrayFor = (value) => {
	if (value instanceof Uint8Array) return value;
	const length = value.length;
	const typedArray = new Uint8Array(length);
	for (let idx = 0; idx < length; idx++) typedArray[idx] = value.charCodeAt(idx);
	return typedArray;
};
var mergeIntoTypedArray = (...arrays) => {
	const arrayCount = arrays.length;
	const typedArrays = [];
	for (let idx = 0; idx < arrayCount; idx++) {
		const element = arrays[idx];
		typedArrays[idx] = element instanceof Uint8Array ? element : typedArrayFor(element);
	}
	let totalSize = 0;
	for (let idx = 0; idx < arrayCount; idx++) totalSize += arrays[idx].length;
	const merged = new Uint8Array(totalSize);
	let offset = 0;
	for (let arrIdx = 0; arrIdx < arrayCount; arrIdx++) {
		const arr = typedArrays[arrIdx];
		for (let byteIdx = 0, arrLen = arr.length; byteIdx < arrLen; byteIdx++) merged[offset++] = arr[byteIdx];
	}
	return merged;
};
var mergeUint8Arrays = (arrays) => {
	let totalSize = 0;
	for (let idx = 0, len = arrays.length; idx < len; idx++) totalSize += arrays[idx].length;
	const mergedBuffer = new Uint8Array(totalSize);
	let offset = 0;
	for (let idx = 0, len = arrays.length; idx < len; idx++) {
		const array = arrays[idx];
		mergedBuffer.set(array, offset);
		offset += array.length;
	}
	return mergedBuffer;
};
var arrayAsString = (array) => {
	let str = "";
	for (let idx = 0, len = array.length; idx < len; idx++) str += charFromCode(array[idx]);
	return str;
};
var byAscendingId = (a, b) => a.id - b.id;
var sortedUniq = (array, indexer) => {
	const uniq = [];
	for (let idx = 0, len = array.length; idx < len; idx++) {
		const curr = array[idx];
		const prev = array[idx - 1];
		if (idx === 0 || indexer(curr) !== indexer(prev)) uniq.push(curr);
	}
	return uniq;
};
var isArrayEqual = (arr1, arr2) => {
	if (arr1.length !== arr2.length) return false;
	for (let i = 0, ii = arr1.length; i < ii; i++) if (arr1[i] !== arr2[i]) return false;
	return true;
};
var reverseArray = (array) => {
	const arrayLen = array.length;
	for (let idx = 0, len = Math.floor(arrayLen / 2); idx < len; idx++) {
		const leftIdx = idx;
		const rightIdx = arrayLen - idx - 1;
		const temp = array[idx];
		array[leftIdx] = array[rightIdx];
		array[rightIdx] = temp;
	}
	return array;
};
var sum = (array) => {
	let total = 0;
	for (let idx = 0, len = array.length; idx < len; idx++) total += array[idx];
	return total;
};
var range = (start, end) => {
	const arr = Array(end - start);
	for (let idx = 0, len = arr.length; idx < len; idx++) arr[idx] = start + idx;
	return arr;
};
var pluckIndices = (arr, indices) => {
	const plucked = Array(indices.length);
	for (let idx = 0, len = indices.length; idx < len; idx++) plucked[idx] = arr[indices[idx]];
	return plucked;
};
var canBeConvertedToUint8Array = (input) => input instanceof Uint8Array || input instanceof ArrayBuffer || typeof input === "string";
var toUint8Array = (input) => {
	if (typeof input === "string") return decodeFromBase64DataUri(input);
	else if (input instanceof ArrayBuffer) return new Uint8Array(input);
	else if (input instanceof Uint8Array) return input;
	else throw new TypeError("`input` must be one of `string | ArrayBuffer | Uint8Array`");
};
//#endregion
//#region src/utils/async.ts
/**
* Returns a Promise that resolves after at least one tick of the
* Macro Task Queue occurs.
*/
var waitForTick = () => new Promise((resolve) => {
	setTimeout(() => resolve(), 0);
});
//#endregion
//#region src/utils/unicode.ts
/**
* Encodes a string to UTF-8.
*
* @param input The string to be encoded.
* @param byteOrderMark Whether or not a byte order marker (BOM) should be added
*                      to the start of the encoding. (default `true`)
* @returns A Uint8Array containing the UTF-8 encoding of the input string.
*
* -----------------------------------------------------------------------------
*
* JavaScript strings are composed of Unicode code points. Code points are
* integers in the range 0 to 1,114,111 (0x10FFFF). When serializing a string,
* it must be encoded as a sequence of words. A word is typically 8, 16, or 32
* bytes in size. As such, Unicode defines three encoding forms: UTF-8, UTF-16,
* and UTF-32. These encoding forms are described in the Unicode standard [1].
* This function implements the UTF-8 encoding form.
*
* -----------------------------------------------------------------------------
*
* In UTF-8, each code point is mapped to a sequence of 1, 2, 3, or 4 bytes.
* Note that the logic which defines this mapping is slightly convoluted, and
* not as straightforward as the mapping logic for UTF-16 or UTF-32. The UTF-8
* mapping logic is as follows [2]:
*
* • If a code point is in the range U+0000..U+007F, then view it as a 7-bit
*   integer: 0bxxxxxxx. Map the code point to 1 byte with the first high order
*   bit set to 0:
*
*       b1=0b0xxxxxxx
*
* • If a code point is in the range U+0080..U+07FF, then view it as an 11-bit
*   integer: 0byyyyyxxxxxx. Map the code point to 2 bytes with the first 5 bits
*   of the code point stored in the first byte, and the last 6 bits stored in
*   the second byte:
*
*       b1=0b110yyyyy    b2=0b10xxxxxx
*
* • If a code point is in the range U+0800..U+FFFF, then view it as a 16-bit
*   integer, 0bzzzzyyyyyyxxxxxx. Map the code point to 3 bytes with the first
*   4 bits stored in the first byte, the next 6 bits stored in the second byte,
*   and the last 6 bits in the third byte:
*
*       b1=0b1110zzzz    b2=0b10yyyyyy    b3=0b10xxxxxx
*
* • If a code point is in the range U+10000...U+10FFFF, then view it as a
*   21-bit integer, 0bvvvzzzzzzyyyyyyxxxxxx. Map the code point to 4 bytes with
*   the first 3 bits stored in the first byte, the next 6 bits stored in the
*   second byte, the next 6 bits stored in the third byte, and the last 6 bits
*   stored in the fourth byte:
*
*       b1=0b11110xxx    b2=0b10zzzzzz    b3=0b10yyyyyy    b4=0b10xxxxxx
*
* -----------------------------------------------------------------------------
*
* It is important to note, when iterating through the code points of a string
* in JavaScript, that if a character is encoded as a surrogate pair it will
* increase the string's length by 2 instead of 1 [4]. For example:
*
* ```
* > 'a'.length
* 1
* > '💩'.length
* 2
* > '語'.length
* 1
* > 'a💩語'.length
* 4
* ```
*
* The results of the above example are explained by the fact that the
* characters 'a' and '語' are not represented by surrogate pairs, but '💩' is.
*
* Because of this idiosyncrasy in JavaScript's string implementation and APIs,
* we must "jump" an extra index after encoding a character as a surrogate
* pair. In practice, this means we must increment the index of our for loop by
* 2 if we encode a surrogate pair, and 1 in all other cases.
*
* -----------------------------------------------------------------------------
*
* References:
*   - [1] https://www.unicode.org/versions/Unicode12.0.0/UnicodeStandard-12.0.pdf
*         3.9  Unicode Encoding Forms - UTF-8
*   - [2] http://www.herongyang.com/Unicode/UTF-8-UTF-8-Encoding.html
*   - [3] http://www.herongyang.com/Unicode/UTF-8-UTF-8-Encoding-Algorithm.html
*   - [4] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/length#Description
*
*/
var utf8Encode = (input, byteOrderMark = true) => {
	const encoded = [];
	if (byteOrderMark) encoded.push(239, 187, 191);
	for (let idx = 0, len = input.length; idx < len;) {
		const codePoint = input.codePointAt(idx);
		if (codePoint < 128) {
			const byte1 = codePoint & 127;
			encoded.push(byte1);
			idx += 1;
		} else if (codePoint < 2048) {
			const byte1 = codePoint >> 6 & 31 | 192;
			const byte2 = codePoint & 63 | 128;
			encoded.push(byte1, byte2);
			idx += 1;
		} else if (codePoint < 65536) {
			const byte1 = codePoint >> 12 & 15 | 224;
			const byte2 = codePoint >> 6 & 63 | 128;
			const byte3 = codePoint & 63 | 128;
			encoded.push(byte1, byte2, byte3);
			idx += 1;
		} else if (codePoint < 1114112) {
			const byte1 = codePoint >> 18 & 7 | 240;
			const byte2 = codePoint >> 12 & 63 | 128;
			const byte3 = codePoint >> 6 & 63 | 128;
			const byte4 = codePoint >> 0 & 63 | 128;
			encoded.push(byte1, byte2, byte3, byte4);
			idx += 2;
		} else throw new Error(`Invalid code point: 0x${toHexString(codePoint)}`);
	}
	return new Uint8Array(encoded);
};
/**
* Encodes a string to UTF-16.
*
* @param input The string to be encoded.
* @param byteOrderMark Whether or not a byte order marker (BOM) should be added
*                      to the start of the encoding. (default `true`)
* @returns A Uint16Array containing the UTF-16 encoding of the input string.
*
* -----------------------------------------------------------------------------
*
* JavaScript strings are composed of Unicode code points. Code points are
* integers in the range 0 to 1,114,111 (0x10FFFF). When serializing a string,
* it must be encoded as a sequence of words. A word is typically 8, 16, or 32
* bytes in size. As such, Unicode defines three encoding forms: UTF-8, UTF-16,
* and UTF-32. These encoding forms are described in the Unicode standard [1].
* This function implements the UTF-16 encoding form.
*
* -----------------------------------------------------------------------------
*
* In UTF-16, each code point is mapped to one or two 16-bit integers. The
* UTF-16 mapping logic is as follows [2]:
*
* • If a code point is in the range U+0000..U+FFFF, then map the code point to
*   a 16-bit integer with the most significant byte first.
*
* • If a code point is in the range U+10000..U+10000, then map the code point
*   to two 16-bit integers. The first integer should contain the high surrogate
*   and the second integer should contain the low surrogate. Both surrogates
*   should be written with the most significant byte first.
*
* -----------------------------------------------------------------------------
*
* It is important to note, when iterating through the code points of a string
* in JavaScript, that if a character is encoded as a surrogate pair it will
* increase the string's length by 2 instead of 1 [4]. For example:
*
* ```
* > 'a'.length
* 1
* > '💩'.length
* 2
* > '語'.length
* 1
* > 'a💩語'.length
* 4
* ```
*
* The results of the above example are explained by the fact that the
* characters 'a' and '語' are not represented by surrogate pairs, but '💩' is.
*
* Because of this idiosyncrasy in JavaScript's string implementation and APIs,
* we must "jump" an extra index after encoding a character as a surrogate
* pair. In practice, this means we must increment the index of our for loop by
* 2 if we encode a surrogate pair, and 1 in all other cases.
*
* -----------------------------------------------------------------------------
*
* References:
*   - [1] https://www.unicode.org/versions/Unicode12.0.0/UnicodeStandard-12.0.pdf
*         3.9  Unicode Encoding Forms - UTF-8
*   - [2] http://www.herongyang.com/Unicode/UTF-16-UTF-16-Encoding.html
*   - [3] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/length#Description
*
*/
var utf16Encode = (input, byteOrderMark = true) => {
	const encoded = [];
	if (byteOrderMark) encoded.push(65279);
	for (let idx = 0, len = input.length; idx < len;) {
		const codePoint = input.codePointAt(idx);
		if (codePoint < 65536) {
			encoded.push(codePoint);
			idx += 1;
		} else if (codePoint < 1114112) {
			encoded.push(highSurrogate(codePoint), lowSurrogate(codePoint));
			idx += 2;
		} else throw new Error(`Invalid code point: 0x${toHexString(codePoint)}`);
	}
	return new Uint16Array(encoded);
};
/**
* Returns `true` if the `codePoint` is within the
* Basic Multilingual Plane (BMP). Code points inside the BMP are not encoded
* with surrogate pairs.
* @param codePoint The code point to be evaluated.
*
* Reference: https://en.wikipedia.org/wiki/UTF-16#Description
*/
var isWithinBMP = (codePoint) => codePoint >= 0 && codePoint <= 65535;
/**
* Returns `true` if the given `codePoint` is valid and must be represented
* with a surrogate pair when encoded.
* @param codePoint The code point to be evaluated.
*
* Reference: https://en.wikipedia.org/wiki/UTF-16#Description
*/
var hasSurrogates = (codePoint) => codePoint >= 65536 && codePoint <= 1114111;
var highSurrogate = (codePoint) => Math.floor((codePoint - 65536) / 1024) + 55296;
var lowSurrogate = (codePoint) => (codePoint - 65536) % 1024 + 56320;
var REPLACEMENT = "�".codePointAt(0);
/**
* Decodes a Uint8Array of data to a string using UTF-16.
*
* Note that this function attempts to recover from erronous input by
* inserting the replacement character (�) to mark invalid code points
* and surrogate pairs.
*
* @param input A Uint8Array containing UTF-16 encoded data
* @param byteOrderMark Whether or not a byte order marker (BOM) should be read
*                      at the start of the encoding. (default `true`)
* @returns The decoded string.
*/
var utf16Decode = (input, byteOrderMark = true) => {
	if (input.length <= 1) return String.fromCodePoint(REPLACEMENT);
	const byteOrder = byteOrderMark ? readBOM(input) : "BigEndian";
	let idx = byteOrderMark ? 2 : 0;
	const codePoints = [];
	while (input.length - idx >= 2) {
		const first = decodeValues(input[idx++], input[idx++], byteOrder);
		if (isHighSurrogate(first)) if (input.length - idx < 2) codePoints.push(REPLACEMENT);
		else {
			const second = decodeValues(input[idx++], input[idx++], byteOrder);
			if (isLowSurrogate(second)) codePoints.push(first, second);
			else codePoints.push(REPLACEMENT);
		}
		else if (isLowSurrogate(first)) {
			idx += 2;
			codePoints.push(REPLACEMENT);
		} else codePoints.push(first);
	}
	if (idx < input.length) codePoints.push(REPLACEMENT);
	return String.fromCodePoint(...codePoints);
};
/**
* Returns `true` if the given `codePoint` is a high surrogate.
* @param codePoint The code point to be evaluated.
*
* Reference: https://en.wikipedia.org/wiki/UTF-16#Description
*/
var isHighSurrogate = (codePoint) => codePoint >= 55296 && codePoint <= 56319;
/**
* Returns `true` if the given `codePoint` is a low surrogate.
* @param codePoint The code point to be evaluated.
*
* Reference: https://en.wikipedia.org/wiki/UTF-16#Description
*/
var isLowSurrogate = (codePoint) => codePoint >= 56320 && codePoint <= 57343;
/**
* Decodes the given utf-16 values first and second using the specified
* byte order.
* @param first The first byte of the encoding.
* @param second The second byte of the encoding.
* @param byteOrder The byte order of the encoding.
* Reference: https://en.wikipedia.org/wiki/UTF-16#Examples
*/
var decodeValues = (first, second, byteOrder) => {
	if (byteOrder === "LittleEndian") return second << 8 | first;
	if (byteOrder === "BigEndian") return first << 8 | second;
	throw new Error(`Invalid byteOrder: ${byteOrder}`);
};
/**
* Returns whether the given array contains a byte order mark for the
* UTF-16BE or UTF-16LE encoding. If it has neither, BigEndian is assumed.
*
* Reference: https://en.wikipedia.org/wiki/Byte_order_mark#UTF-16
*
* @param bytes The byte array to be evaluated.
*/
var readBOM = (bytes) => hasUtf16BigEndianBOM(bytes) ? "BigEndian" : hasUtf16LittleEndianBOM(bytes) ? "LittleEndian" : "BigEndian";
var hasUtf16BigEndianBOM = (bytes) => bytes[0] === 254 && bytes[1] === 255;
var hasUtf16LittleEndianBOM = (bytes) => bytes[0] === 255 && bytes[1] === 254;
var hasUtf16BOM = (bytes) => hasUtf16BigEndianBOM(bytes) || hasUtf16LittleEndianBOM(bytes);
//#endregion
//#region src/utils/numbers.ts
/**
* Converts a number to its string representation in decimal. This function
* differs from simply converting a number to a string with `.toString()`
* because this function's output string will **not** contain exponential
* notation.
*
* Credit: https://stackoverflow.com/a/46545519
*/
var numberToString = (num) => {
	let numStr = String(num);
	if (Math.abs(num) < 1) {
		const e = parseInt(num.toString().split("e-")[1]);
		if (e) {
			const negative = num < 0;
			if (negative) num *= -1;
			num *= Math.pow(10, e - 1);
			numStr = "0." + Array(e).join("0") + num.toString().substring(2);
			if (negative) numStr = "-" + numStr;
		}
	} else {
		let e = parseInt(num.toString().split("+")[1]);
		if (e > 20) {
			e -= 20;
			num /= Math.pow(10, e);
			numStr = num.toString() + Array(e + 1).join("0");
		}
	}
	return numStr;
};
var sizeInBytes = (n) => Math.ceil(n.toString(2).length / 8);
/**
* Converts a number into its constituent bytes and returns them as
* a number[].
*
* Returns most significant byte as first element in array. It may be necessary
* to call .reverse() to get the bits in the desired order.
*
* Example:
*   bytesFor(0x02A41E) => [ 0b10, 0b10100100, 0b11110 ]
*
* Credit for algorithm: https://stackoverflow.com/a/1936865
*/
var bytesFor = (n) => {
	const bytes = new Uint8Array(sizeInBytes(n));
	for (let i = 1; i <= bytes.length; i++) bytes[i - 1] = n >> (bytes.length - i) * 8;
	return bytes;
};
//#endregion
//#region src/utils/errors.ts
var error = (msg) => {
	throw new Error(msg);
};
//#endregion
//#region src/utils/objects.ts
var values = (obj) => Object.keys(obj).map((k) => obj[k]);
var StandardFontValues = values(FontNames);
var isStandardFont = (input) => StandardFontValues.includes(input);
var rectanglesAreEqual = (a, b) => a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
//#endregion
//#region src/utils/validators.ts
var backtick = (val) => `\`${val}\``;
var singleQuote = (val) => `'${val}'`;
var formatValue = (value) => {
	const type = typeof value;
	if (type === "string") return singleQuote(value);
	else if (type === "undefined") return backtick(value);
	else return value;
};
var createValueErrorMsg = (value, valueName, values) => {
	const allowedValues = Array(values.length);
	for (let idx = 0, len = values.length; idx < len; idx++) {
		const v = values[idx];
		allowedValues[idx] = formatValue(v);
	}
	const joinedValues = allowedValues.join(" or ");
	return `${backtick(valueName)} must be one of ${joinedValues}, but was actually ${formatValue(value)}`;
};
var assertIsOneOf = (value, valueName, allowedValues) => {
	if (!Array.isArray(allowedValues)) allowedValues = values(allowedValues);
	for (let idx = 0, len = allowedValues.length; idx < len; idx++) if (value === allowedValues[idx]) return;
	throw new TypeError(createValueErrorMsg(value, valueName, allowedValues));
};
var assertIsOneOfOrUndefined = (value, valueName, allowedValues) => {
	if (!Array.isArray(allowedValues)) allowedValues = values(allowedValues);
	assertIsOneOf(value, valueName, allowedValues.concat(void 0));
};
var assertIsSubset = (values$1, valueName, allowedValues) => {
	if (!Array.isArray(allowedValues)) allowedValues = values(allowedValues);
	for (let idx = 0, len = values$1.length; idx < len; idx++) assertIsOneOf(values$1[idx], valueName, allowedValues);
};
var getType = (val) => {
	if (val === null) return "null";
	if (val === void 0) return "undefined";
	if (typeof val === "string") return "string";
	if (isNaN(val)) return "NaN";
	if (typeof val === "number") return "number";
	if (typeof val === "boolean") return "boolean";
	if (typeof val === "symbol") return "symbol";
	if (typeof val === "bigint") return "bigint";
	if (val.constructor && val.constructor.name) return val.constructor.name;
	if (val.name) return val.name;
	if (val.constructor) return String(val.constructor);
	return String(val);
};
var isType = (value, type) => {
	if (type === "null") return value === null;
	if (type === "undefined") return value === void 0;
	if (type === "string") return typeof value === "string";
	if (type === "number") return typeof value === "number" && !isNaN(value);
	if (type === "boolean") return typeof value === "boolean";
	if (type === "symbol") return typeof value === "symbol";
	if (type === "bigint") return typeof value === "bigint";
	if (type === Date) return value instanceof Date;
	if (type === Array) return value instanceof Array;
	if (type === Uint8Array) return value instanceof Uint8Array;
	if (type === ArrayBuffer) return value instanceof ArrayBuffer;
	if (type === Function) return value instanceof Function;
	return value instanceof type[0];
};
var createTypeErrorMsg = (value, valueName, types) => {
	const allowedTypes = Array(types.length);
	for (let idx = 0, len = types.length; idx < len; idx++) {
		const type = types[idx];
		if (type === "null") allowedTypes[idx] = backtick("null");
		if (type === "undefined") allowedTypes[idx] = backtick("undefined");
		if (type === "string") allowedTypes[idx] = backtick("string");
		else if (type === "number") allowedTypes[idx] = backtick("number");
		else if (type === "boolean") allowedTypes[idx] = backtick("boolean");
		else if (type === "symbol") allowedTypes[idx] = backtick("symbol");
		else if (type === "bigint") allowedTypes[idx] = backtick("bigint");
		else if (type === Array) allowedTypes[idx] = backtick("Array");
		else if (type === Uint8Array) allowedTypes[idx] = backtick("Uint8Array");
		else if (type === ArrayBuffer) allowedTypes[idx] = backtick("ArrayBuffer");
		else allowedTypes[idx] = backtick(type[1]);
	}
	const joinedTypes = allowedTypes.join(" or ");
	return `${backtick(valueName)} must be of type ${joinedTypes}, but was actually of type ${backtick(getType(value))}`;
};
var assertIs = (value, valueName, types) => {
	for (let idx = 0, len = types.length; idx < len; idx++) if (isType(value, types[idx])) return;
	throw new TypeError(createTypeErrorMsg(value, valueName, types));
};
var assertOrUndefined = (value, valueName, types) => {
	assertIs(value, valueName, types.concat("undefined"));
};
var assertEachIs = (values, valueName, types) => {
	for (let idx = 0, len = values.length; idx < len; idx++) assertIs(values[idx], valueName, types);
};
var assertRange = (value, valueName, min, max) => {
	assertIs(value, valueName, ["number"]);
	assertIs(min, "min", ["number"]);
	assertIs(max, "max", ["number"]);
	max = Math.max(min, max);
	if (value < min || value > max) throw new Error(`${backtick(valueName)} must be at least ${min} and at most ${max}, but was actually ${value}`);
};
var assertRangeOrUndefined = (value, valueName, min, max) => {
	assertIs(value, valueName, ["number", "undefined"]);
	if (typeof value === "number") assertRange(value, valueName, min, max);
};
var assertMultiple = (value, valueName, multiplier) => {
	assertIs(value, valueName, ["number"]);
	if (value % multiplier !== 0) throw new Error(`${backtick(valueName)} must be a multiple of ${multiplier}, but was actually ${value}`);
};
var assertInteger = (value, valueName) => {
	if (!Number.isInteger(value)) throw new Error(`${backtick(valueName)} must be an integer, but was actually ${value}`);
};
var assertPositive = (value, valueName) => {
	if (![1, 0].includes(Math.sign(value))) throw new Error(`${backtick(valueName)} must be a positive number or 0, but was actually ${value}`);
};
//#endregion
//#region src/utils/pdfDocEncoding.ts
var pdfDocEncodingToUnicode = /* @__PURE__ */ new Uint16Array(256);
for (let idx = 0; idx < 256; idx++) pdfDocEncodingToUnicode[idx] = idx;
pdfDocEncodingToUnicode[22] = toCharCode("");
pdfDocEncodingToUnicode[24] = toCharCode("˘");
pdfDocEncodingToUnicode[25] = toCharCode("ˇ");
pdfDocEncodingToUnicode[26] = toCharCode("ˆ");
pdfDocEncodingToUnicode[27] = toCharCode("˙");
pdfDocEncodingToUnicode[28] = toCharCode("˝");
pdfDocEncodingToUnicode[29] = toCharCode("˛");
pdfDocEncodingToUnicode[30] = toCharCode("˚");
pdfDocEncodingToUnicode[31] = toCharCode("˜");
pdfDocEncodingToUnicode[127] = toCharCode("�");
pdfDocEncodingToUnicode[128] = toCharCode("•");
pdfDocEncodingToUnicode[129] = toCharCode("†");
pdfDocEncodingToUnicode[130] = toCharCode("‡");
pdfDocEncodingToUnicode[131] = toCharCode("…");
pdfDocEncodingToUnicode[132] = toCharCode("—");
pdfDocEncodingToUnicode[133] = toCharCode("–");
pdfDocEncodingToUnicode[134] = toCharCode("ƒ");
pdfDocEncodingToUnicode[135] = toCharCode("⁄");
pdfDocEncodingToUnicode[136] = toCharCode("‹");
pdfDocEncodingToUnicode[137] = toCharCode("›");
pdfDocEncodingToUnicode[138] = toCharCode("−");
pdfDocEncodingToUnicode[139] = toCharCode("‰");
pdfDocEncodingToUnicode[140] = toCharCode("„");
pdfDocEncodingToUnicode[141] = toCharCode("“");
pdfDocEncodingToUnicode[142] = toCharCode("”");
pdfDocEncodingToUnicode[143] = toCharCode("‘");
pdfDocEncodingToUnicode[144] = toCharCode("’");
pdfDocEncodingToUnicode[145] = toCharCode("‚");
pdfDocEncodingToUnicode[146] = toCharCode("™");
pdfDocEncodingToUnicode[147] = toCharCode("ﬁ");
pdfDocEncodingToUnicode[148] = toCharCode("ﬂ");
pdfDocEncodingToUnicode[149] = toCharCode("Ł");
pdfDocEncodingToUnicode[150] = toCharCode("Œ");
pdfDocEncodingToUnicode[151] = toCharCode("Š");
pdfDocEncodingToUnicode[152] = toCharCode("Ÿ");
pdfDocEncodingToUnicode[153] = toCharCode("Ž");
pdfDocEncodingToUnicode[154] = toCharCode("ı");
pdfDocEncodingToUnicode[155] = toCharCode("ł");
pdfDocEncodingToUnicode[156] = toCharCode("œ");
pdfDocEncodingToUnicode[157] = toCharCode("š");
pdfDocEncodingToUnicode[158] = toCharCode("ž");
pdfDocEncodingToUnicode[159] = toCharCode("�");
pdfDocEncodingToUnicode[160] = toCharCode("€");
pdfDocEncodingToUnicode[173] = toCharCode("�");
/**
* Decode a byte array into a string using PDFDocEncoding.
*
* @param bytes a byte array (decimal representation) containing a string
*              encoded with PDFDocEncoding.
*/
var pdfDocEncodingDecode = (bytes) => {
	const codePoints = Array(bytes.length);
	for (let idx = 0, len = bytes.length; idx < len; idx++) codePoints[idx] = pdfDocEncodingToUnicode[bytes[idx]];
	return String.fromCodePoint(...codePoints);
};
//#endregion
//#region \0@oxc-project+runtime@0.137.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.137.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.137.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.137.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region src/utils/Cache.ts
var _Cache;
var Cache = class {
	constructor(populate) {
		_defineProperty(this, "populate", void 0);
		_defineProperty(this, "value", void 0);
		this.populate = populate;
		this.value = void 0;
	}
	getValue() {
		return this.value;
	}
	access() {
		if (!this.value) this.value = this.populate();
		return this.value;
	}
	invalidate() {
		this.value = void 0;
	}
};
_Cache = Cache;
_defineProperty(Cache, "populatedBy", (populate) => new _Cache(populate));
//#endregion
//#region src/core/errors.ts
var MethodNotImplementedError = class extends Error {
	constructor(className, methodName) {
		const msg = `Method ${className}.${methodName}() not implemented`;
		super(msg);
	}
};
var PrivateConstructorError = class extends Error {
	constructor(className) {
		const msg = `Cannot construct ${className} - it has a private constructor`;
		super(msg);
	}
};
var UnexpectedObjectTypeError = class extends Error {
	constructor(expected, actual) {
		const name = (t) => t?.name ?? t?.constructor?.name;
		const msg = `Expected instance of ${(Array.isArray(expected) ? expected.map(name) : [name(expected)]).join(" or ")}, but got instance of ${actual ? name(actual) : actual}`;
		super(msg);
	}
};
var UnsupportedEncodingError = class extends Error {
	constructor(encoding) {
		const msg = `${encoding} stream encoding not supported`;
		super(msg);
	}
};
var ReparseError = class extends Error {
	constructor(className, methodName) {
		const msg = `Cannot call ${className}.${methodName}() more than once`;
		super(msg);
	}
};
var MissingCatalogError = class extends Error {
	constructor(ref) {
		const msg = `Missing catalog (ref=${ref})`;
		super(msg);
	}
};
var MissingPageContentsEmbeddingError = class extends Error {
	constructor() {
		super(`Can't embed page with missing Contents`);
	}
};
var UnrecognizedStreamTypeError = class extends Error {
	constructor(stream) {
		const msg = `Unrecognized stream type: ${stream?.contructor?.name ?? stream?.name ?? stream}`;
		super(msg);
	}
};
var PageEmbeddingMismatchedContextError = class extends Error {
	constructor() {
		super(`Found mismatched contexts while embedding pages. All pages in the array passed to \`PDFDocument.embedPages()\` must be from the same document.`);
	}
};
var PDFArrayIsNotRectangleError = class extends Error {
	constructor(size) {
		const msg = `Attempted to convert PDFArray with ${size} elements to rectangle, but must have exactly 4 elements.`;
		super(msg);
	}
};
var InvalidPDFDateStringError = class extends Error {
	constructor(value) {
		const msg = `Attempted to convert "${value}" to a date, but it does not match the PDF date string format.`;
		super(msg);
	}
};
var InvalidTargetIndexError = class extends Error {
	constructor(targetIndex, Count) {
		const msg = `Invalid targetIndex specified: targetIndex=${targetIndex} must be less than Count=${Count}`;
		super(msg);
	}
};
var CorruptPageTreeError = class extends Error {
	constructor(targetIndex, operation) {
		const msg = `Failed to ${operation} at targetIndex=${targetIndex} due to corrupt page tree: It is likely that one or more 'Count' entries are invalid`;
		super(msg);
	}
};
var IndexOutOfBoundsError = class extends Error {
	constructor(index, min, max) {
		const msg = `index should be at least ${min} and at most ${max}, but was actually ${index}`;
		super(msg);
	}
};
var InvalidAcroFieldValueError = class extends Error {
	constructor() {
		super(`Attempted to set invalid field value`);
	}
};
var MultiSelectValueError = class extends Error {
	constructor() {
		super(`Attempted to select multiple values for single-select field`);
	}
};
var MissingDAEntryError = class extends Error {
	constructor(fieldName) {
		const msg = `No /DA (default appearance) entry found for field: ${fieldName}`;
		super(msg);
	}
};
var MissingTfOperatorError = class extends Error {
	constructor(fieldName) {
		const msg = `No Tf operator found for DA of field: ${fieldName}`;
		super(msg);
	}
};
var NumberParsingError = class extends Error {
	constructor(pos, value) {
		const msg = `Failed to parse number (line:${pos.line} col:${pos.column} offset=${pos.offset}): "${value}"`;
		super(msg);
	}
};
var PDFParsingError = class extends Error {
	constructor(pos, details) {
		const msg = `Failed to parse PDF document (line:${pos.line} col:${pos.column} offset=${pos.offset}): ${details}`;
		super(msg);
	}
};
var NextByteAssertionError = class extends PDFParsingError {
	constructor(pos, expectedByte, actualByte) {
		const msg = `Expected next byte to be ${expectedByte} but it was actually ${actualByte}`;
		super(pos, msg);
	}
};
var PDFObjectParsingError = class extends PDFParsingError {
	constructor(pos, byte) {
		const msg = `Failed to parse PDF object starting with the following byte: ${byte}`;
		super(pos, msg);
	}
};
var PDFInvalidObjectParsingError = class extends PDFParsingError {
	constructor(pos) {
		super(pos, `Failed to parse invalid PDF object`);
	}
};
var PDFStreamParsingError = class extends PDFParsingError {
	constructor(pos) {
		super(pos, `Failed to parse PDF stream`);
	}
};
var UnbalancedParenthesisError = class extends PDFParsingError {
	constructor(pos) {
		super(pos, `Failed to parse PDF literal string due to unbalanced parenthesis`);
	}
};
var DecompressionBombError = class extends Error {
	constructor(requested, maxSize) {
		const msg = `Decompression bomb detected: requested buffer size ${requested} exceeds maximum allowed size ${maxSize}`;
		super(msg);
	}
};
var StalledParserError = class extends PDFParsingError {
	constructor(pos) {
		super(pos, `Parser stalled`);
	}
};
var MissingPDFHeaderError = class extends PDFParsingError {
	constructor(pos) {
		super(pos, `No PDF header found`);
	}
};
var MissingKeywordError = class extends PDFParsingError {
	constructor(pos, keyword) {
		const msg = `Did not find expected keyword '${arrayAsString(keyword)}'`;
		super(pos, msg);
	}
};
//#endregion
//#region src/core/syntax/CharCodes.ts
var CharCodes = /* @__PURE__ */ function(CharCodes) {
	CharCodes[CharCodes["Null"] = 0] = "Null";
	CharCodes[CharCodes["Backspace"] = 8] = "Backspace";
	CharCodes[CharCodes["Tab"] = 9] = "Tab";
	CharCodes[CharCodes["Newline"] = 10] = "Newline";
	CharCodes[CharCodes["FormFeed"] = 12] = "FormFeed";
	CharCodes[CharCodes["CarriageReturn"] = 13] = "CarriageReturn";
	CharCodes[CharCodes["Space"] = 32] = "Space";
	CharCodes[CharCodes["ExclamationPoint"] = 33] = "ExclamationPoint";
	CharCodes[CharCodes["Hash"] = 35] = "Hash";
	CharCodes[CharCodes["Percent"] = 37] = "Percent";
	CharCodes[CharCodes["LeftParen"] = 40] = "LeftParen";
	CharCodes[CharCodes["RightParen"] = 41] = "RightParen";
	CharCodes[CharCodes["Plus"] = 43] = "Plus";
	CharCodes[CharCodes["Minus"] = 45] = "Minus";
	CharCodes[CharCodes["Dash"] = 45] = "Dash";
	CharCodes[CharCodes["Period"] = 46] = "Period";
	CharCodes[CharCodes["ForwardSlash"] = 47] = "ForwardSlash";
	CharCodes[CharCodes["Zero"] = 48] = "Zero";
	CharCodes[CharCodes["One"] = 49] = "One";
	CharCodes[CharCodes["Two"] = 50] = "Two";
	CharCodes[CharCodes["Three"] = 51] = "Three";
	CharCodes[CharCodes["Four"] = 52] = "Four";
	CharCodes[CharCodes["Five"] = 53] = "Five";
	CharCodes[CharCodes["Six"] = 54] = "Six";
	CharCodes[CharCodes["Seven"] = 55] = "Seven";
	CharCodes[CharCodes["Eight"] = 56] = "Eight";
	CharCodes[CharCodes["Nine"] = 57] = "Nine";
	CharCodes[CharCodes["LessThan"] = 60] = "LessThan";
	CharCodes[CharCodes["GreaterThan"] = 62] = "GreaterThan";
	CharCodes[CharCodes["A"] = 65] = "A";
	CharCodes[CharCodes["D"] = 68] = "D";
	CharCodes[CharCodes["E"] = 69] = "E";
	CharCodes[CharCodes["F"] = 70] = "F";
	CharCodes[CharCodes["O"] = 79] = "O";
	CharCodes[CharCodes["P"] = 80] = "P";
	CharCodes[CharCodes["R"] = 82] = "R";
	CharCodes[CharCodes["LeftSquareBracket"] = 91] = "LeftSquareBracket";
	CharCodes[CharCodes["BackSlash"] = 92] = "BackSlash";
	CharCodes[CharCodes["RightSquareBracket"] = 93] = "RightSquareBracket";
	CharCodes[CharCodes["a"] = 97] = "a";
	CharCodes[CharCodes["b"] = 98] = "b";
	CharCodes[CharCodes["d"] = 100] = "d";
	CharCodes[CharCodes["e"] = 101] = "e";
	CharCodes[CharCodes["f"] = 102] = "f";
	CharCodes[CharCodes["i"] = 105] = "i";
	CharCodes[CharCodes["j"] = 106] = "j";
	CharCodes[CharCodes["l"] = 108] = "l";
	CharCodes[CharCodes["m"] = 109] = "m";
	CharCodes[CharCodes["n"] = 110] = "n";
	CharCodes[CharCodes["o"] = 111] = "o";
	CharCodes[CharCodes["r"] = 114] = "r";
	CharCodes[CharCodes["s"] = 115] = "s";
	CharCodes[CharCodes["t"] = 116] = "t";
	CharCodes[CharCodes["u"] = 117] = "u";
	CharCodes[CharCodes["x"] = 120] = "x";
	CharCodes[CharCodes["LeftCurly"] = 123] = "LeftCurly";
	CharCodes[CharCodes["RightCurly"] = 125] = "RightCurly";
	CharCodes[CharCodes["Tilde"] = 126] = "Tilde";
	return CharCodes;
}(CharCodes || {});
//#endregion
//#region src/core/document/PDFHeader.ts
var _PDFHeader;
var PDFHeader = class {
	constructor(major, minor) {
		_defineProperty(this, "major", void 0);
		_defineProperty(this, "minor", void 0);
		this.major = String(major);
		this.minor = String(minor);
	}
	toString() {
		const bc = charFromCode(129);
		return `%PDF-${this.major}.${this.minor}\n%${bc}${bc}${bc}${bc}`;
	}
	sizeInBytes() {
		return 12 + this.major.length + this.minor.length;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.Percent;
		buffer[offset++] = CharCodes.P;
		buffer[offset++] = CharCodes.D;
		buffer[offset++] = CharCodes.F;
		buffer[offset++] = CharCodes.Dash;
		offset += copyStringIntoBuffer(this.major, buffer, offset);
		buffer[offset++] = CharCodes.Period;
		offset += copyStringIntoBuffer(this.minor, buffer, offset);
		buffer[offset++] = CharCodes.Newline;
		buffer[offset++] = CharCodes.Percent;
		buffer[offset++] = 129;
		buffer[offset++] = 129;
		buffer[offset++] = 129;
		buffer[offset++] = 129;
		return offset - initialOffset;
	}
};
_PDFHeader = PDFHeader;
_defineProperty(PDFHeader, "forVersion", (major, minor) => new _PDFHeader(major, minor));
//#endregion
//#region src/core/objects/PDFObject.ts
var PDFObject = class {
	clone(_context) {
		throw new MethodNotImplementedError(this.constructor.name, "clone");
	}
	toString() {
		throw new MethodNotImplementedError(this.constructor.name, "toString");
	}
	sizeInBytes() {
		throw new MethodNotImplementedError(this.constructor.name, "sizeInBytes");
	}
	copyBytesInto(_buffer, _offset) {
		throw new MethodNotImplementedError(this.constructor.name, "copyBytesInto");
	}
};
//#endregion
//#region src/core/objects/PDFNumber.ts
var _PDFNumber;
var PDFNumber = class PDFNumber extends PDFObject {
	constructor(value) {
		super();
		_defineProperty(this, "numberValue", void 0);
		_defineProperty(this, "stringValue", void 0);
		this.numberValue = value;
		this.stringValue = numberToString(value);
	}
	asNumber() {
		return this.numberValue;
	}
	/** @deprecated in favor of [[PDFNumber.asNumber]] */
	value() {
		return this.numberValue;
	}
	clone() {
		return PDFNumber.of(this.numberValue);
	}
	toString() {
		return this.stringValue;
	}
	sizeInBytes() {
		return this.stringValue.length;
	}
	copyBytesInto(buffer, offset) {
		offset += copyStringIntoBuffer(this.stringValue, buffer, offset);
		return this.stringValue.length;
	}
};
_PDFNumber = PDFNumber;
_defineProperty(PDFNumber, "of", (value) => new _PDFNumber(value));
//#endregion
//#region src/core/objects/PDFArray.ts
var _PDFArray;
var PDFArray = class PDFArray extends PDFObject {
	constructor(context) {
		super();
		_defineProperty(this, "array", void 0);
		_defineProperty(this, "context", void 0);
		this.array = [];
		this.context = context;
	}
	size() {
		return this.array.length;
	}
	push(object) {
		this.array.push(object);
	}
	insert(index, object) {
		this.array.splice(index, 0, object);
	}
	indexOf(object) {
		const index = this.array.indexOf(object);
		return index === -1 ? void 0 : index;
	}
	remove(index) {
		this.array.splice(index, 1);
	}
	set(idx, object) {
		this.array[idx] = object;
	}
	get(index) {
		return this.array[index];
	}
	lookupMaybe(index, ...types) {
		return this.context.lookupMaybe(this.get(index), ...types);
	}
	lookup(index, ...types) {
		return this.context.lookup(this.get(index), ...types);
	}
	asRectangle() {
		if (this.size() !== 4) throw new PDFArrayIsNotRectangleError(this.size());
		const lowerLeftX = this.lookup(0, PDFNumber).asNumber();
		const lowerLeftY = this.lookup(1, PDFNumber).asNumber();
		const upperRightX = this.lookup(2, PDFNumber).asNumber();
		const upperRightY = this.lookup(3, PDFNumber).asNumber();
		return {
			x: lowerLeftX,
			y: lowerLeftY,
			width: upperRightX - lowerLeftX,
			height: upperRightY - lowerLeftY
		};
	}
	asArray() {
		return this.array.slice();
	}
	clone(context) {
		const clone = PDFArray.withContext(context || this.context);
		for (let idx = 0, len = this.size(); idx < len; idx++) clone.push(this.array[idx]);
		return clone;
	}
	toString() {
		let arrayString = "[ ";
		for (let idx = 0, len = this.size(); idx < len; idx++) {
			arrayString += this.get(idx).toString();
			arrayString += " ";
		}
		arrayString += "]";
		return arrayString;
	}
	sizeInBytes() {
		let size = 3;
		for (let idx = 0, len = this.size(); idx < len; idx++) size += this.get(idx).sizeInBytes() + 1;
		return size;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.LeftSquareBracket;
		buffer[offset++] = CharCodes.Space;
		for (let idx = 0, len = this.size(); idx < len; idx++) {
			offset += this.get(idx).copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Space;
		}
		buffer[offset++] = CharCodes.RightSquareBracket;
		return offset - initialOffset;
	}
	scalePDFNumbers(x, y) {
		for (let idx = 0, len = this.size(); idx < len; idx++) {
			const el = this.lookup(idx);
			if (el instanceof PDFNumber) {
				const factor = idx % 2 === 0 ? x : y;
				this.set(idx, PDFNumber.of(el.asNumber() * factor));
			}
		}
	}
};
_PDFArray = PDFArray;
_defineProperty(PDFArray, "withContext", (context) => new _PDFArray(context));
//#endregion
//#region src/core/objects/PDFBool.ts
var _PDFBool;
var ENFORCER$2 = {};
var PDFBool = class extends PDFObject {
	constructor(enforcer, value) {
		if (enforcer !== ENFORCER$2) throw new PrivateConstructorError("PDFBool");
		super();
		_defineProperty(this, "value", void 0);
		this.value = value;
	}
	asBoolean() {
		return this.value;
	}
	clone() {
		return this;
	}
	toString() {
		return String(this.value);
	}
	sizeInBytes() {
		return this.value ? 4 : 5;
	}
	copyBytesInto(buffer, offset) {
		if (this.value) {
			buffer[offset++] = CharCodes.t;
			buffer[offset++] = CharCodes.r;
			buffer[offset++] = CharCodes.u;
			buffer[offset++] = CharCodes.e;
			return 4;
		} else {
			buffer[offset++] = CharCodes.f;
			buffer[offset++] = CharCodes.a;
			buffer[offset++] = CharCodes.l;
			buffer[offset++] = CharCodes.s;
			buffer[offset++] = CharCodes.e;
			return 5;
		}
	}
};
_PDFBool = PDFBool;
_defineProperty(PDFBool, "True", new _PDFBool(ENFORCER$2, true));
_defineProperty(PDFBool, "False", new _PDFBool(ENFORCER$2, false));
//#endregion
//#region src/core/syntax/Delimiters.ts
var IsDelimiter = /* @__PURE__ */ new Uint8Array(256);
IsDelimiter[CharCodes.LeftParen] = 1;
IsDelimiter[CharCodes.RightParen] = 1;
IsDelimiter[CharCodes.LessThan] = 1;
IsDelimiter[CharCodes.GreaterThan] = 1;
IsDelimiter[CharCodes.LeftSquareBracket] = 1;
IsDelimiter[CharCodes.RightSquareBracket] = 1;
IsDelimiter[CharCodes.LeftCurly] = 1;
IsDelimiter[CharCodes.RightCurly] = 1;
IsDelimiter[CharCodes.ForwardSlash] = 1;
IsDelimiter[CharCodes.Percent] = 1;
//#endregion
//#region src/core/syntax/Whitespace.ts
var IsWhitespace = /* @__PURE__ */ new Uint8Array(256);
IsWhitespace[CharCodes.Null] = 1;
IsWhitespace[CharCodes.Tab] = 1;
IsWhitespace[CharCodes.Newline] = 1;
IsWhitespace[CharCodes.FormFeed] = 1;
IsWhitespace[CharCodes.CarriageReturn] = 1;
IsWhitespace[CharCodes.Space] = 1;
//#endregion
//#region src/core/syntax/Irregular.ts
var IsIrregular = /* @__PURE__ */ new Uint8Array(256);
for (let idx = 0, len = 256; idx < len; idx++) IsIrregular[idx] = IsWhitespace[idx] || IsDelimiter[idx] ? 1 : 0;
IsIrregular[CharCodes.Hash] = 1;
//#endregion
//#region src/core/objects/PDFName.ts
var _PDFName;
var decodeName = (name) => name.replace(/#([\dABCDEF]{2})/g, (_, hex) => charFromHexCode(hex));
var isRegularChar = (charCode) => charCode >= CharCodes.ExclamationPoint && charCode <= CharCodes.Tilde && !IsIrregular[charCode];
var ENFORCER$1 = {};
var pool$1 = /* @__PURE__ */ new Map();
var PDFName = class extends PDFObject {
	constructor(enforcer, name) {
		if (enforcer !== ENFORCER$1) throw new PrivateConstructorError("PDFName");
		super();
		_defineProperty(this, "encodedName", void 0);
		let encodedName = "/";
		for (let idx = 0, len = name.length; idx < len; idx++) {
			const character = name[idx];
			const code = toCharCode(character);
			encodedName += isRegularChar(code) ? character : `#${toHexString(code)}`;
		}
		this.encodedName = encodedName;
	}
	asBytes() {
		const bytes = [];
		let hex = "";
		let escaped = false;
		const pushByte = (byte) => {
			if (byte !== void 0) bytes.push(byte);
			escaped = false;
		};
		for (let idx = 1, len = this.encodedName.length; idx < len; idx++) {
			const char = this.encodedName[idx];
			const byte = toCharCode(char);
			const nextChar = this.encodedName[idx + 1];
			if (!escaped) if (byte === CharCodes.Hash) escaped = true;
			else pushByte(byte);
			else if (byte >= CharCodes.Zero && byte <= CharCodes.Nine || byte >= CharCodes.a && byte <= CharCodes.f || byte >= CharCodes.A && byte <= CharCodes.F) {
				hex += char;
				if (hex.length === 2 || !(nextChar >= "0" && nextChar <= "9" || nextChar >= "a" && nextChar <= "f" || nextChar >= "A" && nextChar <= "F")) {
					pushByte(parseInt(hex, 16));
					hex = "";
				}
			} else pushByte(byte);
		}
		return new Uint8Array(bytes);
	}
	decodeText() {
		const bytes = this.asBytes();
		return String.fromCharCode(...Array.from(bytes));
	}
	asString() {
		return this.encodedName;
	}
	/** @deprecated in favor of [[PDFName.asString]] */
	value() {
		return this.encodedName;
	}
	clone() {
		return this;
	}
	toString() {
		return this.encodedName;
	}
	sizeInBytes() {
		return this.encodedName.length;
	}
	copyBytesInto(buffer, offset) {
		offset += copyStringIntoBuffer(this.encodedName, buffer, offset);
		return this.encodedName.length;
	}
};
_PDFName = PDFName;
_defineProperty(PDFName, "of", (name) => {
	const decodedValue = decodeName(name);
	let instance = pool$1.get(decodedValue);
	if (!instance) {
		instance = new _PDFName(ENFORCER$1, decodedValue);
		pool$1.set(decodedValue, instance);
	}
	return instance;
});
_defineProperty(PDFName, "Length", _PDFName.of("Length"));
_defineProperty(PDFName, "FlateDecode", _PDFName.of("FlateDecode"));
_defineProperty(PDFName, "Resources", _PDFName.of("Resources"));
_defineProperty(PDFName, "Font", _PDFName.of("Font"));
_defineProperty(PDFName, "XObject", _PDFName.of("XObject"));
_defineProperty(PDFName, "ExtGState", _PDFName.of("ExtGState"));
_defineProperty(PDFName, "Contents", _PDFName.of("Contents"));
_defineProperty(PDFName, "Type", _PDFName.of("Type"));
_defineProperty(PDFName, "Parent", _PDFName.of("Parent"));
_defineProperty(PDFName, "MediaBox", _PDFName.of("MediaBox"));
_defineProperty(PDFName, "Page", _PDFName.of("Page"));
_defineProperty(PDFName, "Annots", _PDFName.of("Annots"));
_defineProperty(PDFName, "TrimBox", _PDFName.of("TrimBox"));
_defineProperty(PDFName, "ArtBox", _PDFName.of("ArtBox"));
_defineProperty(PDFName, "BleedBox", _PDFName.of("BleedBox"));
_defineProperty(PDFName, "CropBox", _PDFName.of("CropBox"));
_defineProperty(PDFName, "Rotate", _PDFName.of("Rotate"));
_defineProperty(PDFName, "Title", _PDFName.of("Title"));
_defineProperty(PDFName, "Author", _PDFName.of("Author"));
_defineProperty(PDFName, "Subject", _PDFName.of("Subject"));
_defineProperty(PDFName, "Creator", _PDFName.of("Creator"));
_defineProperty(PDFName, "Keywords", _PDFName.of("Keywords"));
_defineProperty(PDFName, "Producer", _PDFName.of("Producer"));
_defineProperty(PDFName, "CreationDate", _PDFName.of("CreationDate"));
_defineProperty(PDFName, "ModDate", _PDFName.of("ModDate"));
//#endregion
//#region src/core/objects/PDFNull.ts
var PDFNull = class extends PDFObject {
	asNull() {
		return null;
	}
	clone() {
		return this;
	}
	toString() {
		return "null";
	}
	sizeInBytes() {
		return 4;
	}
	copyBytesInto(buffer, offset) {
		buffer[offset++] = CharCodes.n;
		buffer[offset++] = CharCodes.u;
		buffer[offset++] = CharCodes.l;
		buffer[offset++] = CharCodes.l;
		return 4;
	}
};
var PDFNull_default = new PDFNull();
//#endregion
//#region src/core/objects/PDFDict.ts
var _PDFDict;
var PDFDict = class PDFDict extends PDFObject {
	constructor(map, context) {
		super();
		_defineProperty(this, "context", void 0);
		_defineProperty(this, "dict", void 0);
		_defineProperty(this, "suppressEncryption", false);
		this.dict = map;
		this.context = context;
	}
	keys() {
		return Array.from(this.dict.keys());
	}
	values() {
		return Array.from(this.dict.values());
	}
	entries() {
		return Array.from(this.dict.entries());
	}
	set(key, value) {
		this.dict.set(key, value);
	}
	get(key, preservePDFNull = false) {
		const value = this.dict.get(key);
		if (value === PDFNull_default && !preservePDFNull) return void 0;
		return value;
	}
	has(key) {
		const value = this.dict.get(key);
		return value !== void 0 && value !== PDFNull_default;
	}
	lookupMaybe(key, ...types) {
		const preservePDFNull = types.includes(PDFNull_default);
		const value = this.context.lookupMaybe(this.get(key, preservePDFNull), ...types);
		if (value === PDFNull_default && !preservePDFNull) return void 0;
		return value;
	}
	lookup(key, ...types) {
		const preservePDFNull = types.includes(PDFNull_default);
		const value = this.context.lookup(this.get(key, preservePDFNull), ...types);
		if (value === PDFNull_default && !preservePDFNull) return void 0;
		return value;
	}
	delete(key) {
		return this.dict.delete(key);
	}
	asMap() {
		return new Map(this.dict);
	}
	/** Generate a random key that doesn't exist in current key set */
	uniqueKey(tag = "") {
		const existingKeys = this.keys();
		let key = PDFName.of(this.context.addRandomSuffix(tag, 10));
		while (existingKeys.includes(key)) key = PDFName.of(this.context.addRandomSuffix(tag, 10));
		return key;
	}
	clone(context) {
		const clone = PDFDict.withContext(context || this.context);
		const entries = this.entries();
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [key, value] = entries[idx];
			clone.set(key, value);
		}
		return clone;
	}
	toString() {
		let dictString = "<<\n";
		const entries = this.entries();
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [key, value] = entries[idx];
			dictString += key.toString() + " " + value.toString() + "\n";
		}
		dictString += ">>";
		return dictString;
	}
	sizeInBytes() {
		let size = 5;
		const entries = this.entries();
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [key, value] = entries[idx];
			size += key.sizeInBytes() + value.sizeInBytes() + 2;
		}
		return size;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.LessThan;
		buffer[offset++] = CharCodes.LessThan;
		buffer[offset++] = CharCodes.Newline;
		const entries = this.entries();
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [key, value] = entries[idx];
			offset += key.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Space;
			offset += value.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
		}
		buffer[offset++] = CharCodes.GreaterThan;
		buffer[offset++] = CharCodes.GreaterThan;
		return offset - initialOffset;
	}
};
_PDFDict = PDFDict;
_defineProperty(PDFDict, "withContext", (context) => new _PDFDict(/* @__PURE__ */ new Map(), context));
_defineProperty(PDFDict, "fromMapWithContext", (map, context) => new _PDFDict(map, context));
//#endregion
//#region src/core/objects/PDFStream.ts
var PDFStream = class extends PDFObject {
	constructor(dict) {
		super();
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	clone(_context) {
		throw new MethodNotImplementedError(this.constructor.name, "clone");
	}
	getContentsString() {
		throw new MethodNotImplementedError(this.constructor.name, "getContentsString");
	}
	getContents() {
		throw new MethodNotImplementedError(this.constructor.name, "getContents");
	}
	getContentsSize() {
		throw new MethodNotImplementedError(this.constructor.name, "getContentsSize");
	}
	updateDict() {
		const contentsSize = this.getContentsSize();
		this.dict.set(PDFName.Length, PDFNumber.of(contentsSize));
	}
	sizeInBytes() {
		this.updateDict();
		return this.dict.sizeInBytes() + this.getContentsSize() + 18;
	}
	toString() {
		this.updateDict();
		let streamString = this.dict.toString();
		streamString += "\nstream\n";
		streamString += this.getContentsString();
		streamString += "\nendstream";
		return streamString;
	}
	copyBytesInto(buffer, offset) {
		this.updateDict();
		const initialOffset = offset;
		offset += this.dict.copyBytesInto(buffer, offset);
		buffer[offset++] = CharCodes.Newline;
		buffer[offset++] = CharCodes.s;
		buffer[offset++] = CharCodes.t;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.a;
		buffer[offset++] = CharCodes.m;
		buffer[offset++] = CharCodes.Newline;
		const contents = this.getContents();
		for (let idx = 0, len = contents.length; idx < len; idx++) buffer[offset++] = contents[idx];
		buffer[offset++] = CharCodes.Newline;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.n;
		buffer[offset++] = CharCodes.d;
		buffer[offset++] = CharCodes.s;
		buffer[offset++] = CharCodes.t;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.a;
		buffer[offset++] = CharCodes.m;
		return offset - initialOffset;
	}
};
//#endregion
//#region src/core/objects/PDFRawStream.ts
var _PDFRawStream;
var PDFRawStream = class PDFRawStream extends PDFStream {
	constructor(dict, contents, transform) {
		super(dict);
		_defineProperty(this, "contents", void 0);
		_defineProperty(this, "transform", void 0);
		this.contents = contents;
		this.transform = transform;
	}
	asUint8Array() {
		return this.contents.slice();
	}
	clone(context) {
		return PDFRawStream.of(this.dict.clone(context), this.contents.slice());
	}
	getContentsString() {
		return arrayAsString(this.contents);
	}
	getContents() {
		return this.contents;
	}
	getContentsSize() {
		return this.contents.length;
	}
};
_PDFRawStream = PDFRawStream;
_defineProperty(PDFRawStream, "of", (dict, contents, transform) => new _PDFRawStream(dict, contents, transform));
//#endregion
//#region src/core/objects/PDFRef.ts
var _PDFRef;
var ENFORCER = {};
var pool = /* @__PURE__ */ new Map();
var PDFRef = class extends PDFObject {
	constructor(enforcer, objectNumber, generationNumber) {
		if (enforcer !== ENFORCER) throw new PrivateConstructorError("PDFRef");
		super();
		_defineProperty(this, "objectNumber", void 0);
		_defineProperty(this, "generationNumber", void 0);
		_defineProperty(this, "tag", void 0);
		this.objectNumber = objectNumber;
		this.generationNumber = generationNumber;
		this.tag = `${objectNumber} ${generationNumber} R`;
	}
	clone() {
		return this;
	}
	toString() {
		return this.tag;
	}
	sizeInBytes() {
		return this.tag.length;
	}
	copyBytesInto(buffer, offset) {
		offset += copyStringIntoBuffer(this.tag, buffer, offset);
		return this.tag.length;
	}
};
_PDFRef = PDFRef;
_defineProperty(PDFRef, "of", (objectNumber, generationNumber = 0) => {
	const tag = `${objectNumber} ${generationNumber} R`;
	let instance = pool.get(tag);
	if (!instance) {
		instance = new _PDFRef(ENFORCER, objectNumber, generationNumber);
		pool.set(tag, instance);
	}
	return instance;
});
//#endregion
//#region src/core/operators/PDFOperator.ts
var _PDFOperator;
var PDFOperator = class PDFOperator {
	constructor(name, args) {
		_defineProperty(this, "name", void 0);
		_defineProperty(this, "args", void 0);
		this.name = name;
		this.args = args || [];
	}
	clone(context) {
		const args = Array(this.args.length);
		for (let idx = 0, len = args.length; idx < len; idx++) {
			const arg = this.args[idx];
			args[idx] = arg instanceof PDFObject ? arg.clone(context) : arg;
		}
		return PDFOperator.of(this.name, args);
	}
	toString() {
		let value = "";
		for (let idx = 0, len = this.args.length; idx < len; idx++) value += String(this.args[idx]) + " ";
		value += this.name;
		return value;
	}
	sizeInBytes() {
		let size = 0;
		for (let idx = 0, len = this.args.length; idx < len; idx++) {
			const arg = this.args[idx];
			size += (arg instanceof PDFObject ? arg.sizeInBytes() : arg.length) + 1;
		}
		size += this.name.length;
		return size;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		for (let idx = 0, len = this.args.length; idx < len; idx++) {
			const arg = this.args[idx];
			if (arg instanceof PDFObject) offset += arg.copyBytesInto(buffer, offset);
			else offset += copyStringIntoBuffer(arg, buffer, offset);
			buffer[offset++] = CharCodes.Space;
		}
		offset += copyStringIntoBuffer(this.name, buffer, offset);
		return offset - initialOffset;
	}
};
_PDFOperator = PDFOperator;
_defineProperty(PDFOperator, "of", (name, args) => new _PDFOperator(name, args));
//#endregion
//#region src/core/operators/PDFOperatorNames.ts
var PDFOperatorNames = /* @__PURE__ */ function(PDFOperatorNames) {
	PDFOperatorNames["NonStrokingColor"] = "sc";
	PDFOperatorNames["NonStrokingColorN"] = "scn";
	PDFOperatorNames["NonStrokingColorRgb"] = "rg";
	PDFOperatorNames["NonStrokingColorGray"] = "g";
	PDFOperatorNames["NonStrokingColorCmyk"] = "k";
	PDFOperatorNames["NonStrokingColorspace"] = "cs";
	PDFOperatorNames["StrokingColor"] = "SC";
	PDFOperatorNames["StrokingColorN"] = "SCN";
	PDFOperatorNames["StrokingColorRgb"] = "RG";
	PDFOperatorNames["StrokingColorGray"] = "G";
	PDFOperatorNames["StrokingColorCmyk"] = "K";
	PDFOperatorNames["StrokingColorspace"] = "CS";
	PDFOperatorNames["BeginMarkedContentSequence"] = "BDC";
	PDFOperatorNames["BeginMarkedContent"] = "BMC";
	PDFOperatorNames["EndMarkedContent"] = "EMC";
	PDFOperatorNames["MarkedContentPointWithProps"] = "DP";
	PDFOperatorNames["MarkedContentPoint"] = "MP";
	PDFOperatorNames["DrawObject"] = "Do";
	PDFOperatorNames["ConcatTransformationMatrix"] = "cm";
	PDFOperatorNames["PopGraphicsState"] = "Q";
	PDFOperatorNames["PushGraphicsState"] = "q";
	PDFOperatorNames["SetFlatness"] = "i";
	PDFOperatorNames["SetGraphicsStateParams"] = "gs";
	PDFOperatorNames["SetLineCapStyle"] = "J";
	PDFOperatorNames["SetLineDashPattern"] = "d";
	PDFOperatorNames["SetLineJoinStyle"] = "j";
	PDFOperatorNames["SetLineMiterLimit"] = "M";
	PDFOperatorNames["SetLineWidth"] = "w";
	PDFOperatorNames["SetTextMatrix"] = "Tm";
	PDFOperatorNames["SetRenderingIntent"] = "ri";
	PDFOperatorNames["AppendRectangle"] = "re";
	PDFOperatorNames["BeginInlineImage"] = "BI";
	PDFOperatorNames["BeginInlineImageData"] = "ID";
	PDFOperatorNames["EndInlineImage"] = "EI";
	PDFOperatorNames["ClipEvenOdd"] = "W*";
	PDFOperatorNames["ClipNonZero"] = "W";
	PDFOperatorNames["CloseAndStroke"] = "s";
	PDFOperatorNames["CloseFillEvenOddAndStroke"] = "b*";
	PDFOperatorNames["CloseFillNonZeroAndStroke"] = "b";
	PDFOperatorNames["ClosePath"] = "h";
	PDFOperatorNames["AppendBezierCurve"] = "c";
	PDFOperatorNames["CurveToReplicateFinalPoint"] = "y";
	PDFOperatorNames["CurveToReplicateInitialPoint"] = "v";
	PDFOperatorNames["EndPath"] = "n";
	PDFOperatorNames["FillEvenOddAndStroke"] = "B*";
	PDFOperatorNames["FillEvenOdd"] = "f*";
	PDFOperatorNames["FillNonZeroAndStroke"] = "B";
	PDFOperatorNames["FillNonZero"] = "f";
	PDFOperatorNames["LegacyFillNonZero"] = "F";
	PDFOperatorNames["LineTo"] = "l";
	PDFOperatorNames["MoveTo"] = "m";
	PDFOperatorNames["ShadingFill"] = "sh";
	PDFOperatorNames["StrokePath"] = "S";
	PDFOperatorNames["BeginText"] = "BT";
	PDFOperatorNames["EndText"] = "ET";
	PDFOperatorNames["MoveText"] = "Td";
	PDFOperatorNames["MoveTextSetLeading"] = "TD";
	PDFOperatorNames["NextLine"] = "T*";
	PDFOperatorNames["SetCharacterSpacing"] = "Tc";
	PDFOperatorNames["SetFontAndSize"] = "Tf";
	PDFOperatorNames["SetTextHorizontalScaling"] = "Tz";
	PDFOperatorNames["SetTextLineHeight"] = "TL";
	PDFOperatorNames["SetTextRenderingMode"] = "Tr";
	PDFOperatorNames["SetTextRise"] = "Ts";
	PDFOperatorNames["SetWordSpacing"] = "Tw";
	PDFOperatorNames["ShowText"] = "Tj";
	PDFOperatorNames["ShowTextAdjusted"] = "TJ";
	PDFOperatorNames["ShowTextLine"] = "'";
	PDFOperatorNames["ShowTextLineAndSpace"] = "\"";
	PDFOperatorNames["Type3D0"] = "d0";
	PDFOperatorNames["Type3D1"] = "d1";
	PDFOperatorNames["BeginCompatibilitySection"] = "BX";
	PDFOperatorNames["EndCompatibilitySection"] = "EX";
	return PDFOperatorNames;
}(PDFOperatorNames || {});
//#endregion
//#region src/core/structures/PDFFlateStream.ts
var PDFFlateStream = class extends PDFStream {
	constructor(dict, encode) {
		super(dict);
		_defineProperty(this, "contentsCache", void 0);
		_defineProperty(this, "encode", void 0);
		_defineProperty(this, "computeContents", () => {
			const unencodedContents = this.getUnencodedContents();
			return this.encode ? pako.deflate(unencodedContents) : unencodedContents;
		});
		this.encode = encode;
		if (encode) dict.set(PDFName.of("Filter"), PDFName.of("FlateDecode"));
		this.contentsCache = Cache.populatedBy(this.computeContents);
	}
	getContents() {
		return this.contentsCache.access();
	}
	getContentsSize() {
		return this.contentsCache.access().length;
	}
	getUnencodedContents() {
		throw new MethodNotImplementedError(this.constructor.name, "getUnencodedContents");
	}
};
//#endregion
//#region src/core/structures/PDFContentStream.ts
var _PDFContentStream;
var PDFContentStream = class PDFContentStream extends PDFFlateStream {
	constructor(dict, operators, encode = true) {
		super(dict, encode);
		_defineProperty(this, "operators", void 0);
		this.operators = operators;
	}
	push(...operators) {
		this.operators.push(...operators);
	}
	clone(context) {
		const operators = Array(this.operators.length);
		for (let idx = 0, len = this.operators.length; idx < len; idx++) operators[idx] = this.operators[idx].clone(context);
		const { dict, encode } = this;
		return PDFContentStream.of(dict.clone(context), operators, encode);
	}
	getContentsString() {
		let value = "";
		for (let idx = 0, len = this.operators.length; idx < len; idx++) value += `${this.operators[idx]}\n`;
		return value;
	}
	getUnencodedContents() {
		const buffer = new Uint8Array(this.getUnencodedContentsSize());
		let offset = 0;
		for (let idx = 0, len = this.operators.length; idx < len; idx++) {
			offset += this.operators[idx].copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
		}
		return buffer;
	}
	getUnencodedContentsSize() {
		let size = 0;
		for (let idx = 0, len = this.operators.length; idx < len; idx++) size += this.operators[idx].sizeInBytes() + 1;
		return size;
	}
};
_PDFContentStream = PDFContentStream;
_defineProperty(PDFContentStream, "of", (dict, operators, encode = true) => new _PDFContentStream(dict, operators, encode));
//#endregion
//#region src/utils/rng.ts
var _SimpleRNG;
/**
* Generates a pseudo random number. Although it is not cryptographically secure
* and uniformly distributed, it is not a concern for the intended use-case,
* which is to generate distinct numbers.
*
* Credit: https://stackoverflow.com/a/19303725/10254049
*/
var SimpleRNG = class {
	constructor(seed) {
		_defineProperty(this, "seed", void 0);
		this.seed = seed;
	}
	nextInt() {
		const x = Math.sin(this.seed++) * 1e4;
		return x - Math.floor(x);
	}
};
_SimpleRNG = SimpleRNG;
_defineProperty(SimpleRNG, "withSeed", (seed) => new _SimpleRNG(seed));
//#endregion
//#region src/core/PDFContext.ts
var _PDFContext;
var byAscendingObjectNumber = ([a], [b]) => a.objectNumber - b.objectNumber;
var PDFContext = class {
	constructor() {
		_defineProperty(this, "isDecrypted", true);
		_defineProperty(this, "largestObjectNumber", void 0);
		_defineProperty(this, "header", void 0);
		_defineProperty(this, "trailerInfo", void 0);
		_defineProperty(this, "rng", void 0);
		_defineProperty(this, "indirectObjects", void 0);
		_defineProperty(this, "pushGraphicsStateContentStreamRef", void 0);
		_defineProperty(this, "popGraphicsStateContentStreamRef", void 0);
		this.largestObjectNumber = 0;
		this.header = PDFHeader.forVersion(1, 7);
		this.trailerInfo = {};
		this.indirectObjects = /* @__PURE__ */ new Map();
		this.rng = SimpleRNG.withSeed(1);
	}
	assign(ref, object) {
		this.indirectObjects.set(ref, object);
		if (ref.objectNumber > this.largestObjectNumber) this.largestObjectNumber = ref.objectNumber;
	}
	nextRef() {
		this.largestObjectNumber += 1;
		return PDFRef.of(this.largestObjectNumber);
	}
	register(object) {
		const ref = this.nextRef();
		this.assign(ref, object);
		return ref;
	}
	delete(ref) {
		return this.indirectObjects.delete(ref);
	}
	lookupMaybe(ref, ...types) {
		const preservePDFNull = types.includes(PDFNull_default);
		const result = ref instanceof PDFRef ? this.indirectObjects.get(ref) : ref;
		if (!result || result === PDFNull_default && !preservePDFNull) return void 0;
		for (let idx = 0, len = types.length; idx < len; idx++) {
			const type = types[idx];
			if (type === PDFNull_default) {
				if (result === PDFNull_default) return result;
			} else if (result instanceof type) return result;
		}
		throw new UnexpectedObjectTypeError(types, result);
	}
	lookup(ref, ...types) {
		const result = ref instanceof PDFRef ? this.indirectObjects.get(ref) : ref;
		if (types.length === 0) return result;
		for (let idx = 0, len = types.length; idx < len; idx++) {
			const type = types[idx];
			if (type === PDFNull_default) {
				if (result === PDFNull_default) return result;
			} else if (result instanceof type) return result;
		}
		throw new UnexpectedObjectTypeError(types, result);
	}
	getObjectRef(pdfObject) {
		const entries = Array.from(this.indirectObjects.entries());
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [ref, object] = entries[idx];
			if (object === pdfObject) return ref;
		}
	}
	enumerateIndirectObjects() {
		return Array.from(this.indirectObjects.entries()).sort(byAscendingObjectNumber);
	}
	obj(literal) {
		if (literal instanceof PDFObject) return literal;
		else if (literal === null || literal === void 0) return PDFNull_default;
		else if (typeof literal === "string") return PDFName.of(literal);
		else if (typeof literal === "number") return PDFNumber.of(literal);
		else if (typeof literal === "boolean") return literal ? PDFBool.True : PDFBool.False;
		else if (Array.isArray(literal)) {
			const array = PDFArray.withContext(this);
			for (let idx = 0, len = literal.length; idx < len; idx++) array.push(this.obj(literal[idx]));
			return array;
		} else {
			const dict = PDFDict.withContext(this);
			const keys = Object.keys(literal);
			for (let idx = 0, len = keys.length; idx < len; idx++) {
				const key = keys[idx];
				const value = literal[key];
				if (value !== void 0) dict.set(PDFName.of(key), this.obj(value));
			}
			return dict;
		}
	}
	stream(contents, dict = {}) {
		return PDFRawStream.of(this.obj(dict), typedArrayFor(contents));
	}
	flateStream(contents, dict = {}) {
		return this.stream(pako.deflate(typedArrayFor(contents)), {
			...dict,
			Filter: "FlateDecode"
		});
	}
	contentStream(operators, dict = {}) {
		return PDFContentStream.of(this.obj(dict), operators);
	}
	formXObject(operators, dict = {}) {
		return this.contentStream(operators, {
			BBox: this.obj([
				0,
				0,
				0,
				0
			]),
			Matrix: this.obj([
				1,
				0,
				0,
				1,
				0,
				0
			]),
			...dict,
			Type: "XObject",
			Subtype: "Form"
		});
	}
	getPushGraphicsStateContentStream() {
		if (this.pushGraphicsStateContentStreamRef) return this.pushGraphicsStateContentStreamRef;
		const dict = this.obj({});
		const op = PDFOperator.of(PDFOperatorNames.PushGraphicsState);
		const stream = PDFContentStream.of(dict, [op]);
		this.pushGraphicsStateContentStreamRef = this.register(stream);
		return this.pushGraphicsStateContentStreamRef;
	}
	getPopGraphicsStateContentStream() {
		if (this.popGraphicsStateContentStreamRef) return this.popGraphicsStateContentStreamRef;
		const dict = this.obj({});
		const op = PDFOperator.of(PDFOperatorNames.PopGraphicsState);
		const stream = PDFContentStream.of(dict, [op]);
		this.popGraphicsStateContentStreamRef = this.register(stream);
		return this.popGraphicsStateContentStreamRef;
	}
	addRandomSuffix(prefix, suffixLength = 4) {
		return `${prefix}-${Math.floor(this.rng.nextInt() * 10 ** suffixLength)}`;
	}
};
_PDFContext = PDFContext;
_defineProperty(PDFContext, "create", () => new _PDFContext());
//#endregion
//#region src/core/structures/PDFPageLeaf.ts
var _PDFPageLeaf;
var PDFPageLeaf = class PDFPageLeaf extends PDFDict {
	constructor(map, context, autoNormalizeCTM = true) {
		super(map, context);
		_defineProperty(this, "normalized", false);
		_defineProperty(this, "autoNormalizeCTM", void 0);
		this.autoNormalizeCTM = autoNormalizeCTM;
	}
	clone(context) {
		const clone = PDFPageLeaf.fromMapWithContext(/* @__PURE__ */ new Map(), context || this.context, this.autoNormalizeCTM);
		const entries = this.entries();
		for (let idx = 0, len = entries.length; idx < len; idx++) {
			const [key, value] = entries[idx];
			clone.set(key, value);
		}
		return clone;
	}
	Parent() {
		return this.lookupMaybe(PDFName.Parent, PDFDict);
	}
	Contents() {
		return this.lookup(PDFName.of("Contents"));
	}
	Annots() {
		return this.lookupMaybe(PDFName.Annots, PDFArray);
	}
	BleedBox() {
		return this.lookupMaybe(PDFName.BleedBox, PDFArray);
	}
	TrimBox() {
		return this.lookupMaybe(PDFName.TrimBox, PDFArray);
	}
	ArtBox() {
		return this.lookupMaybe(PDFName.ArtBox, PDFArray);
	}
	Resources() {
		const dictOrRef = this.getInheritableAttribute(PDFName.Resources);
		return this.context.lookupMaybe(dictOrRef, PDFDict);
	}
	MediaBox() {
		const arrayOrRef = this.getInheritableAttribute(PDFName.MediaBox);
		return this.context.lookup(arrayOrRef, PDFArray);
	}
	CropBox() {
		const arrayOrRef = this.getInheritableAttribute(PDFName.CropBox);
		return this.context.lookupMaybe(arrayOrRef, PDFArray);
	}
	Rotate() {
		const numberOrRef = this.getInheritableAttribute(PDFName.Rotate);
		return this.context.lookupMaybe(numberOrRef, PDFNumber);
	}
	getInheritableAttribute(name) {
		let attribute;
		this.ascend((node) => {
			if (!attribute) attribute = node.get(name);
		});
		return attribute;
	}
	setParent(parentRef) {
		this.set(PDFName.Parent, parentRef);
	}
	addContentStream(contentStreamRef) {
		const Contents = this.normalizedEntries().Contents || this.context.obj([]);
		this.set(PDFName.Contents, Contents);
		Contents.push(contentStreamRef);
	}
	wrapContentStreams(startStream, endStream) {
		const Contents = this.Contents();
		if (Contents instanceof PDFArray) {
			Contents.insert(0, startStream);
			Contents.push(endStream);
			return true;
		}
		return false;
	}
	addAnnot(annotRef) {
		const { Annots } = this.normalizedEntries();
		Annots.push(annotRef);
	}
	removeAnnot(annotRef) {
		const { Annots } = this.normalizedEntries();
		const index = Annots.indexOf(annotRef);
		if (index !== void 0) Annots.remove(index);
	}
	setFontDictionary(name, fontDictRef) {
		const { Font } = this.normalizedEntries();
		Font.set(name, fontDictRef);
	}
	newFontDictionaryKey(tag) {
		const { Font } = this.normalizedEntries();
		return Font.uniqueKey(tag);
	}
	newFontDictionary(tag, fontDictRef) {
		const key = this.newFontDictionaryKey(tag);
		this.setFontDictionary(key, fontDictRef);
		return key;
	}
	setXObject(name, xObjectRef) {
		const { XObject } = this.normalizedEntries();
		XObject.set(name, xObjectRef);
	}
	newXObjectKey(tag) {
		const { XObject } = this.normalizedEntries();
		return XObject.uniqueKey(tag);
	}
	newXObject(tag, xObjectRef) {
		const key = this.newXObjectKey(tag);
		this.setXObject(key, xObjectRef);
		return key;
	}
	setExtGState(name, extGStateRef) {
		const { ExtGState } = this.normalizedEntries();
		ExtGState.set(name, extGStateRef);
	}
	newExtGStateKey(tag) {
		const { ExtGState } = this.normalizedEntries();
		return ExtGState.uniqueKey(tag);
	}
	newExtGState(tag, extGStateRef) {
		const key = this.newExtGStateKey(tag);
		this.setExtGState(key, extGStateRef);
		return key;
	}
	ascend(visitor) {
		visitor(this);
		const Parent = this.Parent();
		if (Parent) Parent.ascend(visitor);
	}
	normalize() {
		if (this.normalized) return;
		const { context } = this;
		const contentsRef = this.get(PDFName.Contents);
		if (this.context.lookup(contentsRef) instanceof PDFStream) this.set(PDFName.Contents, context.obj([contentsRef]));
		if (this.autoNormalizeCTM) this.wrapContentStreams(this.context.getPushGraphicsStateContentStream(), this.context.getPopGraphicsStateContentStream());
		const dictOrRef = this.getInheritableAttribute(PDFName.Resources);
		const Resources = context.lookupMaybe(dictOrRef, PDFDict) || context.obj({});
		this.set(PDFName.Resources, Resources);
		const Font = Resources.lookupMaybe(PDFName.Font, PDFDict) || context.obj({});
		Resources.set(PDFName.Font, Font);
		const XObject = Resources.lookupMaybe(PDFName.XObject, PDFDict) || context.obj({});
		Resources.set(PDFName.XObject, XObject);
		const ExtGState = Resources.lookupMaybe(PDFName.ExtGState, PDFDict) || context.obj({});
		Resources.set(PDFName.ExtGState, ExtGState);
		const Annots = this.Annots() || context.obj([]);
		this.set(PDFName.Annots, Annots);
		this.normalized = true;
	}
	normalizedEntries() {
		this.normalize();
		const Annots = this.Annots();
		const Resources = this.Resources();
		return {
			Annots,
			Resources,
			Contents: this.Contents(),
			Font: Resources.lookup(PDFName.Font, PDFDict),
			XObject: Resources.lookup(PDFName.XObject, PDFDict),
			ExtGState: Resources.lookup(PDFName.ExtGState, PDFDict)
		};
	}
};
_PDFPageLeaf = PDFPageLeaf;
_defineProperty(PDFPageLeaf, "InheritableEntries", [
	"Resources",
	"MediaBox",
	"CropBox",
	"Rotate"
]);
_defineProperty(PDFPageLeaf, "withContextAndParent", (context, parent) => {
	const dict = /* @__PURE__ */ new Map();
	dict.set(PDFName.Type, PDFName.Page);
	dict.set(PDFName.Parent, parent);
	dict.set(PDFName.Resources, context.obj({}));
	dict.set(PDFName.MediaBox, context.obj([
		0,
		0,
		612,
		792
	]));
	return new _PDFPageLeaf(dict, context, false);
});
_defineProperty(PDFPageLeaf, "fromMapWithContext", (map, context, autoNormalizeCTM = true) => new _PDFPageLeaf(map, context, autoNormalizeCTM));
//#endregion
//#region src/core/PDFObjectCopier.ts
var _PDFObjectCopier;
/**
* PDFObjectCopier copies PDFObjects from a src context to a dest context.
* The primary use case for this is to copy pages between PDFs.
*
* _Copying_ an object with a PDFObjectCopier is different from _cloning_ an
* object with its [[PDFObject.clone]] method:
*
* ```
*   const src: PDFContext = ...
*   const dest: PDFContext = ...
*   const originalObject: PDFObject = ...
*   const copiedObject = PDFObjectCopier.for(src, dest).copy(originalObject);
*   const clonedObject = originalObject.clone();
* ```
*
* Copying an object is equivalent to cloning it and then copying over any other
* objects that it references. Note that only dictionaries, arrays, and streams
* (or structures build from them) can contain indirect references to other
* objects. Copying a PDFObject that is not a dictionary, array, or stream is
* supported, but is equivalent to cloning it.
*/
var PDFObjectCopier = class {
	constructor(src, dest) {
		_defineProperty(this, "src", void 0);
		_defineProperty(this, "dest", void 0);
		_defineProperty(this, "traversedObjects", /* @__PURE__ */ new Map());
		_defineProperty(this, "copy", (object) => object instanceof PDFPageLeaf ? this.copyPDFPage(object) : object instanceof PDFDict ? this.copyPDFDict(object) : object instanceof PDFArray ? this.copyPDFArray(object) : object instanceof PDFStream ? this.copyPDFStream(object) : object instanceof PDFRef ? this.copyPDFIndirectObject(object) : object.clone());
		_defineProperty(this, "copyPDFPage", (originalPage) => {
			const clonedPage = originalPage.clone();
			const { InheritableEntries } = PDFPageLeaf;
			for (let idx = 0, len = InheritableEntries.length; idx < len; idx++) {
				const key = PDFName.of(InheritableEntries[idx]);
				const value = clonedPage.getInheritableAttribute(key);
				if (!clonedPage.get(key) && value) clonedPage.set(key, value);
			}
			clonedPage.delete(PDFName.of("Parent"));
			return this.copyPDFDict(clonedPage);
		});
		_defineProperty(this, "copyPDFDict", (originalDict) => {
			if (this.traversedObjects.has(originalDict)) return this.traversedObjects.get(originalDict);
			const clonedDict = originalDict.clone(this.dest);
			this.traversedObjects.set(originalDict, clonedDict);
			const entries = originalDict.entries();
			for (let idx = 0, len = entries.length; idx < len; idx++) {
				const [key, value] = entries[idx];
				clonedDict.set(key, this.copy(value));
			}
			return clonedDict;
		});
		_defineProperty(this, "copyPDFArray", (originalArray) => {
			if (this.traversedObjects.has(originalArray)) return this.traversedObjects.get(originalArray);
			const clonedArray = originalArray.clone(this.dest);
			this.traversedObjects.set(originalArray, clonedArray);
			for (let idx = 0, len = originalArray.size(); idx < len; idx++) {
				const value = originalArray.get(idx);
				clonedArray.set(idx, this.copy(value));
			}
			return clonedArray;
		});
		_defineProperty(this, "copyPDFStream", (originalStream) => {
			if (this.traversedObjects.has(originalStream)) return this.traversedObjects.get(originalStream);
			const clonedStream = originalStream.clone(this.dest);
			this.traversedObjects.set(originalStream, clonedStream);
			const entries = originalStream.dict.entries();
			for (let idx = 0, len = entries.length; idx < len; idx++) {
				const [key, value] = entries[idx];
				clonedStream.dict.set(key, this.copy(value));
			}
			return clonedStream;
		});
		_defineProperty(this, "copyPDFIndirectObject", (ref) => {
			if (!this.traversedObjects.has(ref)) {
				const newRef = this.dest.nextRef();
				this.traversedObjects.set(ref, newRef);
				const dereferencedValue = this.src.lookup(ref);
				if (dereferencedValue) {
					const cloned = this.copy(dereferencedValue);
					this.dest.assign(newRef, cloned);
				}
			}
			return this.traversedObjects.get(ref);
		});
		this.src = src;
		this.dest = dest;
	}
};
_PDFObjectCopier = PDFObjectCopier;
_defineProperty(PDFObjectCopier, "for", (src, dest) => new _PDFObjectCopier(src, dest));
//#endregion
//#region src/core/document/PDFCrossRefSection.ts
var _PDFCrossRefSection;
/**
* Entries should be added using the [[addEntry]] and [[addDeletedEntry]]
* methods **in order of ascending object number**.
*/
var PDFCrossRefSection = class {
	constructor(firstEntry) {
		_defineProperty(this, "subsections", void 0);
		_defineProperty(this, "chunkIdx", void 0);
		_defineProperty(this, "chunkLength", void 0);
		this.subsections = firstEntry ? [[firstEntry]] : [];
		this.chunkIdx = 0;
		this.chunkLength = firstEntry ? 1 : 0;
	}
	addEntry(ref, offset) {
		this.append({
			ref,
			offset,
			deleted: false
		});
	}
	addDeletedEntry(ref, nextFreeObjectNumber) {
		this.append({
			ref,
			offset: nextFreeObjectNumber,
			deleted: true
		});
	}
	toString() {
		let section = `xref\n`;
		for (let rangeIdx = 0, rangeLen = this.subsections.length; rangeIdx < rangeLen; rangeIdx++) {
			const range = this.subsections[rangeIdx];
			section += `${range[0].ref.objectNumber} ${range.length}\n`;
			for (let entryIdx = 0, entryLen = range.length; entryIdx < entryLen; entryIdx++) {
				const entry = range[entryIdx];
				section += padStart(String(entry.offset), 10, "0");
				section += " ";
				section += padStart(String(entry.ref.generationNumber), 5, "0");
				section += " ";
				section += entry.deleted ? "f" : "n";
				section += " \n";
			}
		}
		return section;
	}
	sizeInBytes() {
		let size = 5;
		for (let idx = 0, len = this.subsections.length; idx < len; idx++) {
			const subsection = this.subsections[idx];
			const subsectionLength = subsection.length;
			const [firstEntry] = subsection;
			size += 2;
			size += String(firstEntry.ref.objectNumber).length;
			size += String(subsectionLength).length;
			size += 20 * subsectionLength;
		}
		return size;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.x;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.f;
		buffer[offset++] = CharCodes.Newline;
		offset += this.copySubsectionsIntoBuffer(this.subsections, buffer, offset);
		return offset - initialOffset;
	}
	copySubsectionsIntoBuffer(subsections, buffer, offset) {
		const initialOffset = offset;
		const length = subsections.length;
		for (let idx = 0; idx < length; idx++) {
			const subsection = this.subsections[idx];
			const firstObjectNumber = String(subsection[0].ref.objectNumber);
			offset += copyStringIntoBuffer(firstObjectNumber, buffer, offset);
			buffer[offset++] = CharCodes.Space;
			const rangeLength = String(subsection.length);
			offset += copyStringIntoBuffer(rangeLength, buffer, offset);
			buffer[offset++] = CharCodes.Newline;
			offset += this.copyEntriesIntoBuffer(subsection, buffer, offset);
		}
		return offset - initialOffset;
	}
	copyEntriesIntoBuffer(entries, buffer, offset) {
		const length = entries.length;
		for (let idx = 0; idx < length; idx++) {
			const entry = entries[idx];
			const entryOffset = padStart(String(entry.offset), 10, "0");
			offset += copyStringIntoBuffer(entryOffset, buffer, offset);
			buffer[offset++] = CharCodes.Space;
			const entryGen = padStart(String(entry.ref.generationNumber), 5, "0");
			offset += copyStringIntoBuffer(entryGen, buffer, offset);
			buffer[offset++] = CharCodes.Space;
			buffer[offset++] = entry.deleted ? CharCodes.f : CharCodes.n;
			buffer[offset++] = CharCodes.Space;
			buffer[offset++] = CharCodes.Newline;
		}
		return 20 * length;
	}
	append(currEntry) {
		if (this.chunkLength === 0) {
			this.subsections.push([currEntry]);
			this.chunkIdx = 0;
			this.chunkLength = 1;
			return;
		}
		const chunk = this.subsections[this.chunkIdx];
		const prevEntry = chunk[this.chunkLength - 1];
		if (currEntry.ref.objectNumber - prevEntry.ref.objectNumber > 1) {
			this.subsections.push([currEntry]);
			this.chunkIdx += 1;
			this.chunkLength = 1;
		} else {
			chunk.push(currEntry);
			this.chunkLength += 1;
		}
	}
};
_PDFCrossRefSection = PDFCrossRefSection;
_defineProperty(PDFCrossRefSection, "create", () => new _PDFCrossRefSection({
	ref: PDFRef.of(0, 65535),
	offset: 0,
	deleted: true
}));
_defineProperty(PDFCrossRefSection, "createEmpty", () => new _PDFCrossRefSection());
//#endregion
//#region src/core/document/PDFTrailer.ts
var _PDFTrailer;
var PDFTrailer = class {
	constructor(lastXRefOffset) {
		_defineProperty(this, "lastXRefOffset", void 0);
		this.lastXRefOffset = String(lastXRefOffset);
	}
	toString() {
		return `startxref\n${this.lastXRefOffset}\n%%EOF`;
	}
	sizeInBytes() {
		return 16 + this.lastXRefOffset.length;
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.s;
		buffer[offset++] = CharCodes.t;
		buffer[offset++] = CharCodes.a;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.t;
		buffer[offset++] = CharCodes.x;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.f;
		buffer[offset++] = CharCodes.Newline;
		offset += copyStringIntoBuffer(this.lastXRefOffset, buffer, offset);
		buffer[offset++] = CharCodes.Newline;
		buffer[offset++] = CharCodes.Percent;
		buffer[offset++] = CharCodes.Percent;
		buffer[offset++] = CharCodes.E;
		buffer[offset++] = CharCodes.O;
		buffer[offset++] = CharCodes.F;
		return offset - initialOffset;
	}
};
_PDFTrailer = PDFTrailer;
_defineProperty(PDFTrailer, "forLastCrossRefSectionOffset", (offset) => new _PDFTrailer(offset));
//#endregion
//#region src/core/document/PDFTrailerDict.ts
var _PDFTrailerDict;
var PDFTrailerDict = class {
	constructor(dict) {
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	toString() {
		return `trailer\n${this.dict.toString()}`;
	}
	sizeInBytes() {
		return 8 + this.dict.sizeInBytes();
	}
	copyBytesInto(buffer, offset) {
		const initialOffset = offset;
		buffer[offset++] = CharCodes.t;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.a;
		buffer[offset++] = CharCodes.i;
		buffer[offset++] = CharCodes.l;
		buffer[offset++] = CharCodes.e;
		buffer[offset++] = CharCodes.r;
		buffer[offset++] = CharCodes.Newline;
		offset += this.dict.copyBytesInto(buffer, offset);
		return offset - initialOffset;
	}
};
_PDFTrailerDict = PDFTrailerDict;
_defineProperty(PDFTrailerDict, "of", (dict) => new _PDFTrailerDict(dict));
//#endregion
//#region src/core/structures/PDFObjectStream.ts
var _PDFObjectStream;
var PDFObjectStream = class PDFObjectStream extends PDFFlateStream {
	constructor(context, objects, encode = true) {
		super(context.obj({}), encode);
		_defineProperty(this, "objects", void 0);
		_defineProperty(this, "offsets", void 0);
		_defineProperty(this, "offsetsString", void 0);
		this.objects = objects;
		this.offsets = this.computeObjectOffsets();
		this.offsetsString = this.computeOffsetsString();
		this.dict.set(PDFName.of("Type"), PDFName.of("ObjStm"));
		this.dict.set(PDFName.of("N"), PDFNumber.of(this.objects.length));
		this.dict.set(PDFName.of("First"), PDFNumber.of(this.offsetsString.length));
	}
	getObjectsCount() {
		return this.objects.length;
	}
	clone(context) {
		return PDFObjectStream.withContextAndObjects(context || this.dict.context, this.objects.slice(), this.encode);
	}
	getContentsString() {
		let value = this.offsetsString;
		for (let idx = 0, len = this.objects.length; idx < len; idx++) {
			const [, object] = this.objects[idx];
			value += `${object}\n`;
		}
		return value;
	}
	getUnencodedContents() {
		const buffer = new Uint8Array(this.getUnencodedContentsSize());
		let offset = copyStringIntoBuffer(this.offsetsString, buffer, 0);
		for (let idx = 0, len = this.objects.length; idx < len; idx++) {
			const [, object] = this.objects[idx];
			offset += object.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
		}
		return buffer;
	}
	getUnencodedContentsSize() {
		return this.offsetsString.length + last(this.offsets)[1] + last(this.objects)[1].sizeInBytes() + 1;
	}
	computeOffsetsString() {
		let offsetsString = "";
		for (let idx = 0, len = this.offsets.length; idx < len; idx++) {
			const [objectNumber, offset] = this.offsets[idx];
			offsetsString += `${objectNumber} ${offset} `;
		}
		return offsetsString;
	}
	computeObjectOffsets() {
		let offset = 0;
		const offsets = Array(this.objects.length);
		for (let idx = 0, len = this.objects.length; idx < len; idx++) {
			const [ref, object] = this.objects[idx];
			offsets[idx] = [ref.objectNumber, offset];
			offset += object.sizeInBytes() + 1;
		}
		return offsets;
	}
};
_PDFObjectStream = PDFObjectStream;
_defineProperty(PDFObjectStream, "withContextAndObjects", (context, objects, encode = true) => new _PDFObjectStream(context, objects, encode));
//#endregion
//#region src/core/writers/PDFWriter.ts
var _PDFWriter;
var PDFWriter = class {
	constructor(context, objectsPerTick) {
		_defineProperty(this, "context", void 0);
		_defineProperty(this, "objectsPerTick", void 0);
		_defineProperty(this, "parsedObjects", 0);
		_defineProperty(this, "shouldWaitForTick", (n) => {
			this.parsedObjects += n;
			return this.parsedObjects % this.objectsPerTick === 0;
		});
		this.context = context;
		this.objectsPerTick = objectsPerTick;
	}
	async serializeToBuffer() {
		const { size, header, indirectObjects, xref, trailerDict, trailer } = await this.computeBufferSize();
		let offset = 0;
		const buffer = new Uint8Array(size);
		offset += header.copyBytesInto(buffer, offset);
		buffer[offset++] = CharCodes.Newline;
		buffer[offset++] = CharCodes.Newline;
		for (let idx = 0, len = indirectObjects.length; idx < len; idx++) {
			const [ref, object] = indirectObjects[idx];
			const objectNumber = String(ref.objectNumber);
			offset += copyStringIntoBuffer(objectNumber, buffer, offset);
			buffer[offset++] = CharCodes.Space;
			const generationNumber = String(ref.generationNumber);
			offset += copyStringIntoBuffer(generationNumber, buffer, offset);
			buffer[offset++] = CharCodes.Space;
			buffer[offset++] = CharCodes.o;
			buffer[offset++] = CharCodes.b;
			buffer[offset++] = CharCodes.j;
			buffer[offset++] = CharCodes.Newline;
			offset += object.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
			buffer[offset++] = CharCodes.e;
			buffer[offset++] = CharCodes.n;
			buffer[offset++] = CharCodes.d;
			buffer[offset++] = CharCodes.o;
			buffer[offset++] = CharCodes.b;
			buffer[offset++] = CharCodes.j;
			buffer[offset++] = CharCodes.Newline;
			buffer[offset++] = CharCodes.Newline;
			const n = object instanceof PDFObjectStream ? object.getObjectsCount() : 1;
			if (this.shouldWaitForTick(n)) await waitForTick();
		}
		if (xref) {
			offset += xref.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
		}
		if (trailerDict) {
			offset += trailerDict.copyBytesInto(buffer, offset);
			buffer[offset++] = CharCodes.Newline;
			buffer[offset++] = CharCodes.Newline;
		}
		offset += trailer.copyBytesInto(buffer, offset);
		return buffer;
	}
	computeIndirectObjectSize([ref, object]) {
		return ref.sizeInBytes() + 3 + (object.sizeInBytes() + 9);
	}
	createTrailerDict() {
		return this.context.obj({
			Size: this.context.largestObjectNumber + 1,
			Root: this.context.trailerInfo.Root,
			Encrypt: this.context.trailerInfo.Encrypt,
			Info: this.context.trailerInfo.Info,
			ID: this.context.trailerInfo.ID
		});
	}
	async computeBufferSize() {
		const header = PDFHeader.forVersion(1, 7);
		let size = header.sizeInBytes() + 2;
		const xref = PDFCrossRefSection.create();
		const indirectObjects = this.context.enumerateIndirectObjects();
		for (let idx = 0, len = indirectObjects.length; idx < len; idx++) {
			const indirectObject = indirectObjects[idx];
			const [ref] = indirectObject;
			xref.addEntry(ref, size);
			size += this.computeIndirectObjectSize(indirectObject);
			if (this.shouldWaitForTick(1)) await waitForTick();
		}
		const xrefOffset = size;
		size += xref.sizeInBytes() + 1;
		const trailerDict = PDFTrailerDict.of(this.createTrailerDict());
		size += trailerDict.sizeInBytes() + 2;
		const trailer = PDFTrailer.forLastCrossRefSectionOffset(xrefOffset);
		size += trailer.sizeInBytes();
		return {
			size,
			header,
			indirectObjects,
			xref,
			trailerDict,
			trailer
		};
	}
};
_PDFWriter = PDFWriter;
_defineProperty(PDFWriter, "forContext", (context, objectsPerTick) => new _PDFWriter(context, objectsPerTick));
//#endregion
//#region src/core/objects/PDFInvalidObject.ts
var _PDFInvalidObject;
var PDFInvalidObject = class PDFInvalidObject extends PDFObject {
	constructor(data) {
		super();
		_defineProperty(this, "data", void 0);
		this.data = data;
	}
	clone() {
		return PDFInvalidObject.of(this.data.slice());
	}
	toString() {
		return `PDFInvalidObject(${this.data.length} bytes)`;
	}
	sizeInBytes() {
		return this.data.length;
	}
	copyBytesInto(buffer, offset) {
		const length = this.data.length;
		for (let idx = 0; idx < length; idx++) buffer[offset++] = this.data[idx];
		return length;
	}
};
_PDFInvalidObject = PDFInvalidObject;
_defineProperty(PDFInvalidObject, "of", (data) => new _PDFInvalidObject(data));
//#endregion
//#region src/core/structures/PDFCrossRefStream.ts
var _PDFCrossRefStream;
/**
* Entries should be added using the [[addDeletedEntry]],
* [[addUncompressedEntry]], and [[addCompressedEntry]] methods
* **in order of ascending object number**.
*/
var PDFCrossRefStream = class PDFCrossRefStream extends PDFFlateStream {
	constructor(dict, entries, encode = true) {
		super(dict, encode);
		_defineProperty(this, "entries", void 0);
		_defineProperty(this, "entryTuplesCache", void 0);
		_defineProperty(this, "maxByteWidthsCache", void 0);
		_defineProperty(this, "indexCache", void 0);
		_defineProperty(this, "computeIndex", () => {
			const subsections = [];
			let subsectionLength = 0;
			for (let idx = 0, len = this.entries.length; idx < len; idx++) {
				const currEntry = this.entries[idx];
				const prevEntry = this.entries[idx - 1];
				if (idx === 0) subsections.push(currEntry.ref.objectNumber);
				else if (currEntry.ref.objectNumber - prevEntry.ref.objectNumber > 1) {
					subsections.push(subsectionLength);
					subsections.push(currEntry.ref.objectNumber);
					subsectionLength = 0;
				}
				subsectionLength += 1;
			}
			subsections.push(subsectionLength);
			return subsections;
		});
		_defineProperty(this, "computeEntryTuples", () => {
			const entryTuples = Array(this.entries.length);
			for (let idx = 0, len = this.entries.length; idx < len; idx++) {
				const entry = this.entries[idx];
				if (entry.type === 0) {
					const { type, nextFreeObjectNumber, ref } = entry;
					entryTuples[idx] = [
						type,
						nextFreeObjectNumber,
						ref.generationNumber
					];
				}
				if (entry.type === 1) {
					const { type, offset, ref } = entry;
					entryTuples[idx] = [
						type,
						offset,
						ref.generationNumber
					];
				}
				if (entry.type === 2) {
					const { type, objectStreamRef, index } = entry;
					entryTuples[idx] = [
						type,
						objectStreamRef.objectNumber,
						index
					];
				}
			}
			return entryTuples;
		});
		_defineProperty(this, "computeMaxEntryByteWidths", () => {
			const entryTuples = this.entryTuplesCache.access();
			const widths = [
				0,
				0,
				0
			];
			for (let idx = 0, len = entryTuples.length; idx < len; idx++) {
				const [first, second, third] = entryTuples[idx];
				const firstSize = sizeInBytes(first);
				const secondSize = sizeInBytes(second);
				const thirdSize = sizeInBytes(third);
				if (firstSize > widths[0]) widths[0] = firstSize;
				if (secondSize > widths[1]) widths[1] = secondSize;
				if (thirdSize > widths[2]) widths[2] = thirdSize;
			}
			return widths;
		});
		this.entries = entries || [];
		this.entryTuplesCache = Cache.populatedBy(this.computeEntryTuples);
		this.maxByteWidthsCache = Cache.populatedBy(this.computeMaxEntryByteWidths);
		this.indexCache = Cache.populatedBy(this.computeIndex);
		dict.set(PDFName.of("Type"), PDFName.of("XRef"));
	}
	addDeletedEntry(ref, nextFreeObjectNumber) {
		this.entries.push({
			type: 0,
			ref,
			nextFreeObjectNumber
		});
		this.entryTuplesCache.invalidate();
		this.maxByteWidthsCache.invalidate();
		this.indexCache.invalidate();
		this.contentsCache.invalidate();
	}
	addUncompressedEntry(ref, offset) {
		this.entries.push({
			type: 1,
			ref,
			offset
		});
		this.entryTuplesCache.invalidate();
		this.maxByteWidthsCache.invalidate();
		this.indexCache.invalidate();
		this.contentsCache.invalidate();
	}
	addCompressedEntry(ref, objectStreamRef, index) {
		this.entries.push({
			type: 2,
			ref,
			objectStreamRef,
			index
		});
		this.entryTuplesCache.invalidate();
		this.maxByteWidthsCache.invalidate();
		this.indexCache.invalidate();
		this.contentsCache.invalidate();
	}
	clone(context) {
		const { dict, entries, encode } = this;
		return PDFCrossRefStream.of(dict.clone(context), entries.slice(), encode);
	}
	getContentsString() {
		const entryTuples = this.entryTuplesCache.access();
		const byteWidths = this.maxByteWidthsCache.access();
		let value = "";
		for (let entryIdx = 0, entriesLen = entryTuples.length; entryIdx < entriesLen; entryIdx++) {
			const [first, second, third] = entryTuples[entryIdx];
			const firstBytes = reverseArray(bytesFor(first));
			const secondBytes = reverseArray(bytesFor(second));
			const thirdBytes = reverseArray(bytesFor(third));
			for (let idx = byteWidths[0] - 1; idx >= 0; idx--) value += (firstBytes[idx] || 0).toString(2);
			for (let idx = byteWidths[1] - 1; idx >= 0; idx--) value += (secondBytes[idx] || 0).toString(2);
			for (let idx = byteWidths[2] - 1; idx >= 0; idx--) value += (thirdBytes[idx] || 0).toString(2);
		}
		return value;
	}
	getUnencodedContents() {
		const entryTuples = this.entryTuplesCache.access();
		const byteWidths = this.maxByteWidthsCache.access();
		const buffer = new Uint8Array(this.getUnencodedContentsSize());
		let offset = 0;
		for (let entryIdx = 0, entriesLen = entryTuples.length; entryIdx < entriesLen; entryIdx++) {
			const [first, second, third] = entryTuples[entryIdx];
			const firstBytes = reverseArray(bytesFor(first));
			const secondBytes = reverseArray(bytesFor(second));
			const thirdBytes = reverseArray(bytesFor(third));
			for (let idx = byteWidths[0] - 1; idx >= 0; idx--) buffer[offset++] = firstBytes[idx] || 0;
			for (let idx = byteWidths[1] - 1; idx >= 0; idx--) buffer[offset++] = secondBytes[idx] || 0;
			for (let idx = byteWidths[2] - 1; idx >= 0; idx--) buffer[offset++] = thirdBytes[idx] || 0;
		}
		return buffer;
	}
	getUnencodedContentsSize() {
		return sum(this.maxByteWidthsCache.access()) * this.entries.length;
	}
	updateDict() {
		super.updateDict();
		const byteWidths = this.maxByteWidthsCache.access();
		const index = this.indexCache.access();
		const { context } = this.dict;
		this.dict.set(PDFName.of("W"), context.obj(byteWidths));
		this.dict.set(PDFName.of("Index"), context.obj(index));
	}
};
_PDFCrossRefStream = PDFCrossRefStream;
_defineProperty(PDFCrossRefStream, "create", (dict, encode = true) => {
	const stream = new _PDFCrossRefStream(dict, [], encode);
	stream.addDeletedEntry(PDFRef.of(0, 65535), 0);
	return stream;
});
_defineProperty(PDFCrossRefStream, "of", (dict, entries, encode = true) => new _PDFCrossRefStream(dict, entries, encode));
//#endregion
//#region src/core/writers/PDFStreamWriter.ts
var _PDFStreamWriter;
var PDFStreamWriter = class extends PDFWriter {
	constructor(context, objectsPerTick, encodeStreams, objectsPerStream) {
		super(context, objectsPerTick);
		_defineProperty(this, "encodeStreams", void 0);
		_defineProperty(this, "objectsPerStream", void 0);
		this.encodeStreams = encodeStreams;
		this.objectsPerStream = objectsPerStream;
	}
	async computeBufferSize() {
		let objectNumber = this.context.largestObjectNumber + 1;
		const header = PDFHeader.forVersion(1, 7);
		let size = header.sizeInBytes() + 2;
		const xrefStream = PDFCrossRefStream.create(this.createTrailerDict(), this.encodeStreams);
		const uncompressedObjects = [];
		const compressedObjects = [];
		const objectStreamRefs = [];
		const indirectObjects = this.context.enumerateIndirectObjects();
		for (let idx = 0, len = indirectObjects.length; idx < len; idx++) {
			const indirectObject = indirectObjects[idx];
			const [ref, object] = indirectObject;
			if (ref === this.context.trailerInfo.Encrypt || object instanceof PDFStream || object instanceof PDFInvalidObject || ref.generationNumber !== 0) {
				uncompressedObjects.push(indirectObject);
				xrefStream.addUncompressedEntry(ref, size);
				size += this.computeIndirectObjectSize(indirectObject);
				if (this.shouldWaitForTick(1)) await waitForTick();
			} else {
				let chunk = last(compressedObjects);
				let objectStreamRef = last(objectStreamRefs);
				if (!chunk || chunk.length % this.objectsPerStream === 0) {
					chunk = [];
					compressedObjects.push(chunk);
					objectStreamRef = PDFRef.of(objectNumber++);
					objectStreamRefs.push(objectStreamRef);
				}
				xrefStream.addCompressedEntry(ref, objectStreamRef, chunk.length);
				chunk.push(indirectObject);
			}
		}
		for (let idx = 0, len = compressedObjects.length; idx < len; idx++) {
			const chunk = compressedObjects[idx];
			const ref = objectStreamRefs[idx];
			const objectStream = PDFObjectStream.withContextAndObjects(this.context, chunk, this.encodeStreams);
			xrefStream.addUncompressedEntry(ref, size);
			size += this.computeIndirectObjectSize([ref, objectStream]);
			uncompressedObjects.push([ref, objectStream]);
			if (this.shouldWaitForTick(chunk.length)) await waitForTick();
		}
		const xrefStreamRef = PDFRef.of(objectNumber++);
		xrefStream.dict.set(PDFName.of("Size"), PDFNumber.of(objectNumber));
		xrefStream.addUncompressedEntry(xrefStreamRef, size);
		const xrefOffset = size;
		size += this.computeIndirectObjectSize([xrefStreamRef, xrefStream]);
		uncompressedObjects.push([xrefStreamRef, xrefStream]);
		const trailer = PDFTrailer.forLastCrossRefSectionOffset(xrefOffset);
		size += trailer.sizeInBytes();
		return {
			size,
			header,
			indirectObjects: uncompressedObjects,
			trailer
		};
	}
};
_PDFStreamWriter = PDFStreamWriter;
_defineProperty(PDFStreamWriter, "forContext", (context, objectsPerTick, encodeStreams = true, objectsPerStream = 50) => new _PDFStreamWriter(context, objectsPerTick, encodeStreams, objectsPerStream));
//#endregion
//#region src/core/objects/PDFHexString.ts
var _PDFHexString;
var PDFHexString = class PDFHexString extends PDFObject {
	constructor(value) {
		super();
		_defineProperty(this, "value", void 0);
		this.value = value;
	}
	asBytes() {
		const hex = this.value + (this.value.length % 2 === 1 ? "0" : "");
		const hexLength = hex.length;
		const bytes = new Uint8Array(hex.length / 2);
		let hexOffset = 0;
		let bytesOffset = 0;
		while (hexOffset < hexLength) {
			bytes[bytesOffset] = parseInt(hex.substring(hexOffset, hexOffset + 2), 16);
			hexOffset += 2;
			bytesOffset += 1;
		}
		return bytes;
	}
	decodeText() {
		const bytes = this.asBytes();
		if (hasUtf16BOM(bytes)) return utf16Decode(bytes);
		return pdfDocEncodingDecode(bytes);
	}
	decodeDate() {
		const text = this.decodeText();
		const date = parseDate(text);
		if (!date) throw new InvalidPDFDateStringError(text);
		return date;
	}
	asString() {
		return this.value;
	}
	clone() {
		return PDFHexString.of(this.value);
	}
	toString() {
		return `<${this.value}>`;
	}
	sizeInBytes() {
		return this.value.length + 2;
	}
	copyBytesInto(buffer, offset) {
		buffer[offset++] = CharCodes.LessThan;
		offset += copyStringIntoBuffer(this.value, buffer, offset);
		buffer[offset++] = CharCodes.GreaterThan;
		return this.value.length + 2;
	}
};
_PDFHexString = PDFHexString;
_defineProperty(PDFHexString, "of", (value) => new _PDFHexString(value));
_defineProperty(PDFHexString, "fromText", (value) => {
	const encoded = utf16Encode(value);
	let hex = "";
	for (let idx = 0, len = encoded.length; idx < len; idx++) hex += toHexStringOfMinLength(encoded[idx], 4);
	return new _PDFHexString(hex);
});
//#endregion
//#region src/core/embedders/StandardFontEmbedder.ts
var _StandardFontEmbedder;
/**
* A note of thanks to the developers of https://github.com/foliojs/pdfkit, as
* this class borrows from:
*   https://github.com/foliojs/pdfkit/blob/f91bdd61c164a72ea06be1a43dc0a412afc3925f/lib/font/afm.coffee
*/
var StandardFontEmbedder = class {
	constructor(fontName, customName) {
		_defineProperty(this, "font", void 0);
		_defineProperty(this, "encoding", void 0);
		_defineProperty(this, "fontName", void 0);
		_defineProperty(this, "customName", void 0);
		this.encoding = fontName === FontNames.ZapfDingbats ? Encodings.ZapfDingbats : fontName === FontNames.Symbol ? Encodings.Symbol : Encodings.WinAnsi;
		this.font = Font.load(fontName);
		this.fontName = this.font.FontName;
		this.customName = customName;
	}
	/**
	* Encode the JavaScript string into this font. (JavaScript encodes strings in
	* Unicode, but standard fonts use either WinAnsi, ZapfDingbats, or Symbol
	* encodings)
	*/
	encodeText(text) {
		const glyphs = this.encodeTextAsGlyphs(text);
		const hexCodes = Array(glyphs.length);
		for (let idx = 0, len = glyphs.length; idx < len; idx++) hexCodes[idx] = toHexString(glyphs[idx].code);
		return PDFHexString.of(hexCodes.join(""));
	}
	widthOfTextAtSize(text, size) {
		const glyphs = this.encodeTextAsGlyphs(text);
		let totalWidth = 0;
		for (let idx = 0, len = glyphs.length; idx < len; idx++) {
			const left = glyphs[idx].name;
			const right = (glyphs[idx + 1] || {}).name;
			const kernAmount = this.font.getXAxisKerningForPair(left, right) || 0;
			totalWidth += this.widthOfGlyph(left) + kernAmount;
		}
		const scale = size / 1e3;
		return totalWidth * scale;
	}
	heightOfFontAtSize(size, options = {}) {
		const { descender = true } = options;
		const { Ascender, Descender, FontBBox } = this.font;
		let height = (Ascender || FontBBox[3]) - (Descender || FontBBox[1]);
		if (!descender) height += Descender || 0;
		return height / 1e3 * size;
	}
	sizeOfFontAtHeight(height) {
		const { Ascender, Descender, FontBBox } = this.font;
		const yTop = Ascender || FontBBox[3];
		const yBottom = Descender || FontBBox[1];
		return 1e3 * height / (yTop - yBottom);
	}
	embedIntoContext(context, ref) {
		const fontDict = context.obj({
			Type: "Font",
			Subtype: "Type1",
			BaseFont: this.customName || this.fontName,
			Encoding: this.encoding === Encodings.WinAnsi ? "WinAnsiEncoding" : void 0
		});
		if (ref) {
			context.assign(ref, fontDict);
			return ref;
		} else return context.register(fontDict);
	}
	widthOfGlyph(glyphName) {
		return this.font.getWidthOfGlyph(glyphName) || 250;
	}
	encodeTextAsGlyphs(text) {
		const codePoints = Array.from(text);
		const glyphs = Array(codePoints.length);
		for (let idx = 0, len = codePoints.length; idx < len; idx++) {
			const codePoint = toCodePoint(codePoints[idx]);
			glyphs[idx] = this.encoding.encodeUnicodeCodePoint(codePoint);
		}
		return glyphs;
	}
};
_StandardFontEmbedder = StandardFontEmbedder;
_defineProperty(StandardFontEmbedder, "for", (fontName, customName) => new _StandardFontEmbedder(fontName, customName));
//#endregion
//#region src/core/embedders/CMap.ts
/** `glyphs` should be an array of unique glyphs */
var createCmap = (glyphs, glyphId) => {
	const bfChars = Array(glyphs.length);
	for (let idx = 0, len = glyphs.length; idx < len; idx++) {
		const glyph = glyphs[idx];
		bfChars[idx] = [cmapHexFormat(cmapHexString(glyphId(glyph))), cmapHexFormat(...glyph.codePoints.map(cmapCodePointFormat))];
	}
	return fillCmapTemplate(bfChars);
};
var fillCmapTemplate = (bfChars) => `\
/CIDInit /ProcSet findresource begin
12 dict begin
begincmap
/CIDSystemInfo <<
  /Registry (Adobe)
  /Ordering (UCS)
  /Supplement 0
>> def
/CMapName /Adobe-Identity-UCS def
/CMapType 2 def
1 begincodespacerange
<0000><ffff>
endcodespacerange
${bfChars.length} beginbfchar
${bfChars.map(([glyphId, codePoint]) => `${glyphId} ${codePoint}`).join("\n")}
endbfchar
endcmap
CMapName currentdict /CMap defineresource pop
end
end\
`;
var cmapHexFormat = (...values) => `<${values.join("")}>`;
var cmapHexString = (value) => toHexStringOfMinLength(value, 4);
var cmapCodePointFormat = (codePoint) => {
	if (isWithinBMP(codePoint)) return cmapHexString(codePoint);
	if (hasSurrogates(codePoint)) {
		const hs = highSurrogate(codePoint);
		const ls = lowSurrogate(codePoint);
		return `${cmapHexString(hs)}${cmapHexString(ls)}`;
	}
	const msg = `0x${toHexString(codePoint)} is not a valid UTF-8 or UTF-16 codepoint.`;
	throw new Error(msg);
};
//#endregion
//#region src/core/embedders/FontFlags.ts
var makeFontFlags = (options) => {
	let flags = 0;
	const flipBit = (bit) => {
		flags |= 1 << bit - 1;
	};
	if (options.fixedPitch) flipBit(1);
	if (options.serif) flipBit(2);
	if (options.symbolic) flipBit(3);
	if (options.script) flipBit(4);
	if (options.nonsymbolic) flipBit(6);
	if (options.italic) flipBit(7);
	if (options.allCap) flipBit(17);
	if (options.smallCap) flipBit(18);
	if (options.forceBold) flipBit(19);
	return flags;
};
var deriveFontFlags = (font) => {
	const familyClass = font["OS/2"] ? font["OS/2"].sFamilyClass : 0;
	return makeFontFlags({
		fixedPitch: font.post.isFixedPitch,
		serif: 1 <= familyClass && familyClass <= 7,
		symbolic: true,
		script: familyClass === 10,
		italic: font.head.macStyle.italic
	});
};
//#endregion
//#region src/core/objects/PDFString.ts
var _PDFString;
var PDFString = class PDFString extends PDFObject {
	constructor(value) {
		super();
		_defineProperty(this, "value", void 0);
		this.value = value;
	}
	asBytes() {
		const bytes = [];
		let octal = "";
		let escaped = false;
		const pushByte = (byte) => {
			if (byte !== void 0) bytes.push(byte);
			escaped = false;
		};
		for (let idx = 0, len = this.value.length; idx < len; idx++) {
			const char = this.value[idx];
			const byte = toCharCode(char);
			const nextChar = this.value[idx + 1];
			if (!escaped) if (byte === CharCodes.BackSlash) escaped = true;
			else pushByte(byte);
			else if (byte === CharCodes.Newline) pushByte();
			else if (byte === CharCodes.CarriageReturn) pushByte();
			else if (byte === CharCodes.n) pushByte(CharCodes.Newline);
			else if (byte === CharCodes.r) pushByte(CharCodes.CarriageReturn);
			else if (byte === CharCodes.t) pushByte(CharCodes.Tab);
			else if (byte === CharCodes.b) pushByte(CharCodes.Backspace);
			else if (byte === CharCodes.f) pushByte(CharCodes.FormFeed);
			else if (byte === CharCodes.LeftParen) pushByte(CharCodes.LeftParen);
			else if (byte === CharCodes.RightParen) pushByte(CharCodes.RightParen);
			else if (byte === CharCodes.Backspace) pushByte(CharCodes.BackSlash);
			else if (byte >= CharCodes.Zero && byte <= CharCodes.Seven) {
				octal += char;
				if (octal.length === 3 || !(nextChar >= "0" && nextChar <= "7")) {
					pushByte(parseInt(octal, 8));
					octal = "";
				}
			} else pushByte(byte);
		}
		return new Uint8Array(bytes);
	}
	decodeText() {
		const bytes = this.asBytes();
		if (hasUtf16BOM(bytes)) return utf16Decode(bytes);
		return pdfDocEncodingDecode(bytes);
	}
	decodeDate() {
		const text = this.decodeText();
		const date = parseDate(text);
		if (!date) throw new InvalidPDFDateStringError(text);
		return date;
	}
	asString() {
		return this.value;
	}
	clone() {
		return PDFString.of(this.value);
	}
	toString() {
		return `(${this.value})`;
	}
	sizeInBytes() {
		return this.value.length + 2;
	}
	copyBytesInto(buffer, offset) {
		buffer[offset++] = CharCodes.LeftParen;
		offset += copyStringIntoBuffer(this.value, buffer, offset);
		buffer[offset++] = CharCodes.RightParen;
		return this.value.length + 2;
	}
};
_PDFString = PDFString;
_defineProperty(PDFString, "of", (value) => new _PDFString(value));
_defineProperty(PDFString, "fromDate", (date) => {
	const year = padStart(String(date.getUTCFullYear()), 4, "0");
	const month = padStart(String(date.getUTCMonth() + 1), 2, "0");
	const day = padStart(String(date.getUTCDate()), 2, "0");
	const hours = padStart(String(date.getUTCHours()), 2, "0");
	const mins = padStart(String(date.getUTCMinutes()), 2, "0");
	const secs = padStart(String(date.getUTCSeconds()), 2, "0");
	return new _PDFString(`D:${year}${month}${day}${hours}${mins}${secs}Z`);
});
//#endregion
//#region src/core/embedders/CustomFontEmbedder.ts
/**
* A note of thanks to the developers of https://github.com/foliojs/pdfkit, as
* this class borrows from:
*   https://github.com/devongovett/pdfkit/blob/e71edab0dd4657b5a767804ba86c94c58d01fbca/lib/image/jpeg.coffee
*/
var CustomFontEmbedder = class CustomFontEmbedder {
	static async for(fontkit, fontData, customName, fontFeatures) {
		return new CustomFontEmbedder(fontkit.create(fontData), fontData, customName, fontFeatures);
	}
	constructor(font, fontData, customName, fontFeatures) {
		_defineProperty(this, "font", void 0);
		_defineProperty(this, "scale", void 0);
		_defineProperty(this, "fontData", void 0);
		_defineProperty(this, "fontName", void 0);
		_defineProperty(this, "customName", void 0);
		_defineProperty(this, "fontFeatures", void 0);
		_defineProperty(this, "baseFontName", void 0);
		_defineProperty(this, "glyphCache", void 0);
		_defineProperty(this, "allGlyphsInFontSortedById", () => {
			const glyphs = Array(this.font.characterSet.length);
			for (let idx = 0, len = glyphs.length; idx < len; idx++) {
				const codePoint = this.font.characterSet[idx];
				glyphs[idx] = this.font.glyphForCodePoint(codePoint);
			}
			return sortedUniq(glyphs.sort(byAscendingId), (g) => g.id);
		});
		this.font = font;
		this.scale = 1e3 / this.font.unitsPerEm;
		this.fontData = fontData;
		this.fontName = this.font.postscriptName || "Font";
		this.customName = customName;
		this.fontFeatures = fontFeatures;
		this.baseFontName = "";
		this.glyphCache = Cache.populatedBy(this.allGlyphsInFontSortedById);
	}
	/**
	* Encode the JavaScript string into this font. (JavaScript encodes strings in
	* Unicode, but embedded fonts use their own custom encodings)
	*/
	encodeText(text) {
		const { glyphs } = this.font.layout(text, this.fontFeatures);
		const hexCodes = Array(glyphs.length);
		for (let idx = 0, len = glyphs.length; idx < len; idx++) hexCodes[idx] = toHexStringOfMinLength(glyphs[idx].id, 4);
		return PDFHexString.of(hexCodes.join(""));
	}
	widthOfTextAtSize(text, size) {
		const { glyphs } = this.font.layout(text, this.fontFeatures);
		let totalWidth = 0;
		for (let idx = 0, len = glyphs.length; idx < len; idx++) totalWidth += glyphs[idx].advanceWidth * this.scale;
		const scale = size / 1e3;
		return totalWidth * scale;
	}
	heightOfFontAtSize(size, options = {}) {
		const { descender = true } = options;
		const { ascent, descent, bbox } = this.font;
		let height = (ascent || bbox.maxY) * this.scale - (descent || bbox.minY) * this.scale;
		if (!descender) height -= Math.abs(descent) || 0;
		return height / 1e3 * size;
	}
	sizeOfFontAtHeight(height) {
		const { ascent, descent, bbox } = this.font;
		const yTop = (ascent || bbox.maxY) * this.scale;
		const yBottom = (descent || bbox.minY) * this.scale;
		return 1e3 * height / (yTop - yBottom);
	}
	embedIntoContext(context, ref) {
		this.baseFontName = this.customName || context.addRandomSuffix(this.fontName);
		return this.embedFontDict(context, ref);
	}
	async embedFontDict(context, ref) {
		const cidFontDictRef = await this.embedCIDFontDict(context);
		const unicodeCMapRef = this.embedUnicodeCmap(context);
		const fontDict = context.obj({
			Type: "Font",
			Subtype: "Type0",
			BaseFont: this.baseFontName,
			Encoding: "Identity-H",
			DescendantFonts: [cidFontDictRef],
			ToUnicode: unicodeCMapRef
		});
		if (ref) {
			context.assign(ref, fontDict);
			return ref;
		} else return context.register(fontDict);
	}
	isCFF() {
		return this.font.cff;
	}
	async embedCIDFontDict(context) {
		const fontDescriptorRef = await this.embedFontDescriptor(context);
		const cidFontDict = context.obj({
			Type: "Font",
			Subtype: this.isCFF() ? "CIDFontType0" : "CIDFontType2",
			CIDToGIDMap: "Identity",
			BaseFont: this.baseFontName,
			CIDSystemInfo: {
				Registry: PDFString.of("Adobe"),
				Ordering: PDFString.of("Identity"),
				Supplement: 0
			},
			FontDescriptor: fontDescriptorRef,
			W: this.computeWidths()
		});
		return context.register(cidFontDict);
	}
	async embedFontDescriptor(context) {
		const fontStreamRef = await this.embedFontStream(context);
		const { scale } = this;
		const { italicAngle, ascent, descent, capHeight, xHeight } = this.font;
		const { minX, minY, maxX, maxY } = this.font.bbox;
		const fontDescriptor = context.obj({
			Type: "FontDescriptor",
			FontName: this.baseFontName,
			Flags: deriveFontFlags(this.font),
			FontBBox: [
				minX * scale,
				minY * scale,
				maxX * scale,
				maxY * scale
			],
			ItalicAngle: italicAngle,
			Ascent: ascent * scale,
			Descent: descent * scale,
			CapHeight: (capHeight || ascent) * scale,
			XHeight: (xHeight || 0) * scale,
			StemV: 0,
			[this.isCFF() ? "FontFile3" : "FontFile2"]: fontStreamRef
		});
		return context.register(fontDescriptor);
	}
	async serializeFont() {
		return this.fontData;
	}
	async embedFontStream(context) {
		const fontStream = context.flateStream(await this.serializeFont(), { Subtype: this.isCFF() ? "CIDFontType0C" : void 0 });
		return context.register(fontStream);
	}
	embedUnicodeCmap(context) {
		const cmap = createCmap(this.glyphCache.access(), this.glyphId.bind(this));
		const cmapStream = context.flateStream(cmap);
		return context.register(cmapStream);
	}
	glyphId(glyph) {
		return glyph ? glyph.id : -1;
	}
	computeWidths() {
		const glyphs = this.glyphCache.access();
		const widths = [];
		let currSection = [];
		for (let idx = 0, len = glyphs.length; idx < len; idx++) {
			const currGlyph = glyphs[idx];
			const prevGlyph = glyphs[idx - 1];
			const currGlyphId = this.glyphId(currGlyph);
			const prevGlyphId = this.glyphId(prevGlyph);
			if (idx === 0) widths.push(currGlyphId);
			else if (currGlyphId - prevGlyphId !== 1) {
				widths.push(currSection);
				widths.push(currGlyphId);
				currSection = [];
			}
			currSection.push(currGlyph.advanceWidth * this.scale);
		}
		widths.push(currSection);
		return widths;
	}
};
//#endregion
//#region src/core/embedders/CustomFontSubsetEmbedder.ts
/**
* A note of thanks to the developers of https://github.com/foliojs/pdfkit, as
* this class borrows from:
*   https://github.com/devongovett/pdfkit/blob/e71edab0dd4657b5a767804ba86c94c58d01fbca/lib/image/jpeg.coffee
*/
var CustomFontSubsetEmbedder = class CustomFontSubsetEmbedder extends CustomFontEmbedder {
	static async for(fontkit, fontData, customFontName, fontFeatures) {
		return new CustomFontSubsetEmbedder(fontkit.create(fontData), fontData, customFontName, fontFeatures);
	}
	constructor(font, fontData, customFontName, fontFeatures) {
		super(font, fontData, customFontName, fontFeatures);
		_defineProperty(this, "subset", void 0);
		_defineProperty(this, "glyphs", void 0);
		_defineProperty(this, "glyphIdMap", void 0);
		this.subset = this.font.createSubset();
		this.glyphs = [];
		this.glyphCache = Cache.populatedBy(() => this.glyphs);
		this.glyphIdMap = /* @__PURE__ */ new Map();
	}
	encodeText(text) {
		const { glyphs } = this.font.layout(text, this.fontFeatures);
		const hexCodes = Array(glyphs.length);
		for (let idx = 0, len = glyphs.length; idx < len; idx++) {
			const glyph = glyphs[idx];
			const subsetGlyphId = this.subset.includeGlyph(glyph);
			this.glyphs[subsetGlyphId - 1] = glyph;
			this.glyphIdMap.set(glyph.id, subsetGlyphId);
			hexCodes[idx] = toHexStringOfMinLength(subsetGlyphId, 4);
		}
		this.glyphCache.invalidate();
		return PDFHexString.of(hexCodes.join(""));
	}
	isCFF() {
		return this.subset.cff;
	}
	glyphId(glyph) {
		return glyph ? this.glyphIdMap.get(glyph.id) : -1;
	}
	serializeFont() {
		return new Promise((resolve, reject) => {
			if ("encodeStream" in this.subset) {
				const parts = [];
				this.subset.encodeStream().on("data", (bytes) => parts.push(bytes)).on("end", () => resolve(mergeUint8Arrays(parts))).on("error", (err) => reject(err));
			} else if ("encode" in this.subset) try {
				resolve(this.subset.encode());
			} catch (err) {
				reject(err);
			}
			else reject(/* @__PURE__ */ new Error("Subset does not have an encode method"));
		});
	}
};
//#endregion
//#region src/core/embedders/FileEmbedder.ts
/**
* From the PDF-A3 specification, section **3.1. Requirements - General**.
* See:
* * https://www.pdfa.org/wp-content/uploads/2018/10/PDF20_AN002-AF.pdf
*/
var AFRelationship = /* @__PURE__ */ function(AFRelationship) {
	AFRelationship["Source"] = "Source";
	AFRelationship["Data"] = "Data";
	AFRelationship["Alternative"] = "Alternative";
	AFRelationship["Supplement"] = "Supplement";
	AFRelationship["EncryptedPayload"] = "EncryptedPayload";
	AFRelationship["FormData"] = "EncryptedPayload";
	AFRelationship["Schema"] = "Schema";
	AFRelationship["Unspecified"] = "Unspecified";
	return AFRelationship;
}({});
var FileEmbedder = class FileEmbedder {
	static for(bytes, fileName, options = {}) {
		return new FileEmbedder(bytes, fileName, options);
	}
	constructor(fileData, fileName, options = {}) {
		_defineProperty(this, "fileData", void 0);
		_defineProperty(this, "fileName", void 0);
		_defineProperty(this, "options", void 0);
		this.fileData = fileData;
		this.fileName = fileName;
		this.options = options;
	}
	async embedIntoContext(context, ref) {
		const { mimeType, description, creationDate, modificationDate, afRelationship } = this.options;
		const embeddedFileStream = context.flateStream(this.fileData, {
			Type: "EmbeddedFile",
			Subtype: mimeType ?? void 0,
			Params: {
				Size: this.fileData.length,
				CreationDate: creationDate ? PDFString.fromDate(creationDate) : void 0,
				ModDate: modificationDate ? PDFString.fromDate(modificationDate) : void 0
			}
		});
		const embeddedFileStreamRef = context.register(embeddedFileStream);
		const fileSpecDict = context.obj({
			Type: "Filespec",
			F: PDFString.of(this.fileName),
			UF: PDFHexString.fromText(this.fileName),
			EF: { F: embeddedFileStreamRef },
			Desc: description ? PDFHexString.fromText(description) : void 0,
			AFRelationship: afRelationship ?? void 0
		});
		if (ref) {
			context.assign(ref, fileSpecDict);
			return ref;
		} else return context.register(fileSpecDict);
	}
};
//#endregion
//#region src/core/embedders/JpegEmbedder.ts
var MARKERS = [
	65472,
	65473,
	65474,
	65475,
	65477,
	65478,
	65479,
	65480,
	65481,
	65482,
	65483,
	65484,
	65485,
	65486,
	65487
];
var ChannelToColorSpace = {
	1: "DeviceGray",
	3: "DeviceRGB",
	4: "DeviceCMYK"
};
/**
* A note of thanks to the developers of https://github.com/foliojs/pdfkit, as
* this class borrows from:
*   https://github.com/foliojs/pdfkit/blob/a6af76467ce06bd6a2af4aa7271ccac9ff152a7d/lib/image/jpeg.js
*/
var JpegEmbedder = class JpegEmbedder {
	static async for(imageData) {
		const dataView = new DataView(imageData.buffer);
		if (dataView.getUint16(0) !== 65496) throw new Error("SOI not found in JPEG");
		let pos = 2;
		let marker;
		while (pos < dataView.byteLength) {
			marker = dataView.getUint16(pos);
			pos += 2;
			if (MARKERS.includes(marker)) break;
			pos += dataView.getUint16(pos);
		}
		if (!MARKERS.includes(marker)) throw new Error("Invalid JPEG");
		pos += 2;
		const bitsPerComponent = dataView.getUint8(pos++);
		const height = dataView.getUint16(pos);
		pos += 2;
		const width = dataView.getUint16(pos);
		pos += 2;
		const channelName = ChannelToColorSpace[dataView.getUint8(pos++)];
		if (!channelName) throw new Error("Unknown JPEG channel.");
		return new JpegEmbedder(imageData, bitsPerComponent, width, height, channelName);
	}
	constructor(imageData, bitsPerComponent, width, height, colorSpace) {
		_defineProperty(this, "bitsPerComponent", void 0);
		_defineProperty(this, "height", void 0);
		_defineProperty(this, "width", void 0);
		_defineProperty(this, "colorSpace", void 0);
		_defineProperty(this, "imageData", void 0);
		this.imageData = imageData;
		this.bitsPerComponent = bitsPerComponent;
		this.width = width;
		this.height = height;
		this.colorSpace = colorSpace;
	}
	async embedIntoContext(context, ref) {
		const xObject = context.stream(this.imageData, {
			Type: "XObject",
			Subtype: "Image",
			BitsPerComponent: this.bitsPerComponent,
			Width: this.width,
			Height: this.height,
			ColorSpace: this.colorSpace,
			Filter: "DCTDecode",
			Decode: this.colorSpace === "DeviceCMYK" ? [
				1,
				0,
				1,
				0,
				1,
				0,
				1,
				0
			] : void 0
		});
		if (ref) {
			context.assign(ref, xObject);
			return ref;
		} else return context.register(xObject);
	}
};
//#endregion
//#region src/utils/png.ts
var _PNG;
var isUPNGApi = (value) => !!value && typeof value === "object" && typeof value.decode === "function" && typeof value.toRGBA8 === "function";
var resolveUPNG = (value) => {
	let current = value;
	while (current && typeof current === "object") {
		if (isUPNGApi(current)) return current;
		current = current.default;
	}
	throw new TypeError("Failed to resolve @pdf-lib/upng exports");
};
var UPNG = resolveUPNG(UPNGModule);
var getImageType = (ctype) => {
	if (ctype === 0) return "Greyscale";
	if (ctype === 2) return "Truecolour";
	if (ctype === 3) return "IndexedColour";
	if (ctype === 4) return "GreyscaleWithAlpha";
	if (ctype === 6) return "TruecolourWithAlpha";
	throw new Error(`Unknown color type: ${ctype}`);
};
var splitAlphaChannel = (rgbaChannel) => {
	const pixelCount = Math.floor(rgbaChannel.length / 4);
	const rgbChannel = new Uint8Array(pixelCount * 3);
	const alphaChannel = new Uint8Array(pixelCount * 1);
	let rgbaOffset = 0;
	let rgbOffset = 0;
	let alphaOffset = 0;
	while (rgbaOffset < rgbaChannel.length) {
		rgbChannel[rgbOffset++] = rgbaChannel[rgbaOffset++];
		rgbChannel[rgbOffset++] = rgbaChannel[rgbaOffset++];
		rgbChannel[rgbOffset++] = rgbaChannel[rgbaOffset++];
		alphaChannel[alphaOffset++] = rgbaChannel[rgbaOffset++];
	}
	return {
		rgbChannel,
		alphaChannel
	};
};
var PNG = class {
	constructor(pngData) {
		_defineProperty(this, "rgbChannel", void 0);
		_defineProperty(this, "alphaChannel", void 0);
		_defineProperty(this, "type", void 0);
		_defineProperty(this, "width", void 0);
		_defineProperty(this, "height", void 0);
		_defineProperty(this, "bitsPerComponent", void 0);
		const upng = UPNG.decode(pngData.buffer);
		const frames = UPNG.toRGBA8(upng);
		if (frames.length > 1) throw new Error(`Animated PNGs are not supported`);
		const { rgbChannel, alphaChannel } = splitAlphaChannel(new Uint8Array(frames[0]));
		this.rgbChannel = rgbChannel;
		if (alphaChannel.some((a) => a < 255)) this.alphaChannel = alphaChannel;
		this.type = getImageType(upng.ctype);
		this.width = upng.width;
		this.height = upng.height;
		this.bitsPerComponent = 8;
	}
};
_PNG = PNG;
_defineProperty(PNG, "load", (pngData) => new _PNG(pngData));
//#endregion
//#region src/core/embedders/PngEmbedder.ts
/**
* A note of thanks to the developers of https://github.com/foliojs/pdfkit, as
* this class borrows from:
*   https://github.com/devongovett/pdfkit/blob/e71edab0dd4657b5a767804ba86c94c58d01fbca/lib/image/png.coffee
*/
var PngEmbedder = class PngEmbedder {
	static async for(imageData) {
		return new PngEmbedder(PNG.load(imageData));
	}
	constructor(png) {
		_defineProperty(this, "bitsPerComponent", void 0);
		_defineProperty(this, "height", void 0);
		_defineProperty(this, "width", void 0);
		_defineProperty(this, "colorSpace", void 0);
		_defineProperty(this, "image", void 0);
		this.image = png;
		this.bitsPerComponent = png.bitsPerComponent;
		this.width = png.width;
		this.height = png.height;
		this.colorSpace = "DeviceRGB";
	}
	async embedIntoContext(context, ref) {
		const SMask = this.embedAlphaChannel(context);
		const xObject = context.flateStream(this.image.rgbChannel, {
			Type: "XObject",
			Subtype: "Image",
			BitsPerComponent: this.image.bitsPerComponent,
			Width: this.image.width,
			Height: this.image.height,
			ColorSpace: this.colorSpace,
			SMask
		});
		if (ref) {
			context.assign(ref, xObject);
			return ref;
		} else return context.register(xObject);
	}
	embedAlphaChannel(context) {
		if (!this.image.alphaChannel) return void 0;
		const xObject = context.flateStream(this.image.alphaChannel, {
			Type: "XObject",
			Subtype: "Image",
			Height: this.image.height,
			Width: this.image.width,
			BitsPerComponent: this.image.bitsPerComponent,
			ColorSpace: "DeviceGray",
			Decode: [0, 1]
		});
		return context.register(xObject);
	}
};
//#endregion
//#region src/core/streams/Stream.ts
var Stream = class Stream {
	constructor(buffer, start, length) {
		_defineProperty(this, "bytes", void 0);
		_defineProperty(this, "start", void 0);
		_defineProperty(this, "pos", void 0);
		_defineProperty(this, "end", void 0);
		this.bytes = buffer;
		this.start = start || 0;
		this.pos = this.start;
		this.end = !!start && !!length ? start + length : this.bytes.length;
	}
	get length() {
		return this.end - this.start;
	}
	get isEmpty() {
		return this.length === 0;
	}
	getByte() {
		if (this.pos >= this.end) return -1;
		return this.bytes[this.pos++];
	}
	getUint16() {
		const b0 = this.getByte();
		const b1 = this.getByte();
		if (b0 === -1 || b1 === -1) return -1;
		return (b0 << 8) + b1;
	}
	getInt32() {
		const b0 = this.getByte();
		const b1 = this.getByte();
		const b2 = this.getByte();
		const b3 = this.getByte();
		return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
	}
	getBytes(length, forceClamped = false) {
		const bytes = this.bytes;
		const pos = this.pos;
		const strEnd = this.end;
		if (!length) {
			const subarray = bytes.subarray(pos, strEnd);
			return forceClamped ? new Uint8ClampedArray(subarray) : subarray;
		} else {
			let end = pos + length;
			if (end > strEnd) end = strEnd;
			this.pos = end;
			const subarray = bytes.subarray(pos, end);
			return forceClamped ? new Uint8ClampedArray(subarray) : subarray;
		}
	}
	peekByte() {
		const peekedByte = this.getByte();
		this.pos--;
		return peekedByte;
	}
	peekBytes(length, forceClamped = false) {
		const bytes = this.getBytes(length, forceClamped);
		this.pos -= bytes.length;
		return bytes;
	}
	skip(n) {
		if (!n) n = 1;
		this.pos += n;
	}
	reset() {
		this.pos = this.start;
	}
	moveStart() {
		this.start = this.pos;
	}
	makeSubStream(start, length) {
		return new Stream(this.bytes, start, length);
	}
	decode() {
		return this.bytes;
	}
};
//#endregion
//#region src/core/streams/DecodeStream.ts
var emptyBuffer = /* @__PURE__ */ new Uint8Array(0);
/**
* Maximum allowed decoded buffer size (100 MB).
* Prevents decompression bomb attacks from consuming unbounded memory.
*/
var MAX_DECODED_SIZE = 100 * 1024 * 1024;
/**
* Super class for the decoding streams
*/
var DecodeStream = class {
	constructor(maybeMinBufferLength) {
		_defineProperty(this, "bufferLength", void 0);
		_defineProperty(this, "buffer", void 0);
		_defineProperty(this, "eof", void 0);
		_defineProperty(this, "pos", void 0);
		_defineProperty(this, "minBufferLength", void 0);
		this.pos = 0;
		this.bufferLength = 0;
		this.eof = false;
		this.buffer = emptyBuffer;
		this.minBufferLength = 512;
		if (maybeMinBufferLength) {
			while (this.minBufferLength < maybeMinBufferLength) this.minBufferLength *= 2;
			if (this.minBufferLength > MAX_DECODED_SIZE) this.minBufferLength = MAX_DECODED_SIZE;
		}
	}
	get isEmpty() {
		while (!this.eof && this.bufferLength === 0) this.readBlock();
		return this.bufferLength === 0;
	}
	getByte() {
		const pos = this.pos;
		while (this.bufferLength <= pos) {
			if (this.eof) return -1;
			this.readBlock();
		}
		return this.buffer[this.pos++];
	}
	getUint16() {
		const b0 = this.getByte();
		const b1 = this.getByte();
		if (b0 === -1 || b1 === -1) return -1;
		return (b0 << 8) + b1;
	}
	getInt32() {
		const b0 = this.getByte();
		const b1 = this.getByte();
		const b2 = this.getByte();
		const b3 = this.getByte();
		return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
	}
	getBytes(length, forceClamped = false) {
		let end;
		const pos = this.pos;
		if (length) {
			this.ensureBuffer(pos + length);
			end = pos + length;
			while (!this.eof && this.bufferLength < end) this.readBlock();
			const bufEnd = this.bufferLength;
			if (end > bufEnd) end = bufEnd;
		} else {
			while (!this.eof) this.readBlock();
			end = this.bufferLength;
		}
		this.pos = end;
		const subarray = this.buffer.subarray(pos, end);
		return forceClamped && !(subarray instanceof Uint8ClampedArray) ? new Uint8ClampedArray(subarray) : subarray;
	}
	peekByte() {
		const peekedByte = this.getByte();
		this.pos--;
		return peekedByte;
	}
	peekBytes(length, forceClamped = false) {
		const bytes = this.getBytes(length, forceClamped);
		this.pos -= bytes.length;
		return bytes;
	}
	skip(n) {
		if (!n) n = 1;
		this.pos += n;
	}
	reset() {
		this.pos = 0;
	}
	makeSubStream(start, length) {
		const end = start + length;
		while (this.bufferLength <= end && !this.eof) this.readBlock();
		return new Stream(this.buffer, start, length);
	}
	decode() {
		while (!this.eof) this.readBlock();
		return this.buffer.subarray(0, this.bufferLength);
	}
	readBlock() {
		throw new MethodNotImplementedError(this.constructor.name, "readBlock");
	}
	ensureBuffer(requested) {
		const buffer = this.buffer;
		if (requested <= buffer.byteLength) return buffer;
		if (requested > MAX_DECODED_SIZE) throw new DecompressionBombError(requested, MAX_DECODED_SIZE);
		let size = this.minBufferLength;
		while (size < requested) size *= 2;
		if (size > MAX_DECODED_SIZE) {
			size = MAX_DECODED_SIZE;
			if (size < requested) throw new DecompressionBombError(requested, MAX_DECODED_SIZE);
		}
		const buffer2 = new Uint8Array(size);
		buffer2.set(buffer);
		return this.buffer = buffer2;
	}
};
//#endregion
//#region src/core/streams/Ascii85Stream.ts
var isSpace = (ch) => ch === 32 || ch === 9 || ch === 13 || ch === 10;
var Ascii85Stream = class extends DecodeStream {
	constructor(stream, maybeLength) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		_defineProperty(this, "input", void 0);
		this.stream = stream;
		this.input = /* @__PURE__ */ new Uint8Array(5);
		if (maybeLength) maybeLength = .8 * maybeLength;
	}
	readBlock() {
		const TILDA_CHAR = 126;
		const Z_LOWER_CHAR = 122;
		const EOF = -1;
		const stream = this.stream;
		let c = stream.getByte();
		while (isSpace(c)) c = stream.getByte();
		if (c === EOF || c === TILDA_CHAR) {
			this.eof = true;
			return;
		}
		const bufferLength = this.bufferLength;
		let buffer;
		let i;
		if (c === Z_LOWER_CHAR) {
			buffer = this.ensureBuffer(bufferLength + 4);
			for (i = 0; i < 4; ++i) buffer[bufferLength + i] = 0;
			this.bufferLength += 4;
		} else {
			const input = this.input;
			input[0] = c;
			for (i = 1; i < 5; ++i) {
				c = stream.getByte();
				while (isSpace(c)) c = stream.getByte();
				input[i] = c;
				if (c === EOF || c === TILDA_CHAR) break;
			}
			buffer = this.ensureBuffer(bufferLength + i - 1);
			this.bufferLength += i - 1;
			if (i < 5) {
				for (; i < 5; ++i) input[i] = 117;
				this.eof = true;
			}
			let t = 0;
			for (i = 0; i < 5; ++i) t = t * 85 + (input[i] - 33);
			for (i = 3; i >= 0; --i) {
				buffer[bufferLength + i] = t & 255;
				t >>= 8;
			}
		}
	}
};
//#endregion
//#region src/core/streams/AsciiHexStream.ts
var AsciiHexStream = class extends DecodeStream {
	constructor(stream, maybeLength) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		_defineProperty(this, "firstDigit", void 0);
		this.stream = stream;
		this.firstDigit = -1;
		if (maybeLength) maybeLength = .5 * maybeLength;
	}
	readBlock() {
		const bytes = this.stream.getBytes(8e3);
		if (!bytes.length) {
			this.eof = true;
			return;
		}
		const maxDecodeLength = bytes.length + 1 >> 1;
		const buffer = this.ensureBuffer(this.bufferLength + maxDecodeLength);
		let bufferLength = this.bufferLength;
		let firstDigit = this.firstDigit;
		for (let i = 0, ii = bytes.length; i < ii; i++) {
			const ch = bytes[i];
			let digit;
			if (ch >= 48 && ch <= 57) digit = ch & 15;
			else if (ch >= 65 && ch <= 70 || ch >= 97 && ch <= 102) digit = (ch & 15) + 9;
			else if (ch === 62) {
				this.eof = true;
				break;
			} else continue;
			if (firstDigit < 0) firstDigit = digit;
			else {
				buffer[bufferLength++] = firstDigit << 4 | digit;
				firstDigit = -1;
			}
		}
		if (firstDigit >= 0 && this.eof) {
			buffer[bufferLength++] = firstDigit << 4;
			firstDigit = -1;
		}
		this.firstDigit = firstDigit;
		this.bufferLength = bufferLength;
	}
};
//#endregion
//#region src/core/streams/FlateStream.ts
var codeLenCodeMap = new Int32Array([
	16,
	17,
	18,
	0,
	8,
	7,
	9,
	6,
	10,
	5,
	11,
	4,
	12,
	3,
	13,
	2,
	14,
	1,
	15
]);
var lengthDecode = new Int32Array([
	3,
	4,
	5,
	6,
	7,
	8,
	9,
	10,
	65547,
	65549,
	65551,
	65553,
	131091,
	131095,
	131099,
	131103,
	196643,
	196651,
	196659,
	196667,
	262211,
	262227,
	262243,
	262259,
	327811,
	327843,
	327875,
	327907,
	258,
	258,
	258
]);
var distDecode = new Int32Array([
	1,
	2,
	3,
	4,
	65541,
	65543,
	131081,
	131085,
	196625,
	196633,
	262177,
	262193,
	327745,
	327777,
	393345,
	393409,
	459009,
	459137,
	524801,
	525057,
	590849,
	591361,
	657409,
	658433,
	724993,
	727041,
	794625,
	798721,
	868353,
	876545
]);
var fixedLitCodeTab = [new Int32Array([
	459008,
	524368,
	524304,
	524568,
	459024,
	524400,
	524336,
	590016,
	459016,
	524384,
	524320,
	589984,
	524288,
	524416,
	524352,
	590048,
	459012,
	524376,
	524312,
	589968,
	459028,
	524408,
	524344,
	590032,
	459020,
	524392,
	524328,
	59e4,
	524296,
	524424,
	524360,
	590064,
	459010,
	524372,
	524308,
	524572,
	459026,
	524404,
	524340,
	590024,
	459018,
	524388,
	524324,
	589992,
	524292,
	524420,
	524356,
	590056,
	459014,
	524380,
	524316,
	589976,
	459030,
	524412,
	524348,
	590040,
	459022,
	524396,
	524332,
	590008,
	524300,
	524428,
	524364,
	590072,
	459009,
	524370,
	524306,
	524570,
	459025,
	524402,
	524338,
	590020,
	459017,
	524386,
	524322,
	589988,
	524290,
	524418,
	524354,
	590052,
	459013,
	524378,
	524314,
	589972,
	459029,
	524410,
	524346,
	590036,
	459021,
	524394,
	524330,
	590004,
	524298,
	524426,
	524362,
	590068,
	459011,
	524374,
	524310,
	524574,
	459027,
	524406,
	524342,
	590028,
	459019,
	524390,
	524326,
	589996,
	524294,
	524422,
	524358,
	590060,
	459015,
	524382,
	524318,
	589980,
	459031,
	524414,
	524350,
	590044,
	459023,
	524398,
	524334,
	590012,
	524302,
	524430,
	524366,
	590076,
	459008,
	524369,
	524305,
	524569,
	459024,
	524401,
	524337,
	590018,
	459016,
	524385,
	524321,
	589986,
	524289,
	524417,
	524353,
	590050,
	459012,
	524377,
	524313,
	589970,
	459028,
	524409,
	524345,
	590034,
	459020,
	524393,
	524329,
	590002,
	524297,
	524425,
	524361,
	590066,
	459010,
	524373,
	524309,
	524573,
	459026,
	524405,
	524341,
	590026,
	459018,
	524389,
	524325,
	589994,
	524293,
	524421,
	524357,
	590058,
	459014,
	524381,
	524317,
	589978,
	459030,
	524413,
	524349,
	590042,
	459022,
	524397,
	524333,
	590010,
	524301,
	524429,
	524365,
	590074,
	459009,
	524371,
	524307,
	524571,
	459025,
	524403,
	524339,
	590022,
	459017,
	524387,
	524323,
	589990,
	524291,
	524419,
	524355,
	590054,
	459013,
	524379,
	524315,
	589974,
	459029,
	524411,
	524347,
	590038,
	459021,
	524395,
	524331,
	590006,
	524299,
	524427,
	524363,
	590070,
	459011,
	524375,
	524311,
	524575,
	459027,
	524407,
	524343,
	590030,
	459019,
	524391,
	524327,
	589998,
	524295,
	524423,
	524359,
	590062,
	459015,
	524383,
	524319,
	589982,
	459031,
	524415,
	524351,
	590046,
	459023,
	524399,
	524335,
	590014,
	524303,
	524431,
	524367,
	590078,
	459008,
	524368,
	524304,
	524568,
	459024,
	524400,
	524336,
	590017,
	459016,
	524384,
	524320,
	589985,
	524288,
	524416,
	524352,
	590049,
	459012,
	524376,
	524312,
	589969,
	459028,
	524408,
	524344,
	590033,
	459020,
	524392,
	524328,
	590001,
	524296,
	524424,
	524360,
	590065,
	459010,
	524372,
	524308,
	524572,
	459026,
	524404,
	524340,
	590025,
	459018,
	524388,
	524324,
	589993,
	524292,
	524420,
	524356,
	590057,
	459014,
	524380,
	524316,
	589977,
	459030,
	524412,
	524348,
	590041,
	459022,
	524396,
	524332,
	590009,
	524300,
	524428,
	524364,
	590073,
	459009,
	524370,
	524306,
	524570,
	459025,
	524402,
	524338,
	590021,
	459017,
	524386,
	524322,
	589989,
	524290,
	524418,
	524354,
	590053,
	459013,
	524378,
	524314,
	589973,
	459029,
	524410,
	524346,
	590037,
	459021,
	524394,
	524330,
	590005,
	524298,
	524426,
	524362,
	590069,
	459011,
	524374,
	524310,
	524574,
	459027,
	524406,
	524342,
	590029,
	459019,
	524390,
	524326,
	589997,
	524294,
	524422,
	524358,
	590061,
	459015,
	524382,
	524318,
	589981,
	459031,
	524414,
	524350,
	590045,
	459023,
	524398,
	524334,
	590013,
	524302,
	524430,
	524366,
	590077,
	459008,
	524369,
	524305,
	524569,
	459024,
	524401,
	524337,
	590019,
	459016,
	524385,
	524321,
	589987,
	524289,
	524417,
	524353,
	590051,
	459012,
	524377,
	524313,
	589971,
	459028,
	524409,
	524345,
	590035,
	459020,
	524393,
	524329,
	590003,
	524297,
	524425,
	524361,
	590067,
	459010,
	524373,
	524309,
	524573,
	459026,
	524405,
	524341,
	590027,
	459018,
	524389,
	524325,
	589995,
	524293,
	524421,
	524357,
	590059,
	459014,
	524381,
	524317,
	589979,
	459030,
	524413,
	524349,
	590043,
	459022,
	524397,
	524333,
	590011,
	524301,
	524429,
	524365,
	590075,
	459009,
	524371,
	524307,
	524571,
	459025,
	524403,
	524339,
	590023,
	459017,
	524387,
	524323,
	589991,
	524291,
	524419,
	524355,
	590055,
	459013,
	524379,
	524315,
	589975,
	459029,
	524411,
	524347,
	590039,
	459021,
	524395,
	524331,
	590007,
	524299,
	524427,
	524363,
	590071,
	459011,
	524375,
	524311,
	524575,
	459027,
	524407,
	524343,
	590031,
	459019,
	524391,
	524327,
	589999,
	524295,
	524423,
	524359,
	590063,
	459015,
	524383,
	524319,
	589983,
	459031,
	524415,
	524351,
	590047,
	459023,
	524399,
	524335,
	590015,
	524303,
	524431,
	524367,
	590079
]), 9];
var fixedDistCodeTab = [new Int32Array([
	327680,
	327696,
	327688,
	327704,
	327684,
	327700,
	327692,
	327708,
	327682,
	327698,
	327690,
	327706,
	327686,
	327702,
	327694,
	0,
	327681,
	327697,
	327689,
	327705,
	327685,
	327701,
	327693,
	327709,
	327683,
	327699,
	327691,
	327707,
	327687,
	327703,
	327695,
	0
]), 5];
var FlateStream = class extends DecodeStream {
	constructor(stream, maybeLength) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		_defineProperty(this, "codeSize", void 0);
		_defineProperty(this, "codeBuf", void 0);
		this.stream = stream;
		const cmf = stream.getByte();
		const flg = stream.getByte();
		if (cmf === -1 || flg === -1) throw new Error(`Invalid header in flate stream: ${cmf}, ${flg}`);
		if ((cmf & 15) !== 8) throw new Error(`Unknown compression method in flate stream: ${cmf}, ${flg}`);
		if (((cmf << 8) + flg) % 31 !== 0) throw new Error(`Bad FCHECK in flate stream: ${cmf}, ${flg}`);
		if (flg & 32) throw new Error(`FDICT bit set in flate stream: ${cmf}, ${flg}`);
		this.codeSize = 0;
		this.codeBuf = 0;
	}
	readBlock() {
		let buffer;
		let len;
		const str = this.stream;
		let hdr = this.getBits(3);
		if (hdr & 1) this.eof = true;
		hdr >>= 1;
		if (hdr === 0) {
			let b;
			if ((b = str.getByte()) === -1) throw new Error("Bad block header in flate stream");
			let blockLen = b;
			if ((b = str.getByte()) === -1) throw new Error("Bad block header in flate stream");
			blockLen |= b << 8;
			if ((b = str.getByte()) === -1) throw new Error("Bad block header in flate stream");
			let check = b;
			if ((b = str.getByte()) === -1) throw new Error("Bad block header in flate stream");
			check |= b << 8;
			if (check !== (~blockLen & 65535) && (blockLen !== 0 || check !== 0)) throw new Error("Bad uncompressed block length in flate stream");
			this.codeBuf = 0;
			this.codeSize = 0;
			const bufferLength = this.bufferLength;
			buffer = this.ensureBuffer(bufferLength + blockLen);
			const end = bufferLength + blockLen;
			this.bufferLength = end;
			if (blockLen === 0) {
				if (str.peekByte() === -1) this.eof = true;
			} else for (let n = bufferLength; n < end; ++n) {
				if ((b = str.getByte()) === -1) {
					this.eof = true;
					break;
				}
				buffer[n] = b;
			}
			return;
		}
		let litCodeTable;
		let distCodeTable;
		if (hdr === 1) {
			litCodeTable = fixedLitCodeTab;
			distCodeTable = fixedDistCodeTab;
		} else if (hdr === 2) {
			const numLitCodes = this.getBits(5) + 257;
			const numDistCodes = this.getBits(5) + 1;
			const numCodeLenCodes = this.getBits(4) + 4;
			const codeLenCodeLengths = new Uint8Array(codeLenCodeMap.length);
			let i;
			for (i = 0; i < numCodeLenCodes; ++i) codeLenCodeLengths[codeLenCodeMap[i]] = this.getBits(3);
			const codeLenCodeTab = this.generateHuffmanTable(codeLenCodeLengths);
			len = 0;
			i = 0;
			const codes = numLitCodes + numDistCodes;
			const codeLengths = new Uint8Array(codes);
			let bitsLength;
			let bitsOffset;
			let what;
			while (i < codes) {
				const code = this.getCode(codeLenCodeTab);
				if (code === 16) {
					bitsLength = 2;
					bitsOffset = 3;
					what = len;
				} else if (code === 17) {
					bitsLength = 3;
					bitsOffset = 3;
					what = len = 0;
				} else if (code === 18) {
					bitsLength = 7;
					bitsOffset = 11;
					what = len = 0;
				} else {
					codeLengths[i++] = len = code;
					continue;
				}
				let repeatLength = this.getBits(bitsLength) + bitsOffset;
				while (repeatLength-- > 0) codeLengths[i++] = what;
			}
			litCodeTable = this.generateHuffmanTable(codeLengths.subarray(0, numLitCodes));
			distCodeTable = this.generateHuffmanTable(codeLengths.subarray(numLitCodes, codes));
		} else throw new Error("Unknown block type in flate stream");
		buffer = this.buffer;
		let limit = buffer ? buffer.length : 0;
		let pos = this.bufferLength;
		while (true) {
			let code1 = this.getCode(litCodeTable);
			if (code1 < 256) {
				if (pos + 1 >= limit) {
					buffer = this.ensureBuffer(pos + 1);
					limit = buffer.length;
				}
				buffer[pos++] = code1;
				continue;
			}
			if (code1 === 256) {
				this.bufferLength = pos;
				return;
			}
			code1 -= 257;
			code1 = lengthDecode[code1];
			let code2 = code1 >> 16;
			if (code2 > 0) code2 = this.getBits(code2);
			len = (code1 & 65535) + code2;
			code1 = this.getCode(distCodeTable);
			code1 = distDecode[code1];
			code2 = code1 >> 16;
			if (code2 > 0) code2 = this.getBits(code2);
			const dist = (code1 & 65535) + code2;
			if (pos + len >= limit) {
				buffer = this.ensureBuffer(pos + len);
				limit = buffer.length;
			}
			for (let k = 0; k < len; ++k, ++pos) buffer[pos] = buffer[pos - dist];
		}
	}
	getBits(bits) {
		const str = this.stream;
		let codeSize = this.codeSize;
		let codeBuf = this.codeBuf;
		let b;
		while (codeSize < bits) {
			if ((b = str.getByte()) === -1) throw new Error("Bad encoding in flate stream");
			codeBuf |= b << codeSize;
			codeSize += 8;
		}
		b = codeBuf & (1 << bits) - 1;
		this.codeBuf = codeBuf >> bits;
		this.codeSize = codeSize -= bits;
		return b;
	}
	getCode(table) {
		const str = this.stream;
		const codes = table[0];
		const maxLen = table[1];
		let codeSize = this.codeSize;
		let codeBuf = this.codeBuf;
		let b;
		while (codeSize < maxLen) {
			if ((b = str.getByte()) === -1) break;
			codeBuf |= b << codeSize;
			codeSize += 8;
		}
		const code = codes[codeBuf & (1 << maxLen) - 1];
		if (typeof codes === "number") console.log("FLATE:", code);
		const codeLen = code >> 16;
		const codeVal = code & 65535;
		if (codeLen < 1 || codeSize < codeLen) throw new Error("Bad encoding in flate stream");
		this.codeBuf = codeBuf >> codeLen;
		this.codeSize = codeSize - codeLen;
		return codeVal;
	}
	generateHuffmanTable(lengths) {
		const n = lengths.length;
		let maxLen = 0;
		let i;
		for (i = 0; i < n; ++i) if (lengths[i] > maxLen) maxLen = lengths[i];
		const size = 1 << maxLen;
		const codes = new Int32Array(size);
		for (let len = 1, code = 0, skip = 2; len <= maxLen; ++len, code <<= 1, skip <<= 1) for (let val = 0; val < n; ++val) if (lengths[val] === len) {
			let code2 = 0;
			let t = code;
			for (i = 0; i < len; ++i) {
				code2 = code2 << 1 | t & 1;
				t >>= 1;
			}
			for (i = code2; i < size; i += skip) codes[i] = len << 16 | val;
			++code;
		}
		return [codes, maxLen];
	}
};
//#endregion
//#region src/core/streams/LZWStream.ts
var LZWStream = class extends DecodeStream {
	constructor(stream, maybeLength, earlyChange) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		_defineProperty(this, "cachedData", void 0);
		_defineProperty(this, "bitsCached", void 0);
		_defineProperty(this, "lzwState", void 0);
		this.stream = stream;
		this.cachedData = 0;
		this.bitsCached = 0;
		const maxLzwDictionarySize = 4096;
		const lzwState = {
			earlyChange,
			codeLength: 9,
			nextCode: 258,
			dictionaryValues: new Uint8Array(maxLzwDictionarySize),
			dictionaryLengths: new Uint16Array(maxLzwDictionarySize),
			dictionaryPrevCodes: new Uint16Array(maxLzwDictionarySize),
			currentSequence: new Uint8Array(maxLzwDictionarySize),
			currentSequenceLength: 0
		};
		for (let i = 0; i < 256; ++i) {
			lzwState.dictionaryValues[i] = i;
			lzwState.dictionaryLengths[i] = 1;
		}
		this.lzwState = lzwState;
	}
	readBlock() {
		const blockSize = 512;
		let estimatedDecodedSize = blockSize * 2;
		const decodedSizeDelta = blockSize;
		let i;
		let j;
		let q;
		const lzwState = this.lzwState;
		if (!lzwState) return;
		const earlyChange = lzwState.earlyChange;
		let nextCode = lzwState.nextCode;
		const dictionaryValues = lzwState.dictionaryValues;
		const dictionaryLengths = lzwState.dictionaryLengths;
		const dictionaryPrevCodes = lzwState.dictionaryPrevCodes;
		let codeLength = lzwState.codeLength;
		let prevCode = lzwState.prevCode;
		const currentSequence = lzwState.currentSequence;
		let currentSequenceLength = lzwState.currentSequenceLength;
		let decodedLength = 0;
		let currentBufferLength = this.bufferLength;
		let buffer = this.ensureBuffer(this.bufferLength + estimatedDecodedSize);
		for (i = 0; i < blockSize; i++) {
			const code = this.readBits(codeLength);
			const hasPrev = currentSequenceLength > 0;
			if (!code || code < 256) {
				currentSequence[0] = code;
				currentSequenceLength = 1;
			} else if (code >= 258) if (code < nextCode) {
				currentSequenceLength = dictionaryLengths[code];
				for (j = currentSequenceLength - 1, q = code; j >= 0; j--) {
					currentSequence[j] = dictionaryValues[q];
					q = dictionaryPrevCodes[q];
				}
			} else currentSequence[currentSequenceLength++] = currentSequence[0];
			else if (code === 256) {
				codeLength = 9;
				nextCode = 258;
				currentSequenceLength = 0;
				continue;
			} else {
				this.eof = true;
				delete this.lzwState;
				break;
			}
			if (hasPrev) {
				dictionaryPrevCodes[nextCode] = prevCode;
				dictionaryLengths[nextCode] = dictionaryLengths[prevCode] + 1;
				dictionaryValues[nextCode] = currentSequence[0];
				nextCode++;
				codeLength = nextCode + earlyChange & nextCode + earlyChange - 1 ? codeLength : Math.min(Math.log(nextCode + earlyChange) / .6931471805599453 + 1, 12) | 0;
			}
			prevCode = code;
			decodedLength += currentSequenceLength;
			if (estimatedDecodedSize < decodedLength) {
				do
					estimatedDecodedSize += decodedSizeDelta;
				while (estimatedDecodedSize < decodedLength);
				buffer = this.ensureBuffer(this.bufferLength + estimatedDecodedSize);
			}
			for (j = 0; j < currentSequenceLength; j++) buffer[currentBufferLength++] = currentSequence[j];
		}
		lzwState.nextCode = nextCode;
		lzwState.codeLength = codeLength;
		lzwState.prevCode = prevCode;
		lzwState.currentSequenceLength = currentSequenceLength;
		this.bufferLength = currentBufferLength;
	}
	readBits(n) {
		let bitsCached = this.bitsCached;
		let cachedData = this.cachedData;
		while (bitsCached < n) {
			const c = this.stream.getByte();
			if (c === -1) {
				this.eof = true;
				return null;
			}
			cachedData = cachedData << 8 | c;
			bitsCached += 8;
		}
		this.bitsCached = bitsCached -= n;
		this.cachedData = cachedData;
		return cachedData >>> bitsCached & (1 << n) - 1;
	}
};
//#endregion
//#region src/core/streams/RunLengthStream.ts
var RunLengthStream = class extends DecodeStream {
	constructor(stream, maybeLength) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		this.stream = stream;
	}
	readBlock() {
		const repeatHeader = this.stream.getBytes(2);
		if (!repeatHeader || repeatHeader.length < 2 || repeatHeader[0] === 128) {
			this.eof = true;
			return;
		}
		let buffer;
		let bufferLength = this.bufferLength;
		let n = repeatHeader[0];
		if (n < 128) {
			buffer = this.ensureBuffer(bufferLength + n + 1);
			buffer[bufferLength++] = repeatHeader[1];
			if (n > 0) {
				const source = this.stream.getBytes(n);
				buffer.set(source, bufferLength);
				bufferLength += n;
			}
		} else {
			n = 257 - n;
			const b = repeatHeader[1];
			buffer = this.ensureBuffer(bufferLength + n + 1);
			for (let i = 0; i < n; i++) buffer[bufferLength++] = b;
		}
		this.bufferLength = bufferLength;
	}
};
//#endregion
//#region src/core/streams/decode.ts
var decodeStream = (stream, encoding, params) => {
	if (encoding === PDFName.of("FlateDecode")) return new FlateStream(stream);
	if (encoding === PDFName.of("LZWDecode")) {
		let earlyChange = 1;
		if (params instanceof PDFDict) {
			const EarlyChange = params.lookup(PDFName.of("EarlyChange"));
			if (EarlyChange instanceof PDFNumber) earlyChange = EarlyChange.asNumber();
		}
		return new LZWStream(stream, void 0, earlyChange);
	}
	if (encoding === PDFName.of("ASCII85Decode")) return new Ascii85Stream(stream);
	if (encoding === PDFName.of("ASCIIHexDecode")) return new AsciiHexStream(stream);
	if (encoding === PDFName.of("RunLengthDecode")) return new RunLengthStream(stream);
	throw new UnsupportedEncodingError(encoding.asString());
};
var decodePDFRawStream = ({ dict, contents, transform }) => {
	let stream = new Stream(contents);
	if (transform) stream = transform.createStream(stream, contents.length);
	const Filter = dict.lookup(PDFName.of("Filter"));
	const DecodeParms = dict.lookup(PDFName.of("DecodeParms"));
	if (Filter instanceof PDFName) stream = decodeStream(stream, Filter, DecodeParms);
	else if (Filter instanceof PDFArray) for (let idx = 0, len = Filter.size(); idx < len; idx++) stream = decodeStream(stream, Filter.lookup(idx, PDFName), DecodeParms && DecodeParms.lookupMaybe(idx, PDFDict));
	else if (Filter) throw new UnexpectedObjectTypeError([PDFName, PDFArray], Filter);
	return stream;
};
//#endregion
//#region src/core/embedders/PDFPageEmbedder.ts
var fullPageBoundingBox = (page) => {
	const mediaBox = page.MediaBox();
	return {
		left: 0,
		bottom: 0,
		right: mediaBox.lookup(2, PDFNumber).asNumber() - mediaBox.lookup(0, PDFNumber).asNumber(),
		top: mediaBox.lookup(3, PDFNumber).asNumber() - mediaBox.lookup(1, PDFNumber).asNumber()
	};
};
var boundingBoxAdjustedMatrix = (bb) => [
	1,
	0,
	0,
	1,
	-bb.left,
	-bb.bottom
];
var PDFPageEmbedder = class PDFPageEmbedder {
	static async for(page, boundingBox, transformationMatrix) {
		return new PDFPageEmbedder(page, boundingBox, transformationMatrix);
	}
	constructor(page, boundingBox, transformationMatrix) {
		_defineProperty(this, "width", void 0);
		_defineProperty(this, "height", void 0);
		_defineProperty(this, "boundingBox", void 0);
		_defineProperty(this, "transformationMatrix", void 0);
		_defineProperty(this, "page", void 0);
		this.page = page;
		const bb = boundingBox ?? fullPageBoundingBox(page);
		this.width = bb.right - bb.left;
		this.height = bb.top - bb.bottom;
		this.boundingBox = bb;
		this.transformationMatrix = transformationMatrix ?? boundingBoxAdjustedMatrix(bb);
	}
	async embedIntoContext(context, ref) {
		const { Contents, Resources } = this.page.normalizedEntries();
		if (!Contents) throw new MissingPageContentsEmbeddingError();
		const decodedContents = this.decodeContents(Contents);
		const { left, bottom, right, top } = this.boundingBox;
		const xObject = context.flateStream(decodedContents, {
			Type: "XObject",
			Subtype: "Form",
			FormType: 1,
			BBox: [
				left,
				bottom,
				right,
				top
			],
			Matrix: this.transformationMatrix,
			Resources
		});
		if (ref) {
			context.assign(ref, xObject);
			return ref;
		} else return context.register(xObject);
	}
	decodeContents(contents) {
		const newline = Uint8Array.of(CharCodes.Newline);
		const decodedContents = [];
		for (let idx = 0, len = contents.size(); idx < len; idx++) {
			const stream = contents.lookup(idx, PDFStream);
			let content;
			if (stream instanceof PDFRawStream) content = decodePDFRawStream(stream).decode();
			else if (stream instanceof PDFContentStream) content = stream.getUnencodedContents();
			else throw new UnrecognizedStreamTypeError(stream);
			decodedContents.push(content, newline);
		}
		return mergeIntoTypedArray(...decodedContents);
	}
};
//#endregion
//#region src/core/interactive/ViewerPreferences.ts
var _ViewerPreferences;
var asEnum = (rawValue, enumType) => {
	if (rawValue === void 0) return void 0;
	return enumType[rawValue];
};
var NonFullScreenPageMode = /* @__PURE__ */ function(NonFullScreenPageMode) {
	/**
	* After exiting FullScreen mode, neither the document outline nor thumbnail
	* images should be visible.
	*/
	NonFullScreenPageMode["UseNone"] = "UseNone";
	/** After exiting FullScreen mode, the document outline should be visible. */
	NonFullScreenPageMode["UseOutlines"] = "UseOutlines";
	/** After exiting FullScreen mode, thumbnail images should be visible. */
	NonFullScreenPageMode["UseThumbs"] = "UseThumbs";
	/**
	* After exiting FullScreen mode, the optional content group panel should be
	* visible.
	*/
	NonFullScreenPageMode["UseOC"] = "UseOC";
	return NonFullScreenPageMode;
}({});
var ReadingDirection = /* @__PURE__ */ function(ReadingDirection) {
	/** The predominant reading order is Left to Right. */
	ReadingDirection["L2R"] = "L2R";
	/**
	* The predominant reading order is Right to left (including vertical writing
	* systems, such as Chinese, Japanese and Korean).
	*/
	ReadingDirection["R2L"] = "R2L";
	return ReadingDirection;
}({});
var PrintScaling = /* @__PURE__ */ function(PrintScaling) {
	/** No page scaling. */
	PrintScaling["None"] = "None";
	PrintScaling["AppDefault"] = "AppDefault";
	return PrintScaling;
}({});
var Duplex = /* @__PURE__ */ function(Duplex) {
	/** The PDF reader should print single-sided. */
	Duplex["Simplex"] = "Simplex";
	/**
	* The PDF reader should print double sided and flip on the short edge of the
	* sheet.
	*/
	Duplex["DuplexFlipShortEdge"] = "DuplexFlipShortEdge";
	/**
	* The PDF reader should print double sided and flip on the long edge of the
	* sheet.
	*/
	Duplex["DuplexFlipLongEdge"] = "DuplexFlipLongEdge";
	return Duplex;
}({});
var ViewerPreferences = class {
	/** @ignore */
	constructor(dict) {
		_defineProperty(
			this,
			/** @ignore */
			"dict",
			void 0
		);
		this.dict = dict;
	}
	lookupBool(key) {
		const returnObj = this.dict.lookup(PDFName.of(key));
		if (returnObj instanceof PDFBool) return returnObj;
	}
	lookupName(key) {
		const returnObj = this.dict.lookup(PDFName.of(key));
		if (returnObj instanceof PDFName) return returnObj;
	}
	/** @ignore */
	HideToolbar() {
		return this.lookupBool("HideToolbar");
	}
	/** @ignore */
	HideMenubar() {
		return this.lookupBool("HideMenubar");
	}
	/** @ignore */
	HideWindowUI() {
		return this.lookupBool("HideWindowUI");
	}
	/** @ignore */
	FitWindow() {
		return this.lookupBool("FitWindow");
	}
	/** @ignore */
	CenterWindow() {
		return this.lookupBool("CenterWindow");
	}
	/** @ignore */
	DisplayDocTitle() {
		return this.lookupBool("DisplayDocTitle");
	}
	/** @ignore */
	NonFullScreenPageMode() {
		return this.lookupName("NonFullScreenPageMode");
	}
	/** @ignore */
	Direction() {
		return this.lookupName("Direction");
	}
	/** @ignore */
	PrintScaling() {
		return this.lookupName("PrintScaling");
	}
	/** @ignore */
	Duplex() {
		return this.lookupName("Duplex");
	}
	/** @ignore */
	PickTrayByPDFSize() {
		return this.lookupBool("PickTrayByPDFSize");
	}
	/** @ignore */
	PrintPageRange() {
		const PrintPageRange = this.dict.lookup(PDFName.of("PrintPageRange"));
		if (PrintPageRange instanceof PDFArray) return PrintPageRange;
	}
	/** @ignore */
	NumCopies() {
		const NumCopies = this.dict.lookup(PDFName.of("NumCopies"));
		if (NumCopies instanceof PDFNumber) return NumCopies;
	}
	/**
	* Returns `true` if PDF readers should hide the toolbar menus when displaying
	* this document.
	* @returns Whether or not toolbars should be hidden.
	*/
	getHideToolbar() {
		return this.HideToolbar()?.asBoolean() ?? false;
	}
	/**
	* Returns `true` if PDF readers should hide the menu bar when displaying this
	* document.
	* @returns Whether or not the menu bar should be hidden.
	*/
	getHideMenubar() {
		return this.HideMenubar()?.asBoolean() ?? false;
	}
	/**
	* Returns `true` if PDF readers should hide the user interface elements in
	* the document's window (such as scroll bars and navigation controls),
	* leaving only the document's contents displayed.
	* @returns Whether or not user interface elements should be hidden.
	*/
	getHideWindowUI() {
		return this.HideWindowUI()?.asBoolean() ?? false;
	}
	/**
	* Returns `true` if PDF readers should resize the document's window to fit
	* the size of the first displayed page.
	* @returns Whether or not the window should be resized to fit.
	*/
	getFitWindow() {
		return this.FitWindow()?.asBoolean() ?? false;
	}
	/**
	* Returns `true` if PDF readers should position the document's window in the
	* center of the screen.
	* @returns Whether or not to center the document window.
	*/
	getCenterWindow() {
		return this.CenterWindow()?.asBoolean() ?? false;
	}
	/**
	* Returns `true` if the window's title bar should display the document
	* `Title`, taken from the document metadata (see [[PDFDocument.getTitle]]).
	* Returns `false` if the title bar should instead display the filename of the
	* PDF file.
	* @returns Whether to display the document title.
	*/
	getDisplayDocTitle() {
		return this.DisplayDocTitle()?.asBoolean() ?? false;
	}
	/**
	* Returns the page mode, which tells the PDF reader how to display the
	* document after exiting full-screen mode.
	* @returns The page mode after exiting full-screen mode.
	*/
	getNonFullScreenPageMode() {
		const mode = this.NonFullScreenPageMode()?.decodeText();
		return asEnum(mode, NonFullScreenPageMode) ?? "UseNone";
	}
	/**
	* Returns the predominant reading order for text.
	* @returns The text reading order.
	*/
	getReadingDirection() {
		const direction = this.Direction()?.decodeText();
		return asEnum(direction, ReadingDirection) ?? "L2R";
	}
	/**
	* Returns the page scaling option that the PDF reader should select when the
	* print dialog is displayed.
	* @returns The page scaling option.
	*/
	getPrintScaling() {
		const scaling = this.PrintScaling()?.decodeText();
		return asEnum(scaling, PrintScaling) ?? "AppDefault";
	}
	/**
	* Returns the paper handling option that should be used when printing the
	* file from the print dialog.
	* @returns The paper handling option.
	*/
	getDuplex() {
		const duplex = this.Duplex()?.decodeText();
		return asEnum(duplex, Duplex);
	}
	/**
	* Returns `true` if the PDF page size should be used to select the input
	* paper tray.
	* @returns Whether or not the PDF page size should be used to select the
	*          input paper tray.
	*/
	getPickTrayByPDFSize() {
		return this.PickTrayByPDFSize()?.asBoolean();
	}
	/**
	* Returns an array of page number ranges, which are the values used to
	* initialize the print dialog box when the file is printed. Each range
	* specifies the first (`start`) and last (`end`) pages in a sub-range of
	* pages to be printed. The first page of the PDF file is denoted by 0.
	* For example:
	* ```js
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	* const includesPage3 = viewerPrefs
	*   .getPrintRanges()
	*   .some(pr => pr.start =< 2 && pr.end >= 2)
	* if (includesPage3) console.log('printRange includes page 3')
	* ```
	* @returns An array of objects, each with the properties `start` and `end`,
	*          denoting page indices. If not, specified an empty array is
	*          returned.
	*/
	getPrintPageRange() {
		const rng = this.PrintPageRange();
		if (!rng) return [];
		const pageRanges = [];
		for (let i = 0; i < rng.size(); i += 2) {
			const start = rng.lookup(i, PDFNumber).asNumber();
			const end = rng.lookup(i + 1, PDFNumber).asNumber();
			pageRanges.push({
				start,
				end
			});
		}
		return pageRanges;
	}
	/**
	* Returns the number of copies to be printed when the print dialog is opened
	* for this document.
	* @returns The default number of copies to be printed.
	*/
	getNumCopies() {
		return this.NumCopies()?.asNumber() ?? 1;
	}
	/**
	* Choose whether the PDF reader's toolbars should be hidden while the
	* document is active.
	* @param hideToolbar `true` if the toolbar should be hidden.
	*/
	setHideToolbar(hideToolbar) {
		const HideToolbar = this.dict.context.obj(hideToolbar);
		this.dict.set(PDFName.of("HideToolbar"), HideToolbar);
	}
	/**
	* Choose whether the PDF reader's menu bar should be hidden while the
	* document is active.
	* @param hideMenubar `true` if the menu bar should be hidden.
	*/
	setHideMenubar(hideMenubar) {
		const HideMenubar = this.dict.context.obj(hideMenubar);
		this.dict.set(PDFName.of("HideMenubar"), HideMenubar);
	}
	/**
	* Choose whether the PDF reader should hide user interface elements in the
	* document's window (such as scroll bars and navigation controls), leaving
	* only the document's contents displayed.
	* @param hideWindowUI `true` if the user interface elements should be hidden.
	*/
	setHideWindowUI(hideWindowUI) {
		const HideWindowUI = this.dict.context.obj(hideWindowUI);
		this.dict.set(PDFName.of("HideWindowUI"), HideWindowUI);
	}
	/**
	* Choose whether the PDF reader should resize the document's window to fit
	* the size of the first displayed page.
	* @param fitWindow `true` if the window should be resized.
	*/
	setFitWindow(fitWindow) {
		const FitWindow = this.dict.context.obj(fitWindow);
		this.dict.set(PDFName.of("FitWindow"), FitWindow);
	}
	/**
	* Choose whether the PDF reader should position the document's window in the
	* center of the screen.
	* @param centerWindow `true` if the window should be centered.
	*/
	setCenterWindow(centerWindow) {
		const CenterWindow = this.dict.context.obj(centerWindow);
		this.dict.set(PDFName.of("CenterWindow"), CenterWindow);
	}
	/**
	* Choose whether the window's title bar should display the document `Title`
	* taken from the document metadata (see [[PDFDocument.setTitle]]). If
	* `false`, the title bar should instead display the PDF filename.
	* @param displayTitle `true` if the document title should be displayed.
	*/
	setDisplayDocTitle(displayTitle) {
		const DisplayDocTitle = this.dict.context.obj(displayTitle);
		this.dict.set(PDFName.of("DisplayDocTitle"), DisplayDocTitle);
	}
	/**
	* Choose how the PDF reader should display the document upon exiting
	* full-screen mode. This entry is meaningful only if the value of the
	* `PageMode` entry in the document's [[PDFCatalog]] is `FullScreen`.
	*
	* For example:
	* ```js
	* import { PDFDocument, NonFullScreenPageMode, PDFName } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	*
	* // Set the PageMode
	* pdfDoc.catalog.set(PDFName.of('PageMode'),PDFName.of('FullScreen'))
	*
	* // Set what happens when full-screen is closed
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	* viewerPrefs.setNonFullScreenPageMode(NonFullScreenPageMode.UseOutlines)
	* ```
	*
	* @param nonFullScreenPageMode How the document should be displayed upon
	*                              exiting full screen mode.
	*/
	setNonFullScreenPageMode(nonFullScreenPageMode) {
		assertIsOneOf(nonFullScreenPageMode, "nonFullScreenPageMode", NonFullScreenPageMode);
		const mode = PDFName.of(nonFullScreenPageMode);
		this.dict.set(PDFName.of("NonFullScreenPageMode"), mode);
	}
	/**
	* Choose the predominant reading order for text.
	*
	* This entry has no direct effect on the document's contents or page
	* numbering, but may be used to determine the relative positioning of pages
	* when displayed side by side or printed n-up.
	*
	* For example:
	* ```js
	* import { PDFDocument, ReadingDirection } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	* viewerPrefs.setReadingDirection(ReadingDirection.R2L)
	* ```
	*
	* @param readingDirection The reading order for text.
	*/
	setReadingDirection(readingDirection) {
		assertIsOneOf(readingDirection, "readingDirection", ReadingDirection);
		const direction = PDFName.of(readingDirection);
		this.dict.set(PDFName.of("Direction"), direction);
	}
	/**
	* Choose the page scaling option that should be selected when a print dialog
	* is displayed for this document.
	*
	* For example:
	* ```js
	* import { PDFDocument, PrintScaling } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	* viewerPrefs.setPrintScaling(PrintScaling.None)
	* ```
	*
	* @param printScaling The print scaling option.
	*/
	setPrintScaling(printScaling) {
		assertIsOneOf(printScaling, "printScaling", PrintScaling);
		const scaling = PDFName.of(printScaling);
		this.dict.set(PDFName.of("PrintScaling"), scaling);
	}
	/**
	* Choose the paper handling option that should be selected by default in the
	* print dialog.
	*
	* For example:
	* ```js
	* import { PDFDocument, Duplex } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	* viewerPrefs.setDuplex(Duplex.DuplexFlipShortEdge)
	* ```
	*
	* @param duplex The double or single sided printing option.
	*/
	setDuplex(duplex) {
		assertIsOneOf(duplex, "duplex", Duplex);
		const dup = PDFName.of(duplex);
		this.dict.set(PDFName.of("Duplex"), dup);
	}
	/**
	* Choose whether the PDF document's page size should be used to select the
	* input paper tray when printing. This setting influences only the preset
	* values used to populate the print dialog presented by a PDF reader.
	*
	* If PickTrayByPDFSize is true, the check box in the print dialog associated
	* with input paper tray should be checked. This setting has no effect on
	* operating systems that do not provide the ability to pick the input tray
	* by size.
	*
	* @param pickTrayByPDFSize `true` if the document's page size should be used
	*                          to select the input paper tray.
	*/
	setPickTrayByPDFSize(pickTrayByPDFSize) {
		const PickTrayByPDFSize = this.dict.context.obj(pickTrayByPDFSize);
		this.dict.set(PDFName.of("PickTrayByPDFSize"), PickTrayByPDFSize);
	}
	/**
	* Choose the page numbers used to initialize the print dialog box when the
	* file is printed. The first page of the PDF file is denoted by 0.
	*
	* For example:
	* ```js
	* import { PDFDocument } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	* const viewerPrefs = pdfDoc.catalog.getOrCreateViewerPreferences()
	*
	* // We can set the default print range to only the first page
	* viewerPrefs.setPrintPageRange({ start: 0, end: 0 })
	*
	* // Or we can supply noncontiguous ranges (e.g. pages 1, 3, and 5-7)
	* viewerPrefs.setPrintPageRange([
	*   { start: 0, end: 0 },
	*   { start: 2, end: 2 },
	*   { start: 4, end: 6 },
	* ])
	* ```
	*
	* @param printPageRange An object or array of objects, each with the
	*                       properties `start` and `end`, denoting a range of
	*                       page indices.
	*/
	setPrintPageRange(printPageRange) {
		if (!Array.isArray(printPageRange)) printPageRange = [printPageRange];
		const flatRange = [];
		for (let idx = 0, len = printPageRange.length; idx < len; idx++) {
			flatRange.push(printPageRange[idx].start);
			flatRange.push(printPageRange[idx].end);
		}
		assertEachIs(flatRange, "printPageRange", ["number"]);
		const pageRanges = this.dict.context.obj(flatRange);
		this.dict.set(PDFName.of("PrintPageRange"), pageRanges);
	}
	/**
	* Choose the default number of copies to be printed when the print dialog is
	* opened for this file.
	* @param numCopies The default number of copies.
	*/
	setNumCopies(numCopies) {
		assertRange(numCopies, "numCopies", 1, Number.MAX_VALUE);
		assertInteger(numCopies, "numCopies");
		const NumCopies = this.dict.context.obj(numCopies);
		this.dict.set(PDFName.of("NumCopies"), NumCopies);
	}
};
_ViewerPreferences = ViewerPreferences;
_defineProperty(ViewerPreferences, "fromDict", (dict) => new _ViewerPreferences(dict));
_defineProperty(ViewerPreferences, "create", (context) => {
	const dict = context.obj({});
	return new _ViewerPreferences(dict);
});
//#endregion
//#region src/core/acroform/PDFAcroField.ts
var tfRegex$1 = /\/([^\u0000\t\n\f\r ]+)[\u0000\t\n\f\r ]*(\d*\.\d+|\d+)?[\u0000\t\n\f\r ]+Tf/;
var PDFAcroField = class PDFAcroField {
	constructor(dict, ref) {
		_defineProperty(this, "dict", void 0);
		_defineProperty(this, "ref", void 0);
		this.dict = dict;
		this.ref = ref;
	}
	T() {
		return this.dict.lookupMaybe(PDFName.of("T"), PDFString, PDFHexString);
	}
	Ff() {
		const numberOrRef = this.getInheritableAttribute(PDFName.of("Ff"));
		return this.dict.context.lookupMaybe(numberOrRef, PDFNumber);
	}
	V() {
		const valueOrRef = this.getInheritableAttribute(PDFName.of("V"));
		return this.dict.context.lookup(valueOrRef);
	}
	Kids() {
		return this.dict.lookupMaybe(PDFName.of("Kids"), PDFArray);
	}
	DA() {
		const da = this.dict.lookup(PDFName.of("DA"));
		if (da instanceof PDFString || da instanceof PDFHexString) return da;
	}
	setKids(kids) {
		this.dict.set(PDFName.of("Kids"), this.dict.context.obj(kids));
	}
	getParent() {
		const parentRef = this.dict.get(PDFName.of("Parent"));
		if (parentRef instanceof PDFRef) return new PDFAcroField(this.dict.lookup(PDFName.of("Parent"), PDFDict), parentRef);
	}
	setParent(parent) {
		if (!parent) this.dict.delete(PDFName.of("Parent"));
		else this.dict.set(PDFName.of("Parent"), parent);
	}
	getFullyQualifiedName() {
		const parent = this.getParent();
		if (!parent) return this.getPartialName();
		return `${parent.getFullyQualifiedName()}.${this.getPartialName()}`;
	}
	getPartialName() {
		return this.T()?.decodeText();
	}
	setPartialName(partialName) {
		if (!partialName) this.dict.delete(PDFName.of("T"));
		else this.dict.set(PDFName.of("T"), PDFHexString.fromText(partialName));
	}
	setDefaultAppearance(appearance) {
		this.dict.set(PDFName.of("DA"), PDFString.of(appearance));
	}
	getDefaultAppearance() {
		const DA = this.DA();
		if (DA instanceof PDFHexString) return DA.decodeText();
		return DA?.asString();
	}
	setFontSize(fontSize) {
		const name = this.getFullyQualifiedName() ?? "";
		const da = this.getDefaultAppearance();
		if (!da) throw new MissingDAEntryError(name);
		const daMatch = findLastMatch(da, tfRegex$1);
		if (!daMatch.match) throw new MissingTfOperatorError(name);
		const daStart = da.slice(0, daMatch.pos - daMatch.match[0].length);
		const daEnd = daMatch.pos <= da.length ? da.slice(daMatch.pos) : "";
		const modifiedDa = `${daStart} /${daMatch.match[1]} ${fontSize} Tf ${daEnd}`;
		this.setDefaultAppearance(modifiedDa);
	}
	getFlags() {
		return this.Ff()?.asNumber() ?? 0;
	}
	setFlags(flags) {
		this.dict.set(PDFName.of("Ff"), PDFNumber.of(flags));
	}
	hasFlag(flag) {
		return (this.getFlags() & flag) !== 0;
	}
	setFlag(flag) {
		const flags = this.getFlags();
		this.setFlags(flags | flag);
	}
	clearFlag(flag) {
		const flags = this.getFlags();
		this.setFlags(flags & ~flag);
	}
	setFlagTo(flag, enable) {
		if (enable) this.setFlag(flag);
		else this.clearFlag(flag);
	}
	getInheritableAttribute(name) {
		let attribute;
		this.ascend((node) => {
			if (!attribute) attribute = node.dict.get(name);
		});
		return attribute;
	}
	ascend(visitor) {
		visitor(this);
		const parent = this.getParent();
		if (parent) parent.ascend(visitor);
	}
};
//#endregion
//#region src/core/annotation/BorderStyle.ts
var _BorderStyle;
var BorderStyle = class {
	constructor(dict) {
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	W() {
		const W = this.dict.lookup(PDFName.of("W"));
		if (W instanceof PDFNumber) return W;
	}
	getWidth() {
		return this.W()?.asNumber() ?? 1;
	}
	setWidth(width) {
		const W = this.dict.context.obj(width);
		this.dict.set(PDFName.of("W"), W);
	}
};
_BorderStyle = BorderStyle;
_defineProperty(BorderStyle, "fromDict", (dict) => new _BorderStyle(dict));
//#endregion
//#region src/core/annotation/PDFAnnotation.ts
var _PDFAnnotation;
var PDFAnnotation = class {
	constructor(dict) {
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	Rect() {
		return this.dict.lookup(PDFName.of("Rect"), PDFArray);
	}
	AP() {
		return this.dict.lookupMaybe(PDFName.of("AP"), PDFDict);
	}
	F() {
		const numberOrRef = this.dict.lookup(PDFName.of("F"));
		return this.dict.context.lookupMaybe(numberOrRef, PDFNumber);
	}
	getRectangle() {
		return this.Rect()?.asRectangle() ?? {
			x: 0,
			y: 0,
			width: 0,
			height: 0
		};
	}
	setRectangle(rect) {
		const { x, y, width, height } = rect;
		const Rect = this.dict.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.dict.set(PDFName.of("Rect"), Rect);
	}
	getAppearanceState() {
		const AS = this.dict.lookup(PDFName.of("AS"));
		if (AS instanceof PDFName) return AS;
	}
	setAppearanceState(state) {
		this.dict.set(PDFName.of("AS"), state);
	}
	setAppearances(appearances) {
		this.dict.set(PDFName.of("AP"), appearances);
	}
	ensureAP() {
		let AP = this.AP();
		if (!AP) {
			AP = this.dict.context.obj({});
			this.dict.set(PDFName.of("AP"), AP);
		}
		return AP;
	}
	getNormalAppearance() {
		const N = this.ensureAP().get(PDFName.of("N"));
		if (N instanceof PDFRef || N instanceof PDFDict) return N;
		throw new Error(`Unexpected N type: ${N?.constructor.name}`);
	}
	/** @param appearance A PDFDict or PDFStream (direct or ref) */
	setNormalAppearance(appearance) {
		this.ensureAP().set(PDFName.of("N"), appearance);
	}
	/** @param appearance A PDFDict or PDFStream (direct or ref) */
	setRolloverAppearance(appearance) {
		this.ensureAP().set(PDFName.of("R"), appearance);
	}
	/** @param appearance A PDFDict or PDFStream (direct or ref) */
	setDownAppearance(appearance) {
		this.ensureAP().set(PDFName.of("D"), appearance);
	}
	removeRolloverAppearance() {
		this.AP()?.delete(PDFName.of("R"));
	}
	removeDownAppearance() {
		this.AP()?.delete(PDFName.of("D"));
	}
	getAppearances() {
		const AP = this.AP();
		if (!AP) return void 0;
		return {
			normal: AP.lookup(PDFName.of("N"), PDFDict, PDFStream),
			rollover: AP.lookupMaybe(PDFName.of("R"), PDFDict, PDFStream),
			down: AP.lookupMaybe(PDFName.of("D"), PDFDict, PDFStream)
		};
	}
	getFlags() {
		return this.F()?.asNumber() ?? 0;
	}
	setFlags(flags) {
		this.dict.set(PDFName.of("F"), PDFNumber.of(flags));
	}
	hasFlag(flag) {
		return (this.getFlags() & flag) !== 0;
	}
	setFlag(flag) {
		const flags = this.getFlags();
		this.setFlags(flags | flag);
	}
	clearFlag(flag) {
		const flags = this.getFlags();
		this.setFlags(flags & ~flag);
	}
	setFlagTo(flag, enable) {
		if (enable) this.setFlag(flag);
		else this.clearFlag(flag);
	}
};
_PDFAnnotation = PDFAnnotation;
_defineProperty(PDFAnnotation, "fromDict", (dict) => new _PDFAnnotation(dict));
//#endregion
//#region src/core/annotation/AppearanceCharacteristics.ts
var _AppearanceCharacteristics;
var AppearanceCharacteristics = class {
	constructor(dict) {
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	R() {
		const R = this.dict.lookup(PDFName.of("R"));
		if (R instanceof PDFNumber) return R;
	}
	BC() {
		const BC = this.dict.lookup(PDFName.of("BC"));
		if (BC instanceof PDFArray) return BC;
	}
	BG() {
		const BG = this.dict.lookup(PDFName.of("BG"));
		if (BG instanceof PDFArray) return BG;
	}
	CA() {
		const CA = this.dict.lookup(PDFName.of("CA"));
		if (CA instanceof PDFHexString || CA instanceof PDFString) return CA;
	}
	RC() {
		const RC = this.dict.lookup(PDFName.of("RC"));
		if (RC instanceof PDFHexString || RC instanceof PDFString) return RC;
	}
	AC() {
		const AC = this.dict.lookup(PDFName.of("AC"));
		if (AC instanceof PDFHexString || AC instanceof PDFString) return AC;
	}
	getRotation() {
		return this.R()?.asNumber();
	}
	getBorderColor() {
		const BC = this.BC();
		if (!BC) return void 0;
		const components = [];
		for (let idx = 0, len = BC?.size(); idx < len; idx++) {
			const component = BC.get(idx);
			if (component instanceof PDFNumber) components.push(component.asNumber());
		}
		return components;
	}
	getBackgroundColor() {
		const BG = this.BG();
		if (!BG) return void 0;
		const components = [];
		for (let idx = 0, len = BG?.size(); idx < len; idx++) {
			const component = BG.get(idx);
			if (component instanceof PDFNumber) components.push(component.asNumber());
		}
		return components;
	}
	getCaptions() {
		const CA = this.CA();
		const RC = this.RC();
		const AC = this.AC();
		return {
			normal: CA?.decodeText(),
			rollover: RC?.decodeText(),
			down: AC?.decodeText()
		};
	}
	setRotation(rotation) {
		const R = this.dict.context.obj(rotation);
		this.dict.set(PDFName.of("R"), R);
	}
	setBorderColor(color) {
		const BC = this.dict.context.obj(color);
		this.dict.set(PDFName.of("BC"), BC);
	}
	setBackgroundColor(color) {
		const BG = this.dict.context.obj(color);
		this.dict.set(PDFName.of("BG"), BG);
	}
	setCaptions(captions) {
		const CA = PDFHexString.fromText(captions.normal);
		this.dict.set(PDFName.of("CA"), CA);
		if (captions.rollover) {
			const RC = PDFHexString.fromText(captions.rollover);
			this.dict.set(PDFName.of("RC"), RC);
		} else this.dict.delete(PDFName.of("RC"));
		if (captions.down) {
			const AC = PDFHexString.fromText(captions.down);
			this.dict.set(PDFName.of("AC"), AC);
		} else this.dict.delete(PDFName.of("AC"));
	}
};
_AppearanceCharacteristics = AppearanceCharacteristics;
_defineProperty(AppearanceCharacteristics, "fromDict", (dict) => new _AppearanceCharacteristics(dict));
//#endregion
//#region src/core/annotation/PDFWidgetAnnotation.ts
var _PDFWidgetAnnotation;
var PDFWidgetAnnotation = class extends PDFAnnotation {
	MK() {
		const MK = this.dict.lookup(PDFName.of("MK"));
		if (MK instanceof PDFDict) return MK;
	}
	BS() {
		const BS = this.dict.lookup(PDFName.of("BS"));
		if (BS instanceof PDFDict) return BS;
	}
	DA() {
		const da = this.dict.lookup(PDFName.of("DA"));
		if (da instanceof PDFString || da instanceof PDFHexString) return da;
	}
	P() {
		const P = this.dict.get(PDFName.of("P"));
		if (P instanceof PDFRef) return P;
	}
	setP(page) {
		this.dict.set(PDFName.of("P"), page);
	}
	setDefaultAppearance(appearance) {
		this.dict.set(PDFName.of("DA"), PDFString.of(appearance));
	}
	getDefaultAppearance() {
		const DA = this.DA();
		if (DA instanceof PDFHexString) return DA.decodeText();
		return DA?.asString();
	}
	getAppearanceCharacteristics() {
		const MK = this.MK();
		if (MK) return AppearanceCharacteristics.fromDict(MK);
	}
	getOrCreateAppearanceCharacteristics() {
		const MK = this.MK();
		if (MK) return AppearanceCharacteristics.fromDict(MK);
		const ac = AppearanceCharacteristics.fromDict(this.dict.context.obj({}));
		this.dict.set(PDFName.of("MK"), ac.dict);
		return ac;
	}
	getBorderStyle() {
		const BS = this.BS();
		if (BS) return BorderStyle.fromDict(BS);
	}
	getOrCreateBorderStyle() {
		const BS = this.BS();
		if (BS) return BorderStyle.fromDict(BS);
		const bs = BorderStyle.fromDict(this.dict.context.obj({}));
		this.dict.set(PDFName.of("BS"), bs.dict);
		return bs;
	}
	getOnValue() {
		const normal = this.getAppearances()?.normal;
		if (normal instanceof PDFDict) {
			const keys = normal.keys();
			for (let idx = 0, len = keys.length; idx < len; idx++) {
				const key = keys[idx];
				if (key !== PDFName.of("Off")) return key;
			}
		}
	}
};
_PDFWidgetAnnotation = PDFWidgetAnnotation;
_defineProperty(PDFWidgetAnnotation, "fromDict", (dict) => new _PDFWidgetAnnotation(dict));
_defineProperty(PDFWidgetAnnotation, "create", (context, parent) => {
	const dict = context.obj({
		Type: "Annot",
		Subtype: "Widget",
		Rect: [
			0,
			0,
			0,
			0
		],
		Parent: parent
	});
	return new _PDFWidgetAnnotation(dict);
});
//#endregion
//#region src/core/acroform/PDFAcroTerminal.ts
var _PDFAcroTerminal;
var PDFAcroTerminal = class extends PDFAcroField {
	FT() {
		const nameOrRef = this.getInheritableAttribute(PDFName.of("FT"));
		return this.dict.context.lookup(nameOrRef, PDFName);
	}
	getWidgets() {
		const kidDicts = this.Kids();
		if (!kidDicts) return [PDFWidgetAnnotation.fromDict(this.dict)];
		const widgets = Array(kidDicts.size());
		for (let idx = 0, len = kidDicts.size(); idx < len; idx++) {
			const dict = kidDicts.lookup(idx, PDFDict);
			widgets[idx] = PDFWidgetAnnotation.fromDict(dict);
		}
		return widgets;
	}
	addWidget(ref) {
		const { Kids } = this.normalizedEntries();
		Kids.push(ref);
	}
	removeWidget(idx) {
		const kidDicts = this.Kids();
		if (!kidDicts) {
			if (idx !== 0) throw new IndexOutOfBoundsError(idx, 0, 0);
			this.setKids([]);
		} else {
			if (idx < 0 || idx > kidDicts.size()) throw new IndexOutOfBoundsError(idx, 0, kidDicts.size());
			kidDicts.remove(idx);
		}
	}
	normalizedEntries() {
		let Kids = this.Kids();
		if (!Kids) {
			Kids = this.dict.context.obj([this.ref]);
			this.dict.set(PDFName.of("Kids"), Kids);
		}
		return { Kids };
	}
};
_PDFAcroTerminal = PDFAcroTerminal;
_defineProperty(PDFAcroTerminal, "fromDict", (dict, ref) => new _PDFAcroTerminal(dict, ref));
//#endregion
//#region src/core/acroform/PDFAcroButton.ts
var PDFAcroButton = class extends PDFAcroTerminal {
	Opt() {
		return this.dict.lookupMaybe(PDFName.of("Opt"), PDFString, PDFHexString, PDFArray);
	}
	setOpt(opt) {
		this.dict.set(PDFName.of("Opt"), this.dict.context.obj(opt));
	}
	getExportValues() {
		const opt = this.Opt();
		if (!opt) return void 0;
		if (opt instanceof PDFString || opt instanceof PDFHexString) return [opt];
		const values = [];
		for (let idx = 0, len = opt.size(); idx < len; idx++) {
			const value = opt.lookup(idx);
			if (value instanceof PDFString || value instanceof PDFHexString) values.push(value);
		}
		return values;
	}
	removeExportValue(idx) {
		const opt = this.Opt();
		if (!opt) return;
		if (opt instanceof PDFString || opt instanceof PDFHexString) {
			if (idx !== 0) throw new IndexOutOfBoundsError(idx, 0, 0);
			this.setOpt([]);
		} else {
			if (idx < 0 || idx > opt.size()) throw new IndexOutOfBoundsError(idx, 0, opt.size());
			opt.remove(idx);
		}
	}
	normalizeExportValues() {
		const exportValues = this.getExportValues() ?? [];
		const Opt = [];
		const widgets = this.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const exportVal = exportValues[idx] ?? PDFHexString.fromText(widget.getOnValue()?.decodeText() ?? "");
			Opt.push(exportVal);
		}
		this.setOpt(Opt);
	}
	/**
	* Reuses existing opt if one exists with the same value (assuming
	* `useExistingIdx` is `true`). Returns index of existing (or new) opt.
	*/
	addOpt(opt, useExistingOptIdx) {
		this.normalizeExportValues();
		const optText = opt.decodeText();
		let existingIdx;
		if (useExistingOptIdx) {
			const exportValues = this.getExportValues() ?? [];
			for (let idx = 0, len = exportValues.length; idx < len; idx++) if (exportValues[idx].decodeText() === optText) existingIdx = idx;
		}
		const Opt = this.Opt();
		Opt.push(opt);
		return existingIdx ?? Opt.size() - 1;
	}
	addWidgetWithOpt(widget, opt, useExistingOptIdx) {
		const optIdx = this.addOpt(opt, useExistingOptIdx);
		const apStateValue = PDFName.of(String(optIdx));
		this.addWidget(widget);
		return apStateValue;
	}
};
//#endregion
//#region src/core/acroform/PDFAcroCheckBox.ts
var _PDFAcroCheckBox;
var PDFAcroCheckBox = class extends PDFAcroButton {
	setValue(value) {
		if (value !== (this.getOnValue() ?? PDFName.of("Yes")) && value !== PDFName.of("Off")) throw new InvalidAcroFieldValueError();
		this.dict.set(PDFName.of("V"), value);
		const widgets = this.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const state = widget.getOnValue() === value ? value : PDFName.of("Off");
			widget.setAppearanceState(state);
		}
	}
	getValue() {
		const v = this.V();
		if (v instanceof PDFName) return v;
		return PDFName.of("Off");
	}
	getOnValue() {
		const [widget] = this.getWidgets();
		return widget?.getOnValue();
	}
};
_PDFAcroCheckBox = PDFAcroCheckBox;
_defineProperty(PDFAcroCheckBox, "fromDict", (dict, ref) => new _PDFAcroCheckBox(dict, ref));
_defineProperty(PDFAcroCheckBox, "create", (context) => {
	const dict = context.obj({
		FT: "Btn",
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroCheckBox(dict, ref);
});
//#endregion
//#region src/core/acroform/flags.ts
var flag$1 = (bitIndex) => 1 << bitIndex;
/** From PDF spec table 221 */
var AcroFieldFlags = function(AcroFieldFlags) {
	/**
	* If set, the user may not change the value of the field. Any associated
	* widget annotations will not interact with the user; that is, they will not
	* respond to mouse clicks or change their appearance in response to mouse
	* motions. This flag is useful for fields whose values are computed or
	* imported from a database.
	*/
	AcroFieldFlags[AcroFieldFlags["ReadOnly"] = flag$1(0)] = "ReadOnly";
	/**
	* If set, the field shall have a value at the time it is exported by a
	* submit-form action (see 12.7.5.2, "Submit-Form Action").
	*/
	AcroFieldFlags[AcroFieldFlags["Required"] = flag$1(1)] = "Required";
	/**
	* If set, the field shall not be exported by a submit-form action
	* (see 12.7.5.2, "Submit-Form Action").
	*/
	AcroFieldFlags[AcroFieldFlags["NoExport"] = flag$1(2)] = "NoExport";
	return AcroFieldFlags;
}({});
/** From PDF spec table 226 */
var AcroButtonFlags = function(AcroButtonFlags) {
	/**
	* (Radio buttons only) If set, exactly one radio button shall be selected at
	* all times; selecting the currently selected button has no effect. If clear,
	* clicking the selected button deselects it, leaving no button selected.
	*/
	AcroButtonFlags[AcroButtonFlags["NoToggleToOff"] = flag$1(14)] = "NoToggleToOff";
	/**
	* If set, the field is a set of radio buttons; if clear, the field is a check
	* box. This flag may be set only if the Pushbutton flag is clear.
	*/
	AcroButtonFlags[AcroButtonFlags["Radio"] = flag$1(15)] = "Radio";
	/**
	* If set, the field is a pushbutton that does not retain a permanent value.
	*/
	AcroButtonFlags[AcroButtonFlags["PushButton"] = flag$1(16)] = "PushButton";
	/**
	* If set, a group of radio buttons within a radio button field that use the
	* same value for the on state will turn on and off in unison; that is if one
	* is checked, they are all checked. If clear, the buttons are mutually
	* exclusive (the same behavior as HTML radio buttons).
	*/
	AcroButtonFlags[AcroButtonFlags["RadiosInUnison"] = flag$1(25)] = "RadiosInUnison";
	return AcroButtonFlags;
}({});
/** From PDF spec table 228 */
var AcroTextFlags = function(AcroTextFlags) {
	/**
	* If set, the field may contain multiple lines of text; if clear, the field's
	* text shall be restricted to a single line.
	*/
	AcroTextFlags[AcroTextFlags["Multiline"] = flag$1(12)] = "Multiline";
	/**
	* If set, the field is intended for entering a secure password that should
	* not be echoed visibly to the screen. Characters typed from the keyboard
	* shall instead be echoed in some unreadable form, such as asterisks or
	* bullet characters.
	* > NOTE   To protect password confidentiality, readers should never store
	* >        the value of the text field in the PDF file if this flag is set.
	*/
	AcroTextFlags[AcroTextFlags["Password"] = flag$1(13)] = "Password";
	/**
	* If set, the text entered in the field represents the pathname of a file
	* whose contents shall be submitted as the value of the field.
	*/
	AcroTextFlags[AcroTextFlags["FileSelect"] = flag$1(20)] = "FileSelect";
	/**
	* If set, text entered in the field shall not be spell-checked.
	*/
	AcroTextFlags[AcroTextFlags["DoNotSpellCheck"] = flag$1(22)] = "DoNotSpellCheck";
	/**
	* If set, the field shall not scroll (horizontally for single-line fields,
	* vertically for multiple-line fields) to accommodate more text than fits
	* within its annotation rectangle. Once the field is full, no further text
	* shall be accepted for interactive form filling; for non-interactive form
	* filling, the filler should take care not to add more character than will
	* visibly fit in the defined area.
	*/
	AcroTextFlags[AcroTextFlags["DoNotScroll"] = flag$1(23)] = "DoNotScroll";
	/**
	* May be set only if the MaxLen entry is present in the text field dictionary
	* (see Table 229) and if the Multiline, Password, and FileSelect flags are
	* clear. If set, the field shall be automatically divided into as many
	* equally spaced positions, or combs, as the value of MaxLen, and the text
	* is laid out into those combs.
	*/
	AcroTextFlags[AcroTextFlags["Comb"] = flag$1(24)] = "Comb";
	/**
	* If set, the value of this field shall be a rich text string
	* (see 12.7.3.4, "Rich Text Strings"). If the field has a value, the RV
	* entry of the field dictionary (Table 222) shall specify the rich text
	* string.
	*/
	AcroTextFlags[AcroTextFlags["RichText"] = flag$1(25)] = "RichText";
	return AcroTextFlags;
}({});
/** From PDF spec table 230 */
var AcroChoiceFlags = function(AcroChoiceFlags) {
	/**
	* If set, the field is a combo box; if clear, the field is a list box.
	*/
	AcroChoiceFlags[AcroChoiceFlags["Combo"] = flag$1(17)] = "Combo";
	/**
	* If set, the combo box shall include an editable text box as well as a
	* drop-down list; if clear, it shall include only a drop-down list. This
	* flag shall be used only if the Combo flag is set.
	*/
	AcroChoiceFlags[AcroChoiceFlags["Edit"] = flag$1(18)] = "Edit";
	/**
	* If set, the field's option items shall be sorted alphabetically. This flag
	* is intended for use by writers, not by readers. Conforming readers shall
	* display the options in the order in which they occur in the Opt array
	* (see Table 231).
	*/
	AcroChoiceFlags[AcroChoiceFlags["Sort"] = flag$1(19)] = "Sort";
	/**
	* If set, more than one of the field's option items may be selected
	* simultaneously; if clear, at most one item shall be selected.
	*/
	AcroChoiceFlags[AcroChoiceFlags["MultiSelect"] = flag$1(21)] = "MultiSelect";
	/**
	* If set, text entered in the field shall not be spell-checked. This flag
	* shall not be used unless the Combo and Edit flags are both set.
	*/
	AcroChoiceFlags[AcroChoiceFlags["DoNotSpellCheck"] = flag$1(22)] = "DoNotSpellCheck";
	/**
	* If set, the new value shall be committed as soon as a selection is made
	* (commonly with the pointing device). In this case, supplying a value for
	* a field involves three actions: selecting the field for fill-in,
	* selecting a choice for the fill-in value, and leaving that field, which
	* finalizes or "commits" the data choice and triggers any actions associated
	* with the entry or changing of this data. If this flag is on, then
	* processing does not wait for leaving the field action to occur, but
	* immediately proceeds to the third step.
	*
	* This option enables applications to perform an action once a selection is
	* made, without requiring the user to exit the field. If clear, the new
	* value is not committed until the user exits the field.
	*/
	AcroChoiceFlags[AcroChoiceFlags["CommitOnSelChange"] = flag$1(26)] = "CommitOnSelChange";
	return AcroChoiceFlags;
}({});
//#endregion
//#region src/core/acroform/PDFAcroChoice.ts
var PDFAcroChoice = class extends PDFAcroTerminal {
	setValues(values) {
		if (this.hasFlag(AcroChoiceFlags.Combo) && !this.hasFlag(AcroChoiceFlags.Edit) && !this.valuesAreValid(values)) throw new InvalidAcroFieldValueError();
		if (values.length === 0) this.dict.delete(PDFName.of("V"));
		if (values.length === 1) this.dict.set(PDFName.of("V"), values[0]);
		if (values.length > 1) {
			if (!this.hasFlag(AcroChoiceFlags.MultiSelect)) throw new MultiSelectValueError();
			this.dict.set(PDFName.of("V"), this.dict.context.obj(values));
		}
		this.updateSelectedIndices(values);
	}
	valuesAreValid(values) {
		const options = this.getOptions();
		for (let idx = 0, len = values.length; idx < len; idx++) {
			const val = values[idx].decodeText();
			if (!options.find((o) => val === (o.display || o.value).decodeText())) return false;
		}
		return true;
	}
	updateSelectedIndices(values) {
		if (values.length > 1) {
			const indices = Array(values.length);
			const options = this.getOptions();
			for (let idx = 0, len = values.length; idx < len; idx++) {
				const val = values[idx].decodeText();
				indices[idx] = options.findIndex((o) => val === (o.display || o.value).decodeText());
			}
			this.dict.set(PDFName.of("I"), this.dict.context.obj(indices.sort()));
		} else this.dict.delete(PDFName.of("I"));
	}
	getValues() {
		const v = this.V();
		if (v instanceof PDFString || v instanceof PDFHexString) return [v];
		if (v instanceof PDFArray) {
			const values = [];
			for (let idx = 0, len = v.size(); idx < len; idx++) {
				const value = v.lookup(idx);
				if (value instanceof PDFString || value instanceof PDFHexString) values.push(value);
			}
			return values;
		}
		return [];
	}
	Opt() {
		return this.dict.lookupMaybe(PDFName.of("Opt"), PDFString, PDFHexString, PDFArray);
	}
	setOptions(options) {
		const newOpt = Array(options.length);
		for (let idx = 0, len = options.length; idx < len; idx++) {
			const { value, display } = options[idx];
			newOpt[idx] = this.dict.context.obj([value, display || value]);
		}
		this.dict.set(PDFName.of("Opt"), this.dict.context.obj(newOpt));
	}
	getOptions() {
		const Opt = this.Opt();
		if (Opt instanceof PDFString || Opt instanceof PDFHexString) return [{
			value: Opt,
			display: Opt
		}];
		if (Opt instanceof PDFArray) {
			const res = [];
			for (let idx = 0, len = Opt.size(); idx < len; idx++) {
				const item = Opt.lookup(idx);
				if (item instanceof PDFString || item instanceof PDFHexString) res.push({
					value: item,
					display: item
				});
				if (item instanceof PDFArray) {
					if (item.size() > 0) {
						const first = item.lookup(0, PDFString, PDFHexString);
						const second = item.lookupMaybe(1, PDFString, PDFHexString);
						res.push({
							value: first,
							display: second || first
						});
					}
				}
			}
			return res;
		}
		return [];
	}
};
//#endregion
//#region src/core/acroform/PDFAcroComboBox.ts
var _PDFAcroComboBox;
var PDFAcroComboBox = class extends PDFAcroChoice {};
_PDFAcroComboBox = PDFAcroComboBox;
_defineProperty(PDFAcroComboBox, "fromDict", (dict, ref) => new _PDFAcroComboBox(dict, ref));
_defineProperty(PDFAcroComboBox, "create", (context) => {
	const dict = context.obj({
		FT: "Ch",
		Ff: AcroChoiceFlags.Combo,
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroComboBox(dict, ref);
});
//#endregion
//#region src/core/acroform/PDFAcroNonTerminal.ts
var _PDFAcroNonTerminal;
var PDFAcroNonTerminal = class extends PDFAcroField {
	addField(field) {
		const { Kids } = this.normalizedEntries();
		Kids?.push(field);
	}
	normalizedEntries() {
		let Kids = this.Kids();
		if (!Kids) {
			Kids = this.dict.context.obj([]);
			this.dict.set(PDFName.of("Kids"), Kids);
		}
		return { Kids };
	}
};
_PDFAcroNonTerminal = PDFAcroNonTerminal;
_defineProperty(PDFAcroNonTerminal, "fromDict", (dict, ref) => new _PDFAcroNonTerminal(dict, ref));
_defineProperty(PDFAcroNonTerminal, "create", (context) => {
	const dict = context.obj({});
	const ref = context.register(dict);
	return new _PDFAcroNonTerminal(dict, ref);
});
//#endregion
//#region src/core/acroform/PDFAcroSignature.ts
var _PDFAcroSignature;
var PDFAcroSignature = class extends PDFAcroTerminal {};
_PDFAcroSignature = PDFAcroSignature;
_defineProperty(PDFAcroSignature, "fromDict", (dict, ref) => new _PDFAcroSignature(dict, ref));
//#endregion
//#region src/core/acroform/PDFAcroText.ts
var _PDFAcroText;
var PDFAcroText = class extends PDFAcroTerminal {
	MaxLen() {
		const maxLen = this.dict.lookup(PDFName.of("MaxLen"));
		if (maxLen instanceof PDFNumber) return maxLen;
	}
	Q() {
		const q = this.dict.lookup(PDFName.of("Q"));
		if (q instanceof PDFNumber) return q;
	}
	setMaxLength(maxLength) {
		this.dict.set(PDFName.of("MaxLen"), PDFNumber.of(maxLength));
	}
	removeMaxLength() {
		this.dict.delete(PDFName.of("MaxLen"));
	}
	getMaxLength() {
		return this.MaxLen()?.asNumber();
	}
	setQuadding(quadding) {
		this.dict.set(PDFName.of("Q"), PDFNumber.of(quadding));
	}
	getQuadding() {
		return this.Q()?.asNumber();
	}
	setValue(value) {
		this.dict.set(PDFName.of("V"), value);
	}
	removeValue() {
		this.dict.delete(PDFName.of("V"));
	}
	getValue() {
		const v = this.V();
		if (v instanceof PDFString || v instanceof PDFHexString) return v;
	}
};
_PDFAcroText = PDFAcroText;
_defineProperty(PDFAcroText, "fromDict", (dict, ref) => new _PDFAcroText(dict, ref));
_defineProperty(PDFAcroText, "create", (context) => {
	const dict = context.obj({
		FT: "Tx",
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroText(dict, ref);
});
//#endregion
//#region src/core/acroform/PDFAcroPushButton.ts
var _PDFAcroPushButton;
var PDFAcroPushButton = class extends PDFAcroButton {};
_PDFAcroPushButton = PDFAcroPushButton;
_defineProperty(PDFAcroPushButton, "fromDict", (dict, ref) => new _PDFAcroPushButton(dict, ref));
_defineProperty(PDFAcroPushButton, "create", (context) => {
	const dict = context.obj({
		FT: "Btn",
		Ff: AcroButtonFlags.PushButton,
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroPushButton(dict, ref);
});
//#endregion
//#region src/core/acroform/PDFAcroRadioButton.ts
var _PDFAcroRadioButton;
var PDFAcroRadioButton = class extends PDFAcroButton {
	setValue(value) {
		if (!this.getOnValues().includes(value) && value !== PDFName.of("Off")) throw new InvalidAcroFieldValueError();
		this.dict.set(PDFName.of("V"), value);
		const widgets = this.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const state = widget.getOnValue() === value ? value : PDFName.of("Off");
			widget.setAppearanceState(state);
		}
	}
	getValue() {
		const v = this.V();
		if (v instanceof PDFName) return v;
		return PDFName.of("Off");
	}
	getOnValues() {
		const widgets = this.getWidgets();
		const onValues = [];
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const onValue = widgets[idx].getOnValue();
			if (onValue) onValues.push(onValue);
		}
		return onValues;
	}
};
_PDFAcroRadioButton = PDFAcroRadioButton;
_defineProperty(PDFAcroRadioButton, "fromDict", (dict, ref) => new _PDFAcroRadioButton(dict, ref));
_defineProperty(PDFAcroRadioButton, "create", (context) => {
	const dict = context.obj({
		FT: "Btn",
		Ff: AcroButtonFlags.Radio,
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroRadioButton(dict, ref);
});
//#endregion
//#region src/core/acroform/PDFAcroListBox.ts
var _PDFAcroListBox;
var PDFAcroListBox = class extends PDFAcroChoice {};
_PDFAcroListBox = PDFAcroListBox;
_defineProperty(PDFAcroListBox, "fromDict", (dict, ref) => new _PDFAcroListBox(dict, ref));
_defineProperty(PDFAcroListBox, "create", (context) => {
	const dict = context.obj({
		FT: "Ch",
		Kids: []
	});
	const ref = context.register(dict);
	return new _PDFAcroListBox(dict, ref);
});
//#endregion
//#region src/core/acroform/utils.ts
var createPDFAcroFields = (kidDicts) => {
	if (!kidDicts) return [];
	const kids = [];
	for (let idx = 0, len = kidDicts.size(); idx < len; idx++) {
		const ref = kidDicts.get(idx);
		const dict = kidDicts.lookup(idx);
		if (ref instanceof PDFRef && dict instanceof PDFDict) kids.push([createPDFAcroField(dict, ref), ref]);
	}
	return kids;
};
var createPDFAcroField = (dict, ref) => {
	if (isNonTerminalAcroField(dict)) return PDFAcroNonTerminal.fromDict(dict, ref);
	return createPDFAcroTerminal(dict, ref);
};
var isNonTerminalAcroField = (dict) => {
	const kids = dict.lookup(PDFName.of("Kids"));
	if (kids instanceof PDFArray) for (let idx = 0, len = kids.size(); idx < len; idx++) {
		const kid = kids.lookup(idx);
		if (kid instanceof PDFDict && kid.has(PDFName.of("T"))) return true;
	}
	return false;
};
var createPDFAcroTerminal = (dict, ref) => {
	const ftNameOrRef = getInheritableAttribute(dict, PDFName.of("FT"));
	const type = dict.context.lookup(ftNameOrRef, PDFName);
	if (type === PDFName.of("Btn")) return createPDFAcroButton(dict, ref);
	if (type === PDFName.of("Ch")) return createPDFAcroChoice(dict, ref);
	if (type === PDFName.of("Tx")) return PDFAcroText.fromDict(dict, ref);
	if (type === PDFName.of("Sig")) return PDFAcroSignature.fromDict(dict, ref);
	return PDFAcroTerminal.fromDict(dict, ref);
};
var createPDFAcroButton = (dict, ref) => {
	const ffNumberOrRef = getInheritableAttribute(dict, PDFName.of("Ff"));
	const flags = dict.context.lookupMaybe(ffNumberOrRef, PDFNumber)?.asNumber() ?? 0;
	if (flagIsSet(flags, AcroButtonFlags.PushButton)) return PDFAcroPushButton.fromDict(dict, ref);
	else if (flagIsSet(flags, AcroButtonFlags.Radio)) return PDFAcroRadioButton.fromDict(dict, ref);
	else return PDFAcroCheckBox.fromDict(dict, ref);
};
var createPDFAcroChoice = (dict, ref) => {
	const ffNumberOrRef = getInheritableAttribute(dict, PDFName.of("Ff"));
	if (flagIsSet(dict.context.lookupMaybe(ffNumberOrRef, PDFNumber)?.asNumber() ?? 0, AcroChoiceFlags.Combo)) return PDFAcroComboBox.fromDict(dict, ref);
	else return PDFAcroListBox.fromDict(dict, ref);
};
var flagIsSet = (flags, flag) => (flags & flag) !== 0;
var getInheritableAttribute = (startNode, name) => {
	let attribute;
	ascend(startNode, (node) => {
		if (!attribute) attribute = node.get(name);
	});
	return attribute;
};
var ascend = (startNode, visitor) => {
	visitor(startNode);
	const Parent = startNode.lookupMaybe(PDFName.of("Parent"), PDFDict);
	if (Parent) ascend(Parent, visitor);
};
//#endregion
//#region src/core/acroform/PDFAcroForm.ts
var _PDFAcroForm;
var PDFAcroForm = class {
	constructor(dict) {
		_defineProperty(this, "dict", void 0);
		this.dict = dict;
	}
	Fields() {
		const fields = this.dict.lookup(PDFName.of("Fields"));
		if (fields instanceof PDFArray) return fields;
	}
	getFields() {
		const { Fields } = this.normalizedEntries();
		const fields = Array(Fields.size());
		for (let idx = 0, len = Fields.size(); idx < len; idx++) {
			const ref = Fields.get(idx);
			fields[idx] = [createPDFAcroField(Fields.lookup(idx, PDFDict), ref), ref];
		}
		return fields;
	}
	getAllFields() {
		const allFields = [];
		const pushFields = (fields) => {
			if (!fields) return;
			for (let idx = 0, len = fields.length; idx < len; idx++) {
				const field = fields[idx];
				allFields.push(field);
				const [fieldModel] = field;
				if (fieldModel instanceof PDFAcroNonTerminal) pushFields(createPDFAcroFields(fieldModel.Kids()));
			}
		};
		pushFields(this.getFields());
		return allFields;
	}
	addField(field) {
		const { Fields } = this.normalizedEntries();
		Fields?.push(field);
	}
	removeField(field) {
		const parent = field.getParent();
		const fields = parent === void 0 ? this.normalizedEntries().Fields : parent.Kids();
		const index = fields?.indexOf(field.ref);
		if (fields === void 0 || index === void 0) throw new Error(`Tried to remove inexistent field ${field.getFullyQualifiedName()}`);
		fields.remove(index);
		if (parent !== void 0 && fields.size() === 0) this.removeField(parent);
	}
	normalizedEntries() {
		let Fields = this.Fields();
		if (!Fields) {
			Fields = this.dict.context.obj([]);
			this.dict.set(PDFName.of("Fields"), Fields);
		}
		return { Fields };
	}
};
_PDFAcroForm = PDFAcroForm;
_defineProperty(PDFAcroForm, "fromDict", (dict) => new _PDFAcroForm(dict));
_defineProperty(PDFAcroForm, "create", (context) => {
	const dict = context.obj({ Fields: [] });
	return new _PDFAcroForm(dict);
});
//#endregion
//#region src/core/structures/PDFCatalog.ts
var _PDFCatalog;
var PDFCatalog = class extends PDFDict {
	Pages() {
		return this.lookup(PDFName.of("Pages"), PDFDict);
	}
	AcroForm() {
		return this.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
	}
	getAcroForm() {
		const dict = this.AcroForm();
		if (!dict) return void 0;
		return PDFAcroForm.fromDict(dict);
	}
	getOrCreateAcroForm() {
		let acroForm = this.getAcroForm();
		if (!acroForm) {
			acroForm = PDFAcroForm.create(this.context);
			const acroFormRef = this.context.register(acroForm.dict);
			this.set(PDFName.of("AcroForm"), acroFormRef);
		}
		return acroForm;
	}
	ViewerPreferences() {
		return this.lookupMaybe(PDFName.of("ViewerPreferences"), PDFDict);
	}
	getViewerPreferences() {
		const dict = this.ViewerPreferences();
		if (!dict) return void 0;
		return ViewerPreferences.fromDict(dict);
	}
	getOrCreateViewerPreferences() {
		let viewerPrefs = this.getViewerPreferences();
		if (!viewerPrefs) {
			viewerPrefs = ViewerPreferences.create(this.context);
			const viewerPrefsRef = this.context.register(viewerPrefs.dict);
			this.set(PDFName.of("ViewerPreferences"), viewerPrefsRef);
		}
		return viewerPrefs;
	}
	/**
	* Inserts the given ref as a leaf node of this catalog's page tree at the
	* specified index (zero-based). Also increments the `Count` of each node in
	* the page tree hierarchy to accomodate the new page.
	*
	* Returns the ref of the PDFPageTree node into which `leafRef` was inserted.
	*/
	insertLeafNode(leafRef, index) {
		const pagesRef = this.get(PDFName.of("Pages"));
		return this.Pages().insertLeafNode(leafRef, index) || pagesRef;
	}
	removeLeafNode(index) {
		this.Pages().removeLeafNode(index);
	}
};
_PDFCatalog = PDFCatalog;
_defineProperty(PDFCatalog, "withContextAndPages", (context, pages) => {
	const dict = /* @__PURE__ */ new Map();
	dict.set(PDFName.of("Type"), PDFName.of("Catalog"));
	dict.set(PDFName.of("Pages"), pages);
	return new _PDFCatalog(dict, context);
});
_defineProperty(PDFCatalog, "fromMapWithContext", (map, context) => new _PDFCatalog(map, context));
//#endregion
//#region src/core/structures/PDFPageTree.ts
var _PDFPageTree;
var PDFPageTree = class PDFPageTree extends PDFDict {
	Parent() {
		return this.lookup(PDFName.of("Parent"));
	}
	Kids() {
		return this.lookup(PDFName.of("Kids"), PDFArray);
	}
	Count() {
		return this.lookup(PDFName.of("Count"), PDFNumber);
	}
	pushTreeNode(treeRef) {
		this.Kids().push(treeRef);
	}
	pushLeafNode(leafRef) {
		const Kids = this.Kids();
		this.insertLeafKid(Kids.size(), leafRef);
	}
	/**
	* Inserts the given ref as a leaf node of this page tree at the specified
	* index (zero-based). Also increments the `Count` of each page tree in the
	* hierarchy to accomodate the new page.
	*
	* Returns the ref of the PDFPageTree node into which `leafRef` was inserted,
	* or `undefined` if it was inserted into the root node (the PDFPageTree upon
	* which the method was first called).
	*/
	insertLeafNode(leafRef, targetIndex) {
		const Kids = this.Kids();
		const Count = this.Count().asNumber();
		if (targetIndex > Count) throw new InvalidTargetIndexError(targetIndex, Count);
		let leafsRemainingUntilTarget = targetIndex;
		for (let idx = 0, len = Kids.size(); idx < len; idx++) {
			if (leafsRemainingUntilTarget === 0) {
				this.insertLeafKid(idx, leafRef);
				return;
			}
			const kidRef = Kids.get(idx);
			const kid = this.context.lookup(kidRef);
			if (kid instanceof PDFPageTree) if (kid.Count().asNumber() > leafsRemainingUntilTarget) return kid.insertLeafNode(leafRef, leafsRemainingUntilTarget) || kidRef;
			else leafsRemainingUntilTarget -= kid.Count().asNumber();
			if (kid instanceof PDFPageLeaf) leafsRemainingUntilTarget -= 1;
		}
		if (leafsRemainingUntilTarget === 0) {
			this.insertLeafKid(Kids.size(), leafRef);
			return;
		}
		throw new CorruptPageTreeError(targetIndex, "insertLeafNode");
	}
	/**
	* Removes the leaf node at the specified index (zero-based) from this page
	* tree. Also decrements the `Count` of each page tree in the hierarchy to
	* account for the removed page.
	*
	* If `prune` is true, then intermediate tree nodes will be removed from the
	* tree if they contain 0 children after the leaf node is removed.
	*/
	removeLeafNode(targetIndex, prune = true) {
		const Kids = this.Kids();
		const Count = this.Count().asNumber();
		if (targetIndex >= Count) throw new InvalidTargetIndexError(targetIndex, Count);
		let leafsRemainingUntilTarget = targetIndex;
		for (let idx = 0, len = Kids.size(); idx < len; idx++) {
			const kidRef = Kids.get(idx);
			const kid = this.context.lookup(kidRef);
			if (kid instanceof PDFPageTree) if (kid.Count().asNumber() > leafsRemainingUntilTarget) {
				kid.removeLeafNode(leafsRemainingUntilTarget, prune);
				if (prune && kid.Kids().size() === 0) Kids.remove(idx);
				return;
			} else leafsRemainingUntilTarget -= kid.Count().asNumber();
			if (kid instanceof PDFPageLeaf) if (leafsRemainingUntilTarget === 0) {
				this.removeKid(idx);
				return;
			} else leafsRemainingUntilTarget -= 1;
		}
		throw new CorruptPageTreeError(targetIndex, "removeLeafNode");
	}
	ascend(visitor) {
		visitor(this);
		const Parent = this.Parent();
		if (Parent) Parent.ascend(visitor);
	}
	/** Performs a Post-Order traversal of this page tree */
	traverse(visitor) {
		const Kids = this.Kids();
		for (let idx = 0, len = Kids.size(); idx < len; idx++) {
			const kidRef = Kids.get(idx);
			const kid = this.context.lookup(kidRef);
			if (kid instanceof PDFPageTree) kid.traverse(visitor);
			visitor(kid, kidRef);
		}
	}
	insertLeafKid(kidIdx, leafRef) {
		const Kids = this.Kids();
		this.ascend((node) => {
			const newCount = node.Count().asNumber() + 1;
			node.set(PDFName.of("Count"), PDFNumber.of(newCount));
		});
		Kids.insert(kidIdx, leafRef);
	}
	removeKid(kidIdx) {
		const Kids = this.Kids();
		if (Kids.lookup(kidIdx) instanceof PDFPageLeaf) this.ascend((node) => {
			const newCount = node.Count().asNumber() - 1;
			node.set(PDFName.of("Count"), PDFNumber.of(newCount));
		});
		Kids.remove(kidIdx);
	}
};
_PDFPageTree = PDFPageTree;
_defineProperty(PDFPageTree, "withContext", (context, parent) => {
	const dict = /* @__PURE__ */ new Map();
	dict.set(PDFName.of("Type"), PDFName.of("Pages"));
	dict.set(PDFName.of("Kids"), context.obj([]));
	dict.set(PDFName.of("Count"), context.obj(0));
	if (parent) dict.set(PDFName.of("Parent"), parent);
	return new _PDFPageTree(dict, context);
});
_defineProperty(PDFPageTree, "fromMapWithContext", (map, context) => new _PDFPageTree(map, context));
//#endregion
//#region src/core/syntax/Numeric.ts
var IsDigit = /* @__PURE__ */ new Uint8Array(256);
IsDigit[CharCodes.Zero] = 1;
IsDigit[CharCodes.One] = 1;
IsDigit[CharCodes.Two] = 1;
IsDigit[CharCodes.Three] = 1;
IsDigit[CharCodes.Four] = 1;
IsDigit[CharCodes.Five] = 1;
IsDigit[CharCodes.Six] = 1;
IsDigit[CharCodes.Seven] = 1;
IsDigit[CharCodes.Eight] = 1;
IsDigit[CharCodes.Nine] = 1;
var IsNumericPrefix = /* @__PURE__ */ new Uint8Array(256);
IsNumericPrefix[CharCodes.Period] = 1;
IsNumericPrefix[CharCodes.Plus] = 1;
IsNumericPrefix[CharCodes.Minus] = 1;
var IsNumeric = /* @__PURE__ */ new Uint8Array(256);
for (let idx = 0, len = 256; idx < len; idx++) IsNumeric[idx] = IsDigit[idx] || IsNumericPrefix[idx] ? 1 : 0;
//#endregion
//#region src/core/parser/BaseParser.ts
var { Newline: Newline$1, CarriageReturn: CarriageReturn$1 } = CharCodes;
var BaseParser = class {
	constructor(bytes, capNumbers = false) {
		_defineProperty(this, "bytes", void 0);
		_defineProperty(this, "capNumbers", void 0);
		this.bytes = bytes;
		this.capNumbers = capNumbers;
	}
	parseRawInt() {
		let value = "";
		while (!this.bytes.done()) {
			if (!IsDigit[this.bytes.peek()]) break;
			value += charFromCode(this.bytes.next());
		}
		const numberValue = Number(value);
		if (!value || !isFinite(numberValue)) throw new NumberParsingError(this.bytes.position(), value);
		return numberValue;
	}
	parseRawNumber() {
		let value = "";
		while (!this.bytes.done()) {
			const byte = this.bytes.peek();
			if (!IsNumeric[byte]) break;
			value += charFromCode(this.bytes.next());
			if (byte === CharCodes.Period) break;
		}
		while (!this.bytes.done()) {
			if (!IsDigit[this.bytes.peek()]) break;
			value += charFromCode(this.bytes.next());
		}
		const numberValue = Number(value);
		if (!value || !isFinite(numberValue)) throw new NumberParsingError(this.bytes.position(), value);
		if (numberValue > Number.MAX_SAFE_INTEGER) if (this.capNumbers) {
			const msg = `Parsed number that is too large for some PDF readers: ${value}, using Number.MAX_SAFE_INTEGER instead.`;
			console.warn(msg);
			return Number.MAX_SAFE_INTEGER;
		} else {
			const msg = `Parsed number that is too large for some PDF readers: ${value}, not capping.`;
			console.warn(msg);
		}
		return numberValue;
	}
	skipWhitespace() {
		while (!this.bytes.done() && IsWhitespace[this.bytes.peek()]) this.bytes.next();
	}
	skipLine() {
		while (!this.bytes.done()) {
			const byte = this.bytes.peek();
			if (byte === Newline$1 || byte === CarriageReturn$1) return;
			this.bytes.next();
		}
	}
	skipComment() {
		if (this.bytes.peek() !== CharCodes.Percent) return false;
		while (!this.bytes.done()) {
			const byte = this.bytes.peek();
			if (byte === Newline$1 || byte === CarriageReturn$1) return true;
			this.bytes.next();
		}
		return true;
	}
	skipWhitespaceAndComments() {
		this.skipWhitespace();
		while (this.skipComment()) this.skipWhitespace();
	}
	matchKeyword(keyword) {
		const initialOffset = this.bytes.offset();
		for (let idx = 0, len = keyword.length; idx < len; idx++) if (this.bytes.done() || this.bytes.next() !== keyword[idx]) {
			this.bytes.moveTo(initialOffset);
			return false;
		}
		return true;
	}
};
//#endregion
//#region src/core/parser/ByteStream.ts
var _ByteStream;
var ByteStream = class {
	constructor(bytes) {
		_defineProperty(this, "bytes", void 0);
		_defineProperty(this, "length", void 0);
		_defineProperty(this, "idx", 0);
		_defineProperty(this, "line", 0);
		_defineProperty(this, "column", 0);
		this.bytes = bytes;
		this.length = this.bytes.length;
	}
	moveTo(offset) {
		this.idx = offset;
	}
	next() {
		const byte = this.bytes[this.idx++];
		if (byte === CharCodes.Newline) {
			this.line += 1;
			this.column = 0;
		} else this.column += 1;
		return byte;
	}
	assertNext(expected) {
		if (this.peek() !== expected) throw new NextByteAssertionError(this.position(), expected, this.peek());
		return this.next();
	}
	peek() {
		return this.bytes[this.idx];
	}
	peekAhead(steps) {
		return this.bytes[this.idx + steps];
	}
	peekAt(offset) {
		return this.bytes[offset];
	}
	done() {
		return this.idx >= this.length;
	}
	offset() {
		return this.idx;
	}
	slice(start, end) {
		return this.bytes.slice(start, end);
	}
	position() {
		return {
			line: this.line,
			column: this.column,
			offset: this.idx
		};
	}
};
_ByteStream = ByteStream;
_defineProperty(ByteStream, "of", (bytes) => new _ByteStream(bytes));
_defineProperty(ByteStream, "fromPDFRawStream", (rawStream) => _ByteStream.of(decodePDFRawStream(rawStream).decode()));
//#endregion
//#region src/core/syntax/Keywords.ts
var { Space, CarriageReturn, Newline } = CharCodes;
var stream = [
	CharCodes.s,
	CharCodes.t,
	CharCodes.r,
	CharCodes.e,
	CharCodes.a,
	CharCodes.m
];
var endstream = [
	CharCodes.e,
	CharCodes.n,
	CharCodes.d,
	CharCodes.s,
	CharCodes.t,
	CharCodes.r,
	CharCodes.e,
	CharCodes.a,
	CharCodes.m
];
var Keywords = {
	header: [
		CharCodes.Percent,
		CharCodes.P,
		CharCodes.D,
		CharCodes.F,
		CharCodes.Dash
	],
	eof: [
		CharCodes.Percent,
		CharCodes.Percent,
		CharCodes.E,
		CharCodes.O,
		CharCodes.F
	],
	obj: [
		CharCodes.o,
		CharCodes.b,
		CharCodes.j
	],
	endobj: [
		CharCodes.e,
		CharCodes.n,
		CharCodes.d,
		CharCodes.o,
		CharCodes.b,
		CharCodes.j
	],
	xref: [
		CharCodes.x,
		CharCodes.r,
		CharCodes.e,
		CharCodes.f
	],
	trailer: [
		CharCodes.t,
		CharCodes.r,
		CharCodes.a,
		CharCodes.i,
		CharCodes.l,
		CharCodes.e,
		CharCodes.r
	],
	startxref: [
		CharCodes.s,
		CharCodes.t,
		CharCodes.a,
		CharCodes.r,
		CharCodes.t,
		CharCodes.x,
		CharCodes.r,
		CharCodes.e,
		CharCodes.f
	],
	true: [
		CharCodes.t,
		CharCodes.r,
		CharCodes.u,
		CharCodes.e
	],
	false: [
		CharCodes.f,
		CharCodes.a,
		CharCodes.l,
		CharCodes.s,
		CharCodes.e
	],
	null: [
		CharCodes.n,
		CharCodes.u,
		CharCodes.l,
		CharCodes.l
	],
	stream,
	streamEOF1: [
		...stream,
		Space,
		CarriageReturn,
		Newline
	],
	streamEOF2: [
		...stream,
		CarriageReturn,
		Newline
	],
	streamEOF3: [...stream, CarriageReturn],
	streamEOF4: [...stream, Newline],
	endstream,
	EOF1endstream: [
		CarriageReturn,
		Newline,
		...endstream
	],
	EOF2endstream: [CarriageReturn, ...endstream],
	EOF3endstream: [Newline, ...endstream]
};
//#endregion
//#region src/core/parser/PDFObjectParser.ts
var _PDFObjectParser;
var PDFObjectParser = class extends BaseParser {
	constructor(byteStream, context, capNumbers = false, cryptoFactory) {
		super(byteStream, capNumbers);
		_defineProperty(this, "context", void 0);
		_defineProperty(this, "cryptoFactory", void 0);
		this.context = context;
		this.cryptoFactory = cryptoFactory;
	}
	parseObject(ref) {
		this.skipWhitespaceAndComments();
		if (this.matchKeyword(Keywords.true)) return PDFBool.True;
		if (this.matchKeyword(Keywords.false)) return PDFBool.False;
		if (this.matchKeyword(Keywords.null)) return PDFNull_default;
		const byte = this.bytes.peek();
		if (byte === CharCodes.LessThan && this.bytes.peekAhead(1) === CharCodes.LessThan) return this.parseDictOrStream(ref);
		if (byte === CharCodes.LessThan) return this.parseHexString(ref);
		if (byte === CharCodes.LeftParen) return this.parseString(ref);
		if (byte === CharCodes.ForwardSlash) return this.parseName();
		if (byte === CharCodes.LeftSquareBracket) return this.parseArray(ref);
		if (IsNumeric[byte]) return this.parseNumberOrRef();
		throw new PDFObjectParsingError(this.bytes.position(), byte);
	}
	parseNumberOrRef() {
		const firstNum = this.parseRawNumber();
		this.skipWhitespaceAndComments();
		const lookaheadStart = this.bytes.offset();
		if (IsDigit[this.bytes.peek()]) {
			const secondNum = this.parseRawNumber();
			this.skipWhitespaceAndComments();
			if (this.bytes.peek() === CharCodes.R) {
				this.bytes.assertNext(CharCodes.R);
				return PDFRef.of(firstNum, secondNum);
			}
		}
		this.bytes.moveTo(lookaheadStart);
		return PDFNumber.of(firstNum);
	}
	parseHexString(ref) {
		let value = "";
		this.bytes.assertNext(CharCodes.LessThan);
		while (!this.bytes.done() && this.bytes.peek() !== CharCodes.GreaterThan) value += charFromCode(this.bytes.next());
		this.bytes.assertNext(CharCodes.GreaterThan);
		if (this.cryptoFactory && ref) value = this.cryptoFactory.createCipherTransform(ref.objectNumber, ref.generationNumber).decryptBytes(PDFHexString.of(value).asBytes()).reduce((str, byte) => str + byte.toString(16).padStart(2, "0"), "");
		return PDFHexString.of(value);
	}
	parseString(ref) {
		let nestingLvl = 0;
		let isEscaped = false;
		let value = "";
		while (!this.bytes.done()) {
			const byte = this.bytes.next();
			value += charFromCode(byte);
			if (!isEscaped) {
				if (byte === CharCodes.LeftParen) nestingLvl += 1;
				if (byte === CharCodes.RightParen) nestingLvl -= 1;
			}
			if (byte === CharCodes.BackSlash) isEscaped = !isEscaped;
			else if (isEscaped) isEscaped = false;
			if (nestingLvl === 0) {
				let actualValue = value.substring(1, value.length - 1);
				if (this.cryptoFactory && ref) actualValue = this.cryptoFactory.createCipherTransform(ref.objectNumber, ref.generationNumber).decryptString(actualValue);
				return PDFString.of(actualValue);
			}
		}
		throw new UnbalancedParenthesisError(this.bytes.position());
	}
	parseName() {
		this.bytes.assertNext(CharCodes.ForwardSlash);
		let name = "";
		while (!this.bytes.done()) {
			const byte = this.bytes.peek();
			if (IsWhitespace[byte] || IsDelimiter[byte]) break;
			name += charFromCode(byte);
			this.bytes.next();
		}
		return PDFName.of(name);
	}
	parseArray(ref) {
		this.bytes.assertNext(CharCodes.LeftSquareBracket);
		this.skipWhitespaceAndComments();
		const pdfArray = PDFArray.withContext(this.context);
		while (this.bytes.peek() !== CharCodes.RightSquareBracket) {
			const element = this.parseObject(ref);
			pdfArray.push(element);
			this.skipWhitespaceAndComments();
		}
		this.bytes.assertNext(CharCodes.RightSquareBracket);
		return pdfArray;
	}
	parseDict(ref) {
		this.bytes.assertNext(CharCodes.LessThan);
		this.bytes.assertNext(CharCodes.LessThan);
		this.skipWhitespaceAndComments();
		const dict = /* @__PURE__ */ new Map();
		while (!this.bytes.done() && this.bytes.peek() !== CharCodes.GreaterThan && this.bytes.peekAhead(1) !== CharCodes.GreaterThan) {
			const key = this.parseName();
			const value = this.parseObject(ref);
			dict.set(key, value);
			this.skipWhitespaceAndComments();
		}
		this.skipWhitespaceAndComments();
		this.bytes.assertNext(CharCodes.GreaterThan);
		this.bytes.assertNext(CharCodes.GreaterThan);
		const Type = dict.get(PDFName.of("Type"));
		if (Type === PDFName.of("Catalog")) return PDFCatalog.fromMapWithContext(dict, this.context);
		else if (Type === PDFName.of("Pages")) return PDFPageTree.fromMapWithContext(dict, this.context);
		else if (Type === PDFName.of("Page")) return PDFPageLeaf.fromMapWithContext(dict, this.context);
		else return PDFDict.fromMapWithContext(dict, this.context);
	}
	parseDictOrStream(ref) {
		const startPos = this.bytes.position();
		const dict = this.parseDict(ref);
		this.skipWhitespaceAndComments();
		if (!this.matchKeyword(Keywords.streamEOF1) && !this.matchKeyword(Keywords.streamEOF2) && !this.matchKeyword(Keywords.streamEOF3) && !this.matchKeyword(Keywords.streamEOF4) && !this.matchKeyword(Keywords.stream)) return dict;
		const start = this.bytes.offset();
		let end;
		const Length = dict.get(PDFName.of("Length"));
		if (Length instanceof PDFNumber) {
			end = start + Length.asNumber();
			this.bytes.moveTo(end);
			this.skipWhitespaceAndComments();
			if (!this.matchKeyword(Keywords.endstream)) {
				this.bytes.moveTo(start);
				end = this.findEndOfStreamFallback(startPos);
			}
		} else end = this.findEndOfStreamFallback(startPos);
		let contents = this.bytes.slice(start, end);
		if (this.cryptoFactory && ref) contents = this.cryptoFactory.createCipherTransform(ref.objectNumber, ref.generationNumber).decryptBytes(contents);
		return PDFRawStream.of(dict, contents);
	}
	findEndOfStreamFallback(startPos) {
		let nestingLvl = 1;
		let end = this.bytes.offset();
		while (!this.bytes.done()) {
			end = this.bytes.offset();
			if (this.matchKeyword(Keywords.stream)) nestingLvl += 1;
			else if (this.matchKeyword(Keywords.EOF1endstream) || this.matchKeyword(Keywords.EOF2endstream) || this.matchKeyword(Keywords.EOF3endstream) || this.matchKeyword(Keywords.endstream)) nestingLvl -= 1;
			else this.bytes.next();
			if (nestingLvl === 0) break;
		}
		if (nestingLvl !== 0) throw new PDFStreamParsingError(startPos);
		return end;
	}
};
_PDFObjectParser = PDFObjectParser;
_defineProperty(PDFObjectParser, "forBytes", (bytes, context, capNumbers) => new _PDFObjectParser(ByteStream.of(bytes), context, capNumbers));
_defineProperty(PDFObjectParser, "forByteStream", (byteStream, context, capNumbers = false) => new _PDFObjectParser(byteStream, context, capNumbers));
//#endregion
//#region src/core/parser/PDFObjectStreamParser.ts
var _PDFObjectStreamParser;
var PDFObjectStreamParser = class extends PDFObjectParser {
	constructor(rawStream, shouldWaitForTick) {
		super(ByteStream.fromPDFRawStream(rawStream), rawStream.dict.context);
		_defineProperty(this, "alreadyParsed", void 0);
		_defineProperty(this, "shouldWaitForTick", void 0);
		_defineProperty(this, "firstOffset", void 0);
		_defineProperty(this, "objectCount", void 0);
		const { dict } = rawStream;
		this.alreadyParsed = false;
		this.shouldWaitForTick = shouldWaitForTick || (() => false);
		this.firstOffset = dict.lookup(PDFName.of("First"), PDFNumber).asNumber();
		this.objectCount = dict.lookup(PDFName.of("N"), PDFNumber).asNumber();
	}
	async parseIntoContext() {
		if (this.alreadyParsed) throw new ReparseError("PDFObjectStreamParser", "parseIntoContext");
		this.alreadyParsed = true;
		const offsetsAndObjectNumbers = this.parseOffsetsAndObjectNumbers();
		for (let idx = 0, len = offsetsAndObjectNumbers.length; idx < len; idx++) {
			const { objectNumber, offset } = offsetsAndObjectNumbers[idx];
			this.bytes.moveTo(this.firstOffset + offset);
			const ref = PDFRef.of(objectNumber, 0);
			const object = this.parseObject(ref);
			this.context.assign(ref, object);
			if (this.shouldWaitForTick()) await waitForTick();
		}
	}
	parseOffsetsAndObjectNumbers() {
		const offsetsAndObjectNumbers = [];
		for (let idx = 0, len = this.objectCount; idx < len; idx++) {
			this.skipWhitespaceAndComments();
			const objectNumber = this.parseRawInt();
			this.skipWhitespaceAndComments();
			const offset = this.parseRawInt();
			offsetsAndObjectNumbers.push({
				objectNumber,
				offset
			});
		}
		return offsetsAndObjectNumbers;
	}
};
_PDFObjectStreamParser = PDFObjectStreamParser;
_defineProperty(PDFObjectStreamParser, "forStream", (rawStream, shouldWaitForTick) => new _PDFObjectStreamParser(rawStream, shouldWaitForTick));
//#endregion
//#region src/core/parser/PDFXRefStreamParser.ts
var _PDFXRefStreamParser;
var PDFXRefStreamParser = class {
	constructor(rawStream) {
		_defineProperty(this, "alreadyParsed", void 0);
		_defineProperty(this, "dict", void 0);
		_defineProperty(this, "context", void 0);
		_defineProperty(this, "bytes", void 0);
		_defineProperty(this, "subsections", void 0);
		_defineProperty(this, "byteWidths", void 0);
		this.alreadyParsed = false;
		this.dict = rawStream.dict;
		this.bytes = ByteStream.fromPDFRawStream(rawStream);
		this.context = this.dict.context;
		const Size = this.dict.lookup(PDFName.of("Size"), PDFNumber);
		const Index = this.dict.lookup(PDFName.of("Index"));
		if (Index instanceof PDFArray) {
			this.subsections = [];
			for (let idx = 0, len = Index.size(); idx < len; idx += 2) {
				const firstObjectNumber = Index.lookup(idx + 0, PDFNumber).asNumber();
				const length = Index.lookup(idx + 1, PDFNumber).asNumber();
				this.subsections.push({
					firstObjectNumber,
					length
				});
			}
		} else this.subsections = [{
			firstObjectNumber: 0,
			length: Size.asNumber()
		}];
		const W = this.dict.lookup(PDFName.of("W"), PDFArray);
		this.byteWidths = [
			-1,
			-1,
			-1
		];
		for (let idx = 0, len = W.size(); idx < len; idx++) this.byteWidths[idx] = W.lookup(idx, PDFNumber).asNumber();
	}
	parseIntoContext() {
		if (this.alreadyParsed) throw new ReparseError("PDFXRefStreamParser", "parseIntoContext");
		this.alreadyParsed = true;
		this.context.trailerInfo = {
			Root: this.dict.get(PDFName.of("Root")),
			Encrypt: this.dict.get(PDFName.of("Encrypt")),
			Info: this.dict.get(PDFName.of("Info")),
			ID: this.dict.get(PDFName.of("ID"))
		};
		return this.parseEntries();
	}
	parseEntries() {
		const entries = [];
		const [typeFieldWidth, offsetFieldWidth, genFieldWidth] = this.byteWidths;
		for (let subsectionIdx = 0, subsectionLen = this.subsections.length; subsectionIdx < subsectionLen; subsectionIdx++) {
			const { firstObjectNumber, length } = this.subsections[subsectionIdx];
			for (let objIdx = 0; objIdx < length; objIdx++) {
				let type = 0;
				for (let idx = 0, len = typeFieldWidth; idx < len; idx++) type = type << 8 | this.bytes.next();
				let offset = 0;
				for (let idx = 0, len = offsetFieldWidth; idx < len; idx++) offset = offset << 8 | this.bytes.next();
				let generationNumber = 0;
				for (let idx = 0, len = genFieldWidth; idx < len; idx++) generationNumber = generationNumber << 8 | this.bytes.next();
				if (typeFieldWidth === 0) type = 1;
				const objectNumber = firstObjectNumber + objIdx;
				const entry = {
					ref: PDFRef.of(objectNumber, generationNumber),
					offset,
					deleted: type === 0,
					inObjectStream: type === 2
				};
				entries.push(entry);
			}
		}
		return entries;
	}
};
_PDFXRefStreamParser = PDFXRefStreamParser;
_defineProperty(PDFXRefStreamParser, "forStream", (rawStream) => new _PDFXRefStreamParser(rawStream));
//#endregion
//#region src/core/parser/PDFParser.ts
var _PDFParser;
var PDFParser = class extends PDFObjectParser {
	constructor(pdfBytes, objectsPerTick = Infinity, throwOnInvalidObject = false, capNumbers = false, cryptoFactory) {
		super(ByteStream.of(pdfBytes), PDFContext.create(), capNumbers, cryptoFactory);
		_defineProperty(this, "objectsPerTick", void 0);
		_defineProperty(this, "throwOnInvalidObject", void 0);
		_defineProperty(this, "alreadyParsed", false);
		_defineProperty(this, "parsedObjects", 0);
		_defineProperty(this, "shouldWaitForTick", () => {
			this.parsedObjects += 1;
			return this.parsedObjects % this.objectsPerTick === 0;
		});
		this.objectsPerTick = objectsPerTick;
		this.throwOnInvalidObject = throwOnInvalidObject;
		this.context.isDecrypted = !!cryptoFactory?.encryptionKey;
	}
	async parseDocument() {
		if (this.alreadyParsed) throw new ReparseError("PDFParser", "parseDocument");
		this.alreadyParsed = true;
		this.context.header = this.parseHeader();
		let prevOffset;
		while (!this.bytes.done()) {
			await this.parseDocumentSection();
			const offset = this.bytes.offset();
			if (offset === prevOffset) throw new StalledParserError(this.bytes.position());
			prevOffset = offset;
		}
		this.maybeRecoverRoot();
		if (this.context.lookup(PDFRef.of(0))) {
			console.warn("Removing parsed object: 0 0 R");
			this.context.delete(PDFRef.of(0));
		}
		return this.context;
	}
	maybeRecoverRoot() {
		const isValidCatalog = (obj) => obj instanceof PDFDict && obj.lookup(PDFName.of("Type")) === PDFName.of("Catalog");
		if (!isValidCatalog(this.context.lookup(this.context.trailerInfo.Root))) {
			const indirectObjects = this.context.enumerateIndirectObjects();
			for (let idx = 0, len = indirectObjects.length; idx < len; idx++) {
				const [ref, object] = indirectObjects[idx];
				if (isValidCatalog(object)) this.context.trailerInfo.Root = ref;
			}
		}
	}
	parseHeader() {
		while (!this.bytes.done()) {
			if (this.matchKeyword(Keywords.header)) {
				const major = this.parseRawInt();
				this.bytes.assertNext(CharCodes.Period);
				const minor = this.parseRawInt();
				const header = PDFHeader.forVersion(major, minor);
				this.skipBinaryHeaderComment();
				return header;
			}
			this.bytes.next();
		}
		throw new MissingPDFHeaderError(this.bytes.position());
	}
	parseIndirectObjectHeader() {
		this.skipWhitespaceAndComments();
		const objectNumber = this.parseRawInt();
		this.skipWhitespaceAndComments();
		const generationNumber = this.parseRawInt();
		this.skipWhitespaceAndComments();
		if (!this.matchKeyword(Keywords.obj)) throw new MissingKeywordError(this.bytes.position(), Keywords.obj);
		return PDFRef.of(objectNumber, generationNumber);
	}
	matchIndirectObjectHeader() {
		const initialOffset = this.bytes.offset();
		try {
			this.parseIndirectObjectHeader();
			return true;
		} catch {
			this.bytes.moveTo(initialOffset);
			return false;
		}
	}
	async parseIndirectObject() {
		const ref = this.parseIndirectObjectHeader();
		this.skipWhitespaceAndComments();
		const object = this.parseObject(ref);
		this.skipWhitespaceAndComments();
		this.matchKeyword(Keywords.endobj);
		if (object instanceof PDFRawStream && object.dict.lookup(PDFName.of("Type")) === PDFName.of("ObjStm")) await PDFObjectStreamParser.forStream(object, this.shouldWaitForTick).parseIntoContext();
		else if (object instanceof PDFRawStream && object.dict.lookup(PDFName.of("Type")) === PDFName.of("XRef")) PDFXRefStreamParser.forStream(object).parseIntoContext();
		else this.context.assign(ref, object);
		return ref;
	}
	tryToParseInvalidIndirectObject() {
		const startPos = this.bytes.position();
		const msg = `Trying to parse invalid object: ${JSON.stringify(startPos)})`;
		if (this.throwOnInvalidObject) throw new Error(msg);
		console.warn(msg);
		const ref = this.parseIndirectObjectHeader();
		console.warn(`Invalid object ref: ${ref}`);
		this.skipWhitespaceAndComments();
		const start = this.bytes.offset();
		let failed = true;
		while (!this.bytes.done()) {
			if (this.matchKeyword(Keywords.endobj)) failed = false;
			if (!failed) break;
			this.bytes.next();
		}
		if (failed) throw new PDFInvalidObjectParsingError(startPos);
		const end = this.bytes.offset() - Keywords.endobj.length;
		const object = PDFInvalidObject.of(this.bytes.slice(start, end));
		this.context.assign(ref, object);
		return ref;
	}
	async parseIndirectObjects() {
		this.skipWhitespaceAndComments();
		while (!this.bytes.done() && IsDigit[this.bytes.peek()]) {
			const initialOffset = this.bytes.offset();
			try {
				await this.parseIndirectObject();
			} catch {
				this.bytes.moveTo(initialOffset);
				this.tryToParseInvalidIndirectObject();
			}
			this.skipWhitespaceAndComments();
			this.skipJibberish();
			if (this.shouldWaitForTick()) await waitForTick();
		}
	}
	maybeParseCrossRefSection() {
		this.skipWhitespaceAndComments();
		if (!this.matchKeyword(Keywords.xref)) return;
		this.skipWhitespaceAndComments();
		let objectNumber = -1;
		const xref = PDFCrossRefSection.createEmpty();
		while (!this.bytes.done() && IsDigit[this.bytes.peek()]) {
			const firstInt = this.parseRawInt();
			this.skipWhitespaceAndComments();
			const secondInt = this.parseRawInt();
			this.skipWhitespaceAndComments();
			const byte = this.bytes.peek();
			if (byte === CharCodes.n || byte === CharCodes.f) {
				const ref = PDFRef.of(objectNumber, secondInt);
				if (this.bytes.next() === CharCodes.n) xref.addEntry(ref, firstInt);
				else xref.addDeletedEntry(ref, firstInt);
				objectNumber += 1;
			} else objectNumber = firstInt;
			this.skipWhitespaceAndComments();
		}
		return xref;
	}
	maybeParseTrailerDict() {
		this.skipWhitespaceAndComments();
		if (!this.matchKeyword(Keywords.trailer)) return;
		this.skipWhitespaceAndComments();
		const dict = this.parseDict();
		const { context } = this;
		context.trailerInfo = {
			Root: dict.get(PDFName.of("Root")) || context.trailerInfo.Root,
			Encrypt: dict.get(PDFName.of("Encrypt")) || context.trailerInfo.Encrypt,
			Info: dict.get(PDFName.of("Info")) || context.trailerInfo.Info,
			ID: dict.get(PDFName.of("ID")) || context.trailerInfo.ID
		};
	}
	maybeParseTrailer() {
		this.skipWhitespaceAndComments();
		if (!this.matchKeyword(Keywords.startxref)) return;
		this.skipWhitespaceAndComments();
		const offset = this.parseRawInt();
		this.skipWhitespace();
		this.matchKeyword(Keywords.eof);
		this.skipWhitespaceAndComments();
		this.matchKeyword(Keywords.eof);
		this.skipWhitespaceAndComments();
		return PDFTrailer.forLastCrossRefSectionOffset(offset);
	}
	async parseDocumentSection() {
		await this.parseIndirectObjects();
		this.maybeParseCrossRefSection();
		this.maybeParseTrailerDict();
		this.maybeParseTrailer();
		this.skipJibberish();
	}
	/**
	* This operation is not necessary for valid PDF files. But some invalid PDFs
	* contain jibberish in between indirect objects. This method is designed to
	* skip past that jibberish, should it exist, until it reaches the next
	* indirect object header, an xref table section, or the file trailer.
	*/
	skipJibberish() {
		this.skipWhitespaceAndComments();
		while (!this.bytes.done()) {
			const initialOffset = this.bytes.offset();
			const byte = this.bytes.peek();
			if (byte >= CharCodes.Space && byte <= CharCodes.Tilde) {
				if (this.matchKeyword(Keywords.xref) || this.matchKeyword(Keywords.trailer) || this.matchKeyword(Keywords.startxref) || this.matchIndirectObjectHeader()) {
					this.bytes.moveTo(initialOffset);
					break;
				}
			}
			this.bytes.next();
		}
	}
	/**
	* Skips the binary comment following a PDF header. The specification
	* defines this binary comment (section 7.5.2 File Header) as a sequence of 4
	* or more bytes that are 128 or greater, and which are preceded by a "%".
	*
	* This would imply that to strip out this binary comment, we could check for
	* a sequence of bytes starting with "%", and remove all subsequent bytes that
	* are 128 or greater. This works for many documents that properly comply with
	* the spec. But in the wild, there are PDFs that omit the leading "%", and
	* include bytes that are less than 128 (e.g. 0 or 1). So in order to parse
	* these headers correctly, we just throw out all bytes leading up to the
	* first indirect object header.
	*/
	skipBinaryHeaderComment() {
		this.skipWhitespaceAndComments();
		try {
			const initialOffset = this.bytes.offset();
			this.parseIndirectObjectHeader();
			this.bytes.moveTo(initialOffset);
		} catch {
			this.bytes.next();
			this.skipWhitespaceAndComments();
		}
	}
};
_PDFParser = PDFParser;
_defineProperty(PDFParser, "forBytesWithOptions", (pdfBytes, objectsPerTick, throwOnInvalidObject, capNumbers, cryptoFactory) => new _PDFParser(pdfBytes, objectsPerTick, throwOnInvalidObject, capNumbers, cryptoFactory));
//#endregion
//#region src/core/annotation/flags.ts
var flag = (bitIndex) => 1 << bitIndex;
/** From PDF spec table 165 */
var AnnotationFlags = function(AnnotationFlags) {
	/**
	* If set, do not display the annotation if it does not belong to one of the
	* standard annotation types and no annotation handler is available. If clear,
	* display such an unknown annotation using an appearance stream specified by
	* its appearance dictionary, if any.
	*/
	AnnotationFlags[AnnotationFlags["Invisible"] = flag(0)] = "Invisible";
	/**
	* If set, do not display or print the annotation or allow it to interact with
	* the user, regardless of its annotation type or whether an annotation
	* handler is available.
	*
	* In cases where screen space is limited, the ability to hide and show
	* annotations selectively can be used in combination with appearance streams
	* to display auxiliary pop-up information similar in function to online help
	* systems.
	*/
	AnnotationFlags[AnnotationFlags["Hidden"] = flag(1)] = "Hidden";
	/**
	* If set, print the annotation when the page is printed. If clear, never
	* print the annotation, regardless of whether it is displayed on the screen.
	*
	* This can be useful for annotations representing interactive pushbuttons,
	* which would serve no meaningful purpose on the printed page.
	*/
	AnnotationFlags[AnnotationFlags["Print"] = flag(2)] = "Print";
	/**
	* If set, do not scale the annotation’s appearance to match the magnification
	* of the page. The location of the annotation on the page (defined by the
	* upper-left corner of its annotation rectangle) shall remain fixed,
	* regardless of the page magnification.
	*/
	AnnotationFlags[AnnotationFlags["NoZoom"] = flag(3)] = "NoZoom";
	/**
	* If set, do not rotate the annotation’s appearance to match the rotation of
	* the page. The upper-left corner of the annotation rectangle shall remain in
	* a fixed location on the page, regardless of the page rotation.
	*/
	AnnotationFlags[AnnotationFlags["NoRotate"] = flag(4)] = "NoRotate";
	/**
	* If set, do not display the annotation on the screen or allow it to interact
	* with the user. The annotation may be printed (depending on the setting of
	* the Print flag) but should be considered hidden for purposes of on-screen
	* display and user interaction.
	*/
	AnnotationFlags[AnnotationFlags["NoView"] = flag(5)] = "NoView";
	/**
	* If set, do not allow the annotation to interact with the user. The
	* annotation may be displayed or printed (depending on the settings of the
	* NoView and Print flags) but should not respond to mouse clicks or change
	* its appearance in response to mouse motions.
	*
	* This flag shall be ignored for widget annotations; its function is
	* subsumed by the ReadOnly flag of the associated form field.
	*/
	AnnotationFlags[AnnotationFlags["ReadOnly"] = flag(6)] = "ReadOnly";
	/**
	* If set, do not allow the annotation to be deleted or its properties
	* (including position and size) to be modified by the user. However, this
	* flag does not restrict changes to the annotation’s contents, such as the
	* value of a form field.
	*/
	AnnotationFlags[AnnotationFlags["Locked"] = flag(7)] = "Locked";
	/**
	* If set, invert the interpretation of the NoView flag for certain events.
	*
	* A typical use is to have an annotation that appears only when a mouse
	* cursor is held over it.
	*/
	AnnotationFlags[AnnotationFlags["ToggleNoView"] = flag(8)] = "ToggleNoView";
	/**
	* If set, do not allow the contents of the annotation to be modified by the
	* user. This flag does not restrict deletion of the annotation or changes to
	* other annotation properties, such as position and size.
	*/
	AnnotationFlags[AnnotationFlags["LockedContents"] = flag(9)] = "LockedContents";
	return AnnotationFlags;
}({});
//#endregion
//#region src/api/objects.ts
var asPDFName = (name) => name instanceof PDFName ? name : PDFName.of(name);
var asPDFNumber = (num) => num instanceof PDFNumber ? num : PDFNumber.of(num);
var asNumber = (num) => num instanceof PDFNumber ? num.asNumber() : num;
//#endregion
//#region src/api/rotations.ts
var RotationTypes = /* @__PURE__ */ function(RotationTypes) {
	RotationTypes["Degrees"] = "degrees";
	RotationTypes["Radians"] = "radians";
	return RotationTypes;
}({});
var radians = (radianAngle) => {
	assertIs(radianAngle, "radianAngle", ["number"]);
	return {
		type: "radians",
		angle: radianAngle
	};
};
var degrees = (degreeAngle) => {
	assertIs(degreeAngle, "degreeAngle", ["number"]);
	return {
		type: "degrees",
		angle: degreeAngle
	};
};
var { Radians, Degrees } = RotationTypes;
var degreesToRadians = (degree) => degree * Math.PI / 180;
var radiansToDegrees = (radian) => radian * 180 / Math.PI;
var toRadians = (rotation) => rotation.type === Radians ? rotation.angle : rotation.type === Degrees ? degreesToRadians(rotation.angle) : error(`Invalid rotation: ${JSON.stringify(rotation)}`);
var toDegrees = (rotation) => rotation.type === Radians ? radiansToDegrees(rotation.angle) : rotation.type === Degrees ? rotation.angle : error(`Invalid rotation: ${JSON.stringify(rotation)}`);
var reduceRotation = (degreeAngle = 0) => {
	const quadrants = degreeAngle / 90 % 4;
	if (quadrants === 0) return 0;
	if (quadrants === 1) return 90;
	if (quadrants === 2) return 180;
	if (quadrants === 3) return 270;
	return 0;
};
var adjustDimsForRotation = (dims, degreeAngle = 0) => {
	const rotation = reduceRotation(degreeAngle);
	return rotation === 90 || rotation === 270 ? {
		width: dims.height,
		height: dims.width
	} : {
		width: dims.width,
		height: dims.height
	};
};
var rotateRectangle = (rectangle, borderWidth = 0, degreeAngle = 0) => {
	const { x, y, width: w, height: h } = rectangle;
	const r = reduceRotation(degreeAngle);
	const b = borderWidth / 2;
	if (r === 0) return {
		x: x - b,
		y: y - b,
		width: w,
		height: h
	};
	else if (r === 90) return {
		x: x - h + b,
		y: y - b,
		width: h,
		height: w
	};
	else if (r === 180) return {
		x: x - w + b,
		y: y - h + b,
		width: w,
		height: h
	};
	else if (r === 270) return {
		x: x - b,
		y: y - w + b,
		width: h,
		height: w
	};
	else return {
		x: x - b,
		y: y - b,
		width: w,
		height: h
	};
};
//#endregion
//#region src/api/operators.ts
var clip = () => PDFOperator.of(PDFOperatorNames.ClipNonZero);
var clipEvenOdd = () => PDFOperator.of(PDFOperatorNames.ClipEvenOdd);
var { cos, sin, tan } = Math;
var concatTransformationMatrix = (a, b, c, d, e, f) => PDFOperator.of(PDFOperatorNames.ConcatTransformationMatrix, [
	asPDFNumber(a),
	asPDFNumber(b),
	asPDFNumber(c),
	asPDFNumber(d),
	asPDFNumber(e),
	asPDFNumber(f)
]);
var translate = (xPos, yPos) => concatTransformationMatrix(1, 0, 0, 1, xPos, yPos);
var scale = (xPos, yPos) => concatTransformationMatrix(xPos, 0, 0, yPos, 0, 0);
var rotateRadians = (angle) => concatTransformationMatrix(cos(asNumber(angle)), sin(asNumber(angle)), -sin(asNumber(angle)), cos(asNumber(angle)), 0, 0);
var rotateDegrees = (angle) => rotateRadians(degreesToRadians(asNumber(angle)));
var skewRadians = (xSkewAngle, ySkewAngle) => concatTransformationMatrix(1, tan(asNumber(xSkewAngle)), tan(asNumber(ySkewAngle)), 1, 0, 0);
var skewDegrees = (xSkewAngle, ySkewAngle) => skewRadians(degreesToRadians(asNumber(xSkewAngle)), degreesToRadians(asNumber(ySkewAngle)));
var setDashPattern = (dashArray, dashPhase) => PDFOperator.of(PDFOperatorNames.SetLineDashPattern, [`[${dashArray.map(asPDFNumber).join(" ")}]`, asPDFNumber(dashPhase)]);
var restoreDashPattern = () => setDashPattern([], 0);
var LineCapStyle = /* @__PURE__ */ function(LineCapStyle) {
	LineCapStyle[LineCapStyle["Butt"] = 0] = "Butt";
	LineCapStyle[LineCapStyle["Round"] = 1] = "Round";
	LineCapStyle[LineCapStyle["Projecting"] = 2] = "Projecting";
	return LineCapStyle;
}({});
var setLineCap = (style) => PDFOperator.of(PDFOperatorNames.SetLineCapStyle, [asPDFNumber(style)]);
var LineJoinStyle = /* @__PURE__ */ function(LineJoinStyle) {
	LineJoinStyle[LineJoinStyle["Miter"] = 0] = "Miter";
	LineJoinStyle[LineJoinStyle["Round"] = 1] = "Round";
	LineJoinStyle[LineJoinStyle["Bevel"] = 2] = "Bevel";
	return LineJoinStyle;
}({});
var setLineJoin = (style) => PDFOperator.of(PDFOperatorNames.SetLineJoinStyle, [asPDFNumber(style)]);
var setGraphicsState = (state) => PDFOperator.of(PDFOperatorNames.SetGraphicsStateParams, [asPDFName(state)]);
var pushGraphicsState = () => PDFOperator.of(PDFOperatorNames.PushGraphicsState);
var popGraphicsState = () => PDFOperator.of(PDFOperatorNames.PopGraphicsState);
var setLineWidth = (width) => PDFOperator.of(PDFOperatorNames.SetLineWidth, [asPDFNumber(width)]);
var appendBezierCurve = (x1, y1, x2, y2, x3, y3) => PDFOperator.of(PDFOperatorNames.AppendBezierCurve, [
	asPDFNumber(x1),
	asPDFNumber(y1),
	asPDFNumber(x2),
	asPDFNumber(y2),
	asPDFNumber(x3),
	asPDFNumber(y3)
]);
var appendQuadraticCurve = (x1, y1, x2, y2) => PDFOperator.of(PDFOperatorNames.CurveToReplicateInitialPoint, [
	asPDFNumber(x1),
	asPDFNumber(y1),
	asPDFNumber(x2),
	asPDFNumber(y2)
]);
var closePath = () => PDFOperator.of(PDFOperatorNames.ClosePath);
var moveTo = (xPos, yPos) => PDFOperator.of(PDFOperatorNames.MoveTo, [asPDFNumber(xPos), asPDFNumber(yPos)]);
var lineTo = (xPos, yPos) => PDFOperator.of(PDFOperatorNames.LineTo, [asPDFNumber(xPos), asPDFNumber(yPos)]);
/**
* @param xPos x coordinate for the lower left corner of the rectangle
* @param yPos y coordinate for the lower left corner of the rectangle
* @param width width of the rectangle
* @param height height of the rectangle
*/
var rectangle = (xPos, yPos, width, height) => PDFOperator.of(PDFOperatorNames.AppendRectangle, [
	asPDFNumber(xPos),
	asPDFNumber(yPos),
	asPDFNumber(width),
	asPDFNumber(height)
]);
/**
* @param xPos x coordinate for the lower left corner of the square
* @param yPos y coordinate for the lower left corner of the square
* @param size width and height of the square
*/
var square = (xPos, yPos, size) => rectangle(xPos, yPos, size, size);
var stroke = () => PDFOperator.of(PDFOperatorNames.StrokePath);
var FillRule = /* @__PURE__ */ function(FillRule) {
	FillRule["NonZero"] = "f";
	FillRule["EvenOdd"] = "f*";
	return FillRule;
}({});
var fill = () => PDFOperator.of(PDFOperatorNames.FillNonZero);
var fillEvenOdd = () => PDFOperator.of(PDFOperatorNames.FillEvenOdd);
var fillAndStroke = () => PDFOperator.of(PDFOperatorNames.FillNonZeroAndStroke);
var endPath = () => PDFOperator.of(PDFOperatorNames.EndPath);
var nextLine = () => PDFOperator.of(PDFOperatorNames.NextLine);
var moveText = (x, y) => PDFOperator.of(PDFOperatorNames.MoveText, [asPDFNumber(x), asPDFNumber(y)]);
var showText = (text) => PDFOperator.of(PDFOperatorNames.ShowText, [text]);
var beginText = () => PDFOperator.of(PDFOperatorNames.BeginText);
var endText = () => PDFOperator.of(PDFOperatorNames.EndText);
var setFontAndSize = (name, size) => PDFOperator.of(PDFOperatorNames.SetFontAndSize, [asPDFName(name), asPDFNumber(size)]);
var setCharacterSpacing = (spacing) => PDFOperator.of(PDFOperatorNames.SetCharacterSpacing, [asPDFNumber(spacing)]);
var setWordSpacing = (spacing) => PDFOperator.of(PDFOperatorNames.SetWordSpacing, [asPDFNumber(spacing)]);
/** @param squeeze horizontal character spacing */
var setCharacterSqueeze = (squeeze) => PDFOperator.of(PDFOperatorNames.SetTextHorizontalScaling, [asPDFNumber(squeeze)]);
var setLineHeight = (lineHeight) => PDFOperator.of(PDFOperatorNames.SetTextLineHeight, [asPDFNumber(lineHeight)]);
var setTextRise = (rise) => PDFOperator.of(PDFOperatorNames.SetTextRise, [asPDFNumber(rise)]);
var TextRenderingMode = /* @__PURE__ */ function(TextRenderingMode) {
	TextRenderingMode[TextRenderingMode["Fill"] = 0] = "Fill";
	TextRenderingMode[TextRenderingMode["Outline"] = 1] = "Outline";
	TextRenderingMode[TextRenderingMode["FillAndOutline"] = 2] = "FillAndOutline";
	TextRenderingMode[TextRenderingMode["Invisible"] = 3] = "Invisible";
	TextRenderingMode[TextRenderingMode["FillAndClip"] = 4] = "FillAndClip";
	TextRenderingMode[TextRenderingMode["OutlineAndClip"] = 5] = "OutlineAndClip";
	TextRenderingMode[TextRenderingMode["FillAndOutlineAndClip"] = 6] = "FillAndOutlineAndClip";
	TextRenderingMode[TextRenderingMode["Clip"] = 7] = "Clip";
	return TextRenderingMode;
}({});
var setTextRenderingMode = (mode) => PDFOperator.of(PDFOperatorNames.SetTextRenderingMode, [asPDFNumber(mode)]);
var setTextMatrix = (a, b, c, d, e, f) => PDFOperator.of(PDFOperatorNames.SetTextMatrix, [
	asPDFNumber(a),
	asPDFNumber(b),
	asPDFNumber(c),
	asPDFNumber(d),
	asPDFNumber(e),
	asPDFNumber(f)
]);
var rotateAndSkewTextRadiansAndTranslate = (rotationAngle, xSkewAngle, ySkewAngle, x, y) => setTextMatrix(cos(asNumber(rotationAngle)), sin(asNumber(rotationAngle)) + tan(asNumber(xSkewAngle)), -sin(asNumber(rotationAngle)) + tan(asNumber(ySkewAngle)), cos(asNumber(rotationAngle)), x, y);
var rotateAndSkewTextDegreesAndTranslate = (rotationAngle, xSkewAngle, ySkewAngle, x, y) => rotateAndSkewTextRadiansAndTranslate(degreesToRadians(asNumber(rotationAngle)), degreesToRadians(asNumber(xSkewAngle)), degreesToRadians(asNumber(ySkewAngle)), x, y);
var drawObject = (name) => PDFOperator.of(PDFOperatorNames.DrawObject, [asPDFName(name)]);
var setFillingGrayscaleColor = (gray) => PDFOperator.of(PDFOperatorNames.NonStrokingColorGray, [asPDFNumber(gray)]);
var setStrokingGrayscaleColor = (gray) => PDFOperator.of(PDFOperatorNames.StrokingColorGray, [asPDFNumber(gray)]);
var setFillingRgbColor = (red, green, blue) => PDFOperator.of(PDFOperatorNames.NonStrokingColorRgb, [
	asPDFNumber(red),
	asPDFNumber(green),
	asPDFNumber(blue)
]);
var setStrokingRgbColor = (red, green, blue) => PDFOperator.of(PDFOperatorNames.StrokingColorRgb, [
	asPDFNumber(red),
	asPDFNumber(green),
	asPDFNumber(blue)
]);
var setFillingCmykColor = (cyan, magenta, yellow, key) => PDFOperator.of(PDFOperatorNames.NonStrokingColorCmyk, [
	asPDFNumber(cyan),
	asPDFNumber(magenta),
	asPDFNumber(yellow),
	asPDFNumber(key)
]);
var setStrokingCmykColor = (cyan, magenta, yellow, key) => PDFOperator.of(PDFOperatorNames.StrokingColorCmyk, [
	asPDFNumber(cyan),
	asPDFNumber(magenta),
	asPDFNumber(yellow),
	asPDFNumber(key)
]);
var beginMarkedContent = (tag) => PDFOperator.of(PDFOperatorNames.BeginMarkedContent, [asPDFName(tag)]);
var endMarkedContent = () => PDFOperator.of(PDFOperatorNames.EndMarkedContent);
//#endregion
//#region src/api/colors.ts
var ColorTypes = /* @__PURE__ */ function(ColorTypes) {
	ColorTypes["Grayscale"] = "Grayscale";
	ColorTypes["RGB"] = "RGB";
	ColorTypes["CMYK"] = "CMYK";
	return ColorTypes;
}({});
var grayscale = (gray) => {
	assertRange(gray, "gray", 0, 1);
	return {
		type: "Grayscale",
		gray
	};
};
var rgb = (red, green, blue) => {
	assertRange(red, "red", 0, 1);
	assertRange(green, "green", 0, 1);
	assertRange(blue, "blue", 0, 1);
	return {
		type: "RGB",
		red,
		green,
		blue
	};
};
var cmyk = (cyan, magenta, yellow, key) => {
	assertRange(cyan, "cyan", 0, 1);
	assertRange(magenta, "magenta", 0, 1);
	assertRange(yellow, "yellow", 0, 1);
	assertRange(key, "key", 0, 1);
	return {
		type: "CMYK",
		cyan,
		magenta,
		yellow,
		key
	};
};
var colorString = (color) => {
	assertIs(color, "color", ["string"]);
	const colorDescription = ColorParser(color).unitObject();
	return {
		rgb: rgb(colorDescription.r, colorDescription.g, colorDescription.b),
		alpha: colorDescription.alpha
	};
};
var { Grayscale, RGB, CMYK } = ColorTypes;
var setFillingColor = (color) => color.type === Grayscale ? setFillingGrayscaleColor(color.gray) : color.type === RGB ? setFillingRgbColor(color.red, color.green, color.blue) : color.type === CMYK ? setFillingCmykColor(color.cyan, color.magenta, color.yellow, color.key) : error(`Invalid color: ${JSON.stringify(color)}`);
var setStrokingColor = (color) => color.type === Grayscale ? setStrokingGrayscaleColor(color.gray) : color.type === RGB ? setStrokingRgbColor(color.red, color.green, color.blue) : color.type === CMYK ? setStrokingCmykColor(color.cyan, color.magenta, color.yellow, color.key) : error(`Invalid color: ${JSON.stringify(color)}`);
var componentsToColor = (comps, scale = 1) => comps?.length === 1 ? grayscale(comps[0] * scale) : comps?.length === 3 ? rgb(comps[0] * scale, comps[1] * scale, comps[2] * scale) : comps?.length === 4 ? cmyk(comps[0] * scale, comps[1] * scale, comps[2] * scale, comps[3] * scale) : void 0;
var colorToComponents = (color) => color.type === Grayscale ? [color.gray] : color.type === RGB ? [
	color.red,
	color.green,
	color.blue
] : color.type === CMYK ? [
	color.cyan,
	color.magenta,
	color.yellow,
	color.key
] : error(`Invalid color: ${JSON.stringify(color)}`);
//#endregion
//#region src/api/svgPath.ts
var cx = 0;
var cy = 0;
var px = 0;
var py = 0;
var sx = 0;
var sy = 0;
var parameters = /* @__PURE__ */ new Map([
	["A", 7],
	["a", 7],
	["C", 6],
	["c", 6],
	["H", 1],
	["h", 1],
	["L", 2],
	["l", 2],
	["M", 2],
	["m", 2],
	["Q", 4],
	["q", 4],
	["S", 4],
	["s", 4],
	["T", 2],
	["t", 2],
	["V", 1],
	["v", 1],
	["Z", 0],
	["z", 0]
]);
var parse$2 = (path) => {
	let cmd;
	const ret = [];
	let args = [];
	let curArg = "";
	let foundDecimal = false;
	let params = 0;
	for (const c of path) if (parameters.has(c)) {
		params = parameters.get(c);
		if (cmd) {
			if (curArg.length > 0) args[args.length] = +curArg;
			ret[ret.length] = {
				cmd,
				args
			};
			args = [];
			curArg = "";
			foundDecimal = false;
		}
		cmd = c;
	} else if ([" ", ","].includes(c) || c === "-" && curArg.length > 0 && curArg[curArg.length - 1] !== "e" || c === "." && foundDecimal) {
		if (curArg.length === 0) continue;
		if (args.length === params) {
			ret[ret.length] = {
				cmd,
				args
			};
			args = [+curArg];
			if (cmd === "M") cmd = "L";
			if (cmd === "m") cmd = "l";
		} else args[args.length] = +curArg;
		foundDecimal = c === ".";
		curArg = ["-", "."].includes(c) ? c : "";
	} else {
		curArg += c;
		if (c === ".") foundDecimal = true;
	}
	if (curArg.length > 0) if (args.length === params) {
		ret[ret.length] = {
			cmd,
			args
		};
		args = [+curArg];
		if (cmd === "M") cmd = "L";
		if (cmd === "m") cmd = "l";
	} else args[args.length] = +curArg;
	ret[ret.length] = {
		cmd,
		args
	};
	return ret;
};
var apply = (commands) => {
	cx = cy = px = py = sx = sy = 0;
	let cmds = [];
	for (let i = 0; i < commands.length; i++) {
		const c = commands[i];
		if (c.cmd && typeof runners[c.cmd] === "function") {
			const cmd = runners[c.cmd](c.args);
			if (Array.isArray(cmd)) cmds = cmds.concat(cmd);
			else cmds.push(cmd);
		}
	}
	return cmds;
};
var runners = {
	M(a) {
		cx = a[0];
		cy = a[1];
		px = py = null;
		sx = cx;
		sy = cy;
		return moveTo(cx, cy);
	},
	m(a) {
		cx += a[0];
		cy += a[1];
		px = py = null;
		sx = cx;
		sy = cy;
		return moveTo(cx, cy);
	},
	C(a) {
		cx = a[4];
		cy = a[5];
		px = a[2];
		py = a[3];
		return appendBezierCurve(a[0], a[1], a[2], a[3], a[4], a[5]);
	},
	c(a) {
		const cmd = appendBezierCurve(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy, a[4] + cx, a[5] + cy);
		px = cx + a[2];
		py = cy + a[3];
		cx += a[4];
		cy += a[5];
		return cmd;
	},
	S(a) {
		if (px === null || py === null) {
			px = cx;
			py = cy;
		}
		const cmd = appendBezierCurve(cx - (px - cx), cy - (py - cy), a[0], a[1], a[2], a[3]);
		px = a[0];
		py = a[1];
		cx = a[2];
		cy = a[3];
		return cmd;
	},
	s(a) {
		if (px === null || py === null) {
			px = cx;
			py = cy;
		}
		const cmd = appendBezierCurve(cx - (px - cx), cy - (py - cy), cx + a[0], cy + a[1], cx + a[2], cy + a[3]);
		px = cx + a[0];
		py = cy + a[1];
		cx += a[2];
		cy += a[3];
		return cmd;
	},
	Q(a) {
		px = a[0];
		py = a[1];
		cx = a[2];
		cy = a[3];
		return appendQuadraticCurve(a[0], a[1], cx, cy);
	},
	q(a) {
		const cmd = appendQuadraticCurve(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy);
		px = cx + a[0];
		py = cy + a[1];
		cx += a[2];
		cy += a[3];
		return cmd;
	},
	T(a) {
		if (px === null || py === null) {
			px = cx;
			py = cy;
		} else {
			px = cx - (px - cx);
			py = cy - (py - cy);
		}
		const cmd = appendQuadraticCurve(px, py, a[0], a[1]);
		cx = a[0];
		cy = a[1];
		return cmd;
	},
	t(a) {
		if (px === null || py === null) {
			px = cx;
			py = cy;
		} else {
			px = cx - (px - cx);
			py = cy - (py - cy);
		}
		const cmd = appendQuadraticCurve(px, py, cx + a[0], cy + a[1]);
		cx += a[0];
		cy += a[1];
		return cmd;
	},
	A(a) {
		const cmds = solveArc(cx, cy, a);
		cx = a[5];
		cy = a[6];
		return cmds;
	},
	a(a) {
		a[5] += cx;
		a[6] += cy;
		const cmds = solveArc(cx, cy, a);
		cx = a[5];
		cy = a[6];
		return cmds;
	},
	L(a) {
		cx = a[0];
		cy = a[1];
		px = py = null;
		return lineTo(cx, cy);
	},
	l(a) {
		cx += a[0];
		cy += a[1];
		px = py = null;
		return lineTo(cx, cy);
	},
	H(a) {
		cx = a[0];
		px = py = null;
		return lineTo(cx, cy);
	},
	h(a) {
		cx += a[0];
		px = py = null;
		return lineTo(cx, cy);
	},
	V(a) {
		cy = a[0];
		px = py = null;
		return lineTo(cx, cy);
	},
	v(a) {
		cy += a[0];
		px = py = null;
		return lineTo(cx, cy);
	},
	Z() {
		const cmd = closePath();
		cx = sx;
		cy = sy;
		return cmd;
	},
	z() {
		const cmd = closePath();
		cx = sx;
		cy = sy;
		return cmd;
	}
};
var solveArc = (x, y, coords) => {
	const [rx, ry, rot, large, sweep, ex, ey] = coords;
	const segs = arcToSegments(ex, ey, rx, ry, large, sweep, rot, x, y);
	const cmds = [];
	for (const seg of segs) {
		const bez = segmentToBezier(...seg);
		cmds.push(appendBezierCurve(...bez));
	}
	return cmds;
};
var arcToSegments = (x, y, rx, ry, large, sweep, rotateX, ox, oy) => {
	const th = rotateX * (Math.PI / 180);
	const sinTh = Math.sin(th);
	const cosTh = Math.cos(th);
	rx = Math.abs(rx);
	ry = Math.abs(ry);
	px = cosTh * (ox - x) * .5 + sinTh * (oy - y) * .5;
	py = cosTh * (oy - y) * .5 - sinTh * (ox - x) * .5;
	let pl = px * px / (rx * rx) + py * py / (ry * ry);
	if (pl > 1) {
		pl = Math.sqrt(pl);
		rx *= pl;
		ry *= pl;
	}
	const a00 = cosTh / rx;
	const a01 = sinTh / rx;
	const a10 = -sinTh / ry;
	const a11 = cosTh / ry;
	const x0 = a00 * ox + a01 * oy;
	const y0 = a10 * ox + a11 * oy;
	const x1 = a00 * x + a01 * y;
	const y1 = a10 * x + a11 * y;
	let sfactorSq = 1 / ((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0)) - .25;
	if (sfactorSq < 0) sfactorSq = 0;
	let sfactor = Math.sqrt(sfactorSq);
	if (sweep === large) sfactor = -sfactor;
	const xc = .5 * (x0 + x1) - sfactor * (y1 - y0);
	const yc = .5 * (y0 + y1) + sfactor * (x1 - x0);
	const th0 = Math.atan2(y0 - yc, x0 - xc);
	let thArc = Math.atan2(y1 - yc, x1 - xc) - th0;
	if (thArc < 0 && sweep === 1) thArc += 2 * Math.PI;
	else if (thArc > 0 && sweep === 0) thArc -= 2 * Math.PI;
	const segments = Math.ceil(Math.abs(thArc / (Math.PI * .5 + .001)));
	const result = [];
	for (let i = 0; i < segments; i++) result[i] = [
		xc,
		yc,
		th0 + i * thArc / segments,
		th0 + (i + 1) * thArc / segments,
		rx,
		ry,
		sinTh,
		cosTh
	];
	return result;
};
var segmentToBezier = (cx1, cy1, th0, th1, rx, ry, sinTh, cosTh) => {
	const a00 = cosTh * rx;
	const a01 = -sinTh * ry;
	const a10 = sinTh * rx;
	const a11 = cosTh * ry;
	const thHalf = .5 * (th1 - th0);
	const t = 8 / 3 * Math.sin(thHalf * .5) * Math.sin(thHalf * .5) / Math.sin(thHalf);
	const x1 = cx1 + Math.cos(th0) - t * Math.sin(th0);
	const y1 = cy1 + Math.sin(th0) + t * Math.cos(th0);
	const x3 = cx1 + Math.cos(th1);
	const y3 = cy1 + Math.sin(th1);
	const x2 = x3 + t * Math.sin(th1);
	const y2 = y3 - t * Math.cos(th1);
	return [
		a00 * x1 + a01 * y1,
		a10 * x1 + a11 * y1,
		a00 * x2 + a01 * y2,
		a10 * x2 + a11 * y2,
		a00 * x3 + a01 * y3,
		a10 * x3 + a11 * y3
	];
};
var svgPathToOperators = (path) => apply(parse$2(path));
//#endregion
//#region src/api/operations.ts
var clipSpace = ({ topLeft, topRight, bottomRight, bottomLeft }) => [
	moveTo(topLeft.x, topLeft.y),
	lineTo(topRight.x, topRight.y),
	lineTo(bottomRight.x, bottomRight.y),
	lineTo(bottomLeft.x, bottomLeft.y),
	closePath(),
	clip(),
	endPath()
];
var clipSpaces = (spaces) => spaces.flatMap(clipSpace);
var drawText = (line, options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	beginText(),
	setFillingColor(options.color),
	setFontAndSize(options.font, options.size),
	rotateAndSkewTextRadiansAndTranslate(toRadians(options.rotate), toRadians(options.xSkew), toRadians(options.ySkew), options.x, options.y),
	showText(line),
	endText(),
	popGraphicsState()
].filter(Boolean);
var drawLinesOfText = (lines, options) => {
	const operators = [
		pushGraphicsState(),
		options.graphicsState && setGraphicsState(options.graphicsState),
		...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
		options.matrix && concatTransformationMatrix(...options.matrix),
		beginText(),
		setFillingColor(options.color),
		setFontAndSize(options.font, options.size),
		setLineHeight(options.lineHeight),
		rotateAndSkewTextRadiansAndTranslate(toRadians(options.rotate), toRadians(options.xSkew), toRadians(options.ySkew), options.x, options.y)
	].filter(Boolean);
	for (let idx = 0, len = lines.length; idx < len; idx++) operators.push(showText(lines[idx]), nextLine());
	operators.push(endText(), popGraphicsState());
	return operators;
};
var drawImage = (name, options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
	options.matrix && concatTransformationMatrix(...options.matrix),
	translate(options.x, options.y),
	rotateRadians(toRadians(options.rotate)),
	scale(options.width, options.height),
	skewRadians(toRadians(options.xSkew), toRadians(options.ySkew)),
	drawObject(name),
	popGraphicsState()
].filter(Boolean);
var drawPage = (name, options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	translate(options.x, options.y),
	rotateRadians(toRadians(options.rotate)),
	scale(options.xScale, options.yScale),
	skewRadians(toRadians(options.xSkew), toRadians(options.ySkew)),
	drawObject(name),
	popGraphicsState()
].filter(Boolean);
var drawLine = (options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
	options.matrix && concatTransformationMatrix(...options.matrix),
	options.color && setStrokingColor(options.color),
	setLineWidth(options.thickness),
	setDashPattern(options.dashArray ?? [], options.dashPhase ?? 0),
	moveTo(options.start.x, options.start.y),
	options.lineCap && setLineCap(options.lineCap),
	moveTo(options.start.x, options.start.y),
	lineTo(options.end.x, options.end.y),
	stroke(),
	popGraphicsState()
].filter(Boolean);
var drawRectangle = (options) => {
	let ops = [];
	if (!options.radius || asNumber(options.radius) <= 0) ops = [
		moveTo(0, 0),
		lineTo(0, options.height),
		lineTo(options.width, options.height),
		lineTo(options.width, 0),
		closePath()
	];
	else {
		let radius = asNumber(options.radius);
		const width = asNumber(options.width);
		const height = asNumber(options.height);
		if (radius > width / 2 || radius > height / 2) radius = Math.min(width / 2, height / 2);
		const offset = KAPPA * radius;
		ops = [
			moveTo(0, radius),
			appendBezierCurve(0, radius - offset, radius - offset, 0, radius, 0),
			lineTo(width - radius, 0),
			appendBezierCurve(width - radius + offset, 0, width, radius - offset, width, radius),
			lineTo(width, height - radius),
			appendBezierCurve(width, height - radius + offset, width - radius + offset, height, width - radius, height),
			lineTo(radius, height),
			appendBezierCurve(radius - offset, height, 0, height - radius + offset, 0, height - radius),
			closePath()
		];
	}
	return [
		pushGraphicsState(),
		options.graphicsState && setGraphicsState(options.graphicsState),
		options.color && setFillingColor(options.color),
		options.borderColor && setStrokingColor(options.borderColor),
		setLineWidth(options.borderWidth),
		options.borderLineCap && setLineCap(options.borderLineCap),
		setDashPattern(options.borderDashArray ?? [], options.borderDashPhase ?? 0),
		...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
		options.matrix && concatTransformationMatrix(...options.matrix),
		translate(options.x, options.y),
		rotateRadians(toRadians(options.rotate)),
		skewRadians(toRadians(options.xSkew), toRadians(options.ySkew)),
		...ops,
		options.color && options.borderWidth ? fillAndStroke() : options.color ? fill() : options.borderColor ? stroke() : closePath(),
		popGraphicsState()
	].filter(Boolean);
};
var KAPPA = 4 * ((Math.sqrt(2) - 1) / 3);
/** @deprecated */
var drawEllipsePath = (config) => {
	let x = asNumber(config.x);
	let y = asNumber(config.y);
	const xScale = asNumber(config.xScale);
	const yScale = asNumber(config.yScale);
	x -= xScale;
	y -= yScale;
	const ox = xScale * KAPPA;
	const oy = yScale * KAPPA;
	const xe = x + xScale * 2;
	const ye = y + yScale * 2;
	const xm = x + xScale;
	const ym = y + yScale;
	return [
		pushGraphicsState(),
		moveTo(x, ym),
		appendBezierCurve(x, ym - oy, xm - ox, y, xm, y),
		appendBezierCurve(xm + ox, y, xe, ym - oy, xe, ym),
		appendBezierCurve(xe, ym + oy, xm + ox, ye, xm, ye),
		appendBezierCurve(xm - ox, ye, x, ym + oy, x, ym),
		popGraphicsState()
	];
};
var drawEllipseCurves = (config) => {
	const centerX = asNumber(config.x);
	const centerY = asNumber(config.y);
	const xScale = asNumber(config.xScale);
	const yScale = asNumber(config.yScale);
	const x = -xScale;
	const y = -yScale;
	const ox = xScale * KAPPA;
	const oy = yScale * KAPPA;
	const xe = x + xScale * 2;
	const ye = y + yScale * 2;
	const xm = x + xScale;
	const ym = y + yScale;
	return [
		translate(centerX, centerY),
		rotateRadians(toRadians(config.rotate)),
		moveTo(x, ym),
		appendBezierCurve(x, ym - oy, xm - ox, y, xm, y),
		appendBezierCurve(xm + ox, y, xe, ym - oy, xe, ym),
		appendBezierCurve(xe, ym + oy, xm + ox, ye, xm, ye),
		appendBezierCurve(xm - ox, ye, x, ym + oy, x, ym)
	];
};
var drawEllipse = (options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	options.color && setFillingColor(options.color),
	options.borderColor && setStrokingColor(options.borderColor),
	...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
	options.matrix && concatTransformationMatrix(...options.matrix),
	setLineWidth(options.borderWidth),
	options.borderLineCap && setLineCap(options.borderLineCap),
	setDashPattern(options.borderDashArray ?? [], options.borderDashPhase ?? 0),
	...options.rotate === void 0 ? drawEllipsePath({
		x: options.x,
		y: options.y,
		xScale: options.xScale,
		yScale: options.yScale
	}) : drawEllipseCurves({
		x: options.x,
		y: options.y,
		xScale: options.xScale,
		yScale: options.yScale,
		rotate: options.rotate ?? degrees(0)
	}),
	options.color && options.borderWidth ? fillAndStroke() : options.color ? fill() : options.borderColor ? stroke() : closePath(),
	popGraphicsState()
].filter(Boolean);
var drawSvgPath = (path, options) => [
	pushGraphicsState(),
	options.graphicsState && setGraphicsState(options.graphicsState),
	...options.clipSpaces ? clipSpaces(options.clipSpaces) : [],
	options.matrix && concatTransformationMatrix(...options.matrix),
	translate(options.x, options.y),
	rotateRadians(toRadians(options.rotate ?? degrees(0))),
	options.scale && scale(options.scale, options.scale),
	options.color && setFillingColor(options.color),
	options.borderColor && setStrokingColor(options.borderColor),
	options.borderWidth && setLineWidth(options.borderWidth),
	options.borderLineCap && setLineCap(options.borderLineCap),
	setDashPattern(options.borderDashArray ?? [], options.borderDashPhase ?? 0),
	...svgPathToOperators(path),
	options.color && options.borderWidth ? fillAndStroke() : options.color ? options.fillRule === FillRule.EvenOdd ? fillEvenOdd() : fill() : options.borderColor ? stroke() : closePath(),
	popGraphicsState()
].filter(Boolean);
var drawCheckMark = (options) => {
	const size = asNumber(options.size);
	return [
		pushGraphicsState(),
		options.color && setStrokingColor(options.color),
		setLineWidth(options.thickness),
		translate(options.x, options.y),
		moveTo(-.675 * size, -.07601036269430045 * size),
		lineTo(-.25 * size, -.49 * size),
		lineTo(.69 * size, .475 * size),
		stroke(),
		popGraphicsState()
	].filter(Boolean);
};
var rotateInPlace = (options) => options.rotation === 0 ? [translate(0, 0), rotateDegrees(0)] : options.rotation === 90 ? [translate(options.width, 0), rotateDegrees(90)] : options.rotation === 180 ? [translate(options.width, options.height), rotateDegrees(180)] : options.rotation === 270 ? [translate(0, options.height), rotateDegrees(270)] : [];
var drawCheckBox = (options) => {
	const outline = drawRectangle({
		x: options.x,
		y: options.y,
		width: options.width,
		height: options.height,
		borderWidth: options.borderWidth,
		color: options.color,
		borderColor: options.borderColor,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	if (!options.filled) return outline;
	const width = asNumber(options.width);
	const height = asNumber(options.height);
	const checkMarkSize = Math.min(width, height) / 2;
	const checkMark = drawCheckMark({
		x: width / 2,
		y: height / 2,
		size: checkMarkSize,
		thickness: options.thickness,
		color: options.markColor
	});
	return [
		pushGraphicsState(),
		...outline,
		...checkMark,
		popGraphicsState()
	];
};
var drawRadioButton = (options) => {
	const width = asNumber(options.width);
	const height = asNumber(options.height);
	const outlineScale = Math.min(width, height) / 2;
	const outline = drawEllipse({
		x: options.x,
		y: options.y,
		xScale: outlineScale,
		yScale: outlineScale,
		color: options.color,
		borderColor: options.borderColor,
		borderWidth: options.borderWidth
	});
	if (!options.filled) return outline;
	const dot = drawEllipse({
		x: options.x,
		y: options.y,
		xScale: outlineScale * .45,
		yScale: outlineScale * .45,
		color: options.dotColor,
		borderColor: void 0,
		borderWidth: 0
	});
	return [
		pushGraphicsState(),
		...outline,
		...dot,
		popGraphicsState()
	];
};
var drawButton = (options) => {
	const background = drawRectangle({
		x: asNumber(options.x),
		y: asNumber(options.y),
		width: asNumber(options.width),
		height: asNumber(options.height),
		borderWidth: options.borderWidth,
		color: options.color,
		borderColor: options.borderColor,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	const lines = drawTextLines(options.textLines, {
		color: options.textColor,
		font: options.font,
		size: options.fontSize,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	return [
		pushGraphicsState(),
		...background,
		...lines,
		popGraphicsState()
	];
};
var drawTextLines = (lines, options) => {
	const operators = [
		beginText(),
		setFillingColor(options.color),
		setFontAndSize(options.font, options.size)
	];
	for (let idx = 0, len = lines.length; idx < len; idx++) {
		const { encoded, x, y } = lines[idx];
		operators.push(rotateAndSkewTextRadiansAndTranslate(toRadians(options.rotate), toRadians(options.xSkew), toRadians(options.ySkew), x, y), showText(encoded));
	}
	operators.push(endText());
	return operators;
};
var drawTextField = (options) => {
	const x = asNumber(options.x);
	const y = asNumber(options.y);
	const width = asNumber(options.width);
	const height = asNumber(options.height);
	const borderWidth = asNumber(options.borderWidth);
	const padding = asNumber(options.padding);
	const clipX = x + borderWidth / 2 + padding;
	const clipY = y + borderWidth / 2 + padding;
	const clipWidth = width - (borderWidth / 2 + padding) * 2;
	const clipHeight = height - (borderWidth / 2 + padding) * 2;
	const clippingArea = [
		moveTo(clipX, clipY),
		lineTo(clipX, clipY + clipHeight),
		lineTo(clipX + clipWidth, clipY + clipHeight),
		lineTo(clipX + clipWidth, clipY),
		closePath(),
		clip(),
		endPath()
	];
	const background = drawRectangle({
		x,
		y,
		width,
		height,
		borderWidth: options.borderWidth,
		color: options.color,
		borderColor: options.borderColor,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	const lines = drawTextLines(options.textLines, {
		color: options.textColor,
		font: options.font,
		size: options.fontSize,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	const markedContent = [
		beginMarkedContent("Tx"),
		pushGraphicsState(),
		...lines,
		popGraphicsState(),
		endMarkedContent()
	];
	return [
		pushGraphicsState(),
		...background,
		...clippingArea,
		...markedContent,
		popGraphicsState()
	];
};
var drawOptionList = (options) => {
	const x = asNumber(options.x);
	const y = asNumber(options.y);
	const width = asNumber(options.width);
	const height = asNumber(options.height);
	const lineHeight = asNumber(options.lineHeight);
	const borderWidth = asNumber(options.borderWidth);
	const padding = asNumber(options.padding);
	const clipX = x + borderWidth / 2 + padding;
	const clipY = y + borderWidth / 2 + padding;
	const clipWidth = width - (borderWidth / 2 + padding) * 2;
	const clipHeight = height - (borderWidth / 2 + padding) * 2;
	const clippingArea = [
		moveTo(clipX, clipY),
		lineTo(clipX, clipY + clipHeight),
		lineTo(clipX + clipWidth, clipY + clipHeight),
		lineTo(clipX + clipWidth, clipY),
		closePath(),
		clip(),
		endPath()
	];
	const background = drawRectangle({
		x,
		y,
		width,
		height,
		borderWidth: options.borderWidth,
		color: options.color,
		borderColor: options.borderColor,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	const highlights = [];
	for (let idx = 0, len = options.selectedLines.length; idx < len; idx++) {
		const line = options.textLines[options.selectedLines[idx]];
		highlights.push(...drawRectangle({
			x: line.x - padding,
			y: line.y - (lineHeight - line.height) / 2,
			width: width - borderWidth,
			height: line.height + (lineHeight - line.height) / 2,
			borderWidth: 0,
			color: options.selectedColor,
			borderColor: void 0,
			rotate: degrees(0),
			xSkew: degrees(0),
			ySkew: degrees(0)
		}));
	}
	const lines = drawTextLines(options.textLines, {
		color: options.textColor,
		font: options.font,
		size: options.fontSize,
		rotate: degrees(0),
		xSkew: degrees(0),
		ySkew: degrees(0)
	});
	const markedContent = [
		beginMarkedContent("Tx"),
		pushGraphicsState(),
		...lines,
		popGraphicsState(),
		endMarkedContent()
	];
	return [
		pushGraphicsState(),
		...background,
		...highlights,
		...clippingArea,
		...markedContent,
		popGraphicsState()
	];
};
//#endregion
//#region src/api/errors.ts
var EncryptedPDFError = class extends Error {
	constructor() {
		super("Input document to `PDFDocument.load` is encrypted. You can use `PDFDocument.load(..., { ignoreEncryption: true })` if you wish to load the document anyways.");
	}
};
var FontkitNotRegisteredError = class extends Error {
	constructor() {
		super("Input to `PDFDocument.embedFont` was a custom font, but no `fontkit` instance was found. You must register a `fontkit` instance with `PDFDocument.registerFontkit(...)` before embedding custom fonts.");
	}
};
var ForeignPageError = class extends Error {
	constructor() {
		super("A `page` passed to `PDFDocument.addPage` or `PDFDocument.insertPage` was from a different (foreign) PDF document. If you want to copy pages from one PDFDocument to another, you must use `PDFDocument.copyPages(...)` to copy the pages before adding or inserting them.");
	}
};
var RemovePageFromEmptyDocumentError = class extends Error {
	constructor() {
		super("PDFDocument has no pages so `PDFDocument.removePage` cannot be called");
	}
};
var NoSuchFieldError = class extends Error {
	constructor(name) {
		const msg = `PDFDocument has no form field with the name "${name}"`;
		super(msg);
	}
};
var UnexpectedFieldTypeError = class extends Error {
	constructor(name, expected, actual) {
		const msg = `Expected field "${name}" to be of type ${expected?.name}, but it is actually of type ${actual?.constructor?.name ?? actual}`;
		super(msg);
	}
};
var MissingOnValueCheckError = class extends Error {
	constructor(onValue) {
		const msg = `Failed to select check box due to missing onValue: "${onValue}"`;
		super(msg);
	}
};
var FieldAlreadyExistsError = class extends Error {
	constructor(name) {
		const msg = `A field already exists with the specified name: "${name}"`;
		super(msg);
	}
};
var InvalidFieldNamePartError = class extends Error {
	constructor(namePart) {
		const msg = `Field name contains invalid component: "${namePart}"`;
		super(msg);
	}
};
var FieldExistsAsNonTerminalError = class extends Error {
	constructor(name) {
		const msg = `A non-terminal field already exists with the specified name: "${name}"`;
		super(msg);
	}
};
var RichTextFieldReadError = class extends Error {
	constructor(fieldName) {
		const msg = `Reading rich text fields is not supported: Attempted to read rich text field: ${fieldName}`;
		super(msg);
	}
};
var CombedTextLayoutError = class extends Error {
	constructor(lineLength, cellCount) {
		const msg = `Failed to layout combed text as lineLength=${lineLength} is greater than cellCount=${cellCount}`;
		super(msg);
	}
};
var ExceededMaxLengthError = class extends Error {
	constructor(textLength, maxLength, name) {
		const msg = `Attempted to set text with length=${textLength} for TextField with maxLength=${maxLength} and name=${name}`;
		super(msg);
	}
};
var InvalidMaxLengthError = class extends Error {
	constructor(textLength, maxLength, name) {
		const msg = `Attempted to set maxLength=${maxLength}, which is less than ${textLength}, the length of this field's current value (name=${name})`;
		super(msg);
	}
};
//#endregion
//#region src/api/text/alignment.ts
var TextAlignment = /* @__PURE__ */ function(TextAlignment) {
	TextAlignment[TextAlignment["Left"] = 0] = "Left";
	TextAlignment[TextAlignment["Center"] = 1] = "Center";
	TextAlignment[TextAlignment["Right"] = 2] = "Right";
	return TextAlignment;
}({});
//#endregion
//#region src/api/text/layout.ts
var MIN_FONT_SIZE = 4;
var MAX_FONT_SIZE = 500;
var computeFontSize = (lines, font, bounds, multiline = false) => {
	let fontSize = MIN_FONT_SIZE;
	while (fontSize < MAX_FONT_SIZE) {
		let linesUsed = 0;
		for (let lineIdx = 0, lineLen = lines.length; lineIdx < lineLen; lineIdx++) {
			linesUsed += 1;
			const words = lines[lineIdx].split(" ");
			let spaceInLineRemaining = bounds.width;
			for (let idx = 0, len = words.length; idx < len; idx++) {
				const word = idx === len - 1 ? words[idx] : words[idx] + " ";
				const widthOfWord = font.widthOfTextAtSize(word, fontSize);
				spaceInLineRemaining -= widthOfWord;
				if (spaceInLineRemaining <= 0) {
					linesUsed += 1;
					spaceInLineRemaining = bounds.width - widthOfWord;
				}
			}
		}
		if (!multiline && linesUsed > lines.length) return fontSize - 1;
		const height = font.heightAtSize(fontSize);
		if ((height + height * .2) * linesUsed > Math.abs(bounds.height)) return fontSize - 1;
		fontSize += 1;
	}
	return fontSize;
};
var computeCombedFontSize = (line, font, bounds, cellCount) => {
	const cellWidth = bounds.width / cellCount;
	const cellHeight = bounds.height;
	let fontSize = MIN_FONT_SIZE;
	const chars = charSplit(line);
	while (fontSize < MAX_FONT_SIZE) {
		for (let idx = 0, len = chars.length; idx < len; idx++) {
			const c = chars[idx];
			if (font.widthOfTextAtSize(c, fontSize) > cellWidth * .75) return fontSize - 1;
		}
		if (font.heightAtSize(fontSize, { descender: false }) > cellHeight) return fontSize - 1;
		fontSize += 1;
	}
	return fontSize;
};
var lastIndexOfWhitespace = (line) => {
	for (let idx = line.length; idx > 0; idx--) if (/\s/.test(line[idx])) return idx;
};
var splitOutLines = (input, maxWidth, font, fontSize) => {
	let lastWhitespaceIdx = input.length;
	while (lastWhitespaceIdx > 0) {
		const line = input.substring(0, lastWhitespaceIdx);
		const encoded = font.encodeText(line);
		const width = font.widthOfTextAtSize(line, fontSize);
		if (width < maxWidth) return {
			line,
			encoded,
			width,
			remainder: input.substring(lastWhitespaceIdx) || void 0
		};
		lastWhitespaceIdx = lastIndexOfWhitespace(line) ?? 0;
	}
	return {
		line: input,
		encoded: font.encodeText(input),
		width: font.widthOfTextAtSize(input, fontSize),
		remainder: void 0
	};
};
var layoutMultilineText = (text, { alignment, fontSize, font, bounds }) => {
	const lines = lineSplit(cleanText(text));
	if (fontSize === void 0 || fontSize === 0) fontSize = computeFontSize(lines, font, bounds, true);
	const height = font.heightAtSize(fontSize);
	const lineHeight = height + height * .2;
	const textLines = [];
	let minX = bounds.x;
	let minY = bounds.y;
	let maxX = bounds.x + bounds.width;
	let maxY = bounds.y + bounds.height;
	let y = bounds.y + bounds.height;
	for (let idx = 0, len = lines.length; idx < len; idx++) {
		let prevRemainder = lines[idx];
		while (prevRemainder !== void 0) {
			const { line, encoded, width, remainder } = splitOutLines(prevRemainder, bounds.width, font, fontSize);
			const x = alignment === TextAlignment.Left ? bounds.x : alignment === TextAlignment.Center ? bounds.x + bounds.width / 2 - width / 2 : alignment === TextAlignment.Right ? bounds.x + bounds.width - width : bounds.x;
			y -= lineHeight;
			if (x < minX) minX = x;
			if (y < minY) minY = y;
			if (x + width > maxX) maxX = x + width;
			if (y + height > maxY) maxY = y + height;
			textLines.push({
				text: line,
				encoded,
				width,
				height,
				x,
				y
			});
			prevRemainder = remainder?.trim();
		}
	}
	return {
		fontSize,
		lineHeight,
		lines: textLines,
		bounds: {
			x: minX,
			y: minY,
			width: maxX - minX,
			height: maxY - minY
		}
	};
};
var layoutCombedText = (text, { fontSize, font, bounds, cellCount }) => {
	const line = mergeLines(cleanText(text));
	if (line.length > cellCount) throw new CombedTextLayoutError(line.length, cellCount);
	if (fontSize === void 0 || fontSize === 0) fontSize = computeCombedFontSize(line, font, bounds, cellCount);
	const cellWidth = bounds.width / cellCount;
	const height = font.heightAtSize(fontSize, { descender: false });
	const y = bounds.y + (bounds.height / 2 - height / 2);
	const cells = [];
	let minX = bounds.x;
	let minY = bounds.y;
	let maxX = bounds.x + bounds.width;
	let maxY = bounds.y + bounds.height;
	let cellOffset = 0;
	let charOffset = 0;
	while (cellOffset < cellCount) {
		const [char, charLength] = charAtIndex(line, charOffset);
		const encoded = font.encodeText(char);
		const width = font.widthOfTextAtSize(char, fontSize);
		const x = bounds.x + (cellWidth * cellOffset + cellWidth / 2) - width / 2;
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x + width > maxX) maxX = x + width;
		if (y + height > maxY) maxY = y + height;
		cells.push({
			text: line,
			encoded,
			width,
			height,
			x,
			y
		});
		cellOffset += 1;
		charOffset += charLength;
	}
	return {
		fontSize,
		cells,
		bounds: {
			x: minX,
			y: minY,
			width: maxX - minX,
			height: maxY - minY
		}
	};
};
var layoutSinglelineText = (text, { alignment, fontSize, font, bounds }) => {
	const line = mergeLines(cleanText(text));
	if (fontSize === void 0 || fontSize === 0) fontSize = computeFontSize([line], font, bounds);
	const encoded = font.encodeText(line);
	const width = font.widthOfTextAtSize(line, fontSize);
	const height = font.heightAtSize(fontSize, { descender: false });
	const x = alignment === TextAlignment.Left ? bounds.x : alignment === TextAlignment.Center ? bounds.x + bounds.width / 2 - width / 2 : alignment === TextAlignment.Right ? bounds.x + bounds.width - width : bounds.x;
	const y = bounds.y + (bounds.height / 2 - height / 2);
	return {
		fontSize,
		line: {
			text: line,
			encoded,
			width,
			height,
			x,
			y
		},
		bounds: {
			x,
			y,
			width,
			height
		}
	};
};
//#endregion
//#region src/api/form/appearances.ts
/********************* Appearance Provider Functions **************************/
var normalizeAppearance = (appearance) => {
	if ("normal" in appearance) return appearance;
	return { normal: appearance };
};
var tfRegex = /\/([^\s]+)\s+(\d+(?:\.\d+)?)\s+Tf/;
var getDefaultFontSize = (field) => {
	const daMatch = findLastMatch(field.getDefaultAppearance() ?? "", tfRegex).match ?? [];
	const defaultFontSize = Number(daMatch[2]);
	return isFinite(defaultFontSize) ? defaultFontSize : void 0;
};
var colorRegex = /(\d+(?:\.\d+)?)\s*(\d+(?:\.\d+)?)?\s*(\d+(?:\.\d+)?)?\s*(\d+(?:\.\d+)?)?\s+(g|rg|k)/;
var getDefaultColor = (field) => {
	const [, c1, c2, c3, c4, colorSpace] = findLastMatch(field.getDefaultAppearance() ?? "", colorRegex).match ?? [];
	if (colorSpace === "g" && c1) return grayscale(Number(c1));
	if (colorSpace === "rg" && c1 && c2 && c3) return rgb(Number(c1), Number(c2), Number(c3));
	if (colorSpace === "k" && c1 && c2 && c3 && c4) return cmyk(Number(c1), Number(c2), Number(c3), Number(c4));
};
var updateDefaultAppearance = (field, color, font, fontSize = 0) => {
	const da = [setFillingColor(color).toString(), setFontAndSize(font?.name ?? "dummy__noop", fontSize).toString()].join("\n");
	field.setDefaultAppearance(da);
};
var defaultCheckBoxAppearanceProvider = (checkBox, widget) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(checkBox.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const borderWidth = widget.getBorderStyle()?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor()) ?? black;
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	const downBackgroundColor = componentsToColor(ap?.getBackgroundColor(), .8);
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor) updateDefaultAppearance(widget, textColor);
	else updateDefaultAppearance(checkBox.acroField, textColor);
	const options = {
		x: 0 + borderWidth / 2,
		y: 0 + borderWidth / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		thickness: 1.5,
		borderWidth,
		borderColor,
		markColor: textColor
	};
	return {
		normal: {
			on: [...rotate, ...drawCheckBox({
				...options,
				color: normalBackgroundColor,
				filled: true
			})],
			off: [...rotate, ...drawCheckBox({
				...options,
				color: normalBackgroundColor,
				filled: false
			})]
		},
		down: {
			on: [...rotate, ...drawCheckBox({
				...options,
				color: downBackgroundColor,
				filled: true
			})],
			off: [...rotate, ...drawCheckBox({
				...options,
				color: downBackgroundColor,
				filled: false
			})]
		}
	};
};
var defaultRadioGroupAppearanceProvider = (radioGroup, widget) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(radioGroup.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const borderWidth = widget.getBorderStyle()?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor()) ?? black;
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	const downBackgroundColor = componentsToColor(ap?.getBackgroundColor(), .8);
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor) updateDefaultAppearance(widget, textColor);
	else updateDefaultAppearance(radioGroup.acroField, textColor);
	const options = {
		x: width / 2,
		y: height / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		borderWidth,
		borderColor,
		dotColor: textColor
	};
	return {
		normal: {
			on: [...rotate, ...drawRadioButton({
				...options,
				color: normalBackgroundColor,
				filled: true
			})],
			off: [...rotate, ...drawRadioButton({
				...options,
				color: normalBackgroundColor,
				filled: false
			})]
		},
		down: {
			on: [...rotate, ...drawRadioButton({
				...options,
				color: downBackgroundColor,
				filled: true
			})],
			off: [...rotate, ...drawRadioButton({
				...options,
				color: downBackgroundColor,
				filled: false
			})]
		}
	};
};
var defaultButtonAppearanceProvider = (button, widget, font) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(button.acroField);
	const widgetFontSize = getDefaultFontSize(widget);
	const fieldFontSize = getDefaultFontSize(button.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const bs = widget.getBorderStyle();
	const captions = ap?.getCaptions();
	const normalText = captions?.normal ?? "";
	const downText = captions?.down ?? normalText ?? "";
	const borderWidth = bs?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor());
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	const downBackgroundColor = componentsToColor(ap?.getBackgroundColor(), .8);
	const bounds = {
		x: borderWidth,
		y: borderWidth,
		width: width - borderWidth * 2,
		height: height - borderWidth * 2
	};
	const normalLayout = layoutSinglelineText(normalText, {
		alignment: TextAlignment.Center,
		fontSize: widgetFontSize ?? fieldFontSize,
		font,
		bounds
	});
	const downLayout = layoutSinglelineText(downText, {
		alignment: TextAlignment.Center,
		fontSize: widgetFontSize ?? fieldFontSize,
		font,
		bounds
	});
	const fontSize = Math.min(normalLayout.fontSize, downLayout.fontSize);
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor || widgetFontSize !== void 0) updateDefaultAppearance(widget, textColor, font, fontSize);
	else updateDefaultAppearance(button.acroField, textColor, font, fontSize);
	const options = {
		x: 0 + borderWidth / 2,
		y: 0 + borderWidth / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		borderWidth,
		borderColor,
		textColor,
		font: font.name,
		fontSize
	};
	return {
		normal: [...rotate, ...drawButton({
			...options,
			color: normalBackgroundColor,
			textLines: [normalLayout.line]
		})],
		down: [...rotate, ...drawButton({
			...options,
			color: downBackgroundColor,
			textLines: [downLayout.line]
		})]
	};
};
var defaultTextFieldAppearanceProvider = (textField, widget, font) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(textField.acroField);
	const widgetFontSize = getDefaultFontSize(widget);
	const fieldFontSize = getDefaultFontSize(textField.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const bs = widget.getBorderStyle();
	const text = textField.getText() ?? "";
	const borderWidth = bs?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor());
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	let textLines;
	let fontSize;
	const padding = textField.isCombed() ? 0 : 1;
	const bounds = {
		x: borderWidth + padding,
		y: borderWidth + padding,
		width: width - (borderWidth + padding) * 2,
		height: height - (borderWidth + padding) * 2
	};
	if (textField.isMultiline()) {
		const layout = layoutMultilineText(text, {
			alignment: textField.getAlignment(),
			fontSize: widgetFontSize ?? fieldFontSize,
			font,
			bounds
		});
		textLines = layout.lines;
		fontSize = layout.fontSize;
	} else if (textField.isCombed()) {
		const layout = layoutCombedText(text, {
			fontSize: widgetFontSize ?? fieldFontSize,
			font,
			bounds,
			cellCount: textField.getMaxLength() ?? 0
		});
		textLines = layout.cells;
		fontSize = layout.fontSize;
	} else {
		const layout = layoutSinglelineText(text, {
			alignment: textField.getAlignment(),
			fontSize: widgetFontSize ?? fieldFontSize,
			font,
			bounds
		});
		textLines = [layout.line];
		fontSize = layout.fontSize;
	}
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor || widgetFontSize !== void 0) updateDefaultAppearance(widget, textColor, font, fontSize);
	else updateDefaultAppearance(textField.acroField, textColor, font, fontSize);
	const options = {
		x: 0 + borderWidth / 2,
		y: 0 + borderWidth / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		borderWidth: borderWidth ?? 0,
		borderColor,
		textColor,
		font: font.name,
		fontSize,
		color: normalBackgroundColor,
		textLines,
		padding
	};
	return [...rotate, ...drawTextField(options)];
};
var defaultDropdownAppearanceProvider = (dropdown, widget, font) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(dropdown.acroField);
	const widgetFontSize = getDefaultFontSize(widget);
	const fieldFontSize = getDefaultFontSize(dropdown.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const bs = widget.getBorderStyle();
	const text = dropdown.getSelected()[0] ?? "";
	const borderWidth = bs?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor());
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	const padding = 1;
	const bounds = {
		x: borderWidth + padding,
		y: borderWidth + padding,
		width: width - (borderWidth + padding) * 2,
		height: height - (borderWidth + padding) * 2
	};
	const { line, fontSize } = layoutSinglelineText(text, {
		alignment: TextAlignment.Left,
		fontSize: widgetFontSize ?? fieldFontSize,
		font,
		bounds
	});
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor || widgetFontSize !== void 0) updateDefaultAppearance(widget, textColor, font, fontSize);
	else updateDefaultAppearance(dropdown.acroField, textColor, font, fontSize);
	const options = {
		x: 0 + borderWidth / 2,
		y: 0 + borderWidth / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		borderWidth: borderWidth ?? 0,
		borderColor,
		textColor,
		font: font.name,
		fontSize,
		color: normalBackgroundColor,
		textLines: [line],
		padding
	};
	return [...rotate, ...drawTextField(options)];
};
var defaultOptionListAppearanceProvider = (optionList, widget, font) => {
	const widgetColor = getDefaultColor(widget);
	const fieldColor = getDefaultColor(optionList.acroField);
	const widgetFontSize = getDefaultFontSize(widget);
	const fieldFontSize = getDefaultFontSize(optionList.acroField);
	const rectangle = widget.getRectangle();
	const ap = widget.getAppearanceCharacteristics();
	const borderWidth = widget.getBorderStyle()?.getWidth() ?? 0;
	const rotation = reduceRotation(ap?.getRotation());
	const { width, height } = adjustDimsForRotation(rectangle, rotation);
	const rotate = rotateInPlace({
		...rectangle,
		rotation
	});
	const black = rgb(0, 0, 0);
	const borderColor = componentsToColor(ap?.getBorderColor());
	const normalBackgroundColor = componentsToColor(ap?.getBackgroundColor());
	const options = optionList.getOptions();
	const selected = optionList.getSelected();
	if (optionList.isSorted()) options.sort();
	let text = "";
	for (let idx = 0, len = options.length; idx < len; idx++) {
		text += options[idx];
		if (idx < len - 1) text += "\n";
	}
	const padding = 1;
	const bounds = {
		x: borderWidth + padding,
		y: borderWidth + padding,
		width: width - (borderWidth + padding) * 2,
		height: height - (borderWidth + padding) * 2
	};
	const { lines, fontSize, lineHeight } = layoutMultilineText(text, {
		alignment: TextAlignment.Left,
		fontSize: widgetFontSize ?? fieldFontSize,
		font,
		bounds
	});
	const selectedLines = [];
	for (let idx = 0, len = lines.length; idx < len; idx++) {
		const line = lines[idx];
		if (selected.includes(line.text)) selectedLines.push(idx);
	}
	const blue = rgb(153 / 255, 193 / 255, 218 / 255);
	const textColor = widgetColor ?? fieldColor ?? black;
	if (widgetColor || widgetFontSize !== void 0) updateDefaultAppearance(widget, textColor, font, fontSize);
	else updateDefaultAppearance(optionList.acroField, textColor, font, fontSize);
	return [...rotate, ...drawOptionList({
		x: 0 + borderWidth / 2,
		y: 0 + borderWidth / 2,
		width: width - borderWidth,
		height: height - borderWidth,
		borderWidth: borderWidth ?? 0,
		borderColor,
		textColor,
		font: font.name,
		fontSize,
		color: normalBackgroundColor,
		textLines: lines,
		lineHeight,
		selectedColor: blue,
		selectedLines,
		padding
	})];
};
//#endregion
//#region src/api/PDFEmbeddedPage.ts
var _PDFEmbeddedPage;
/**
* Represents a PDF page that has been embedded in a [[PDFDocument]].
*/
var PDFEmbeddedPage = class {
	constructor(ref, doc, embedder) {
		_defineProperty(
			this,
			/** The unique reference assigned to this embedded page within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this embedded page belongs. */
			"doc",
			void 0
		);
		_defineProperty(
			this,
			/** The width of this page in pixels. */
			"width",
			void 0
		);
		_defineProperty(
			this,
			/** The height of this page in pixels. */
			"height",
			void 0
		);
		_defineProperty(this, "alreadyEmbedded", false);
		_defineProperty(this, "embedder", void 0);
		assertIs(ref, "ref", [[PDFRef, "PDFRef"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		assertIs(embedder, "embedder", [[PDFPageEmbedder, "PDFPageEmbedder"]]);
		this.ref = ref;
		this.doc = doc;
		this.width = embedder.width;
		this.height = embedder.height;
		this.embedder = embedder;
	}
	/**
	* Compute the width and height of this page after being scaled by the
	* given `factor`. For example:
	* ```js
	* embeddedPage.width  // => 500
	* embeddedPage.height // => 250
	*
	* const scaled = embeddedPage.scale(0.5)
	* scaled.width  // => 250
	* scaled.height // => 125
	* ```
	* This operation is often useful before drawing a page with
	* [[PDFPage.drawPage]] to compute the `width` and `height` options.
	* @param factor The factor by which this page should be scaled.
	* @returns The width and height of the page after being scaled.
	*/
	scale(factor) {
		assertIs(factor, "factor", ["number"]);
		return {
			width: this.width * factor,
			height: this.height * factor
		};
	}
	/**
	* Get the width and height of this page. For example:
	* ```js
	* const { width, height } = embeddedPage.size()
	* ```
	* @returns The width and height of the page.
	*/
	size() {
		return this.scale(1);
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFDocument.save]] and [[PDFDocument.saveAsBase64]] methods will
	* > automatically ensure all embeddable pages get embedded.
	*
	* Embed this embeddable page in its document.
	*
	* @returns Resolves when the embedding is complete.
	*/
	async embed() {
		if (!this.alreadyEmbedded) {
			await this.embedder.embedIntoContext(this.doc.context, this.ref);
			this.alreadyEmbedded = true;
		}
	}
};
_PDFEmbeddedPage = PDFEmbeddedPage;
_defineProperty(PDFEmbeddedPage, "of", (ref, doc, embedder) => new _PDFEmbeddedPage(ref, doc, embedder));
//#endregion
//#region src/api/PDFFont.ts
var _PDFFont;
/**
* Represents a font that has been embedded in a [[PDFDocument]].
*/
var PDFFont = class {
	constructor(ref, doc, embedder) {
		_defineProperty(
			this,
			/** The unique reference assigned to this font within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this font belongs. */
			"doc",
			void 0
		);
		_defineProperty(
			this,
			/** The name of this font. */
			"name",
			void 0
		);
		_defineProperty(this, "modified", true);
		_defineProperty(this, "embedder", void 0);
		assertIs(ref, "ref", [[PDFRef, "PDFRef"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		assertIs(embedder, "embedder", [[CustomFontEmbedder, "CustomFontEmbedder"], [StandardFontEmbedder, "StandardFontEmbedder"]]);
		this.ref = ref;
		this.doc = doc;
		this.name = embedder.fontName;
		this.embedder = embedder;
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFPage.drawText]] method will automatically encode the text it is
	* > given.
	*
	* Encodes a string of text in this font.
	*
	* @param text The text to be encoded.
	* @returns The encoded text as a hex string.
	*/
	encodeText(text) {
		assertIs(text, "text", ["string"]);
		this.modified = true;
		return this.embedder.encodeText(text);
	}
	/**
	* Measure the width of a string of text drawn in this font at a given size.
	* For example:
	* ```js
	* const width = font.widthOfTextAtSize('Foo Bar Qux Baz', 36)
	* ```
	* @param text The string of text to be measured.
	* @param size The font size to be used for this measurement.
	* @returns The width of the string of text when drawn in this font at the
	*          given size.
	*/
	widthOfTextAtSize(text, size) {
		assertIs(text, "text", ["string"]);
		assertIs(size, "size", ["number"]);
		return this.embedder.widthOfTextAtSize(text, size);
	}
	/**
	* Measure the height of this font at a given size. For example:
	* ```js
	* const height = font.heightAtSize(24)
	* ```
	*
	* The `options.descender` value controls whether or not the font's
	* descender is included in the height calculation.
	*
	* @param size The font size to be used for this measurement.
	* @param options The options to be used when computing this measurement.
	* @returns The height of this font at the given size.
	*/
	heightAtSize(size, options) {
		assertIs(size, "size", ["number"]);
		assertOrUndefined(options?.descender, "options.descender", ["boolean"]);
		return this.embedder.heightOfFontAtSize(size, { descender: options?.descender ?? true });
	}
	/**
	* Compute the font size at which this font is a given height. For example:
	* ```js
	* const fontSize = font.sizeAtHeight(12)
	* ```
	* @param height The height to be used for this calculation.
	* @returns The font size at which this font is the given height.
	*/
	sizeAtHeight(height) {
		assertIs(height, "height", ["number"]);
		return this.embedder.sizeOfFontAtHeight(height);
	}
	/**
	* Get the set of unicode code points that can be represented by this font.
	* @returns The set of unicode code points supported by this font.
	*/
	getCharacterSet() {
		if (this.embedder instanceof StandardFontEmbedder) return this.embedder.encoding.supportedCodePoints;
		else return this.embedder.font.characterSet;
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFDocument.save]] and [[PDFDocument.saveAsBase64]] methods will
	* > automatically ensure all fonts get embedded.
	*
	* Embed this font in its document.
	*
	* @returns Resolves when the embedding is complete.
	*/
	async embed() {
		if (this.modified) {
			await this.embedder.embedIntoContext(this.doc.context, this.ref);
			this.modified = false;
		}
	}
};
_PDFFont = PDFFont;
_defineProperty(PDFFont, "of", (ref, doc, embedder) => new _PDFFont(ref, doc, embedder));
//#endregion
//#region src/api/PDFImage.ts
var _PDFImage;
/**
* Represents an image that has been embedded in a [[PDFDocument]].
*/
var PDFImage = class {
	constructor(ref, doc, embedder) {
		_defineProperty(
			this,
			/** The unique reference assigned to this image within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this image belongs. */
			"doc",
			void 0
		);
		_defineProperty(
			this,
			/** The width of this image in pixels. */
			"width",
			void 0
		);
		_defineProperty(
			this,
			/** The height of this image in pixels. */
			"height",
			void 0
		);
		_defineProperty(this, "embedder", void 0);
		_defineProperty(this, "embedTask", void 0);
		assertIs(ref, "ref", [[PDFRef, "PDFRef"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		assertIs(embedder, "embedder", [[JpegEmbedder, "JpegEmbedder"], [PngEmbedder, "PngEmbedder"]]);
		this.ref = ref;
		this.doc = doc;
		this.width = embedder.width;
		this.height = embedder.height;
		this.embedder = embedder;
	}
	/**
	* Compute the width and height of this image after being scaled by the
	* given `factor`. For example:
	* ```js
	* image.width  // => 500
	* image.height // => 250
	*
	* const scaled = image.scale(0.5)
	* scaled.width  // => 250
	* scaled.height // => 125
	* ```
	* This operation is often useful before drawing an image with
	* [[PDFPage.drawImage]] to compute the `width` and `height` options.
	* @param factor The factor by which this image should be scaled.
	* @returns The width and height of the image after being scaled.
	*/
	scale(factor) {
		assertIs(factor, "factor", ["number"]);
		return {
			width: this.width * factor,
			height: this.height * factor
		};
	}
	/**
	* Get the width and height of this image after scaling it as large as
	* possible while maintaining its aspect ratio and not exceeding the
	* specified `width` and `height`. For example:
	* ```
	* image.width  // => 500
	* image.height // => 250
	*
	* const scaled = image.scaleToFit(750, 1000)
	* scaled.width  // => 750
	* scaled.height // => 375
	* ```
	* The `width` and `height` parameters can also be thought of as the width
	* and height of a box that the scaled image must fit within.
	* @param width The bounding box's width.
	* @param height The bounding box's height.
	* @returns The width and height of the image after being scaled.
	*/
	scaleToFit(width, height) {
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const imgWidthScale = width / this.width;
		const imgHeightScale = height / this.height;
		const scale = Math.min(imgWidthScale, imgHeightScale);
		return this.scale(scale);
	}
	/**
	* Get the width and height of this image. For example:
	* ```js
	* const { width, height } = image.size()
	* ```
	* @returns The width and height of the image.
	*/
	size() {
		return this.scale(1);
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFDocument.save]] and [[PDFDocument.saveAsBase64]] methods will
	* > automatically ensure all images get embedded.
	*
	* Embed this image in its document.
	*
	* @returns Resolves when the embedding is complete.
	*/
	async embed() {
		if (!this.embedder) return;
		if (!this.embedTask) {
			const { doc, ref } = this;
			this.embedTask = this.embedder.embedIntoContext(doc.context, ref);
		}
		await this.embedTask;
		this.embedder = void 0;
	}
};
_PDFImage = PDFImage;
_defineProperty(PDFImage, "of", (ref, doc, embedder) => new _PDFImage(ref, doc, embedder));
//#endregion
//#region src/api/image/alignment.ts
var ImageAlignment = /* @__PURE__ */ function(ImageAlignment) {
	ImageAlignment[ImageAlignment["Left"] = 0] = "Left";
	ImageAlignment[ImageAlignment["Center"] = 1] = "Center";
	ImageAlignment[ImageAlignment["Right"] = 2] = "Right";
	return ImageAlignment;
}({});
//#endregion
//#region src/api/form/PDFField.ts
var assertFieldAppearanceOptions = (options) => {
	assertOrUndefined(options?.x, "options.x", ["number"]);
	assertOrUndefined(options?.y, "options.y", ["number"]);
	assertOrUndefined(options?.width, "options.width", ["number"]);
	assertOrUndefined(options?.height, "options.height", ["number"]);
	assertOrUndefined(options?.textColor, "options.textColor", [[Object, "Color"]]);
	assertOrUndefined(options?.backgroundColor, "options.backgroundColor", [[Object, "Color"]]);
	assertOrUndefined(options?.borderColor, "options.borderColor", [[Object, "Color"]]);
	assertOrUndefined(options?.borderWidth, "options.borderWidth", ["number"]);
	assertOrUndefined(options?.rotate, "options.rotate", [[Object, "Rotation"]]);
};
/**
* Represents a field of a [[PDFForm]].
*
* This class is effectively abstract. All fields in a [[PDFForm]] will
* actually be an instance of a subclass of this class.
*
* Note that each field in a PDF is represented by a single field object.
* However, a given field object may be rendered at multiple locations within
* the document (across one or more pages). The rendering of a field is
* controlled by its widgets. Each widget causes its field to be displayed at a
* particular location in the document.
*
* Most of the time each field in a PDF has only a single widget, and thus is
* only rendered once. However, if a field is rendered multiple times, it will
* have multiple widgets - one for each location it is rendered.
*
* This abstraction of field objects and widgets is defined in the PDF
* specification and dictates how PDF files store fields and where they are
* to be rendered.
*/
var PDFField = class {
	constructor(acroField, ref, doc) {
		_defineProperty(
			this,
			/** The low-level PDFAcroTerminal wrapped by this field. */
			"acroField",
			void 0
		);
		_defineProperty(
			this,
			/** The unique reference assigned to this field within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this field belongs. */
			"doc",
			void 0
		);
		assertIs(acroField, "acroField", [[PDFAcroTerminal, "PDFAcroTerminal"]]);
		assertIs(ref, "ref", [[PDFRef, "PDFRef"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		this.acroField = acroField;
		this.ref = ref;
		this.doc = doc;
	}
	/**
	* Get the fully qualified name of this field. For example:
	* ```js
	* const fields = form.getFields()
	* fields.forEach(field => {
	*   const name = field.getName()
	*   console.log('Field name:', name)
	* })
	* ```
	* Note that PDF fields are structured as a tree. Each field is the
	* descendent of a series of ancestor nodes all the way up to the form node,
	* which is always the root of the tree. Each node in the tree (except for
	* the form node) has a partial name. Partial names can be composed of any
	* unicode characters except a period (`.`). The fully qualified name of a
	* field is composed of the partial names of all its ancestors joined
	* with periods. This means that splitting the fully qualified name on
	* periods and taking the last element of the resulting array will give you
	* the partial name of a specific field.
	* @returns The fully qualified name of this field.
	*/
	getName() {
		return this.acroField.getFullyQualifiedName() ?? "";
	}
	/**
	* Returns `true` if this field is read only. This means that PDF readers
	* will not allow users to interact with the field or change its value. See
	* [[PDFField.enableReadOnly]] and [[PDFField.disableReadOnly]].
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* if (field.isReadOnly()) console.log('Read only is enabled')
	* ```
	* @returns Whether or not this is a read only field.
	*/
	isReadOnly() {
		return this.acroField.hasFlag(AcroFieldFlags.ReadOnly);
	}
	/**
	* Prevent PDF readers from allowing users to interact with this field or
	* change its value. The field will not respond to mouse or keyboard input.
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* field.enableReadOnly()
	* ```
	* Useful for fields whose values are computed, imported from a database, or
	* prefilled by software before being displayed to the user.
	*/
	enableReadOnly() {
		this.acroField.setFlagTo(AcroFieldFlags.ReadOnly, true);
	}
	/**
	* Allow users to interact with this field and change its value in PDF
	* readers via mouse and keyboard input. For example:
	* ```js
	* const field = form.getField('some.field')
	* field.disableReadOnly()
	* ```
	*/
	disableReadOnly() {
		this.acroField.setFlagTo(AcroFieldFlags.ReadOnly, false);
	}
	/**
	* Returns `true` if this field must have a value when the form is submitted.
	* See [[PDFField.enableRequired]] and [[PDFField.disableRequired]].
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* if (field.isRequired()) console.log('Field is required')
	* ```
	* @returns Whether or not this field is required.
	*/
	isRequired() {
		return this.acroField.hasFlag(AcroFieldFlags.Required);
	}
	/**
	* Require this field to have a value when the form is submitted.
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* field.enableRequired()
	* ```
	*/
	enableRequired() {
		this.acroField.setFlagTo(AcroFieldFlags.Required, true);
	}
	/**
	* Do not require this field to have a value when the form is submitted.
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* field.disableRequired()
	* ```
	*/
	disableRequired() {
		this.acroField.setFlagTo(AcroFieldFlags.Required, false);
	}
	/**
	* Returns `true` if this field's value should be exported when the form is
	* submitted. See [[PDFField.enableExporting]] and
	* [[PDFField.disableExporting]].
	* For example:
	* ```js
	* const field = form.getField('some.field')
	* if (field.isExported()) console.log('Exporting is enabled')
	* ```
	* @returns Whether or not this field's value should be exported.
	*/
	isExported() {
		return !this.acroField.hasFlag(AcroFieldFlags.NoExport);
	}
	/**
	* Indicate that this field's value should be exported when the form is
	* submitted in a PDF reader. For example:
	* ```js
	* const field = form.getField('some.field')
	* field.enableExporting()
	* ```
	*/
	enableExporting() {
		this.acroField.setFlagTo(AcroFieldFlags.NoExport, false);
	}
	/**
	* Indicate that this field's value should **not** be exported when the form
	* is submitted in a PDF reader. For example:
	* ```js
	* const field = form.getField('some.field')
	* field.disableExporting()
	* ```
	*/
	disableExporting() {
		this.acroField.setFlagTo(AcroFieldFlags.NoExport, true);
	}
	/** @ignore */
	needsAppearancesUpdate() {
		throw new MethodNotImplementedError(this.constructor.name, "needsAppearancesUpdate");
	}
	/** @ignore */
	defaultUpdateAppearances(_font) {
		throw new MethodNotImplementedError(this.constructor.name, "defaultUpdateAppearances");
	}
	markAsDirty() {
		this.doc.getForm().markFieldAsDirty(this.ref);
	}
	markAsClean() {
		this.doc.getForm().markFieldAsClean(this.ref);
	}
	isDirty() {
		return this.doc.getForm().fieldIsDirty(this.ref);
	}
	createWidget(options) {
		const textColor = options.textColor;
		const backgroundColor = options.backgroundColor;
		const borderColor = options.borderColor;
		const borderWidth = options.borderWidth;
		const degreesAngle = toDegrees(options.rotate);
		const caption = options.caption;
		const x = options.x;
		const y = options.y;
		const width = options.width + borderWidth;
		const height = options.height + borderWidth;
		const hidden = Boolean(options.hidden);
		const pageRef = options.page;
		assertMultiple(degreesAngle, "degreesAngle", 90);
		const widget = PDFWidgetAnnotation.create(this.doc.context, this.ref);
		const rect = rotateRectangle({
			x,
			y,
			width,
			height
		}, borderWidth, degreesAngle);
		widget.setRectangle(rect);
		if (pageRef) widget.setP(pageRef);
		const ac = widget.getOrCreateAppearanceCharacteristics();
		if (backgroundColor) ac.setBackgroundColor(colorToComponents(backgroundColor));
		ac.setRotation(degreesAngle);
		if (caption) ac.setCaptions({ normal: caption });
		if (borderColor) ac.setBorderColor(colorToComponents(borderColor));
		const bs = widget.getOrCreateBorderStyle();
		if (borderWidth !== void 0) bs.setWidth(borderWidth);
		widget.setFlagTo(AnnotationFlags.Print, true);
		widget.setFlagTo(AnnotationFlags.Hidden, hidden);
		widget.setFlagTo(AnnotationFlags.Invisible, false);
		if (textColor) {
			const newDa = (this.acroField.getDefaultAppearance() ?? "") + "\n" + setFillingColor(textColor).toString();
			this.acroField.setDefaultAppearance(newDa);
		}
		return widget;
	}
	updateWidgetAppearanceWithFont(widget, font, { normal, rollover, down }) {
		this.updateWidgetAppearances(widget, {
			normal: this.createAppearanceStream(widget, normal, font),
			rollover: rollover && this.createAppearanceStream(widget, rollover, font),
			down: down && this.createAppearanceStream(widget, down, font)
		});
	}
	updateOnOffWidgetAppearance(widget, onValue, { normal, rollover, down }) {
		this.updateWidgetAppearances(widget, {
			normal: this.createAppearanceDict(widget, normal, onValue),
			rollover: rollover && this.createAppearanceDict(widget, rollover, onValue),
			down: down && this.createAppearanceDict(widget, down, onValue)
		});
	}
	updateWidgetAppearances(widget, { normal, rollover, down }) {
		widget.setNormalAppearance(normal);
		if (rollover) widget.setRolloverAppearance(rollover);
		else widget.removeRolloverAppearance();
		if (down) widget.setDownAppearance(down);
		else widget.removeDownAppearance();
	}
	createAppearanceStream(widget, appearance, font) {
		const { context } = this.acroField.dict;
		const { width, height } = widget.getRectangle();
		const Resources = font && { Font: { [font.name]: font.ref } };
		const stream = context.formXObject(appearance, {
			Resources,
			BBox: context.obj([
				0,
				0,
				width,
				height
			]),
			Matrix: context.obj([
				1,
				0,
				0,
				1,
				0,
				0
			])
		});
		return context.register(stream);
	}
	/**
	* Create a FormXObject of the supplied image and add it to context.
	* The FormXObject size is calculated based on the widget (including
	* the alignment).
	* @param widget The widget that should display the image.
	* @param alignment The alignment of the image.
	* @param image The image that should be displayed.
	* @returns The ref for the FormXObject that was added to the context.
	*/
	createImageAppearanceStream(widget, image, alignment) {
		const { context } = this.acroField.dict;
		const rectangle = widget.getRectangle();
		const ap = widget.getAppearanceCharacteristics();
		const borderWidth = widget.getBorderStyle()?.getWidth() ?? 0;
		const rotation = reduceRotation(ap?.getRotation());
		const rotate = rotateInPlace({
			...rectangle,
			rotation
		});
		const adj = adjustDimsForRotation(rectangle, rotation);
		const imageDims = image.scaleToFit(adj.width - borderWidth * 2, adj.height - borderWidth * 2);
		const options = {
			x: borderWidth,
			y: borderWidth,
			width: imageDims.width,
			height: imageDims.height,
			rotate: degrees(0),
			xSkew: degrees(0),
			ySkew: degrees(0)
		};
		if (alignment === ImageAlignment.Center) {
			options.x += (adj.width - borderWidth * 2) / 2 - imageDims.width / 2;
			options.y += (adj.height - borderWidth * 2) / 2 - imageDims.height / 2;
		} else if (alignment === ImageAlignment.Right) {
			options.x = adj.width - borderWidth - imageDims.width;
			options.y = adj.height - borderWidth - imageDims.height;
		}
		const imageName = this.doc.context.addRandomSuffix("Image", 10);
		const appearance = [...rotate, ...drawImage(imageName, options)];
		const Resources = { XObject: { [imageName]: image.ref } };
		const stream = context.formXObject(appearance, {
			Resources,
			BBox: context.obj([
				0,
				0,
				rectangle.width,
				rectangle.height
			]),
			Matrix: context.obj([
				1,
				0,
				0,
				1,
				0,
				0
			])
		});
		return context.register(stream);
	}
	createAppearanceDict(widget, appearance, onValue) {
		const { context } = this.acroField.dict;
		const onStreamRef = this.createAppearanceStream(widget, appearance.on);
		const offStreamRef = this.createAppearanceStream(widget, appearance.off);
		const appearanceDict = context.obj({});
		appearanceDict.set(onValue, onStreamRef);
		appearanceDict.set(PDFName.of("Off"), offStreamRef);
		return appearanceDict;
	}
};
//#endregion
//#region src/api/form/PDFCheckBox.ts
var _PDFCheckBox;
/**
* Represents a check box field of a [[PDFForm]].
*
* [[PDFCheckBox]] fields are interactive boxes that users can click with their
* mouse. This type of [[PDFField]] has two states: `on` and `off`. The purpose
* of a check box is to enable users to select from one or more options, where
* each option is represented by a single check box. Check boxes are typically
* square in shape and display a check mark when they are in the `on` state.
*/
var PDFCheckBox = class extends PDFField {
	constructor(acroCheckBox, ref, doc) {
		super(acroCheckBox, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroCheckBox wrapped by this check box. */
			"acroField",
			void 0
		);
		assertIs(acroCheckBox, "acroCheckBox", [[PDFAcroCheckBox, "PDFAcroCheckBox"]]);
		this.acroField = acroCheckBox;
	}
	/**
	* Mark this check box. This operation is analogous to a human user clicking
	* a check box to fill it in a PDF reader. This method will update the
	* underlying state of the check box field to indicate it has been selected.
	* PDF libraries and readers will be able to extract this value from the
	* saved document and determine that it was selected.
	*
	* For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* checkBox.check()
	* ```
	*
	* This method will mark this check box as dirty, causing its appearance
	* streams to be updated when either [[PDFDocument.save]] or
	* [[PDFForm.updateFieldAppearances]] is called. The updated appearance
	* streams will display a check mark inside the widgets of this check box
	* field.
	*/
	check() {
		const onValue = this.acroField.getOnValue() ?? PDFName.of("Yes");
		this.markAsDirty();
		this.acroField.setValue(onValue);
	}
	/**
	* Clears this check box. This operation is analogous to a human user clicking
	* a check box to unmark it in a PDF reader. This method will update the
	* underlying state of the check box field to indicate it has been deselected.
	* PDF libraries and readers will be able to extract this value from the
	* saved document and determine that it was not selected.
	*
	* For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* checkBox.uncheck()
	* ```
	*
	* This method will mark this check box as dirty. See [[PDFCheckBox.check]]
	* for more details about what this means.
	*/
	uncheck() {
		this.markAsDirty();
		this.acroField.setValue(PDFName.of("Off"));
	}
	/**
	* Returns `true` if this check box is selected (either by a human user via
	* a PDF reader, or else programmatically via software). For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* if (checkBox.isChecked()) console.log('check box is selected')
	* ```
	* @returns Whether or not this check box is selected.
	*/
	isChecked() {
		const onValue = this.acroField.getOnValue();
		return !!onValue && onValue === this.acroField.getValue();
	}
	/**
	* Show this check box on the specified page. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const checkBox = form.createCheckBox('some.checkBox.field')
	*
	* checkBox.addToPage(page, {
	*   x: 50,
	*   y: 75,
	*   width: 25,
	*   height: 25,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	* })
	* ```
	* This will create a new widget for this check box field.
	* @param page The page to which this check box widget should be added.
	* @param options The options to be used when adding this check box widget.
	*/
	addToPage(page, options) {
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		if (!options) options = {};
		if (!("textColor" in options)) options.textColor = rgb(0, 0, 0);
		if (!("backgroundColor" in options)) options.backgroundColor = rgb(1, 1, 1);
		if (!("borderColor" in options)) options.borderColor = rgb(0, 0, 0);
		if (!("borderWidth" in options)) options.borderWidth = 1;
		const widget = this.createWidget({
			x: options.x ?? 0,
			y: options.y ?? 0,
			width: options.width ?? 50,
			height: options.height ?? 50,
			textColor: options.textColor,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth ?? 0,
			rotate: options.rotate ?? degrees(0),
			hidden: options.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		this.acroField.addWidget(widgetRef);
		widget.setAppearanceState(PDFName.of("Off"));
		this.updateWidgetAppearance(widget, PDFName.of("Yes"));
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if any of this check box's widgets do not have an
	* appearance stream for its current state. For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* if (checkBox.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this check box needs an appearance update.
	*/
	needsAppearancesUpdate() {
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const state = widget.getAppearanceState();
			const normal = widget.getAppearances()?.normal;
			if (!(normal instanceof PDFDict)) return true;
			if (state && !normal.has(state)) return true;
		}
		return false;
	}
	/**
	* Update the appearance streams for each of this check box's widgets using
	* the default appearance provider for check boxes. For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* checkBox.defaultUpdateAppearances()
	* ```
	*/
	defaultUpdateAppearances() {
		this.updateAppearances();
	}
	/**
	* Update the appearance streams for each of this check box's widgets using
	* the given appearance provider. If no `provider` is passed, the default
	* appearance provider for check boxs will be used. For example:
	* ```js
	* const checkBox = form.getCheckBox('some.checkBox.field')
	* checkBox.updateAppearances((field, widget) => {
	*   ...
	*   return {
	*     normal: { on: drawCheckBox(...), off: drawCheckBox(...) },
	*     down: { on: drawCheckBox(...), off: drawCheckBox(...) },
	*   }
	* })
	* ```
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(provider) {
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const onValue = widget.getOnValue() ?? PDFName.of("Yes");
			if (!onValue) continue;
			this.updateWidgetAppearance(widget, onValue, provider);
		}
		this.markAsClean();
	}
	updateWidgetAppearance(widget, onValue, provider) {
		const appearances = normalizeAppearance((provider ?? defaultCheckBoxAppearanceProvider)(this, widget));
		this.updateOnOffWidgetAppearance(widget, onValue, appearances);
	}
};
_PDFCheckBox = PDFCheckBox;
_defineProperty(PDFCheckBox, "of", (acroCheckBox, ref, doc) => new _PDFCheckBox(acroCheckBox, ref, doc));
//#endregion
//#region src/api/form/PDFDropdown.ts
var _PDFDropdown;
/**
* Represents a dropdown field of a [[PDFForm]].
*
* [[PDFDropdown]] fields are interactive text boxes that display a single
* element (the currently selected value). The purpose of a dropdown is to
* enable users to select a single option from a set of possible options. Users
* can click on a dropdown to view the full list of options it provides.
* Clicking on an option in the list will cause it to be selected and displayed
* in the dropdown's text box. Some dropdowns allow users to enter text
* directly into the box from their keyboard, rather than only being allowed to
* choose an option from the list (see [[PDFDropdown.isEditable]]).
*/
var PDFDropdown = class extends PDFField {
	constructor(acroComboBox, ref, doc) {
		super(acroComboBox, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroComboBox wrapped by this dropdown. */
			"acroField",
			void 0
		);
		assertIs(acroComboBox, "acroComboBox", [[PDFAcroComboBox, "PDFAcroComboBox"]]);
		this.acroField = acroComboBox;
	}
	/**
	* Get the list of available options for this dropdown. These options will be
	* displayed to users who click on this dropdown in a PDF reader.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* const options = dropdown.getOptions()
	* console.log('Dropdown options:', options)
	* ```
	* @returns The options for this dropdown.
	*/
	getOptions() {
		const rawOptions = this.acroField.getOptions();
		const options = Array(rawOptions.length);
		for (let idx = 0, len = options.length; idx < len; idx++) {
			const { display, value } = rawOptions[idx];
			options[idx] = (display ?? value).decodeText();
		}
		return options;
	}
	/**
	* Get the selected options for this dropdown. These are the values that were
	* selected by a human user via a PDF reader, or programatically via
	* software.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* const selections = dropdown.getSelected()
	* console.log('Dropdown selections:', selections)
	* ```
	* > **NOTE:** Note that PDF readers only display one selected option when
	* > rendering dropdowns. However, the PDF specification does allow for
	* > multiple values to be selected in a dropdown. As such, the `pdf-lib`
	* > API supports this. However, in most cases the array returned by this
	* > method will contain only a single element (or no elements).
	* @returns The selected options in this dropdown.
	*/
	getSelected() {
		const values = this.acroField.getValues();
		const selected = Array(values.length);
		for (let idx = 0, len = values.length; idx < len; idx++) selected[idx] = values[idx].decodeText();
		return selected;
	}
	/**
	* Set the list of options that are available for this dropdown. These are
	* the values that will be available for users to select when they view this
	* dropdown in a PDF reader. Note that preexisting options for this dropdown
	* will be removed. Only the values passed as `options` will be available to
	* select.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('planets.dropdown')
	* dropdown.setOptions(['Earth', 'Mars', 'Pluto', 'Venus'])
	* ```
	* @param options The options that should be available in this dropdown.
	*/
	setOptions(options) {
		assertIs(options, "options", [Array]);
		const optionObjects = Array(options.length);
		for (let idx = 0, len = options.length; idx < len; idx++) optionObjects[idx] = { value: PDFHexString.fromText(options[idx]) };
		this.acroField.setOptions(optionObjects);
	}
	/**
	* Add to the list of options that are available for this dropdown. Users
	* will be able to select these values in a PDF reader. In addition to the
	* values passed as `options`, any preexisting options for this dropdown will
	* still be available for users to select.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('rockets.dropdown')
	* dropdown.addOptions(['Saturn IV', 'Falcon Heavy'])
	* ```
	* @param options New options that should be available in this dropdown.
	*/
	addOptions(options) {
		assertIs(options, "options", ["string", Array]);
		const optionsArr = Array.isArray(options) ? options : [options];
		const existingOptions = this.acroField.getOptions();
		const newOptions = Array(optionsArr.length);
		for (let idx = 0, len = optionsArr.length; idx < len; idx++) newOptions[idx] = { value: PDFHexString.fromText(optionsArr[idx]) };
		this.acroField.setOptions(existingOptions.concat(newOptions));
	}
	/**
	* Select one or more values for this dropdown. This operation is analogous
	* to a human user opening the dropdown in a PDF reader and clicking on a
	* value to select it. This method will update the underlying state of the
	* dropdown to indicate which values have been selected. PDF libraries and
	* readers will be able to extract these values from the saved document and
	* determine which values were selected.
	*
	* For example:
	* ```js
	* const dropdown = form.getDropdown('best.superhero.dropdown')
	* dropdown.select('One Punch Man')
	* ```
	*
	* This method will mark this dropdown as dirty, causing its appearance
	* streams to be updated when either [[PDFDocument.save]] or
	* [[PDFForm.updateFieldAppearances]] is called. The updated streams will
	* display the selected option inside the widgets of this dropdown.
	*
	* **IMPORTANT:** The default font used to update appearance streams is
	* [[StandardFonts.Helvetica]]. Note that this is a WinAnsi font. This means
	* that encoding errors will be thrown if the selected option for this field
	* contains characters outside the WinAnsi character set (the latin alphabet).
	*
	* Embedding a custom font and passing it to
	* [[PDFForm.updateFieldAppearances]] or [[PDFDropdown.updateAppearances]]
	* allows you to generate appearance streams with characters outside the
	* latin alphabet (assuming the custom font supports them).
	*
	* Selecting an option that does not exist in this dropdown's option list
	* (see [[PDFDropdown.getOptions]]) will enable editing on this dropdown
	* (see [[PDFDropdown.enableEditing]]).
	*
	* > **NOTE:** PDF readers only display one selected option when rendering
	* > dropdowns. However, the PDF specification does allow for multiple values
	* > to be selected in a dropdown. As such, the `pdf-lib` API supports this.
	* > However, it is not recommended to select more than one value with this
	* > method, as only one will be visible. [[PDFOptionList]] fields are better
	* > suited for displaying multiple selected values.
	*
	* @param options The options to be selected.
	* @param merge Whether or not existing selections should be preserved.
	*/
	select(options, merge = false) {
		assertIs(options, "options", ["string", Array]);
		assertIs(merge, "merge", ["boolean"]);
		const optionsArr = Array.isArray(options) ? options : [options];
		const validOptions = this.getOptions();
		if (optionsArr.find((option) => !validOptions.includes(option))) this.enableEditing();
		this.markAsDirty();
		if (optionsArr.length > 1 || optionsArr.length === 1 && merge) this.enableMultiselect();
		const values = Array(optionsArr.length);
		for (let idx = 0, len = optionsArr.length; idx < len; idx++) values[idx] = PDFHexString.fromText(optionsArr[idx]);
		if (merge) {
			const existingValues = this.acroField.getValues();
			this.acroField.setValues(existingValues.concat(values));
		} else this.acroField.setValues(values);
	}
	/**
	* Clear all selected values for this dropdown. This operation is equivalent
	* to selecting an empty list. This method will update the underlying state
	* of the dropdown to indicate that no values have been selected.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.clear()
	* ```
	* This method will mark this text field as dirty. See [[PDFDropdown.select]]
	* for more details about what this means.
	*/
	clear() {
		this.markAsDirty();
		this.acroField.setValues([]);
	}
	/**
	* Set the font size for this field. Larger font sizes will result in larger
	* text being displayed when PDF readers render this dropdown. Font sizes may
	* be integer or floating point numbers. Supplying a negative font size will
	* cause this method to throw an error.
	*
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.setFontSize(4)
	* dropdown.setFontSize(15.7)
	* ```
	*
	* > This method depends upon the existence of a default appearance
	* > (`/DA`) string. If this field does not have a default appearance string,
	* > or that string does not contain a font size (via the `Tf` operator),
	* > then this method will throw an error.
	*
	* @param fontSize The font size to be used when rendering text in this field.
	*/
	setFontSize(fontSize) {
		assertPositive(fontSize, "fontSize");
		this.acroField.setFontSize(fontSize);
		this.markAsDirty();
	}
	/**
	* Returns `true` if users are allowed to edit the selected value of this
	* dropdown directly and are not constrained by the list of available
	* options. See [[PDFDropdown.enableEditing]] and
	* [[PDFDropdown.disableEditing]]. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.isEditable()) console.log('Editing is enabled')
	* ```
	* @returns Whether or not this dropdown is editable.
	*/
	isEditable() {
		return this.acroField.hasFlag(AcroChoiceFlags.Edit);
	}
	/**
	* Allow users to edit the selected value of this dropdown in PDF readers
	* with their keyboard. This means that the selected value of this dropdown
	* will not be constrained by the list of available options. However, if this
	* dropdown has any available options, users will still be allowed to select
	* from that list.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.enableEditing()
	* ```
	*/
	enableEditing() {
		this.acroField.setFlagTo(AcroChoiceFlags.Edit, true);
	}
	/**
	* Do not allow users to edit the selected value of this dropdown in PDF
	* readers with their keyboard. This will constrain the selected value of
	* this dropdown to the list of available options. Users will only be able
	* to select an option from that list.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.disableEditing()
	* ```
	*/
	disableEditing() {
		this.acroField.setFlagTo(AcroChoiceFlags.Edit, false);
	}
	/**
	* Returns `true` if the option list of this dropdown is always displayed
	* in alphabetical order, irrespective of the order in which the options
	* were added to the dropdown. See [[PDFDropdown.enableSorting]] and
	* [[PDFDropdown.disableSorting]]. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.isSorted()) console.log('Sorting is enabled')
	* ```
	* @returns Whether or not this dropdown's options are sorted.
	*/
	isSorted() {
		return this.acroField.hasFlag(AcroChoiceFlags.Sort);
	}
	/**
	* Always display the option list of this dropdown in alphabetical order,
	* irrespective of the order in which the options were added to this dropdown.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.enableSorting()
	* ```
	*/
	enableSorting() {
		this.acroField.setFlagTo(AcroChoiceFlags.Sort, true);
	}
	/**
	* Do not always display the option list of this dropdown in alphabetical
	* order. Instead, display the options in whichever order they were added
	* to the list. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.disableSorting()
	* ```
	*/
	disableSorting() {
		this.acroField.setFlagTo(AcroChoiceFlags.Sort, false);
	}
	/**
	* Returns `true` if multiple options can be selected from this dropdown's
	* option list. See [[PDFDropdown.enableMultiselect]] and
	* [[PDFDropdown.disableMultiselect]]. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.isMultiselect()) console.log('Multiselect is enabled')
	* ```
	* @returns Whether or not multiple options can be selected.
	*/
	isMultiselect() {
		return this.acroField.hasFlag(AcroChoiceFlags.MultiSelect);
	}
	/**
	* Allow users to select more than one option from this dropdown's option
	* list. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.enableMultiselect()
	* ```
	*/
	enableMultiselect() {
		this.acroField.setFlagTo(AcroChoiceFlags.MultiSelect, true);
	}
	/**
	* Do not allow users to select more than one option from this dropdown's
	* option list. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.disableMultiselect()
	* ```
	*/
	disableMultiselect() {
		this.acroField.setFlagTo(AcroChoiceFlags.MultiSelect, false);
	}
	/**
	* Returns `true` if the selected option should be spell checked by PDF
	* readers. Spell checking will only be performed if this dropdown allows
	* editing (see [[PDFDropdown.isEditable]]). See
	* [[PDFDropdown.enableSpellChecking]] and
	* [[PDFDropdown.disableSpellChecking]]. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.isSpellChecked()) console.log('Spell checking is enabled')
	* ```
	* @returns Whether or not this dropdown can be spell checked.
	*/
	isSpellChecked() {
		return !this.acroField.hasFlag(AcroChoiceFlags.DoNotSpellCheck);
	}
	/**
	* Allow PDF readers to spell check the selected option of this dropdown.
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.enableSpellChecking()
	* ```
	*/
	enableSpellChecking() {
		this.acroField.setFlagTo(AcroChoiceFlags.DoNotSpellCheck, false);
	}
	/**
	* Do not allow PDF readers to spell check the selected option of this
	* dropdown. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.disableSpellChecking()
	* ```
	*/
	disableSpellChecking() {
		this.acroField.setFlagTo(AcroChoiceFlags.DoNotSpellCheck, true);
	}
	/**
	* Returns `true` if the option selected by a user is stored, or "committed",
	* when the user clicks the option. The alternative is that the user's
	* selection is stored when the user leaves this dropdown field (by clicking
	* outside of it - on another field, for example). See
	* [[PDFDropdown.enableSelectOnClick]] and
	* [[PDFDropdown.disableSelectOnClick]]. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.isSelectOnClick()) console.log('Select on click is enabled')
	* ```
	* @returns Whether or not options are selected immediately after they are
	*          clicked.
	*/
	isSelectOnClick() {
		return this.acroField.hasFlag(AcroChoiceFlags.CommitOnSelChange);
	}
	/**
	* Store the option selected by a user immediately after the user clicks the
	* option. Do not wait for the user to leave this dropdown field (by clicking
	* outside of it - on another field, for example). For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.enableSelectOnClick()
	* ```
	*/
	enableSelectOnClick() {
		this.acroField.setFlagTo(AcroChoiceFlags.CommitOnSelChange, true);
	}
	/**
	* Wait to store the option selected by a user until they leave this dropdown
	* field (by clicking outside of it - on another field, for example).
	* For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.disableSelectOnClick()
	* ```
	*/
	disableSelectOnClick() {
		this.acroField.setFlagTo(AcroChoiceFlags.CommitOnSelChange, false);
	}
	/**
	* Show this dropdown on the specified page. For example:
	* ```js
	* const ubuntuFont = await pdfDoc.embedFont(ubuntuFontBytes)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const dropdown = form.createDropdown('best.gundam')
	* dropdown.setOptions(['Exia', 'Dynames'])
	* dropdown.select('Exia')
	*
	* dropdown.addToPage(page, {
	*   x: 50,
	*   y: 75,
	*   width: 200,
	*   height: 100,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	*   font: ubuntuFont,
	* })
	* ```
	* This will create a new widget for this dropdown field.
	* @param page The page to which this dropdown widget should be added.
	* @param options The options to be used when adding this dropdown widget.
	*/
	addToPage(page, options) {
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		if (!options) options = {};
		if (!("textColor" in options)) options.textColor = rgb(0, 0, 0);
		if (!("backgroundColor" in options)) options.backgroundColor = rgb(1, 1, 1);
		if (!("borderColor" in options)) options.borderColor = rgb(0, 0, 0);
		if (!("borderWidth" in options)) options.borderWidth = 1;
		const widget = this.createWidget({
			x: options.x ?? 0,
			y: options.y ?? 0,
			width: options.width ?? 200,
			height: options.height ?? 50,
			textColor: options.textColor,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth ?? 0,
			rotate: options.rotate ?? degrees(0),
			hidden: options.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		this.acroField.addWidget(widgetRef);
		const font = options.font ?? this.doc.getForm().getDefaultFont();
		this.updateWidgetAppearance(widget, font);
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if this dropdown has been marked as dirty, or if any of
	* this dropdown's widgets do not have an appearance stream. For example:
	* ```js
	* const dropdown = form.getDropdown('some.dropdown.field')
	* if (dropdown.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this dropdown needs an appearance update.
	*/
	needsAppearancesUpdate() {
		if (this.isDirty()) return true;
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) if (!(widgets[idx].getAppearances()?.normal instanceof PDFStream)) return true;
		return false;
	}
	/**
	* Update the appearance streams for each of this dropdown's widgets using
	* the default appearance provider for dropdowns. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.defaultUpdateAppearances(helvetica)
	* ```
	* @param font The font to be used for creating the appearance streams.
	*/
	defaultUpdateAppearances(font) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		this.updateAppearances(font);
	}
	/**
	* Update the appearance streams for each of this dropdown's widgets using
	* the given appearance provider. If no `provider` is passed, the default
	* appearance provider for dropdowns will be used. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const dropdown = form.getDropdown('some.dropdown.field')
	* dropdown.updateAppearances(helvetica, (field, widget, font) => {
	*   ...
	*   return drawTextField(...)
	* })
	* ```
	* @param font The font to be used for creating the appearance streams.
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(font, provider) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			this.updateWidgetAppearance(widget, font, provider);
		}
		this.markAsClean();
	}
	updateWidgetAppearance(widget, font, provider) {
		const appearances = normalizeAppearance((provider ?? defaultDropdownAppearanceProvider)(this, widget, font));
		this.updateWidgetAppearanceWithFont(widget, font, appearances);
	}
};
_PDFDropdown = PDFDropdown;
_defineProperty(PDFDropdown, "of", (acroComboBox, ref, doc) => new _PDFDropdown(acroComboBox, ref, doc));
//#endregion
//#region src/api/form/PDFOptionList.ts
var _PDFOptionList;
/**
* Represents an option list field of a [[PDFForm]].
*
* [[PDFOptionList]] fields are interactive lists of options. The purpose of an
* option list is to enable users to select one or more options from a set of
* possible options. Users are able to see the full set of options without
* first having to click on the field (though scrolling may be necessary).
* Clicking an option in the list will cause it to be selected and displayed
* with a highlighted background. Some option lists allow users to select
* more than one option (see [[PDFOptionList.isMultiselect]]).
*/
var PDFOptionList = class extends PDFField {
	constructor(acroListBox, ref, doc) {
		super(acroListBox, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroListBox wrapped by this option list. */
			"acroField",
			void 0
		);
		assertIs(acroListBox, "acroListBox", [[PDFAcroListBox, "PDFAcroListBox"]]);
		this.acroField = acroListBox;
	}
	/**
	* Get the list of available options for this option list. These options will
	* be displayed to users who view this option list in a PDF reader.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* const options = optionList.getOptions()
	* console.log('Option List options:', options)
	* ```
	* @returns The options for this option list.
	*/
	getOptions() {
		const rawOptions = this.acroField.getOptions();
		const options = Array(rawOptions.length);
		for (let idx = 0, len = options.length; idx < len; idx++) {
			const { display, value } = rawOptions[idx];
			options[idx] = (display ?? value).decodeText();
		}
		return options;
	}
	/**
	* Get the selected options for this option list. These are the values that
	* were selected by a human user via a PDF reader, or programatically via
	* software.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* const selections = optionList.getSelected()
	* console.log('Option List selections:', selections)
	* ```
	* @returns The selected options for this option list.
	*/
	getSelected() {
		const values = this.acroField.getValues();
		const selected = Array(values.length);
		for (let idx = 0, len = values.length; idx < len; idx++) selected[idx] = values[idx].decodeText();
		return selected;
	}
	/**
	* Set the list of options that are available for this option list. These are
	* the values that will be available for users to select when they view this
	* option list in a PDF reader. Note that preexisting options for this
	* option list will be removed. Only the values passed as `options` will be
	* available to select.
	*
	* For example:
	* ```js
	* const optionList = form.getOptionList('planets.optionList')
	* optionList.setOptions(['Earth', 'Mars', 'Pluto', 'Venus'])
	* ```
	*
	* This method will mark this option list as dirty, causing its appearance
	* streams to be updated when either [[PDFDocument.save]] or
	* [[PDFForm.updateFieldAppearances]] is called. The updated streams will
	* display the options this field contains inside the widgets of this text
	* field (with selected options highlighted).
	*
	* **IMPORTANT:** The default font used to update appearance streams is
	* [[StandardFonts.Helvetica]]. Note that this is a WinAnsi font. This means
	* that encoding errors will be thrown if this field contains any options
	* with characters outside the WinAnsi character set (the latin alphabet).
	*
	* Embedding a custom font and passing it to
	* [[PDFForm.updateFieldAppearances]] or [[PDFOptionList.updateAppearances]]
	* allows you to generate appearance streams with characters outside the
	* latin alphabet (assuming the custom font supports them).
	*
	* @param options The options that should be available in this option list.
	*/
	setOptions(options) {
		assertIs(options, "options", [Array]);
		this.markAsDirty();
		const optionObjects = Array(options.length);
		for (let idx = 0, len = options.length; idx < len; idx++) optionObjects[idx] = { value: PDFHexString.fromText(options[idx]) };
		this.acroField.setOptions(optionObjects);
	}
	/**
	* Add to the list of options that are available for this option list. Users
	* will be able to select these values in a PDF reader. In addition to the
	* values passed as `options`, any preexisting options for this option list
	* will still be available for users to select.
	* For example:
	* ```js
	* const optionList = form.getOptionList('rockets.optionList')
	* optionList.addOptions(['Saturn IV', 'Falcon Heavy'])
	* ```
	* This method will mark this option list as dirty. See
	* [[PDFOptionList.setOptions]] for more details about what this means.
	* @param options New options that should be available in this option list.
	*/
	addOptions(options) {
		assertIs(options, "options", ["string", Array]);
		this.markAsDirty();
		const optionsArr = Array.isArray(options) ? options : [options];
		const existingOptions = this.acroField.getOptions();
		const newOptions = Array(optionsArr.length);
		for (let idx = 0, len = optionsArr.length; idx < len; idx++) newOptions[idx] = { value: PDFHexString.fromText(optionsArr[idx]) };
		this.acroField.setOptions(existingOptions.concat(newOptions));
	}
	/**
	* Select one or more values for this option list. This operation is analogous
	* to a human user opening the option list in a PDF reader and clicking on one
	* or more values to select them. This method will update the underlying state
	* of the option list to indicate which values have been selected. PDF
	* libraries and readers will be able to extract these values from the saved
	* document and determine which values were selected.
	* For example:
	* ```js
	* const optionList = form.getOptionList('best.superheroes.optionList')
	* optionList.select(['One Punch Man', 'Iron Man'])
	* ```
	* This method will mark this option list as dirty. See
	* [[PDFOptionList.setOptions]] for more details about what this means.
	* @param options The options to be selected.
	* @param merge Whether or not existing selections should be preserved.
	*/
	select(options, merge = false) {
		assertIs(options, "options", ["string", Array]);
		assertIs(merge, "merge", ["boolean"]);
		const optionsArr = Array.isArray(options) ? options : [options];
		assertIsSubset(optionsArr, "option", this.getOptions());
		this.markAsDirty();
		if (optionsArr.length > 1 || optionsArr.length === 1 && merge) this.enableMultiselect();
		const values = Array(optionsArr.length);
		for (let idx = 0, len = optionsArr.length; idx < len; idx++) values[idx] = PDFHexString.fromText(optionsArr[idx]);
		if (merge) {
			const existingValues = this.acroField.getValues();
			this.acroField.setValues(existingValues.concat(values));
		} else this.acroField.setValues(values);
	}
	/**
	* Clear all selected values for this option list. This operation is
	* equivalent to selecting an empty list. This method will update the
	* underlying state of the option list to indicate that no values have been
	* selected.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.clear()
	* ```
	* This method will mark this option list as dirty. See
	* [[PDFOptionList.setOptions]] for more details about what this means.
	*/
	clear() {
		this.markAsDirty();
		this.acroField.setValues([]);
	}
	/**
	* Set the font size for the text in this field. There needs to be a
	* default appearance string (DA) set with a font value specified
	* for this to work. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.setFontSize(4);
	* ```
	* @param fontSize The font size to set the font to.
	*/
	/**
	* Set the font size for this field. Larger font sizes will result in larger
	* text being displayed when PDF readers render this option list. Font sizes
	* may be integer or floating point numbers. Supplying a negative font size
	* will cause this method to throw an error.
	*
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.setFontSize(4)
	* optionList.setFontSize(15.7)
	* ```
	*
	* > This method depends upon the existence of a default appearance
	* > (`/DA`) string. If this field does not have a default appearance string,
	* > or that string does not contain a font size (via the `Tf` operator),
	* > then this method will throw an error.
	*
	* @param fontSize The font size to be used when rendering text in this field.
	*/
	setFontSize(fontSize) {
		assertPositive(fontSize, "fontSize");
		this.acroField.setFontSize(fontSize);
		this.markAsDirty();
	}
	/**
	* Returns `true` if the options of this option list are always displayed
	* in alphabetical order, irrespective of the order in which the options
	* were added to the option list. See [[PDFOptionList.enableSorting]] and
	* [[PDFOptionList.disableSorting]]. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* if (optionList.isSorted()) console.log('Sorting is enabled')
	* ```
	* @returns Whether or not this option list is sorted.
	*/
	isSorted() {
		return this.acroField.hasFlag(AcroChoiceFlags.Sort);
	}
	/**
	* Always display the options of this option list in alphabetical order,
	* irrespective of the order in which the options were added to this option
	* list.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.enableSorting()
	* ```
	*/
	enableSorting() {
		this.acroField.setFlagTo(AcroChoiceFlags.Sort, true);
	}
	/**
	* Do not always display the options of this option list in alphabetical
	* order. Instead, display the options in whichever order they were added
	* to this option list. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.disableSorting()
	* ```
	*/
	disableSorting() {
		this.acroField.setFlagTo(AcroChoiceFlags.Sort, false);
	}
	/**
	* Returns `true` if multiple options can be selected from this option list.
	* See [[PDFOptionList.enableMultiselect]] and
	* [[PDFOptionList.disableMultiselect]]. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* if (optionList.isMultiselect()) console.log('Multiselect is enabled')
	* ```
	* @returns Whether or not multiple options can be selected.
	*/
	isMultiselect() {
		return this.acroField.hasFlag(AcroChoiceFlags.MultiSelect);
	}
	/**
	* Allow users to select more than one option from this option list.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.enableMultiselect()
	* ```
	*/
	enableMultiselect() {
		this.acroField.setFlagTo(AcroChoiceFlags.MultiSelect, true);
	}
	/**
	* Do not allow users to select more than one option from this option list.
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.disableMultiselect()
	* ```
	*/
	disableMultiselect() {
		this.acroField.setFlagTo(AcroChoiceFlags.MultiSelect, false);
	}
	/**
	* Returns `true` if the option selected by a user is stored, or "committed",
	* when the user clicks the option. The alternative is that the user's
	* selection is stored when the user leaves this option list field (by
	* clicking outside of it - on another field, for example). See
	* [[PDFOptionList.enableSelectOnClick]] and
	* [[PDFOptionList.disableSelectOnClick]]. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* if (optionList.isSelectOnClick()) console.log('Select on click is enabled')
	* ```
	* @returns Whether or not options are selected immediately after they are
	*          clicked.
	*/
	isSelectOnClick() {
		return this.acroField.hasFlag(AcroChoiceFlags.CommitOnSelChange);
	}
	/**
	* Store the option selected by a user immediately after the user clicks the
	* option. Do not wait for the user to leave this option list field (by
	* clicking outside of it - on another field, for example). For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.enableSelectOnClick()
	* ```
	*/
	enableSelectOnClick() {
		this.acroField.setFlagTo(AcroChoiceFlags.CommitOnSelChange, true);
	}
	/**
	* Wait to store the option selected by a user until they leave this option
	* list field (by clicking outside of it - on another field, for example).
	* For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.disableSelectOnClick()
	* ```
	*/
	disableSelectOnClick() {
		this.acroField.setFlagTo(AcroChoiceFlags.CommitOnSelChange, false);
	}
	/**
	* Show this option list on the specified page. For example:
	* ```js
	* const ubuntuFont = await pdfDoc.embedFont(ubuntuFontBytes)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const optionList = form.createOptionList('best.gundams')
	* optionList.setOptions(['Exia', 'Dynames', 'Kyrios', 'Virtue'])
	* optionList.select(['Exia', 'Virtue'])
	*
	* optionList.addToPage(page, {
	*   x: 50,
	*   y: 75,
	*   width: 200,
	*   height: 100,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	*   font: ubuntuFont,
	* })
	* ```
	* This will create a new widget for this option list field.
	* @param page The page to which this option list widget should be added.
	* @param options The options to be used when adding this option list widget.
	*/
	addToPage(page, options) {
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		if (!options) options = {};
		if (!("textColor" in options)) options.textColor = rgb(0, 0, 0);
		if (!("backgroundColor" in options)) options.backgroundColor = rgb(1, 1, 1);
		if (!("borderColor" in options)) options.borderColor = rgb(0, 0, 0);
		if (!("borderWidth" in options)) options.borderWidth = 1;
		const widget = this.createWidget({
			x: options.x ?? 0,
			y: options.y ?? 0,
			width: options.width ?? 200,
			height: options.height ?? 100,
			textColor: options.textColor,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth ?? 0,
			rotate: options.rotate ?? degrees(0),
			hidden: options.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		this.acroField.addWidget(widgetRef);
		const font = options.font ?? this.doc.getForm().getDefaultFont();
		this.updateWidgetAppearance(widget, font);
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if this option list has been marked as dirty, or if any of
	* this option list's widgets do not have an appearance stream. For example:
	* ```js
	* const optionList = form.getOptionList('some.optionList.field')
	* if (optionList.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this option list needs an appearance update.
	*/
	needsAppearancesUpdate() {
		if (this.isDirty()) return true;
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) if (!(widgets[idx].getAppearances()?.normal instanceof PDFStream)) return true;
		return false;
	}
	/**
	* Update the appearance streams for each of this option list's widgets using
	* the default appearance provider for option lists. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.defaultUpdateAppearances(helvetica)
	* ```
	* @param font The font to be used for creating the appearance streams.
	*/
	defaultUpdateAppearances(font) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		this.updateAppearances(font);
	}
	/**
	* Update the appearance streams for each of this option list's widgets using
	* the given appearance provider. If no `provider` is passed, the default
	* appearance provider for option lists will be used. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const optionList = form.getOptionList('some.optionList.field')
	* optionList.updateAppearances(helvetica, (field, widget, font) => {
	*   ...
	*   return drawOptionList(...)
	* })
	* ```
	* @param font The font to be used for creating the appearance streams.
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(font, provider) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			this.updateWidgetAppearance(widget, font, provider);
		}
		this.markAsClean();
	}
	updateWidgetAppearance(widget, font, provider) {
		const appearances = normalizeAppearance((provider ?? defaultOptionListAppearanceProvider)(this, widget, font));
		this.updateWidgetAppearanceWithFont(widget, font, appearances);
	}
};
_PDFOptionList = PDFOptionList;
_defineProperty(PDFOptionList, "of", (acroListBox, ref, doc) => new _PDFOptionList(acroListBox, ref, doc));
//#endregion
//#region src/api/form/PDFRadioGroup.ts
var _PDFRadioGroup;
/**
* Represents a radio group field of a [[PDFForm]].
*
* [[PDFRadioGroup]] fields are collections of radio buttons. The purpose of a
* radio group is to enable users to select one option from a set of mutually
* exclusive choices. Each choice in a radio group is represented by a radio
* button. Radio buttons each have two states: `on` and `off`. At most one
* radio button in a group may be in the `on` state at any time. Users can
* click on a radio button to select it (and thereby automatically deselect any
* other radio button that might have already been selected). Some radio
* groups allow users to toggle a selected radio button `off` by clicking on
* it (see [[PDFRadioGroup.isOffToggleable]]).
*
* Note that some radio groups allow multiple radio buttons to be in the `on`
* state at the same type **if** they represent the same underlying value (see
* [[PDFRadioGroup.isMutuallyExclusive]]).
*/
var PDFRadioGroup = class extends PDFField {
	constructor(acroRadioButton, ref, doc) {
		super(acroRadioButton, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroRadioButton wrapped by this radio group. */
			"acroField",
			void 0
		);
		assertIs(acroRadioButton, "acroRadioButton", [[PDFAcroRadioButton, "PDFAcroRadioButton"]]);
		this.acroField = acroRadioButton;
	}
	/**
	* Get the list of available options for this radio group. Each option is
	* represented by a radio button. These radio buttons are displayed at
	* various locations in the document, potentially on different pages (though
	* typically they are stacked horizontally or vertically on the same page).
	* For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* const options = radioGroup.getOptions()
	* console.log('Radio Group options:', options)
	* ```
	* @returns The options for this radio group.
	*/
	getOptions() {
		const exportValues = this.acroField.getExportValues();
		if (exportValues) {
			const exportOptions = Array(exportValues.length);
			for (let idx = 0, len = exportValues.length; idx < len; idx++) exportOptions[idx] = exportValues[idx].decodeText();
			return exportOptions;
		}
		const onValues = this.acroField.getOnValues();
		const onOptions = Array(onValues.length);
		for (let idx = 0, len = onOptions.length; idx < len; idx++) onOptions[idx] = onValues[idx].decodeText();
		return onOptions;
	}
	/**
	* Get the selected option for this radio group. The selected option is
	* represented by the radio button in this group that is turned on. At most
	* one radio button in a group can be selected. If no buttons in this group
	* are selected, `undefined` is returned.
	* For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* const selected = radioGroup.getSelected()
	* console.log('Selected radio button:', selected)
	* ```
	* @returns The selected option for this radio group.
	*/
	getSelected() {
		const value = this.acroField.getValue();
		if (value === PDFName.of("Off")) return void 0;
		const exportValues = this.acroField.getExportValues();
		if (exportValues) {
			const onValues = this.acroField.getOnValues();
			for (let idx = 0, len = onValues.length; idx < len; idx++) if (onValues[idx] === value) return exportValues[idx].decodeText();
		}
		return value.decodeText();
	}
	/**
	* Select an option for this radio group. This operation is analogous to a
	* human user clicking one of the radio buttons in this group via a PDF
	* reader to toggle it on. This method will update the underlying state of
	* the radio group to indicate which option has been selected. PDF libraries
	* and readers will be able to extract this value from the saved document and
	* determine which option was selected.
	*
	* For example:
	* ```js
	* const radioGroup = form.getRadioGroup('best.superhero.radioGroup')
	* radioGroup.select('One Punch Man')
	* ```
	*
	* This method will mark this radio group as dirty, causing its appearance
	* streams to be updated when either [[PDFDocument.save]] or
	* [[PDFForm.updateFieldAppearances]] is called. The updated appearance
	* streams will display a dot inside the widget of this check box field
	* that represents the selected option.
	*
	* @param option The option to be selected.
	*/
	select(option) {
		assertIs(option, "option", ["string"]);
		assertIsOneOf(option, "option", this.getOptions());
		this.markAsDirty();
		const onValues = this.acroField.getOnValues();
		const exportValues = this.acroField.getExportValues();
		if (exportValues) {
			for (let idx = 0, len = exportValues.length; idx < len; idx++) if (exportValues[idx].decodeText() === option) this.acroField.setValue(onValues[idx]);
		} else for (let idx = 0, len = onValues.length; idx < len; idx++) {
			const value = onValues[idx];
			if (value.decodeText() === option) this.acroField.setValue(value);
		}
	}
	/**
	* Clear any selected option for this dropdown. This will result in all
	* radio buttons in this group being toggled off. This method will update
	* the underlying state of the dropdown to indicate that no radio buttons
	* have been selected.
	* For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.clear()
	* ```
	* This method will mark this radio group as dirty. See
	* [[PDFRadioGroup.select]] for more details about what this means.
	*/
	clear() {
		this.markAsDirty();
		this.acroField.setValue(PDFName.of("Off"));
	}
	/**
	* Returns `true` if users can click on radio buttons in this group to toggle
	* them off. The alternative is that once a user clicks on a radio button
	* to select it, the only way to deselect it is by selecting on another radio
	* button in the group. See [[PDFRadioGroup.enableOffToggling]] and
	* [[PDFRadioGroup.disableOffToggling]]. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* if (radioGroup.isOffToggleable()) console.log('Off toggling is enabled')
	* ```
	*/
	isOffToggleable() {
		return !this.acroField.hasFlag(AcroButtonFlags.NoToggleToOff);
	}
	/**
	* Allow users to click on selected radio buttons in this group to toggle
	* them off. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.enableOffToggling()
	* ```
	* > **NOTE:** This feature is documented in the PDF specification
	* > (Table 226). However, most PDF readers do not respect this option and
	* > prevent users from toggling radio buttons off even when it is enabled.
	* > At the time of this writing (9/6/2020) Mac's Preview software did
	* > respect the option. Adobe Acrobat, Foxit Reader, and Google Chrome did
	* > not.
	*/
	enableOffToggling() {
		this.acroField.setFlagTo(AcroButtonFlags.NoToggleToOff, false);
	}
	/**
	* Prevent users from clicking on selected radio buttons in this group to
	* toggle them off. Clicking on a selected radio button will have no effect.
	* The only way to deselect a selected radio button is to click on a
	* different radio button in the group. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.disableOffToggling()
	* ```
	*/
	disableOffToggling() {
		this.acroField.setFlagTo(AcroButtonFlags.NoToggleToOff, true);
	}
	/**
	* Returns `true` if the radio buttons in this group are mutually exclusive.
	* This means that when the user selects a radio button, only that specific
	* button will be turned on. Even if other radio buttons in the group
	* represent the same value, they will not be enabled. The alternative to
	* this is that clicking a radio button will select that button along with
	* any other radio buttons in the group that share the same value. See
	* [[PDFRadioGroup.enableMutualExclusion]] and
	* [[PDFRadioGroup.disableMutualExclusion]].
	* For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* if (radioGroup.isMutuallyExclusive()) console.log('Mutual exclusion is enabled')
	* ```
	*/
	isMutuallyExclusive() {
		return !this.acroField.hasFlag(AcroButtonFlags.RadiosInUnison);
	}
	/**
	* When the user clicks a radio button in this group it will be selected. In
	* addition, any other radio buttons in this group that share the same
	* underlying value will also be selected. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.enableMutualExclusion()
	* ```
	* Note that this option must be enabled prior to adding options to the
	* radio group. It does not currently apply retroactively to existing
	* radio buttons in the group.
	*/
	enableMutualExclusion() {
		this.acroField.setFlagTo(AcroButtonFlags.RadiosInUnison, false);
	}
	/**
	* When the user clicks a radio button in this group only it will be selected.
	* No other radio buttons in the group will be selected, even if they share
	* the same underlying value. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.disableMutualExclusion()
	* ```
	* Note that this option must be disabled prior to adding options to the
	* radio group. It does not currently apply retroactively to existing
	* radio buttons in the group.
	*/
	disableMutualExclusion() {
		this.acroField.setFlagTo(AcroButtonFlags.RadiosInUnison, true);
	}
	/**
	* Add a new radio button to this group on the specified page. For example:
	* ```js
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const radioGroup = form.createRadioGroup('best.gundam')
	*
	* const options = {
	*   x: 50,
	*   width: 25,
	*   height: 25,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	* }
	*
	* radioGroup.addOptionToPage('Exia', page, { ...options, y: 50 })
	* radioGroup.addOptionToPage('Dynames', page, { ...options, y: 110 })
	* ```
	* This will create a new radio button widget for this radio group field.
	* @param option The option that the radio button widget represents.
	* @param page The page to which the radio button widget should be added.
	* @param options The options to be used when adding the radio button widget.
	*/
	addOptionToPage(option, page, options) {
		assertIs(option, "option", ["string"]);
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		const widget = this.createWidget({
			x: options?.x ?? 0,
			y: options?.y ?? 0,
			width: options?.width ?? 50,
			height: options?.height ?? 50,
			textColor: options?.textColor ?? rgb(0, 0, 0),
			backgroundColor: options?.backgroundColor ?? rgb(1, 1, 1),
			borderColor: options?.borderColor ?? rgb(0, 0, 0),
			borderWidth: options?.borderWidth ?? 1,
			rotate: options?.rotate ?? degrees(0),
			hidden: options?.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		const apStateValue = this.acroField.addWidgetWithOpt(widgetRef, PDFHexString.fromText(option), !this.isMutuallyExclusive());
		widget.setAppearanceState(PDFName.of("Off"));
		this.updateWidgetAppearance(widget, apStateValue);
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if any of this group's radio button widgets do not have an
	* appearance stream for their current state. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* if (radioGroup.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this radio group needs an appearance update.
	*/
	needsAppearancesUpdate() {
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const state = widget.getAppearanceState();
			const normal = widget.getAppearances()?.normal;
			if (!(normal instanceof PDFDict)) return true;
			if (state && !normal.has(state)) return true;
		}
		return false;
	}
	/**
	* Update the appearance streams for each of this group's radio button widgets
	* using the default appearance provider for radio groups. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.defaultUpdateAppearances()
	* ```
	*/
	defaultUpdateAppearances() {
		this.updateAppearances();
	}
	/**
	* Update the appearance streams for each of this group's radio button widgets
	* using the given appearance provider. If no `provider` is passed, the
	* default appearance provider for radio groups will be used. For example:
	* ```js
	* const radioGroup = form.getRadioGroup('some.radioGroup.field')
	* radioGroup.updateAppearances((field, widget) => {
	*   ...
	*   return {
	*     normal: { on: drawRadioButton(...), off: drawRadioButton(...) },
	*     down: { on: drawRadioButton(...), off: drawRadioButton(...) },
	*   }
	* })
	* ```
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(provider) {
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const onValue = widget.getOnValue();
			if (!onValue) continue;
			this.updateWidgetAppearance(widget, onValue, provider);
		}
	}
	updateWidgetAppearance(widget, onValue, provider) {
		const appearances = normalizeAppearance((provider ?? defaultRadioGroupAppearanceProvider)(this, widget));
		this.updateOnOffWidgetAppearance(widget, onValue, appearances);
	}
};
_PDFRadioGroup = PDFRadioGroup;
_defineProperty(PDFRadioGroup, "of", (acroRadioButton, ref, doc) => new _PDFRadioGroup(acroRadioButton, ref, doc));
//#endregion
//#region src/api/form/PDFSignature.ts
var _PDFSignature;
/**
* Represents a signature field of a [[PDFForm]].
*
* [[PDFSignature]] fields are digital signatures. `pdf-lib` does not
* currently provide any specialized APIs for creating digital signatures or
* reading the contents of existing digital signatures.
*/
var PDFSignature = class extends PDFField {
	constructor(acroSignature, ref, doc) {
		super(acroSignature, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroSignature wrapped by this signature. */
			"acroField",
			void 0
		);
		assertIs(acroSignature, "acroSignature", [[PDFAcroSignature, "PDFAcroSignature"]]);
		this.acroField = acroSignature;
	}
	needsAppearancesUpdate() {
		return false;
	}
};
_PDFSignature = PDFSignature;
_defineProperty(PDFSignature, "of", (acroSignature, ref, doc) => new _PDFSignature(acroSignature, ref, doc));
//#endregion
//#region src/api/form/PDFTextField.ts
var _PDFTextField;
/**
* Represents a text field of a [[PDFForm]].
*
* [[PDFTextField]] fields are boxes that display text entered by the user. The
* purpose of a text field is to enable users to enter text or view text values
* in the document prefilled by software. Users can click on a text field and
* input text via their keyboard. Some text fields allow multiple lines of text
* to be entered (see [[PDFTextField.isMultiline]]).
*/
var PDFTextField = class extends PDFField {
	constructor(acroText, ref, doc) {
		super(acroText, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroText wrapped by this text field. */
			"acroField",
			void 0
		);
		assertIs(acroText, "acroText", [[PDFAcroText, "PDFAcroText"]]);
		this.acroField = acroText;
	}
	/**
	* Get the text that this field contains. This text is visible to users who
	* view this field in a PDF reader.
	*
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* const text = textField.getText()
	* console.log('Text field contents:', text)
	* ```
	*
	* Note that if this text field contains no underlying value, `undefined`
	* will be returned. Text fields may also contain an underlying value that
	* is simply an empty string (`''`). This detail is largely irrelevant for
	* most applications. In general, you'll want to treat both cases the same
	* way and simply consider the text field to be empty. In either case, the
	* text field will appear empty to users when viewed in a PDF reader.
	*
	* An error will be thrown if this is a rich text field. `pdf-lib` does not
	* support reading rich text fields. Nor do most PDF readers and writers.
	* Rich text fields are based on XFA (XML Forms Architecture). Relatively few
	* PDFs use rich text fields or XFA. Unlike PDF itself, XFA is not an ISO
	* standard. XFA has been deprecated in PDF 2.0:
	* * https://en.wikipedia.org/wiki/XFA
	* * http://blog.pdfshareforms.com/pdf-2-0-release-bid-farewell-xfa-forms/
	*
	* @returns The text contained in this text field.
	*/
	getText() {
		const value = this.acroField.getValue();
		if (!value && this.isRichFormatted()) throw new RichTextFieldReadError(this.getName());
		return value?.decodeText();
	}
	/**
	* Set the text for this field. This operation is analogous to a human user
	* clicking on the text field in a PDF reader and typing in text via their
	* keyboard. This method will update the underlying state of the text field
	* to indicate what text has been set. PDF libraries and readers will be able
	* to extract these values from the saved document and determine what text
	* was set.
	*
	* For example:
	* ```js
	* const textField = form.getTextField('best.superhero.text.field')
	* textField.setText('One Punch Man')
	* ```
	*
	* This method will mark this text field as dirty, causing its appearance
	* streams to be updated when either [[PDFDocument.save]] or
	* [[PDFForm.updateFieldAppearances]] is called. The updated streams will
	* display the text this field contains inside the widgets of this text
	* field.
	*
	* **IMPORTANT:** The default font used to update appearance streams is
	* [[StandardFonts.Helvetica]]. Note that this is a WinAnsi font. This means
	* that encoding errors will be thrown if this field contains text outside
	* the WinAnsi character set (the latin alphabet).
	*
	* Embedding a custom font and passing it to
	* [[PDFForm.updateFieldAppearances]] or [[PDFTextField.updateAppearances]]
	* allows you to generate appearance streams with characters outside the
	* latin alphabet (assuming the custom font supports them).
	*
	* If this is a rich text field, it will be converted to a standard text
	* field in order to set the text. `pdf-lib` does not support writing rich
	* text strings. Nor do most PDF readers and writers. See
	* [[PDFTextField.getText]] for more information about rich text fields and
	* their deprecation in PDF 2.0.
	*
	* @param text The text this field should contain.
	*/
	setText(text) {
		assertOrUndefined(text, "text", ["string"]);
		const maxLength = this.getMaxLength();
		if (maxLength !== void 0 && text && text.length > maxLength) throw new ExceededMaxLengthError(text.length, maxLength, this.getName());
		this.markAsDirty();
		this.disableRichFormatting();
		if (text) this.acroField.setValue(PDFHexString.fromText(text));
		else this.acroField.removeValue();
	}
	/**
	* Get the alignment for this text field. This value represents the
	* justification of the text when it is displayed to the user in PDF readers.
	* There are three possible alignments: left, center, and right. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* const alignment = textField.getAlignment()
	* if (alignment === TextAlignment.Left) console.log('Text is left justified')
	* if (alignment === TextAlignment.Center) console.log('Text is centered')
	* if (alignment === TextAlignment.Right) console.log('Text is right justified')
	* ```
	* @returns The alignment of this text field.
	*/
	getAlignment() {
		const quadding = this.acroField.getQuadding();
		return quadding === 0 ? TextAlignment.Left : quadding === 1 ? TextAlignment.Center : quadding === 2 ? TextAlignment.Right : TextAlignment.Left;
	}
	/**
	* Set the alignment for this text field. This will determine the
	* justification of the text when it is displayed to the user in PDF readers.
	* There are three possible alignments: left, center, and right. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	*
	* // Text will be left justified when displayed
	* textField.setAlignment(TextAlignment.Left)
	*
	* // Text will be centered when displayed
	* textField.setAlignment(TextAlignment.Center)
	*
	* // Text will be right justified when displayed
	* textField.setAlignment(TextAlignment.Right)
	* ```
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	* @param alignment The alignment for this text field.
	*/
	setAlignment(alignment) {
		assertIsOneOf(alignment, "alignment", TextAlignment);
		this.markAsDirty();
		this.acroField.setQuadding(alignment);
	}
	/**
	* Get the maximum length of this field. This value represents the maximum
	* number of characters that can be typed into this field by the user. If
	* this field does not have a maximum length, `undefined` is returned.
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* const maxLength = textField.getMaxLength()
	* if (maxLength === undefined) console.log('No max length')
	* else console.log(`Max length is ${maxLength}`)
	* ```
	* @returns The maximum number of characters allowed in this field, or
	*          `undefined` if no limit exists.
	*/
	getMaxLength() {
		return this.acroField.getMaxLength();
	}
	/**
	* Set the maximum length of this field. This limits the number of characters
	* that can be typed into this field by the user. This also limits the length
	* of the string that can be passed to [[PDFTextField.setText]]. This limit
	* can be removed by passing `undefined` as `maxLength`. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	*
	* // Allow between 0 and 5 characters to be entered
	* textField.setMaxLength(5)
	*
	* // Allow any number of characters to be entered
	* textField.setMaxLength(undefined)
	* ```
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	* @param maxLength The maximum number of characters allowed in this field, or
	*                  `undefined` to remove the limit.
	*/
	setMaxLength(maxLength) {
		assertRangeOrUndefined(maxLength, "maxLength", 0, Number.MAX_SAFE_INTEGER);
		this.markAsDirty();
		if (maxLength === void 0) this.acroField.removeMaxLength();
		else {
			const text = this.getText();
			if (text && text.length > maxLength) throw new InvalidMaxLengthError(text.length, maxLength, this.getName());
			this.acroField.setMaxLength(maxLength);
		}
	}
	/**
	* Remove the maximum length for this text field. This allows any number of
	* characters to be typed into this field by the user. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.removeMaxLength()
	* ```
	* Calling this method is equivalent to passing `undefined` to
	* [[PDFTextField.setMaxLength]].
	*/
	removeMaxLength() {
		this.markAsDirty();
		this.acroField.removeMaxLength();
	}
	/**
	* Display an image inside the bounds of this text field's widgets. For example:
	* ```js
	* const pngImage = await pdfDoc.embedPng(...)
	* const textField = form.getTextField('some.text.field')
	* textField.setImage(pngImage)
	* ```
	* This will update the appearances streams for each of this text field's widgets.
	* @param image The image that should be displayed.
	*/
	setImage(image) {
		const fieldAlignment = this.getAlignment();
		const alignment = fieldAlignment === TextAlignment.Center ? ImageAlignment.Center : fieldAlignment === TextAlignment.Right ? ImageAlignment.Right : ImageAlignment.Left;
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const streamRef = this.createImageAppearanceStream(widget, image, alignment);
			this.updateWidgetAppearances(widget, { normal: streamRef });
		}
		this.markAsClean();
	}
	/**
	* Set the font size for this field. Larger font sizes will result in larger
	* text being displayed when PDF readers render this text field. Font sizes
	* may be integer or floating point numbers. Supplying a negative font size
	* will cause this method to throw an error.
	*
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.setFontSize(4)
	* textField.setFontSize(15.7)
	* ```
	*
	* > This method depends upon the existence of a default appearance
	* > (`/DA`) string. If this field does not have a default appearance string,
	* > or that string does not contain a font size (via the `Tf` operator),
	* > then this method will throw an error.
	*
	* @param fontSize The font size to be used when rendering text in this field.
	*/
	setFontSize(fontSize) {
		assertPositive(fontSize, "fontSize");
		this.acroField.setFontSize(fontSize);
		this.markAsDirty();
	}
	/**
	* Returns `true` if each line of text is shown on a new line when this
	* field is displayed in a PDF reader. The alternative is that all lines of
	* text are merged onto a single line when displayed. See
	* [[PDFTextField.enableMultiline]] and [[PDFTextField.disableMultiline]].
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isMultiline()) console.log('Multiline is enabled')
	* ```
	* @returns Whether or not this is a multiline text field.
	*/
	isMultiline() {
		return this.acroField.hasFlag(AcroTextFlags.Multiline);
	}
	/**
	* Display each line of text on a new line when this field is displayed in a
	* PDF reader. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableMultiline()
	* ```
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	*/
	enableMultiline() {
		this.markAsDirty();
		this.acroField.setFlagTo(AcroTextFlags.Multiline, true);
	}
	/**
	* Display each line of text on the same line when this field is displayed
	* in a PDF reader. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableMultiline()
	* ```
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	*/
	disableMultiline() {
		this.markAsDirty();
		this.acroField.setFlagTo(AcroTextFlags.Multiline, false);
	}
	/**
	* Returns `true` if this is a password text field. This means that the field
	* is intended for storing a secure password. See
	* [[PDFTextField.enablePassword]] and [[PDFTextField.disablePassword]].
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isPassword()) console.log('Password is enabled')
	* ```
	* @returns Whether or not this is a password text field.
	*/
	isPassword() {
		return this.acroField.hasFlag(AcroTextFlags.Password);
	}
	/**
	* Indicate that this text field is intended for storing a secure password.
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enablePassword()
	* ```
	* Values entered into password text fields should not be displayed on the
	* screen by PDF readers. Most PDF readers will display the value as
	* asterisks or bullets. PDF readers should never store values entered by the
	* user into password text fields. Similarly, applications should not
	* write data to a password text field.
	*
	* **Please note that this method does not cause entered values to be
	* encrypted or secured in any way! It simply sets a flag that PDF software
	* and readers can access to determine the _purpose_ of this field.**
	*/
	enablePassword() {
		this.acroField.setFlagTo(AcroTextFlags.Password, true);
	}
	/**
	* Indicate that this text field is **not** intended for storing a secure
	* password. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disablePassword()
	* ```
	*/
	disablePassword() {
		this.acroField.setFlagTo(AcroTextFlags.Password, false);
	}
	/**
	* Returns `true` if the contents of this text field represent a file path.
	* See [[PDFTextField.enableFileSelection]] and
	* [[PDFTextField.disableFileSelection]]. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isFileSelector()) console.log('Is a file selector')
	* ```
	* @returns Whether or not this field should contain file paths.
	*/
	isFileSelector() {
		return this.acroField.hasFlag(AcroTextFlags.FileSelect);
	}
	/**
	* Indicate that this text field is intended to store a file path. The
	* contents of the file stored at that path should be submitted as the value
	* of the field. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableFileSelection()
	* ```
	*/
	enableFileSelection() {
		this.acroField.setFlagTo(AcroTextFlags.FileSelect, true);
	}
	/**
	* Indicate that this text field is **not** intended to store a file path.
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableFileSelection()
	* ```
	*/
	disableFileSelection() {
		this.acroField.setFlagTo(AcroTextFlags.FileSelect, false);
	}
	/**
	* Returns `true` if the text entered in this field should be spell checked
	* by PDF readers. See [[PDFTextField.enableSpellChecking]] and
	* [[PDFTextField.disableSpellChecking]]. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isSpellChecked()) console.log('Spell checking is enabled')
	* ```
	* @returns Whether or not this field should be spell checked.
	*/
	isSpellChecked() {
		return !this.acroField.hasFlag(AcroTextFlags.DoNotSpellCheck);
	}
	/**
	* Allow PDF readers to spell check the text entered in this field.
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableSpellChecking()
	* ```
	*/
	enableSpellChecking() {
		this.acroField.setFlagTo(AcroTextFlags.DoNotSpellCheck, false);
	}
	/**
	* Do not allow PDF readers to spell check the text entered in this field.
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableSpellChecking()
	* ```
	*/
	disableSpellChecking() {
		this.acroField.setFlagTo(AcroTextFlags.DoNotSpellCheck, true);
	}
	/**
	* Returns `true` if PDF readers should allow the user to scroll the text
	* field when its contents do not fit within the field's view bounds. See
	* [[PDFTextField.enableScrolling]] and [[PDFTextField.disableScrolling]].
	* For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isScrollable()) console.log('Scrolling is enabled')
	* ```
	* @returns Whether or not the field is scrollable in PDF readers.
	*/
	isScrollable() {
		return !this.acroField.hasFlag(AcroTextFlags.DoNotScroll);
	}
	/**
	* Allow PDF readers to present a scroll bar to the user when the contents
	* of this text field do not fit within its view bounds. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableScrolling()
	* ```
	* A horizontal scroll bar should be shown for singleline fields. A vertical
	* scroll bar should be shown for multiline fields.
	*/
	enableScrolling() {
		this.acroField.setFlagTo(AcroTextFlags.DoNotScroll, false);
	}
	/**
	* Do not allow PDF readers to present a scroll bar to the user when the
	* contents of this text field do not fit within its view bounds. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableScrolling()
	* ```
	*/
	disableScrolling() {
		this.acroField.setFlagTo(AcroTextFlags.DoNotScroll, true);
	}
	/**
	* Returns `true` if this is a combed text field. This means that the field
	* is split into `n` equal size cells with one character in each (where `n`
	* is equal to the max length of the text field). The result is that all
	* characters in this field are displayed an equal distance apart from one
	* another. See [[PDFTextField.enableCombing]] and
	* [[PDFTextField.disableCombing]]. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isCombed()) console.log('Combing is enabled')
	* ```
	* Note that in order for a text field to be combed, the following must be
	* true (in addition to enabling combing):
	* * It must not be a multiline field (see [[PDFTextField.isMultiline]])
	* * It must not be a password field (see [[PDFTextField.isPassword]])
	* * It must not be a file selector field (see [[PDFTextField.isFileSelector]])
	* * It must have a max length defined (see [[PDFTextField.setMaxLength]])
	* @returns Whether or not this field is combed.
	*/
	isCombed() {
		return this.acroField.hasFlag(AcroTextFlags.Comb) && !this.isMultiline() && !this.isPassword() && !this.isFileSelector() && this.getMaxLength() !== void 0;
	}
	/**
	* Split this field into `n` equal size cells with one character in each
	* (where `n` is equal to the max length of the text field). This will cause
	* all characters in the field to be displayed an equal distance apart from
	* one another. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableCombing()
	* ```
	*
	* In addition to calling this method, text fields must have a max length
	* defined in order to be combed (see [[PDFTextField.setMaxLength]]).
	*
	* This method will also call the following three methods internally:
	* * [[PDFTextField.disableMultiline]]
	* * [[PDFTextField.disablePassword]]
	* * [[PDFTextField.disableFileSelection]]
	*
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	*/
	enableCombing() {
		if (this.getMaxLength() === void 0) console.warn(`PDFTextFields must have a max length in order to be combed`);
		this.markAsDirty();
		this.disableMultiline();
		this.disablePassword();
		this.disableFileSelection();
		this.acroField.setFlagTo(AcroTextFlags.Comb, true);
	}
	/**
	* Turn off combing for this text field. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableCombing()
	* ```
	* See [[PDFTextField.isCombed]] and [[PDFTextField.enableCombing]] for more
	* information about what combing is.
	*
	* This method will mark this text field as dirty. See
	* [[PDFTextField.setText]] for more details about what this means.
	*/
	disableCombing() {
		this.markAsDirty();
		this.acroField.setFlagTo(AcroTextFlags.Comb, false);
	}
	/**
	* Returns `true` if this text field contains rich text. See
	* [[PDFTextField.enableRichFormatting]] and
	* [[PDFTextField.disableRichFormatting]]. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.isRichFormatted()) console.log('Rich formatting enabled')
	* ```
	* @returns Whether or not this field contains rich text.
	*/
	isRichFormatted() {
		return this.acroField.hasFlag(AcroTextFlags.RichText);
	}
	/**
	* Indicate that this field contains XFA data - or rich text. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.enableRichFormatting()
	* ```
	* Note that `pdf-lib` does not support reading or writing rich text fields.
	* Nor do most PDF readers and writers. Rich text fields are based on XFA
	* (XML Forms Architecture). Relatively few PDFs use rich text fields or XFA.
	* Unlike PDF itself, XFA is not an ISO standard. XFA has been deprecated in
	* PDF 2.0:
	* * https://en.wikipedia.org/wiki/XFA
	* * http://blog.pdfshareforms.com/pdf-2-0-release-bid-farewell-xfa-forms/
	*/
	enableRichFormatting() {
		this.acroField.setFlagTo(AcroTextFlags.RichText, true);
	}
	/**
	* Indicate that this is a standard text field that does not XFA data (rich
	* text). For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* textField.disableRichFormatting()
	* ```
	*/
	disableRichFormatting() {
		this.acroField.setFlagTo(AcroTextFlags.RichText, false);
	}
	/**
	* Show this text field on the specified page. For example:
	* ```js
	* const ubuntuFont = await pdfDoc.embedFont(ubuntuFontBytes)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const textField = form.createTextField('best.gundam')
	* textField.setText('Exia')
	*
	* textField.addToPage(page, {
	*   x: 50,
	*   y: 75,
	*   width: 200,
	*   height: 100,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	*   font: ubuntuFont,
	* })
	* ```
	* This will create a new widget for this text field.
	* @param page The page to which this text field widget should be added.
	* @param options The options to be used when adding this text field widget.
	*/
	addToPage(page, options) {
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		if (!options) options = {};
		if (!("textColor" in options)) options.textColor = rgb(0, 0, 0);
		if (!("backgroundColor" in options)) options.backgroundColor = rgb(1, 1, 1);
		if (!("borderColor" in options)) options.borderColor = rgb(0, 0, 0);
		if (!("borderWidth" in options)) options.borderWidth = 1;
		const widget = this.createWidget({
			x: options.x ?? 0,
			y: options.y ?? 0,
			width: options.width ?? 200,
			height: options.height ?? 50,
			textColor: options.textColor,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth ?? 0,
			rotate: options.rotate ?? degrees(0),
			hidden: options.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		this.acroField.addWidget(widgetRef);
		const font = options.font ?? this.doc.getForm().getDefaultFont();
		this.updateWidgetAppearance(widget, font);
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if this text field has been marked as dirty, or if any of
	* this text field's widgets do not have an appearance stream. For example:
	* ```js
	* const textField = form.getTextField('some.text.field')
	* if (textField.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this text field needs an appearance update.
	*/
	needsAppearancesUpdate() {
		if (this.isDirty()) return true;
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) if (!(widgets[idx].getAppearances()?.normal instanceof PDFStream)) return true;
		return false;
	}
	/**
	* Update the appearance streams for each of this text field's widgets using
	* the default appearance provider for text fields. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const textField = form.getTextField('some.text.field')
	* textField.defaultUpdateAppearances(helvetica)
	* ```
	* @param font The font to be used for creating the appearance streams.
	*/
	defaultUpdateAppearances(font) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		this.updateAppearances(font);
	}
	/**
	* Update the appearance streams for each of this text field's widgets using
	* the given appearance provider. If no `provider` is passed, the default
	* appearance provider for text fields will be used. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const textField = form.getTextField('some.text.field')
	* textField.updateAppearances(helvetica, (field, widget, font) => {
	*   ...
	*   return drawTextField(...)
	* })
	* ```
	* @param font The font to be used for creating the appearance streams.
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(font, provider) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			this.updateWidgetAppearance(widget, font, provider);
		}
		this.markAsClean();
	}
	updateWidgetAppearance(widget, font, provider) {
		const appearances = normalizeAppearance((provider ?? defaultTextFieldAppearanceProvider)(this, widget, font));
		this.updateWidgetAppearanceWithFont(widget, font, appearances);
	}
};
_PDFTextField = PDFTextField;
_defineProperty(PDFTextField, "of", (acroText, ref, doc) => new _PDFTextField(acroText, ref, doc));
//#endregion
//#region src/api/StandardFonts.ts
var StandardFonts = /* @__PURE__ */ function(StandardFonts) {
	StandardFonts["Courier"] = "Courier";
	StandardFonts["CourierBold"] = "Courier-Bold";
	StandardFonts["CourierOblique"] = "Courier-Oblique";
	StandardFonts["CourierBoldOblique"] = "Courier-BoldOblique";
	StandardFonts["Helvetica"] = "Helvetica";
	StandardFonts["HelveticaBold"] = "Helvetica-Bold";
	StandardFonts["HelveticaOblique"] = "Helvetica-Oblique";
	StandardFonts["HelveticaBoldOblique"] = "Helvetica-BoldOblique";
	StandardFonts["TimesRoman"] = "Times-Roman";
	StandardFonts["TimesRomanBold"] = "Times-Bold";
	StandardFonts["TimesRomanItalic"] = "Times-Italic";
	StandardFonts["TimesRomanBoldItalic"] = "Times-BoldItalic";
	StandardFonts["Symbol"] = "Symbol";
	StandardFonts["ZapfDingbats"] = "ZapfDingbats";
	return StandardFonts;
}({});
//#endregion
//#region src/api/form/PDFForm.ts
var _PDFForm;
/**
* Represents the interactive form of a [[PDFDocument]].
*
* Interactive forms (sometimes called _AcroForms_) are collections of fields
* designed to gather information from a user. A PDF document may contains any
* number of fields that appear on various pages, all of which make up a single,
* global interactive form spanning the entire document. This means that
* instances of [[PDFDocument]] shall contain at most one [[PDFForm]].
*
* The fields of an interactive form are represented by [[PDFField]] instances.
*/
var PDFForm = class {
	constructor(acroForm, doc) {
		_defineProperty(
			this,
			/** The low-level PDFAcroForm wrapped by this form. */
			"acroForm",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this form belongs. */
			"doc",
			void 0
		);
		_defineProperty(this, "dirtyFields", void 0);
		_defineProperty(this, "defaultFontCache", void 0);
		_defineProperty(this, "embedDefaultFont", () => this.doc.embedStandardFont(StandardFonts.Helvetica));
		assertIs(acroForm, "acroForm", [[PDFAcroForm, "PDFAcroForm"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		this.acroForm = acroForm;
		this.doc = doc;
		this.dirtyFields = /* @__PURE__ */ new Set();
		this.defaultFontCache = Cache.populatedBy(this.embedDefaultFont);
	}
	/**
	* Returns `true` if this [[PDFForm]] has XFA data. Most PDFs with form
	* fields do not use XFA as it is not widely supported by PDF readers.
	*
	* > `pdf-lib` does not support creation, modification, or reading of XFA
	* > fields.
	*
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* if (form.hasXFA()) console.log('PDF has XFA data')
	* ```
	* @returns Whether or not this form has XFA data.
	*/
	hasXFA() {
		return this.acroForm.dict.has(PDFName.of("XFA"));
	}
	/**
	* Disconnect the XFA data from this [[PDFForm]] (if any exists). This will
	* force readers to fallback to standard fields if the [[PDFDocument]]
	* contains any. For example:
	*
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* form.deleteXFA()
	* ```
	*/
	deleteXFA() {
		this.acroForm.dict.delete(PDFName.of("XFA"));
	}
	/**
	* Get all fields contained in this [[PDFForm]]. For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const fields = form.getFields()
	* fields.forEach(field => {
	*   const type = field.constructor.name
	*   const name = field.getName()
	*   console.log(`${type}: ${name}`)
	* })
	* ```
	* @returns An array of all fields in this form.
	*/
	getFields() {
		const allFields = this.acroForm.getAllFields();
		const fields = [];
		for (let idx = 0, len = allFields.length; idx < len; idx++) {
			const [acroField, ref] = allFields[idx];
			const field = convertToPDFField(acroField, ref, this.doc);
			if (field) fields.push(field);
		}
		return fields;
	}
	/**
	* Get the field in this [[PDFForm]] with the given name. For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const field = form.getFieldMaybe('Page1.Foo.Bar[0]')
	* if (field) console.log('Field exists!')
	* ```
	* @param name A fully qualified field name.
	* @returns The field with the specified name, if one exists.
	*/
	getFieldMaybe(name) {
		assertIs(name, "name", ["string"]);
		const fields = this.getFields();
		for (let idx = 0, len = fields.length; idx < len; idx++) {
			const field = fields[idx];
			if (field.getName() === name) return field;
		}
	}
	/**
	* Get the field in this [[PDFForm]] with the given name. For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const field = form.getField('Page1.Foo.Bar[0]')
	* ```
	* If no field exists with the provided name, an error will be thrown.
	* @param name A fully qualified field name.
	* @returns The field with the specified name.
	*/
	getField(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getFieldMaybe(name);
		if (field) return field;
		throw new NoSuchFieldError(name);
	}
	/**
	* Get the button field in this [[PDFForm]] with the given name. For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const button = form.getButton('Page1.Foo.Button[0]')
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a button.
	* @param name A fully qualified button name.
	* @returns The button with the specified name.
	*/
	getButton(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFButton) return field;
		throw new UnexpectedFieldTypeError(name, PDFButton, field);
	}
	/**
	* Get the check box field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const checkBox = form.getCheckBox('Page1.Foo.CheckBox[0]')
	* checkBox.check()
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a check box.
	* @param name A fully qualified check box name.
	* @returns The check box with the specified name.
	*/
	getCheckBox(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFCheckBox) return field;
		throw new UnexpectedFieldTypeError(name, PDFCheckBox, field);
	}
	/**
	* Get the dropdown field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const dropdown = form.getDropdown('Page1.Foo.Dropdown[0]')
	* const options = dropdown.getOptions()
	* dropdown.select(options[0])
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a dropdown.
	* @param name A fully qualified dropdown name.
	* @returns The dropdown with the specified name.
	*/
	getDropdown(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFDropdown) return field;
		throw new UnexpectedFieldTypeError(name, PDFDropdown, field);
	}
	/**
	* Get the option list field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const optionList = form.getOptionList('Page1.Foo.OptionList[0]')
	* const options = optionList.getOptions()
	* optionList.select(options[0])
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not an option list.
	* @param name A fully qualified option list name.
	* @returns The option list with the specified name.
	*/
	getOptionList(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFOptionList) return field;
		throw new UnexpectedFieldTypeError(name, PDFOptionList, field);
	}
	/**
	* Get the radio group field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const radioGroup = form.getRadioGroup('Page1.Foo.RadioGroup[0]')
	* const options = radioGroup.getOptions()
	* radioGroup.select(options[0])
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a radio group.
	* @param name A fully qualified radio group name.
	* @returns The radio group with the specified name.
	*/
	getRadioGroup(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFRadioGroup) return field;
		throw new UnexpectedFieldTypeError(name, PDFRadioGroup, field);
	}
	/**
	* Get the signature field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const signature = form.getSignature('Page1.Foo.Signature[0]')
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a signature.
	* @param name A fully qualified signature name.
	* @returns The signature with the specified name.
	*/
	getSignature(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFSignature) return field;
		throw new UnexpectedFieldTypeError(name, PDFSignature, field);
	}
	/**
	* Get the text field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const textField = form.getTextField('Page1.Foo.TextField[0]')
	* textField.setText('Are you designed to act or to be acted upon?')
	* ```
	* An error will be thrown if no field exists with the provided name, or if
	* the field exists but is not a text field.
	* @param name A fully qualified text field name.
	* @returns The text field with the specified name.
	*/
	getTextField(name) {
		assertIs(name, "name", ["string"]);
		const field = this.getField(name);
		if (field instanceof PDFTextField) return field;
		throw new UnexpectedFieldTypeError(name, PDFTextField, field);
	}
	/**
	* Create a new button field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const button = form.createButton('cool.new.button')
	*
	* button.addToPage('Do Stuff', font, page)
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new button.
	* @returns The new button field.
	*/
	createButton(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const button = PDFAcroPushButton.create(this.doc.context);
		button.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [button, button.ref], nameParts.terminal);
		return PDFButton.of(button, button.ref, this.doc);
	}
	/**
	* Create a new check box field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const checkBox = form.createCheckBox('cool.new.checkBox')
	*
	* checkBox.addToPage(page)
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new check box.
	* @returns The new check box field.
	*/
	createCheckBox(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const checkBox = PDFAcroCheckBox.create(this.doc.context);
		checkBox.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [checkBox, checkBox.ref], nameParts.terminal);
		return PDFCheckBox.of(checkBox, checkBox.ref, this.doc);
	}
	/**
	* Create a new dropdown field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const dropdown = form.createDropdown('cool.new.dropdown')
	*
	* dropdown.addToPage(font, page)
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new dropdown.
	* @returns The new dropdown field.
	*/
	createDropdown(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const comboBox = PDFAcroComboBox.create(this.doc.context);
		comboBox.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [comboBox, comboBox.ref], nameParts.terminal);
		return PDFDropdown.of(comboBox, comboBox.ref, this.doc);
	}
	/**
	* Create a new option list field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const optionList = form.createOptionList('cool.new.optionList')
	*
	* optionList.addToPage(font, page)
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new option list.
	* @returns The new option list field.
	*/
	createOptionList(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const listBox = PDFAcroListBox.create(this.doc.context);
		listBox.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [listBox, listBox.ref], nameParts.terminal);
		return PDFOptionList.of(listBox, listBox.ref, this.doc);
	}
	/**
	* Create a new radio group field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const radioGroup = form.createRadioGroup('cool.new.radioGroup')
	*
	* radioGroup.addOptionToPage('is-dog', page, { y: 0 })
	* radioGroup.addOptionToPage('is-cat', page, { y: 75 })
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new radio group.
	* @returns The new radio group field.
	*/
	createRadioGroup(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const radioButton = PDFAcroRadioButton.create(this.doc.context);
		radioButton.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [radioButton, radioButton.ref], nameParts.terminal);
		return PDFRadioGroup.of(radioButton, radioButton.ref, this.doc);
	}
	/**
	* Create a new text field in this [[PDFForm]] with the given name.
	* For example:
	* ```js
	* const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const textField = form.createTextField('cool.new.textField')
	*
	* textField.addToPage(font, page)
	* ```
	* An error will be thrown if a field already exists with the provided name.
	* @param name The fully qualified name for the new radio group.
	* @returns The new radio group field.
	*/
	createTextField(name) {
		assertIs(name, "name", ["string"]);
		const nameParts = splitFieldName(name);
		const parent = this.findOrCreateNonTerminals(nameParts.nonTerminal);
		const text = PDFAcroText.create(this.doc.context);
		text.setPartialName(nameParts.terminal);
		addFieldToParent(parent, [text, text.ref], nameParts.terminal);
		return PDFTextField.of(text, text.ref, this.doc);
	}
	/**
	* Flatten all fields in this [[PDFForm]].
	*
	* Flattening a form field will take the current appearance for each of that
	* field's widgets and make them part of their page's content stream. All form
	* fields and annotations associated are then removed. Note that once a form
	* has been flattened its fields can no longer be accessed or edited.
	*
	* This operation is often used after filling form fields to ensure a
	* consistent appearance across different PDF readers and/or printers.
	* Another common use case is to copy a template document with form fields
	* into another document. In this scenario you would load the template
	* document, fill its fields, flatten it, and then copy its pages into the
	* recipient document - the filled fields will be copied over.
	*
	* For example:
	* ```js
	* const form = pdfDoc.getForm();
	* form.flatten();
	* ```
	*/
	flatten(options = { updateFieldAppearances: true }) {
		if (options.updateFieldAppearances) this.updateFieldAppearances();
		const fields = this.getFields();
		for (let i = 0, lenFields = fields.length; i < lenFields; i++) {
			const field = fields[i];
			const widgets = field.acroField.getWidgets();
			for (let j = 0, lenWidgets = widgets.length; j < lenWidgets; j++) try {
				const widget = widgets[j];
				const page = this.findWidgetPage(widget);
				const widgetRef = this.findWidgetAppearanceRef(field, widget);
				const xObjectKey = page.node.newXObject("FlatWidget", widgetRef);
				const rectangle = widget.getRectangle();
				const operators = [
					pushGraphicsState(),
					translate(rectangle.x, rectangle.y),
					...rotateInPlace({
						...rectangle,
						rotation: 0
					}),
					drawObject(xObjectKey),
					popGraphicsState()
				].filter(Boolean);
				page.pushOperators(...operators);
			} catch (err) {
				console.error(err);
			}
			this.removeField(field);
		}
	}
	/**
	* Remove a field from this [[PDFForm]].
	*
	* For example:
	* ```js
	* const form = pdfDoc.getForm();
	* const ageField = form.getFields().find(x => x.getName() === 'Age');
	* form.removeField(ageField);
	* ```
	*/
	removeField(field) {
		const widgets = field.acroField.getWidgets();
		const pages = /* @__PURE__ */ new Set();
		for (let i = 0, len = widgets.length; i < len; i++) try {
			const widget = widgets[i];
			const widgetRef = this.findWidgetAppearanceRef(field, widget);
			const page = this.findWidgetPage(widget);
			pages.add(page);
			page.node.removeAnnot(widgetRef);
		} catch (err) {
			console.error(err);
		}
		pages.forEach((page) => page.node.removeAnnot(field.ref));
		this.acroForm.removeField(field.acroField);
		const fieldKids = field.acroField.normalizedEntries().Kids;
		const kidsCount = fieldKids.size();
		for (let childIndex = 0; childIndex < kidsCount; childIndex++) {
			const child = fieldKids.get(childIndex);
			if (child instanceof PDFRef) this.doc.context.delete(child);
		}
		this.doc.context.delete(field.ref);
	}
	/**
	* Update the appearance streams for all widgets of all fields in this
	* [[PDFForm]]. Appearance streams will only be created for a widget if it
	* does not have any existing appearance streams, or the field's value has
	* changed (e.g. by calling [[PDFTextField.setText]] or
	* [[PDFDropdown.select]]).
	*
	* For example:
	* ```js
	* const courier = await pdfDoc.embedFont(StandardFonts.Courier)
	* const form = pdfDoc.getForm()
	* form.updateFieldAppearances(courier)
	* ```
	*
	* **IMPORTANT:** The default value for the `font` parameter is
	* [[StandardFonts.Helvetica]]. Note that this is a WinAnsi font. This means
	* that encoding errors will be thrown if any fields contain text with
	* characters outside the WinAnsi character set (the latin alphabet).
	*
	* Embedding a custom font and passing that as the `font`
	* parameter allows you to generate appearance streams with non WinAnsi
	* characters (assuming your custom font supports them).
	*
	* > **NOTE:** The [[PDFDocument.save]] method will call this method to
	* > update appearances automatically if a form was accessed via the
	* > [[PDFDocument.getForm]] method prior to saving.
	*
	* @param font Optionally, the font to use when creating new appearances.
	*/
	updateFieldAppearances(font) {
		assertOrUndefined(font, "font", [[PDFFont, "PDFFont"]]);
		font = font ?? this.getDefaultFont();
		const fields = this.getFields();
		for (let idx = 0, len = fields.length; idx < len; idx++) {
			const field = fields[idx];
			if (field.needsAppearancesUpdate()) field.defaultUpdateAppearances(font);
		}
	}
	/**
	* Mark a field as dirty. This will cause its appearance streams to be
	* updated by [[PDFForm.updateFieldAppearances]].
	* ```js
	* const form = pdfDoc.getForm()
	* const field = form.getField('foo.bar')
	* form.markFieldAsDirty(field.ref)
	* ```
	* @param fieldRef The reference to the field that should be marked.
	*/
	markFieldAsDirty(fieldRef) {
		assertOrUndefined(fieldRef, "fieldRef", [[PDFRef, "PDFRef"]]);
		this.dirtyFields.add(fieldRef);
	}
	/**
	* Mark a field as dirty. This will cause its appearance streams to not be
	* updated by [[PDFForm.updateFieldAppearances]].
	* ```js
	* const form = pdfDoc.getForm()
	* const field = form.getField('foo.bar')
	* form.markFieldAsClean(field.ref)
	* ```
	* @param fieldRef The reference to the field that should be marked.
	*/
	markFieldAsClean(fieldRef) {
		assertOrUndefined(fieldRef, "fieldRef", [[PDFRef, "PDFRef"]]);
		this.dirtyFields.delete(fieldRef);
	}
	/**
	* Returns `true` is the specified field has been marked as dirty.
	* ```js
	* const form = pdfDoc.getForm()
	* const field = form.getField('foo.bar')
	* if (form.fieldIsDirty(field.ref)) console.log('Field is dirty')
	* ```
	* @param fieldRef The reference to the field that should be checked.
	* @returns Whether or not the specified field is dirty.
	*/
	fieldIsDirty(fieldRef) {
		assertOrUndefined(fieldRef, "fieldRef", [[PDFRef, "PDFRef"]]);
		return this.dirtyFields.has(fieldRef);
	}
	getDefaultFont() {
		return this.defaultFontCache.access();
	}
	findWidgetPage(widget) {
		const pageRef = widget.P();
		let page = this.doc.getPages().find((x) => x.ref === pageRef);
		if (page === void 0) {
			const widgetRef = this.doc.context.getObjectRef(widget.dict);
			if (widgetRef === void 0) throw new Error("Could not find PDFRef for PDFObject");
			page = this.doc.findPageForAnnotationRef(widgetRef);
			if (page === void 0) throw new Error(`Could not find page for PDFRef ${widgetRef}`);
		}
		return page;
	}
	findWidgetAppearanceRef(field, widget) {
		let refOrDict = widget.getNormalAppearance();
		if (refOrDict instanceof PDFDict && (field instanceof PDFCheckBox || field instanceof PDFRadioGroup)) {
			const value = field.acroField.getValue();
			const ref = refOrDict.get(value) ?? refOrDict.get(PDFName.of("Off"));
			if (ref instanceof PDFRef) refOrDict = ref;
		}
		if (!(refOrDict instanceof PDFRef)) {
			const name = field.getName();
			throw new Error(`Failed to extract appearance ref for: ${name}`);
		}
		return refOrDict;
	}
	findOrCreateNonTerminals(partialNames) {
		let nonTerminal = [this.acroForm];
		for (let idx = 0, len = partialNames.length; idx < len; idx++) {
			const namePart = partialNames[idx];
			if (!namePart) throw new InvalidFieldNamePartError(namePart);
			const [parent, parentRef] = nonTerminal;
			const res = this.findNonTerminal(namePart, parent);
			if (res) nonTerminal = res;
			else {
				const node = PDFAcroNonTerminal.create(this.doc.context);
				node.setPartialName(namePart);
				node.setParent(parentRef);
				const nodeRef = this.doc.context.register(node.dict);
				parent.addField(nodeRef);
				nonTerminal = [node, nodeRef];
			}
		}
		return nonTerminal;
	}
	findNonTerminal(partialName, parent) {
		const fields = parent instanceof PDFAcroForm ? this.acroForm.getFields() : createPDFAcroFields(parent.Kids());
		for (let idx = 0, len = fields.length; idx < len; idx++) {
			const [field, ref] = fields[idx];
			if (field.getPartialName() === partialName) {
				if (field instanceof PDFAcroNonTerminal) return [field, ref];
				throw new FieldAlreadyExistsError(partialName);
			}
		}
	}
};
_PDFForm = PDFForm;
_defineProperty(PDFForm, "of", (acroForm, doc) => new _PDFForm(acroForm, doc));
var convertToPDFField = (field, ref, doc) => {
	if (field instanceof PDFAcroPushButton) return PDFButton.of(field, ref, doc);
	if (field instanceof PDFAcroCheckBox) return PDFCheckBox.of(field, ref, doc);
	if (field instanceof PDFAcroComboBox) return PDFDropdown.of(field, ref, doc);
	if (field instanceof PDFAcroListBox) return PDFOptionList.of(field, ref, doc);
	if (field instanceof PDFAcroText) return PDFTextField.of(field, ref, doc);
	if (field instanceof PDFAcroRadioButton) return PDFRadioGroup.of(field, ref, doc);
	if (field instanceof PDFAcroSignature) return PDFSignature.of(field, ref, doc);
};
var splitFieldName = (fullyQualifiedName) => {
	if (fullyQualifiedName.length === 0) throw new Error("PDF field names must not be empty strings");
	const parts = fullyQualifiedName.split(".");
	for (let idx = 0, len = parts.length; idx < len; idx++) if (parts[idx] === "") throw new Error(`Periods in PDF field names must be separated by at least one character: "${fullyQualifiedName}"`);
	if (parts.length === 1) return {
		nonTerminal: [],
		terminal: parts[0]
	};
	return {
		nonTerminal: parts.slice(0, parts.length - 1),
		terminal: parts[parts.length - 1]
	};
};
var addFieldToParent = ([parent, parentRef], [field, fieldRef], partialName) => {
	const entries = parent.normalizedEntries();
	const fields = createPDFAcroFields("Kids" in entries ? entries.Kids : entries.Fields);
	for (let idx = 0, len = fields.length; idx < len; idx++) if (fields[idx][0].getPartialName() === partialName) throw new FieldAlreadyExistsError(partialName);
	parent.addField(fieldRef);
	field.setParent(parentRef);
};
//#endregion
//#region src/api/sizes.ts
var PageSizes = {
	"4A0": [4767.87, 6740.79],
	"2A0": [3370.39, 4767.87],
	A0: [2383.94, 3370.39],
	A1: [1683.78, 2383.94],
	A2: [1190.55, 1683.78],
	A3: [841.89, 1190.55],
	A4: [595.28, 841.89],
	A5: [419.53, 595.28],
	A6: [297.64, 419.53],
	A7: [209.76, 297.64],
	A8: [147.4, 209.76],
	A9: [104.88, 147.4],
	A10: [73.7, 104.88],
	B0: [2834.65, 4008.19],
	B1: [2004.09, 2834.65],
	B2: [1417.32, 2004.09],
	B3: [1000.63, 1417.32],
	B4: [708.66, 1000.63],
	B5: [498.9, 708.66],
	B6: [354.33, 498.9],
	B7: [249.45, 354.33],
	B8: [175.75, 249.45],
	B9: [124.72, 175.75],
	B10: [87.87, 124.72],
	C0: [2599.37, 3676.54],
	C1: [1836.85, 2599.37],
	C2: [1298.27, 1836.85],
	C3: [918.43, 1298.27],
	C4: [649.13, 918.43],
	C5: [459.21, 649.13],
	C6: [323.15, 459.21],
	C7: [229.61, 323.15],
	C8: [161.57, 229.61],
	C9: [113.39, 161.57],
	C10: [79.37, 113.39],
	RA0: [2437.8, 3458.27],
	RA1: [1729.13, 2437.8],
	RA2: [1218.9, 1729.13],
	RA3: [864.57, 1218.9],
	RA4: [609.45, 864.57],
	SRA0: [2551.18, 3628.35],
	SRA1: [1814.17, 2551.18],
	SRA2: [1275.59, 1814.17],
	SRA3: [907.09, 1275.59],
	SRA4: [637.8, 907.09],
	Executive: [521.86, 756],
	Folio: [612, 936],
	Legal: [612, 1008],
	Letter: [612, 792],
	Tabloid: [792, 1224]
};
//#endregion
//#region src/api/PDFDocumentOptions.ts
var ParseSpeeds = /* @__PURE__ */ function(ParseSpeeds) {
	ParseSpeeds[ParseSpeeds["Fastest"] = Infinity] = "Fastest";
	ParseSpeeds[ParseSpeeds["Fast"] = 1500] = "Fast";
	ParseSpeeds[ParseSpeeds["Medium"] = 500] = "Medium";
	ParseSpeeds[ParseSpeeds["Slow"] = 100] = "Slow";
	return ParseSpeeds;
}({});
//#endregion
//#region src/api/PDFEmbeddedFile.ts
var _PDFEmbeddedFile;
/**
* Represents a file that has been embedded in a [[PDFDocument]].
*/
var PDFEmbeddedFile = class {
	constructor(ref, doc, embedder) {
		_defineProperty(
			this,
			/** The unique reference assigned to this embedded file within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this embedded file belongs. */
			"doc",
			void 0
		);
		_defineProperty(this, "alreadyEmbedded", false);
		_defineProperty(this, "embedder", void 0);
		this.ref = ref;
		this.doc = doc;
		this.embedder = embedder;
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFDocument.save]] and [[PDFDocument.saveAsBase64]] methods will
	* > automatically ensure all embeddable files get embedded.
	*
	* Embed this embeddable file in its document.
	*
	* @returns Resolves when the embedding is complete.
	*/
	async embed() {
		if (!this.alreadyEmbedded) {
			const ref = await this.embedder.embedIntoContext(this.doc.context, this.ref);
			if (!this.doc.catalog.has(PDFName.of("Names"))) this.doc.catalog.set(PDFName.of("Names"), this.doc.context.obj({}));
			const Names = this.doc.catalog.lookup(PDFName.of("Names"), PDFDict);
			if (!Names.has(PDFName.of("EmbeddedFiles"))) Names.set(PDFName.of("EmbeddedFiles"), this.doc.context.obj({}));
			const EmbeddedFiles = Names.lookup(PDFName.of("EmbeddedFiles"), PDFDict);
			if (!EmbeddedFiles.has(PDFName.of("Names"))) EmbeddedFiles.set(PDFName.of("Names"), this.doc.context.obj([]));
			const EFNames = EmbeddedFiles.lookup(PDFName.of("Names"), PDFArray);
			EFNames.push(PDFHexString.fromText(this.embedder.fileName));
			EFNames.push(ref);
			/**
			* The AF-Tag is needed to achieve PDF-A3 compliance for embedded files
			*
			* The following document outlines the uses cases of the associated files (AF) tag.
			* See:
			* https://www.pdfa.org/wp-content/uploads/2018/10/PDF20_AN002-AF.pdf
			*/
			if (!this.doc.catalog.has(PDFName.of("AF"))) this.doc.catalog.set(PDFName.of("AF"), this.doc.context.obj([]));
			this.doc.catalog.lookup(PDFName.of("AF"), PDFArray).push(ref);
			this.alreadyEmbedded = true;
		}
	}
};
_PDFEmbeddedFile = PDFEmbeddedFile;
_defineProperty(PDFEmbeddedFile, "of", (ref, doc, embedder) => new _PDFEmbeddedFile(ref, doc, embedder));
//#endregion
//#region src/api/PDFJavaScript.ts
var _PDFJavaScript;
/**
* Represents JavaScript that has been embedded in a [[PDFDocument]].
*/
var PDFJavaScript = class {
	constructor(ref, doc, embedder) {
		_defineProperty(
			this,
			/** The unique reference assigned to this embedded script within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this embedded script belongs. */
			"doc",
			void 0
		);
		_defineProperty(this, "alreadyEmbedded", false);
		_defineProperty(this, "embedder", void 0);
		this.ref = ref;
		this.doc = doc;
		this.embedder = embedder;
	}
	/**
	* > **NOTE:** You probably don't need to call this method directly. The
	* > [[PDFDocument.save]] and [[PDFDocument.saveAsBase64]] methods will
	* > automatically ensure all JavaScripts get embedded.
	*
	* Embed this JavaScript in its document.
	*
	* @returns Resolves when the embedding is complete.
	*/
	async embed() {
		if (!this.alreadyEmbedded) {
			const { catalog, context } = this.doc;
			const ref = await this.embedder.embedIntoContext(this.doc.context, this.ref);
			if (!catalog.has(PDFName.of("Names"))) catalog.set(PDFName.of("Names"), context.obj({}));
			const Names = catalog.lookup(PDFName.of("Names"), PDFDict);
			if (!Names.has(PDFName.of("JavaScript"))) Names.set(PDFName.of("JavaScript"), context.obj({}));
			const Javascript = Names.lookup(PDFName.of("JavaScript"), PDFDict);
			if (!Javascript.has(PDFName.of("Names"))) Javascript.set(PDFName.of("Names"), context.obj([]));
			const JSNames = Javascript.lookup(PDFName.of("Names"), PDFArray);
			JSNames.push(PDFHexString.fromText(this.embedder.scriptName));
			JSNames.push(ref);
			this.alreadyEmbedded = true;
		}
	}
};
_PDFJavaScript = PDFJavaScript;
_defineProperty(PDFJavaScript, "of", (ref, doc, embedder) => new _PDFJavaScript(ref, doc, embedder));
//#endregion
//#region src/core/embedders/JavaScriptEmbedder.ts
var JavaScriptEmbedder = class JavaScriptEmbedder {
	static for(script, scriptName) {
		return new JavaScriptEmbedder(script, scriptName);
	}
	constructor(script, scriptName) {
		_defineProperty(this, "script", void 0);
		_defineProperty(this, "scriptName", void 0);
		this.script = script;
		this.scriptName = scriptName;
	}
	async embedIntoContext(context, ref) {
		const jsActionDict = context.obj({
			Type: "Action",
			S: "JavaScript",
			JS: PDFHexString.fromText(this.script)
		});
		if (ref) {
			context.assign(ref, jsActionDict);
			return ref;
		} else return context.register(jsActionDict);
	}
};
//#endregion
//#region src/core/streams/DecryptStream.ts
var chunkSize = 512;
var DecryptStream = class extends DecodeStream {
	constructor(stream, decrypt, maybeLength) {
		super(maybeLength);
		_defineProperty(this, "stream", void 0);
		_defineProperty(this, "initialized", void 0);
		_defineProperty(this, "nextChunk", void 0);
		_defineProperty(this, "decrypt", void 0);
		this.stream = stream;
		this.decrypt = decrypt;
		this.nextChunk = null;
		this.initialized = false;
	}
	readBlock() {
		let chunk;
		if (this.initialized) chunk = this.nextChunk;
		else {
			chunk = this.stream.getBytes(chunkSize);
			this.initialized = true;
		}
		if (!chunk || chunk.length === 0) {
			this.eof = true;
			return;
		}
		this.nextChunk = this.stream.getBytes(chunkSize);
		const hasMoreData = this.nextChunk && this.nextChunk.length > 0;
		const decrypt = this.decrypt;
		chunk = decrypt(chunk, !hasMoreData);
		const bufferLength = this.bufferLength, newLength = bufferLength + chunk.length;
		this.ensureBuffer(newLength).set(chunk, bufferLength);
		this.bufferLength = newLength;
	}
};
//#endregion
//#region src/core/crypto.ts
var ARCFourCipher = class {
	constructor(key) {
		_defineProperty(this, "s", void 0);
		_defineProperty(this, "a", void 0);
		_defineProperty(this, "b", void 0);
		this.a = 0;
		this.b = 0;
		const s = /* @__PURE__ */ new Uint8Array(256);
		const keyLength = key.length;
		for (let i = 0; i < 256; ++i) s[i] = i;
		for (let i = 0, j = 0; i < 256; ++i) {
			const tmp = s[i];
			j = j + tmp + key[i % keyLength] & 255;
			s[i] = s[j];
			s[j] = tmp;
		}
		this.s = s;
	}
	encryptBlock(data) {
		let a = this.a, b = this.b;
		const s = this.s;
		const n = data.length;
		const output = new Uint8Array(n);
		for (let i = 0; i < n; ++i) {
			a = a + 1 & 255;
			const tmp = s[a];
			b = b + tmp & 255;
			const tmp2 = s[b];
			s[a] = tmp2;
			s[b] = tmp;
			output[i] = data[i] ^ s[tmp + tmp2 & 255];
		}
		this.a = a;
		this.b = b;
		return output;
	}
	decryptBlock(data) {
		return this.encryptBlock(data);
	}
	encrypt(data) {
		return this.encryptBlock(data);
	}
};
var calculateMD5 = (function calculateMD5Closure() {
	const r = new Uint8Array([
		7,
		12,
		17,
		22,
		7,
		12,
		17,
		22,
		7,
		12,
		17,
		22,
		7,
		12,
		17,
		22,
		5,
		9,
		14,
		20,
		5,
		9,
		14,
		20,
		5,
		9,
		14,
		20,
		5,
		9,
		14,
		20,
		4,
		11,
		16,
		23,
		4,
		11,
		16,
		23,
		4,
		11,
		16,
		23,
		4,
		11,
		16,
		23,
		6,
		10,
		15,
		21,
		6,
		10,
		15,
		21,
		6,
		10,
		15,
		21,
		6,
		10,
		15,
		21
	]);
	const k = new Int32Array([
		-680876936,
		-389564586,
		606105819,
		-1044525330,
		-176418897,
		1200080426,
		-1473231341,
		-45705983,
		1770035416,
		-1958414417,
		-42063,
		-1990404162,
		1804603682,
		-40341101,
		-1502002290,
		1236535329,
		-165796510,
		-1069501632,
		643717713,
		-373897302,
		-701558691,
		38016083,
		-660478335,
		-405537848,
		568446438,
		-1019803690,
		-187363961,
		1163531501,
		-1444681467,
		-51403784,
		1735328473,
		-1926607734,
		-378558,
		-2022574463,
		1839030562,
		-35309556,
		-1530992060,
		1272893353,
		-155497632,
		-1094730640,
		681279174,
		-358537222,
		-722521979,
		76029189,
		-640364487,
		-421815835,
		530742520,
		-995338651,
		-198630844,
		1126891415,
		-1416354905,
		-57434055,
		1700485571,
		-1894986606,
		-1051523,
		-2054922799,
		1873313359,
		-30611744,
		-1560198380,
		1309151649,
		-145523070,
		-1120210379,
		718787259,
		-343485551
	]);
	function hash(data, offset, length) {
		let h0 = 1732584193, h1 = -271733879, h2 = -1732584194, h3 = 271733878;
		const paddedLength = length + 72 & -64;
		const padded = new Uint8Array(paddedLength);
		let i, j;
		for (i = 0; i < length; ++i) padded[i] = data[offset++];
		padded[i++] = 128;
		const n = paddedLength - 8;
		while (i < n) padded[i++] = 0;
		padded[i++] = length << 3 & 255;
		padded[i++] = length >> 5 & 255;
		padded[i++] = length >> 13 & 255;
		padded[i++] = length >> 21 & 255;
		padded[i++] = length >>> 29 & 255;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		const w = /* @__PURE__ */ new Int32Array(16);
		for (i = 0; i < paddedLength;) {
			for (j = 0; j < 16; ++j, i += 4) w[j] = padded[i] | padded[i + 1] << 8 | padded[i + 2] << 16 | padded[i + 3] << 24;
			let a = h0, b = h1, c = h2, d = h3, f, g;
			for (j = 0; j < 64; ++j) {
				if (j < 16) {
					f = b & c | ~b & d;
					g = j;
				} else if (j < 32) {
					f = d & b | ~d & c;
					g = 5 * j + 1 & 15;
				} else if (j < 48) {
					f = b ^ c ^ d;
					g = 3 * j + 5 & 15;
				} else {
					f = c ^ (b | ~d);
					g = 7 * j & 15;
				}
				const tmp = d, rotateArg = a + f + k[j] + w[g] | 0, rotate = r[j];
				d = c;
				c = b;
				b = b + (rotateArg << rotate | rotateArg >>> 32 - rotate) | 0;
				a = tmp;
			}
			h0 = h0 + a | 0;
			h1 = h1 + b | 0;
			h2 = h2 + c | 0;
			h3 = h3 + d | 0;
		}
		return new Uint8Array([
			h0 & 255,
			h0 >> 8 & 255,
			h0 >> 16 & 255,
			h0 >>> 24 & 255,
			h1 & 255,
			h1 >> 8 & 255,
			h1 >> 16 & 255,
			h1 >>> 24 & 255,
			h2 & 255,
			h2 >> 8 & 255,
			h2 >> 16 & 255,
			h2 >>> 24 & 255,
			h3 & 255,
			h3 >> 8 & 255,
			h3 >> 16 & 255,
			h3 >>> 24 & 255
		]);
	}
	return hash;
})();
var Word64 = class {
	constructor(highInteger, lowInteger) {
		_defineProperty(this, "low", void 0);
		_defineProperty(this, "high", void 0);
		this.high = highInteger | 0;
		this.low = lowInteger | 0;
	}
	and(word) {
		this.high &= word.high;
		this.low &= word.low;
	}
	xor(word) {
		this.high ^= word.high;
		this.low ^= word.low;
	}
	or(word) {
		this.high |= word.high;
		this.low |= word.low;
	}
	shiftRight(places) {
		if (places >= 32) {
			this.low = this.high >>> places - 32 | 0;
			this.high = 0;
		} else {
			this.low = this.low >>> places | this.high << 32 - places;
			this.high = this.high >>> places | 0;
		}
	}
	shiftLeft(places) {
		if (places >= 32) {
			this.high = this.low << places - 32;
			this.low = 0;
		} else {
			this.high = this.high << places | this.low >>> 32 - places;
			this.low <<= places;
		}
	}
	rotateRight(places) {
		let low, high;
		if (places & 32) {
			high = this.low;
			low = this.high;
		} else {
			low = this.low;
			high = this.high;
		}
		places &= 31;
		this.low = low >>> places | high << 32 - places;
		this.high = high >>> places | low << 32 - places;
	}
	not() {
		this.high = ~this.high;
		this.low = ~this.low;
	}
	add(word) {
		const lowAdd = (this.low >>> 0) + (word.low >>> 0);
		let highAdd = (this.high >>> 0) + (word.high >>> 0);
		if (lowAdd > 4294967295) highAdd += 1;
		this.low = lowAdd | 0;
		this.high = highAdd | 0;
	}
	copyTo(bytes, offset) {
		bytes[offset] = this.high >>> 24 & 255;
		bytes[offset + 1] = this.high >> 16 & 255;
		bytes[offset + 2] = this.high >> 8 & 255;
		bytes[offset + 3] = this.high & 255;
		bytes[offset + 4] = this.low >>> 24 & 255;
		bytes[offset + 5] = this.low >> 16 & 255;
		bytes[offset + 6] = this.low >> 8 & 255;
		bytes[offset + 7] = this.low & 255;
	}
	assign(word) {
		this.high = word.high;
		this.low = word.low;
	}
};
var calculateSHA256 = (function calculateSHA256Closure() {
	function rotr(x, n) {
		return x >>> n | x << 32 - n;
	}
	function ch(x, y, z) {
		return x & y ^ ~x & z;
	}
	function maj(x, y, z) {
		return x & y ^ x & z ^ y & z;
	}
	function sigma(x) {
		return rotr(x, 2) ^ rotr(x, 13) ^ rotr(x, 22);
	}
	function sigmaPrime(x) {
		return rotr(x, 6) ^ rotr(x, 11) ^ rotr(x, 25);
	}
	function littleSigma(x) {
		return rotr(x, 7) ^ rotr(x, 18) ^ x >>> 3;
	}
	function littleSigmaPrime(x) {
		return rotr(x, 17) ^ rotr(x, 19) ^ x >>> 10;
	}
	const k = [
		1116352408,
		1899447441,
		3049323471,
		3921009573,
		961987163,
		1508970993,
		2453635748,
		2870763221,
		3624381080,
		310598401,
		607225278,
		1426881987,
		1925078388,
		2162078206,
		2614888103,
		3248222580,
		3835390401,
		4022224774,
		264347078,
		604807628,
		770255983,
		1249150122,
		1555081692,
		1996064986,
		2554220882,
		2821834349,
		2952996808,
		3210313671,
		3336571891,
		3584528711,
		113926993,
		338241895,
		666307205,
		773529912,
		1294757372,
		1396182291,
		1695183700,
		1986661051,
		2177026350,
		2456956037,
		2730485921,
		2820302411,
		3259730800,
		3345764771,
		3516065817,
		3600352804,
		4094571909,
		275423344,
		430227734,
		506948616,
		659060556,
		883997877,
		958139571,
		1322822218,
		1537002063,
		1747873779,
		1955562222,
		2024104815,
		2227730452,
		2361852424,
		2428436474,
		2756734187,
		3204031479,
		3329325298
	];
	function hash(data, offset, length) {
		let h0 = 1779033703, h1 = 3144134277, h2 = 1013904242, h3 = 2773480762, h4 = 1359893119, h5 = 2600822924, h6 = 528734635, h7 = 1541459225;
		const paddedLength = Math.ceil((length + 9) / 64) * 64;
		const padded = new Uint8Array(paddedLength);
		let i, j;
		for (i = 0; i < length; ++i) padded[i] = data[offset++];
		padded[i++] = 128;
		const n = paddedLength - 8;
		while (i < n) padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = length >>> 29 & 255;
		padded[i++] = length >> 21 & 255;
		padded[i++] = length >> 13 & 255;
		padded[i++] = length >> 5 & 255;
		padded[i++] = length << 3 & 255;
		const w = /* @__PURE__ */ new Uint32Array(64);
		for (i = 0; i < paddedLength;) {
			for (j = 0; j < 16; ++j) {
				w[j] = padded[i] << 24 | padded[i + 1] << 16 | padded[i + 2] << 8 | padded[i + 3];
				i += 4;
			}
			for (j = 16; j < 64; ++j) w[j] = littleSigmaPrime(w[j - 2]) + w[j - 7] + littleSigma(w[j - 15]) + w[j - 16] | 0;
			let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7, t1, t2;
			for (j = 0; j < 64; ++j) {
				t1 = h + sigmaPrime(e) + ch(e, f, g) + k[j] + w[j];
				t2 = sigma(a) + maj(a, b, c);
				h = g;
				g = f;
				f = e;
				e = d + t1 | 0;
				d = c;
				c = b;
				b = a;
				a = t1 + t2 | 0;
			}
			h0 = h0 + a | 0;
			h1 = h1 + b | 0;
			h2 = h2 + c | 0;
			h3 = h3 + d | 0;
			h4 = h4 + e | 0;
			h5 = h5 + f | 0;
			h6 = h6 + g | 0;
			h7 = h7 + h | 0;
		}
		return new Uint8Array([
			h0 >> 24 & 255,
			h0 >> 16 & 255,
			h0 >> 8 & 255,
			h0 & 255,
			h1 >> 24 & 255,
			h1 >> 16 & 255,
			h1 >> 8 & 255,
			h1 & 255,
			h2 >> 24 & 255,
			h2 >> 16 & 255,
			h2 >> 8 & 255,
			h2 & 255,
			h3 >> 24 & 255,
			h3 >> 16 & 255,
			h3 >> 8 & 255,
			h3 & 255,
			h4 >> 24 & 255,
			h4 >> 16 & 255,
			h4 >> 8 & 255,
			h4 & 255,
			h5 >> 24 & 255,
			h5 >> 16 & 255,
			h5 >> 8 & 255,
			h5 & 255,
			h6 >> 24 & 255,
			h6 >> 16 & 255,
			h6 >> 8 & 255,
			h6 & 255,
			h7 >> 24 & 255,
			h7 >> 16 & 255,
			h7 >> 8 & 255,
			h7 & 255
		]);
	}
	return hash;
})();
var calculateSHA512 = (function calculateSHA512Closure() {
	function ch(result, x, y, z, tmp) {
		result.assign(x);
		result.and(y);
		tmp.assign(x);
		tmp.not();
		tmp.and(z);
		result.xor(tmp);
	}
	function maj(result, x, y, z, tmp) {
		result.assign(x);
		result.and(y);
		tmp.assign(x);
		tmp.and(z);
		result.xor(tmp);
		tmp.assign(y);
		tmp.and(z);
		result.xor(tmp);
	}
	function sigma(result, x, tmp) {
		result.assign(x);
		result.rotateRight(28);
		tmp.assign(x);
		tmp.rotateRight(34);
		result.xor(tmp);
		tmp.assign(x);
		tmp.rotateRight(39);
		result.xor(tmp);
	}
	function sigmaPrime(result, x, tmp) {
		result.assign(x);
		result.rotateRight(14);
		tmp.assign(x);
		tmp.rotateRight(18);
		result.xor(tmp);
		tmp.assign(x);
		tmp.rotateRight(41);
		result.xor(tmp);
	}
	function littleSigma(result, x, tmp) {
		result.assign(x);
		result.rotateRight(1);
		tmp.assign(x);
		tmp.rotateRight(8);
		result.xor(tmp);
		tmp.assign(x);
		tmp.shiftRight(7);
		result.xor(tmp);
	}
	function littleSigmaPrime(result, x, tmp) {
		result.assign(x);
		result.rotateRight(19);
		tmp.assign(x);
		tmp.rotateRight(61);
		result.xor(tmp);
		tmp.assign(x);
		tmp.shiftRight(6);
		result.xor(tmp);
	}
	const k = [
		new Word64(1116352408, 3609767458),
		new Word64(1899447441, 602891725),
		new Word64(3049323471, 3964484399),
		new Word64(3921009573, 2173295548),
		new Word64(961987163, 4081628472),
		new Word64(1508970993, 3053834265),
		new Word64(2453635748, 2937671579),
		new Word64(2870763221, 3664609560),
		new Word64(3624381080, 2734883394),
		new Word64(310598401, 1164996542),
		new Word64(607225278, 1323610764),
		new Word64(1426881987, 3590304994),
		new Word64(1925078388, 4068182383),
		new Word64(2162078206, 991336113),
		new Word64(2614888103, 633803317),
		new Word64(3248222580, 3479774868),
		new Word64(3835390401, 2666613458),
		new Word64(4022224774, 944711139),
		new Word64(264347078, 2341262773),
		new Word64(604807628, 2007800933),
		new Word64(770255983, 1495990901),
		new Word64(1249150122, 1856431235),
		new Word64(1555081692, 3175218132),
		new Word64(1996064986, 2198950837),
		new Word64(2554220882, 3999719339),
		new Word64(2821834349, 766784016),
		new Word64(2952996808, 2566594879),
		new Word64(3210313671, 3203337956),
		new Word64(3336571891, 1034457026),
		new Word64(3584528711, 2466948901),
		new Word64(113926993, 3758326383),
		new Word64(338241895, 168717936),
		new Word64(666307205, 1188179964),
		new Word64(773529912, 1546045734),
		new Word64(1294757372, 1522805485),
		new Word64(1396182291, 2643833823),
		new Word64(1695183700, 2343527390),
		new Word64(1986661051, 1014477480),
		new Word64(2177026350, 1206759142),
		new Word64(2456956037, 344077627),
		new Word64(2730485921, 1290863460),
		new Word64(2820302411, 3158454273),
		new Word64(3259730800, 3505952657),
		new Word64(3345764771, 106217008),
		new Word64(3516065817, 3606008344),
		new Word64(3600352804, 1432725776),
		new Word64(4094571909, 1467031594),
		new Word64(275423344, 851169720),
		new Word64(430227734, 3100823752),
		new Word64(506948616, 1363258195),
		new Word64(659060556, 3750685593),
		new Word64(883997877, 3785050280),
		new Word64(958139571, 3318307427),
		new Word64(1322822218, 3812723403),
		new Word64(1537002063, 2003034995),
		new Word64(1747873779, 3602036899),
		new Word64(1955562222, 1575990012),
		new Word64(2024104815, 1125592928),
		new Word64(2227730452, 2716904306),
		new Word64(2361852424, 442776044),
		new Word64(2428436474, 593698344),
		new Word64(2756734187, 3733110249),
		new Word64(3204031479, 2999351573),
		new Word64(3329325298, 3815920427),
		new Word64(3391569614, 3928383900),
		new Word64(3515267271, 566280711),
		new Word64(3940187606, 3454069534),
		new Word64(4118630271, 4000239992),
		new Word64(116418474, 1914138554),
		new Word64(174292421, 2731055270),
		new Word64(289380356, 3203993006),
		new Word64(460393269, 320620315),
		new Word64(685471733, 587496836),
		new Word64(852142971, 1086792851),
		new Word64(1017036298, 365543100),
		new Word64(1126000580, 2618297676),
		new Word64(1288033470, 3409855158),
		new Word64(1501505948, 4234509866),
		new Word64(1607167915, 987167468),
		new Word64(1816402316, 1246189591)
	];
	function hash(data, offset, length, mode384 = false) {
		let h0, h1, h2, h3, h4, h5, h6, h7;
		if (!mode384) {
			h0 = new Word64(1779033703, 4089235720);
			h1 = new Word64(3144134277, 2227873595);
			h2 = new Word64(1013904242, 4271175723);
			h3 = new Word64(2773480762, 1595750129);
			h4 = new Word64(1359893119, 2917565137);
			h5 = new Word64(2600822924, 725511199);
			h6 = new Word64(528734635, 4215389547);
			h7 = new Word64(1541459225, 327033209);
		} else {
			h0 = new Word64(3418070365, 3238371032);
			h1 = new Word64(1654270250, 914150663);
			h2 = new Word64(2438529370, 812702999);
			h3 = new Word64(355462360, 4144912697);
			h4 = new Word64(1731405415, 4290775857);
			h5 = new Word64(2394180231, 1750603025);
			h6 = new Word64(3675008525, 1694076839);
			h7 = new Word64(1203062813, 3204075428);
		}
		const paddedLength = Math.ceil((length + 17) / 128) * 128;
		const padded = new Uint8Array(paddedLength);
		let i, j;
		for (i = 0; i < length; ++i) padded[i] = data[offset++];
		padded[i++] = 128;
		const n = paddedLength - 16;
		while (i < n) padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = 0;
		padded[i++] = length >>> 29 & 255;
		padded[i++] = length >> 21 & 255;
		padded[i++] = length >> 13 & 255;
		padded[i++] = length >> 5 & 255;
		padded[i++] = length << 3 & 255;
		const w = Array(80);
		for (i = 0; i < 80; i++) w[i] = new Word64(0, 0);
		let a = new Word64(0, 0), b = new Word64(0, 0), c = new Word64(0, 0);
		let d = new Word64(0, 0), e = new Word64(0, 0), f = new Word64(0, 0);
		let g = new Word64(0, 0), h = new Word64(0, 0);
		const t1 = new Word64(0, 0), t2 = new Word64(0, 0);
		const tmp1 = new Word64(0, 0), tmp2 = new Word64(0, 0);
		let tmp3;
		for (i = 0; i < paddedLength;) {
			for (j = 0; j < 16; ++j) {
				w[j].high = padded[i] << 24 | padded[i + 1] << 16 | padded[i + 2] << 8 | padded[i + 3];
				w[j].low = padded[i + 4] << 24 | padded[i + 5] << 16 | padded[i + 6] << 8 | padded[i + 7];
				i += 8;
			}
			for (j = 16; j < 80; ++j) {
				tmp3 = w[j];
				littleSigmaPrime(tmp3, w[j - 2], tmp2);
				tmp3.add(w[j - 7]);
				littleSigma(tmp1, w[j - 15], tmp2);
				tmp3.add(tmp1);
				tmp3.add(w[j - 16]);
			}
			a.assign(h0);
			b.assign(h1);
			c.assign(h2);
			d.assign(h3);
			e.assign(h4);
			f.assign(h5);
			g.assign(h6);
			h.assign(h7);
			for (j = 0; j < 80; ++j) {
				t1.assign(h);
				sigmaPrime(tmp1, e, tmp2);
				t1.add(tmp1);
				ch(tmp1, e, f, g, tmp2);
				t1.add(tmp1);
				t1.add(k[j]);
				t1.add(w[j]);
				sigma(t2, a, tmp2);
				maj(tmp1, a, b, c, tmp2);
				t2.add(tmp1);
				tmp3 = h;
				h = g;
				g = f;
				f = e;
				d.add(t1);
				e = d;
				d = c;
				c = b;
				b = a;
				tmp3.assign(t1);
				tmp3.add(t2);
				a = tmp3;
			}
			h0.add(a);
			h1.add(b);
			h2.add(c);
			h3.add(d);
			h4.add(e);
			h5.add(f);
			h6.add(g);
			h7.add(h);
		}
		let result;
		if (!mode384) {
			result = /* @__PURE__ */ new Uint8Array(64);
			h0.copyTo(result, 0);
			h1.copyTo(result, 8);
			h2.copyTo(result, 16);
			h3.copyTo(result, 24);
			h4.copyTo(result, 32);
			h5.copyTo(result, 40);
			h6.copyTo(result, 48);
			h7.copyTo(result, 56);
		} else {
			result = /* @__PURE__ */ new Uint8Array(48);
			h0.copyTo(result, 0);
			h1.copyTo(result, 8);
			h2.copyTo(result, 16);
			h3.copyTo(result, 24);
			h4.copyTo(result, 32);
			h5.copyTo(result, 40);
		}
		return result;
	}
	return hash;
})();
function calculateSHA384(data, offset, length) {
	return calculateSHA512(data, offset, length, true);
}
var NullCipher = class {
	decryptBlock(data) {
		return data;
	}
	encrypt(data) {
		return data;
	}
};
var AESBaseCipher = class AESBaseCipher {
	constructor() {
		_defineProperty(this, "_s", void 0);
		_defineProperty(this, "_keySize", void 0);
		_defineProperty(this, "_key", void 0);
		_defineProperty(this, "_cyclesOfRepetition", void 0);
		_defineProperty(this, "_inv_s", void 0);
		_defineProperty(this, "_mix", void 0);
		_defineProperty(this, "_mixCol", void 0);
		_defineProperty(this, "buffer", void 0);
		_defineProperty(this, "bufferPosition", void 0);
		_defineProperty(this, "bufferLength", void 0);
		_defineProperty(this, "iv", void 0);
		if (this.constructor === AESBaseCipher) throw new Error("Cannot initialize AESBaseCipher.");
		this._s = new Uint8Array([
			99,
			124,
			119,
			123,
			242,
			107,
			111,
			197,
			48,
			1,
			103,
			43,
			254,
			215,
			171,
			118,
			202,
			130,
			201,
			125,
			250,
			89,
			71,
			240,
			173,
			212,
			162,
			175,
			156,
			164,
			114,
			192,
			183,
			253,
			147,
			38,
			54,
			63,
			247,
			204,
			52,
			165,
			229,
			241,
			113,
			216,
			49,
			21,
			4,
			199,
			35,
			195,
			24,
			150,
			5,
			154,
			7,
			18,
			128,
			226,
			235,
			39,
			178,
			117,
			9,
			131,
			44,
			26,
			27,
			110,
			90,
			160,
			82,
			59,
			214,
			179,
			41,
			227,
			47,
			132,
			83,
			209,
			0,
			237,
			32,
			252,
			177,
			91,
			106,
			203,
			190,
			57,
			74,
			76,
			88,
			207,
			208,
			239,
			170,
			251,
			67,
			77,
			51,
			133,
			69,
			249,
			2,
			127,
			80,
			60,
			159,
			168,
			81,
			163,
			64,
			143,
			146,
			157,
			56,
			245,
			188,
			182,
			218,
			33,
			16,
			255,
			243,
			210,
			205,
			12,
			19,
			236,
			95,
			151,
			68,
			23,
			196,
			167,
			126,
			61,
			100,
			93,
			25,
			115,
			96,
			129,
			79,
			220,
			34,
			42,
			144,
			136,
			70,
			238,
			184,
			20,
			222,
			94,
			11,
			219,
			224,
			50,
			58,
			10,
			73,
			6,
			36,
			92,
			194,
			211,
			172,
			98,
			145,
			149,
			228,
			121,
			231,
			200,
			55,
			109,
			141,
			213,
			78,
			169,
			108,
			86,
			244,
			234,
			101,
			122,
			174,
			8,
			186,
			120,
			37,
			46,
			28,
			166,
			180,
			198,
			232,
			221,
			116,
			31,
			75,
			189,
			139,
			138,
			112,
			62,
			181,
			102,
			72,
			3,
			246,
			14,
			97,
			53,
			87,
			185,
			134,
			193,
			29,
			158,
			225,
			248,
			152,
			17,
			105,
			217,
			142,
			148,
			155,
			30,
			135,
			233,
			206,
			85,
			40,
			223,
			140,
			161,
			137,
			13,
			191,
			230,
			66,
			104,
			65,
			153,
			45,
			15,
			176,
			84,
			187,
			22
		]);
		this._inv_s = new Uint8Array([
			82,
			9,
			106,
			213,
			48,
			54,
			165,
			56,
			191,
			64,
			163,
			158,
			129,
			243,
			215,
			251,
			124,
			227,
			57,
			130,
			155,
			47,
			255,
			135,
			52,
			142,
			67,
			68,
			196,
			222,
			233,
			203,
			84,
			123,
			148,
			50,
			166,
			194,
			35,
			61,
			238,
			76,
			149,
			11,
			66,
			250,
			195,
			78,
			8,
			46,
			161,
			102,
			40,
			217,
			36,
			178,
			118,
			91,
			162,
			73,
			109,
			139,
			209,
			37,
			114,
			248,
			246,
			100,
			134,
			104,
			152,
			22,
			212,
			164,
			92,
			204,
			93,
			101,
			182,
			146,
			108,
			112,
			72,
			80,
			253,
			237,
			185,
			218,
			94,
			21,
			70,
			87,
			167,
			141,
			157,
			132,
			144,
			216,
			171,
			0,
			140,
			188,
			211,
			10,
			247,
			228,
			88,
			5,
			184,
			179,
			69,
			6,
			208,
			44,
			30,
			143,
			202,
			63,
			15,
			2,
			193,
			175,
			189,
			3,
			1,
			19,
			138,
			107,
			58,
			145,
			17,
			65,
			79,
			103,
			220,
			234,
			151,
			242,
			207,
			206,
			240,
			180,
			230,
			115,
			150,
			172,
			116,
			34,
			231,
			173,
			53,
			133,
			226,
			249,
			55,
			232,
			28,
			117,
			223,
			110,
			71,
			241,
			26,
			113,
			29,
			41,
			197,
			137,
			111,
			183,
			98,
			14,
			170,
			24,
			190,
			27,
			252,
			86,
			62,
			75,
			198,
			210,
			121,
			32,
			154,
			219,
			192,
			254,
			120,
			205,
			90,
			244,
			31,
			221,
			168,
			51,
			136,
			7,
			199,
			49,
			177,
			18,
			16,
			89,
			39,
			128,
			236,
			95,
			96,
			81,
			127,
			169,
			25,
			181,
			74,
			13,
			45,
			229,
			122,
			159,
			147,
			201,
			156,
			239,
			160,
			224,
			59,
			77,
			174,
			42,
			245,
			176,
			200,
			235,
			187,
			60,
			131,
			83,
			153,
			97,
			23,
			43,
			4,
			126,
			186,
			119,
			214,
			38,
			225,
			105,
			20,
			99,
			85,
			33,
			12,
			125
		]);
		this._mix = new Uint32Array([
			0,
			235474187,
			470948374,
			303765277,
			941896748,
			908933415,
			607530554,
			708780849,
			1883793496,
			2118214995,
			1817866830,
			1649639237,
			1215061108,
			1181045119,
			1417561698,
			1517767529,
			3767586992,
			4003061179,
			4236429990,
			4069246893,
			3635733660,
			3602770327,
			3299278474,
			3400528769,
			2430122216,
			2664543715,
			2362090238,
			2193862645,
			2835123396,
			2801107407,
			3035535058,
			3135740889,
			3678124923,
			3576870512,
			3341394285,
			3374361702,
			3810496343,
			3977675356,
			4279080257,
			4043610186,
			2876494627,
			2776292904,
			3076639029,
			3110650942,
			2472011535,
			2640243204,
			2403728665,
			2169303058,
			1001089995,
			899835584,
			666464733,
			699432150,
			59727847,
			226906860,
			530400753,
			294930682,
			1273168787,
			1172967064,
			1475418501,
			1509430414,
			1942435775,
			2110667444,
			1876241833,
			1641816226,
			2910219766,
			2743034109,
			2976151520,
			3211623147,
			2505202138,
			2606453969,
			2302690252,
			2269728455,
			3711829422,
			3543599269,
			3240894392,
			3475313331,
			3843699074,
			3943906441,
			4178062228,
			4144047775,
			1306967366,
			1139781709,
			1374988112,
			1610459739,
			1975683434,
			2076935265,
			1775276924,
			1742315127,
			1034867998,
			866637845,
			566021896,
			800440835,
			92987698,
			193195065,
			429456164,
			395441711,
			1984812685,
			2017778566,
			1784663195,
			1683407248,
			1315562145,
			1080094634,
			1383856311,
			1551037884,
			101039829,
			135050206,
			437757123,
			337553864,
			1042385657,
			807962610,
			573804783,
			742039012,
			2531067453,
			2564033334,
			2328828971,
			2227573024,
			2935566865,
			2700099354,
			3001755655,
			3168937228,
			3868552805,
			3902563182,
			4203181171,
			4102977912,
			3736164937,
			3501741890,
			3265478751,
			3433712980,
			1106041591,
			1340463100,
			1576976609,
			1408749034,
			2043211483,
			2009195472,
			1708848333,
			1809054150,
			832877231,
			1068351396,
			766945465,
			599762354,
			159417987,
			126454664,
			361929877,
			463180190,
			2709260871,
			2943682380,
			3178106961,
			3009879386,
			2572697195,
			2538681184,
			2236228733,
			2336434550,
			3509871135,
			3745345300,
			3441850377,
			3274667266,
			3910161971,
			3877198648,
			4110568485,
			4211818798,
			2597806476,
			2497604743,
			2261089178,
			2295101073,
			2733856160,
			2902087851,
			3202437046,
			2968011453,
			3936291284,
			3835036895,
			4136440770,
			4169408201,
			3535486456,
			3702665459,
			3467192302,
			3231722213,
			2051518780,
			1951317047,
			1716890410,
			1750902305,
			1113818384,
			1282050075,
			1584504582,
			1350078989,
			168810852,
			67556463,
			371049330,
			404016761,
			841739592,
			1008918595,
			775550814,
			540080725,
			3969562369,
			3801332234,
			4035489047,
			4269907996,
			3569255213,
			3669462566,
			3366754619,
			3332740144,
			2631065433,
			2463879762,
			2160117071,
			2395588676,
			2767645557,
			2868897406,
			3102011747,
			3069049960,
			202008497,
			33778362,
			270040487,
			504459436,
			875451293,
			975658646,
			675039627,
			641025152,
			2084704233,
			1917518562,
			1615861247,
			1851332852,
			1147550661,
			1248802510,
			1484005843,
			1451044056,
			933301370,
			967311729,
			733156972,
			632953703,
			260388950,
			25965917,
			328671808,
			496906059,
			1206477858,
			1239443753,
			1543208500,
			1441952575,
			2144161806,
			1908694277,
			1675577880,
			1842759443,
			3610369226,
			3644379585,
			3408119516,
			3307916247,
			4011190502,
			3776767469,
			4077384432,
			4245618683,
			2809771154,
			2842737049,
			3144396420,
			3043140495,
			2673705150,
			2438237621,
			2203032232,
			2370213795
		]);
		this._mixCol = /* @__PURE__ */ new Uint8Array(256);
		for (let i = 0; i < 256; i++) if (i < 128) this._mixCol[i] = i << 1;
		else this._mixCol[i] = i << 1 ^ 27;
		this.buffer = /* @__PURE__ */ new Uint8Array(16);
		this.bufferPosition = 0;
	}
	_expandKey(_cipherKey) {
		throw new Error("Cannot call `_expandKey` on the base class");
	}
	_decrypt(input, key) {
		let t, u, v;
		const state = /* @__PURE__ */ new Uint8Array(16);
		state.set(input);
		for (let j = 0, k = this._keySize; j < 16; ++j, ++k) state[j] ^= key[k];
		for (let i = this._cyclesOfRepetition - 1; i >= 1; --i) {
			t = state[13];
			state[13] = state[9];
			state[9] = state[5];
			state[5] = state[1];
			state[1] = t;
			t = state[14];
			u = state[10];
			state[14] = state[6];
			state[10] = state[2];
			state[6] = t;
			state[2] = u;
			t = state[15];
			u = state[11];
			v = state[7];
			state[15] = state[3];
			state[11] = t;
			state[7] = u;
			state[3] = v;
			for (let j = 0; j < 16; ++j) state[j] = this._inv_s[state[j]];
			for (let j = 0, k = i * 16; j < 16; ++j, ++k) state[j] ^= key[k];
			for (let j = 0; j < 16; j += 4) {
				const s0 = this._mix[state[j]];
				const s1 = this._mix[state[j + 1]];
				const s2 = this._mix[state[j + 2]];
				const s3 = this._mix[state[j + 3]];
				t = s0 ^ s1 >>> 8 ^ s1 << 24 ^ s2 >>> 16 ^ s2 << 16 ^ s3 >>> 24 ^ s3 << 8;
				state[j] = t >>> 24 & 255;
				state[j + 1] = t >> 16 & 255;
				state[j + 2] = t >> 8 & 255;
				state[j + 3] = t & 255;
			}
		}
		t = state[13];
		state[13] = state[9];
		state[9] = state[5];
		state[5] = state[1];
		state[1] = t;
		t = state[14];
		u = state[10];
		state[14] = state[6];
		state[10] = state[2];
		state[6] = t;
		state[2] = u;
		t = state[15];
		u = state[11];
		v = state[7];
		state[15] = state[3];
		state[11] = t;
		state[7] = u;
		state[3] = v;
		for (let j = 0; j < 16; ++j) {
			state[j] = this._inv_s[state[j]];
			state[j] ^= key[j];
		}
		return state;
	}
	_encrypt(input, key) {
		const s = this._s;
		let t, u, v;
		const state = /* @__PURE__ */ new Uint8Array(16);
		state.set(input);
		for (let j = 0; j < 16; ++j) state[j] ^= key[j];
		for (let i = 1; i < this._cyclesOfRepetition; i++) {
			for (let j = 0; j < 16; ++j) state[j] = s[state[j]];
			v = state[1];
			state[1] = state[5];
			state[5] = state[9];
			state[9] = state[13];
			state[13] = v;
			v = state[2];
			u = state[6];
			state[2] = state[10];
			state[6] = state[14];
			state[10] = v;
			state[14] = u;
			v = state[3];
			u = state[7];
			t = state[11];
			state[3] = state[15];
			state[7] = v;
			state[11] = u;
			state[15] = t;
			for (let j = 0; j < 16; j += 4) {
				const s0 = state[j + 0];
				const s1 = state[j + 1];
				const s2 = state[j + 2];
				const s3 = state[j + 3];
				t = s0 ^ s1 ^ s2 ^ s3;
				state[j + 0] ^= t ^ this._mixCol[s0 ^ s1];
				state[j + 1] ^= t ^ this._mixCol[s1 ^ s2];
				state[j + 2] ^= t ^ this._mixCol[s2 ^ s3];
				state[j + 3] ^= t ^ this._mixCol[s3 ^ s0];
			}
			for (let j = 0, k = i * 16; j < 16; ++j, ++k) state[j] ^= key[k];
		}
		for (let j = 0; j < 16; ++j) state[j] = s[state[j]];
		v = state[1];
		state[1] = state[5];
		state[5] = state[9];
		state[9] = state[13];
		state[13] = v;
		v = state[2];
		u = state[6];
		state[2] = state[10];
		state[6] = state[14];
		state[10] = v;
		state[14] = u;
		v = state[3];
		u = state[7];
		t = state[11];
		state[3] = state[15];
		state[7] = v;
		state[11] = u;
		state[15] = t;
		for (let j = 0, k = this._keySize; j < 16; ++j, ++k) state[j] ^= key[k];
		return state;
	}
	_decryptBlock2(data, finalize) {
		const sourceLength = data.length;
		let buffer = this.buffer, bufferLength = this.bufferPosition;
		const result = [];
		let iv = this.iv;
		for (let i = 0; i < sourceLength; ++i) {
			buffer[bufferLength] = data[i];
			++bufferLength;
			if (bufferLength < 16) continue;
			const plain = this._decrypt(buffer, this._key);
			for (let j = 0; j < 16; ++j) plain[j] ^= iv[j];
			iv = buffer;
			result.push(plain);
			buffer = /* @__PURE__ */ new Uint8Array(16);
			bufferLength = 0;
		}
		this.buffer = buffer;
		this.bufferLength = bufferLength;
		this.iv = iv;
		if (result.length === 0) return /* @__PURE__ */ new Uint8Array(0);
		let outputLength = 16 * result.length;
		if (finalize) {
			const lastBlock = result[result.length - 1];
			let psLen = lastBlock[15];
			if (psLen <= 16) {
				for (let i = 15, ii = 16 - psLen; i >= ii; --i) if (lastBlock[i] !== psLen) {
					psLen = 0;
					break;
				}
				outputLength -= psLen;
				result[result.length - 1] = lastBlock.subarray(0, 16 - psLen);
			}
		}
		const output = new Uint8Array(outputLength);
		for (let i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) output.set(result[i], j);
		return output;
	}
	decryptBlock(data, finalize, iv) {
		const sourceLength = data.length;
		const buffer = this.buffer;
		let bufferLength = this.bufferPosition;
		if (iv) this.iv = iv;
		else {
			for (let i = 0; bufferLength < 16 && i < sourceLength; ++i, ++bufferLength) buffer[bufferLength] = data[i];
			if (bufferLength < 16) {
				this.bufferLength = bufferLength;
				return /* @__PURE__ */ new Uint8Array(0);
			}
			this.iv = buffer;
			data = data.subarray(16);
		}
		this.buffer = /* @__PURE__ */ new Uint8Array(16);
		this.bufferLength = 0;
		this.decryptBlock = this._decryptBlock2;
		return this.decryptBlock(data, finalize);
	}
	encrypt(data, iv) {
		const sourceLength = data.length;
		let buffer = this.buffer, bufferLength = this.bufferPosition;
		const result = [];
		if (!iv) iv = /* @__PURE__ */ new Uint8Array(16);
		for (let i = 0; i < sourceLength; ++i) {
			buffer[bufferLength] = data[i];
			++bufferLength;
			if (bufferLength < 16) continue;
			for (let j = 0; j < 16; ++j) buffer[j] ^= iv[j];
			const cipher = this._encrypt(buffer, this._key);
			iv = cipher;
			result.push(cipher);
			buffer = /* @__PURE__ */ new Uint8Array(16);
			bufferLength = 0;
		}
		this.buffer = buffer;
		this.bufferLength = bufferLength;
		this.iv = iv;
		if (result.length === 0) return /* @__PURE__ */ new Uint8Array(0);
		const outputLength = 16 * result.length;
		const output = new Uint8Array(outputLength);
		for (let i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) output.set(result[i], j);
		return output;
	}
};
var AES128Cipher = class extends AESBaseCipher {
	constructor(key) {
		super();
		_defineProperty(this, "_rcon", void 0);
		this._cyclesOfRepetition = 10;
		this._keySize = 160;
		this._rcon = new Uint8Array([
			141,
			1,
			2,
			4,
			8,
			16,
			32,
			64,
			128,
			27,
			54,
			108,
			216,
			171,
			77,
			154,
			47,
			94,
			188,
			99,
			198,
			151,
			53,
			106,
			212,
			179,
			125,
			250,
			239,
			197,
			145,
			57,
			114,
			228,
			211,
			189,
			97,
			194,
			159,
			37,
			74,
			148,
			51,
			102,
			204,
			131,
			29,
			58,
			116,
			232,
			203,
			141,
			1,
			2,
			4,
			8,
			16,
			32,
			64,
			128,
			27,
			54,
			108,
			216,
			171,
			77,
			154,
			47,
			94,
			188,
			99,
			198,
			151,
			53,
			106,
			212,
			179,
			125,
			250,
			239,
			197,
			145,
			57,
			114,
			228,
			211,
			189,
			97,
			194,
			159,
			37,
			74,
			148,
			51,
			102,
			204,
			131,
			29,
			58,
			116,
			232,
			203,
			141,
			1,
			2,
			4,
			8,
			16,
			32,
			64,
			128,
			27,
			54,
			108,
			216,
			171,
			77,
			154,
			47,
			94,
			188,
			99,
			198,
			151,
			53,
			106,
			212,
			179,
			125,
			250,
			239,
			197,
			145,
			57,
			114,
			228,
			211,
			189,
			97,
			194,
			159,
			37,
			74,
			148,
			51,
			102,
			204,
			131,
			29,
			58,
			116,
			232,
			203,
			141,
			1,
			2,
			4,
			8,
			16,
			32,
			64,
			128,
			27,
			54,
			108,
			216,
			171,
			77,
			154,
			47,
			94,
			188,
			99,
			198,
			151,
			53,
			106,
			212,
			179,
			125,
			250,
			239,
			197,
			145,
			57,
			114,
			228,
			211,
			189,
			97,
			194,
			159,
			37,
			74,
			148,
			51,
			102,
			204,
			131,
			29,
			58,
			116,
			232,
			203,
			141,
			1,
			2,
			4,
			8,
			16,
			32,
			64,
			128,
			27,
			54,
			108,
			216,
			171,
			77,
			154,
			47,
			94,
			188,
			99,
			198,
			151,
			53,
			106,
			212,
			179,
			125,
			250,
			239,
			197,
			145,
			57,
			114,
			228,
			211,
			189,
			97,
			194,
			159,
			37,
			74,
			148,
			51,
			102,
			204,
			131,
			29,
			58,
			116,
			232,
			203,
			141
		]);
		this._key = this._expandKey(key);
	}
	_expandKey(cipherKey) {
		const b = 176;
		const s = this._s;
		const rcon = this._rcon;
		const result = new Uint8Array(b);
		result.set(cipherKey);
		for (let j = 16, i = 1; j < b; ++i) {
			let t1 = result[j - 3];
			let t2 = result[j - 2];
			let t3 = result[j - 1];
			let t4 = result[j - 4];
			t1 = s[t1];
			t2 = s[t2];
			t3 = s[t3];
			t4 = s[t4];
			t1 ^= rcon[i];
			for (let n = 0; n < 4; ++n) {
				result[j] = t1 ^= result[j - 16];
				j++;
				result[j] = t2 ^= result[j - 16];
				j++;
				result[j] = t3 ^= result[j - 16];
				j++;
				result[j] = t4 ^= result[j - 16];
				j++;
			}
		}
		return result;
	}
};
var AES256Cipher = class extends AESBaseCipher {
	constructor(key) {
		super();
		this._cyclesOfRepetition = 14;
		this._keySize = 224;
		this._key = this._expandKey(key);
	}
	_expandKey(cipherKey) {
		const b = 240;
		const s = this._s;
		const result = new Uint8Array(b);
		result.set(cipherKey);
		let r = 1;
		let t1 = 0, t2 = 0, t3 = 0, t4 = 0;
		for (let j = 32, i = 1; j < b; ++i) {
			if (j % 32 === 16) {
				t1 = s[t1];
				t2 = s[t2];
				t3 = s[t3];
				t4 = s[t4];
			} else if (j % 32 === 0) {
				t1 = result[j - 3];
				t2 = result[j - 2];
				t3 = result[j - 1];
				t4 = result[j - 4];
				t1 = s[t1];
				t2 = s[t2];
				t3 = s[t3];
				t4 = s[t4];
				t1 ^= r;
				if ((r <<= 1) >= 256) r = (r ^ 27) & 255;
			}
			for (let n = 0; n < 4; ++n) {
				result[j] = t1 ^= result[j - 32];
				j++;
				result[j] = t2 ^= result[j - 32];
				j++;
				result[j] = t3 ^= result[j - 32];
				j++;
				result[j] = t4 ^= result[j - 32];
				j++;
			}
		}
		return result;
	}
};
var PDF17 = class {
	checkOwnerPassword(password, ownerValidationSalt, userBytes, ownerPassword) {
		const hashData = new Uint8Array(password.length + 56);
		hashData.set(password, 0);
		hashData.set(ownerValidationSalt, password.length);
		hashData.set(userBytes, password.length + ownerValidationSalt.length);
		return isArrayEqual(calculateSHA256(hashData, 0, hashData.length), ownerPassword);
	}
	checkUserPassword(password, userValidationSalt, userPassword) {
		const hashData = new Uint8Array(password.length + 8);
		hashData.set(password, 0);
		hashData.set(userValidationSalt, password.length);
		return isArrayEqual(calculateSHA256(hashData, 0, hashData.length), userPassword);
	}
	getOwnerKey(password, ownerKeySalt, userBytes, ownerEncryption) {
		const hashData = new Uint8Array(password.length + 56);
		hashData.set(password, 0);
		hashData.set(ownerKeySalt, password.length);
		hashData.set(userBytes, password.length + ownerKeySalt.length);
		return new AES256Cipher(calculateSHA256(hashData, 0, hashData.length)).decryptBlock(ownerEncryption, false, /* @__PURE__ */ new Uint8Array(16));
	}
	getUserKey(password, userKeySalt, userEncryption) {
		const hashData = new Uint8Array(password.length + 8);
		hashData.set(password, 0);
		hashData.set(userKeySalt, password.length);
		return new AES256Cipher(calculateSHA256(hashData, 0, hashData.length)).decryptBlock(userEncryption, false, /* @__PURE__ */ new Uint8Array(16));
	}
};
var PDF20 = class {
	calculatePDF20Hash(password, input, userBytes) {
		let k = calculateSHA256(input, 0, input.length).subarray(0, 32);
		let e = new Uint8Array([0]);
		let i = 0;
		while (i < 64 || e[e.length - 1] > i - 32) {
			const combinedLength = password.length + k.length + userBytes.length, combinedArray = new Uint8Array(combinedLength);
			let writeOffset = 0;
			combinedArray.set(password, writeOffset);
			writeOffset += password.length;
			combinedArray.set(k, writeOffset);
			writeOffset += k.length;
			combinedArray.set(userBytes, writeOffset);
			const k1 = new Uint8Array(combinedLength * 64);
			for (let j = 0, pos = 0; j < 64; j++, pos += combinedLength) k1.set(combinedArray, pos);
			e = new AES128Cipher(k.subarray(0, 16)).encrypt(k1, k.subarray(16, 32));
			const remainder = e.slice(0, 16).reduce((a, b) => a + b, 0) % 3;
			if (remainder === 0) k = calculateSHA256(e, 0, e.length);
			else if (remainder === 1) k = calculateSHA384(e, 0, e.length);
			else if (remainder === 2) k = calculateSHA512(e, 0, e.length);
			i++;
		}
		return k.subarray(0, 32);
	}
	hash(password, concatBytes, userBytes) {
		return this.calculatePDF20Hash(password, concatBytes, userBytes);
	}
	checkOwnerPassword(password, ownerValidationSalt, userBytes, ownerPassword) {
		const hashData = new Uint8Array(password.length + 56);
		hashData.set(password, 0);
		hashData.set(ownerValidationSalt, password.length);
		hashData.set(userBytes, password.length + ownerValidationSalt.length);
		return isArrayEqual(this.calculatePDF20Hash(password, hashData, userBytes), ownerPassword);
	}
	checkUserPassword(password, userValidationSalt, userPassword) {
		const hashData = new Uint8Array(password.length + 8);
		hashData.set(password, 0);
		hashData.set(userValidationSalt, password.length);
		return isArrayEqual(this.calculatePDF20Hash(password, hashData, /* @__PURE__ */ new Uint8Array()), userPassword);
	}
	getOwnerKey(password, ownerKeySalt, userBytes, ownerEncryption) {
		const hashData = new Uint8Array(password.length + 56);
		hashData.set(password, 0);
		hashData.set(ownerKeySalt, password.length);
		hashData.set(userBytes, password.length + ownerKeySalt.length);
		return new AES256Cipher(this.calculatePDF20Hash(password, hashData, userBytes)).decryptBlock(ownerEncryption, false, /* @__PURE__ */ new Uint8Array(16));
	}
	getUserKey(password, userKeySalt, userEncryption) {
		const hashData = new Uint8Array(password.length + 8);
		hashData.set(password, 0);
		hashData.set(userKeySalt, password.length);
		return new AES256Cipher(this.calculatePDF20Hash(password, hashData, /* @__PURE__ */ new Uint8Array())).decryptBlock(userEncryption, false, /* @__PURE__ */ new Uint8Array(16));
	}
};
var CipherTransform = class {
	constructor(stringCipherConstructor, streamCipherConstructor) {
		_defineProperty(this, "StringCipherConstructor", void 0);
		_defineProperty(this, "StreamCipherConstructor", void 0);
		this.StringCipherConstructor = stringCipherConstructor;
		this.StreamCipherConstructor = streamCipherConstructor;
	}
	createStream(stream, length) {
		const cipher = this.StreamCipherConstructor();
		return new DecryptStream(stream, function cipherTransformDecryptStream(data, finalize) {
			return cipher.decryptBlock(data, finalize);
		}, length);
	}
	decryptString(s) {
		const cipher = this.StringCipherConstructor();
		let data = stringAsByteArray(s);
		data = cipher.decryptBlock(data, true);
		return arrayAsString(data);
	}
	decryptBytes(d) {
		return this.StringCipherConstructor().decryptBlock(d, true);
	}
	encryptString(s) {
		const cipher = this.StringCipherConstructor();
		if (cipher instanceof AESBaseCipher) {
			const pad = 16 - s.length % 16;
			s += String.fromCharCode(pad).repeat(pad);
			const iv = /* @__PURE__ */ new Uint8Array(16);
			if (typeof crypto !== "undefined") crypto.getRandomValues(iv);
			else for (let i = 0; i < 16; i++) iv[i] = Math.floor(256 * Math.random());
			let data = stringAsByteArray(s);
			data = cipher.encrypt(data, iv);
			const buf = new Uint8Array(16 + data.length);
			buf.set(iv);
			buf.set(data, 16);
			return arrayAsString(buf);
		}
		let data = stringAsByteArray(s);
		data = cipher.encrypt(data);
		return arrayAsString(data);
	}
};
var CipherTransformFactory = class {
	constructor(dict, fileIdBytes, password) {
		_defineProperty(this, "encryptMetadata", void 0);
		_defineProperty(this, "encryptionKey", void 0);
		_defineProperty(this, "algorithm", void 0);
		_defineProperty(this, "filterName", void 0);
		_defineProperty(this, "dict", void 0);
		_defineProperty(this, "cf", void 0);
		_defineProperty(this, "stmf", void 0);
		_defineProperty(this, "strf", void 0);
		_defineProperty(this, "eff", void 0);
		_defineProperty(this, "defaultPasswordBytes", new Uint8Array([
			40,
			191,
			78,
			94,
			78,
			117,
			138,
			65,
			100,
			0,
			78,
			86,
			255,
			250,
			1,
			8,
			46,
			46,
			0,
			182,
			208,
			104,
			62,
			128,
			47,
			12,
			169,
			254,
			100,
			83,
			105,
			122
		]));
		_defineProperty(this, "identityName", PDFName.of("Identity"));
		const filter = dict.get(PDFName.of("Filter"));
		if (filter.asString() !== "/Standard") throw new Error("unknown encryption method");
		this.filterName = filter.asString();
		this.dict = dict;
		const algorithm = dict.get(PDFName.of("V")).asNumber();
		if (!Number.isInteger(algorithm) || algorithm !== 1 && algorithm !== 2 && algorithm !== 4 && algorithm !== 5) throw new Error("unsupported encryption algorithm");
		this.algorithm = algorithm;
		let keyLength = dict.get(PDFName.of("Length")).asNumber();
		if (!keyLength) if (algorithm <= 3) keyLength = 40;
		else {
			const cfDict = dict.get(PDFName.of("CF"));
			const streamCryptoName = dict.get(PDFName.of("StmF"));
			if (cfDict instanceof PDFDict && streamCryptoName instanceof PDFName) {
				cfDict.suppressEncryption = true;
				const handlerDict = cfDict.get(PDFName.of(streamCryptoName.asString()));
				let keyLen = null;
				if (handlerDict) keyLen = handlerDict.get(PDFName.of("Length"));
				keyLength = keyLen && keyLen.asNumber() || 128;
				if (keyLength < 40) keyLength <<= 3;
			}
		}
		if (!Number.isInteger(keyLength) || keyLength < 40 || keyLength % 8 !== 0) throw new Error("invalid key length");
		const oPdfStr = dict.get(PDFName.of("O")).asBytes();
		const uPdfStr = dict.get(PDFName.of("U")).asBytes();
		const ownerPassword = oPdfStr.subarray(0, 32);
		const userPassword = uPdfStr.subarray(0, 32);
		const flags = dict.get(PDFName.of("P")).asNumber();
		const revision = dict.get(PDFName.of("R")).asNumber();
		const encryptMetadata = (algorithm === 4 || algorithm === 5) && dict.get(PDFName.of("EncryptMetadata"))?.asBoolean() !== false;
		this.encryptMetadata = encryptMetadata;
		let passwordBytes;
		if (password) {
			if (revision === 6) try {
				password = unescape(encodeURIComponent(password));
			} catch {
				console.warn("CipherTransformFactory: Unable to convert UTF8 encoded password.");
			}
			passwordBytes = stringAsByteArray(password);
		}
		let encryptionKey;
		if (algorithm !== 5) encryptionKey = this.prepareKeyData(fileIdBytes, passwordBytes, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata);
		else {
			const ownerValidationSalt = oPdfStr.subarray(32, 40);
			const ownerKeySalt = oPdfStr.subarray(40, 48);
			const uBytes = uPdfStr.subarray(0, 48);
			const userValidationSalt = uPdfStr.subarray(32, 40);
			const userKeySalt = uPdfStr.subarray(40, 48);
			const ownerEncryption = dict.get(PDFName.of("OE")).asBytes();
			const userEncryption = dict.get(PDFName.of("UE")).asBytes();
			const perms = dict.get(PDFName.of("Perms")).asBytes();
			encryptionKey = this.createEncryptionKey20(revision, passwordBytes, ownerPassword, ownerValidationSalt, ownerKeySalt, uBytes, userPassword, userValidationSalt, userKeySalt, ownerEncryption, userEncryption, perms);
		}
		if (!encryptionKey && !password) throw new Error("NEEDS PASSWORD");
		else if (!encryptionKey && password) {
			const decodedPassword = this.decodeUserPassword(passwordBytes, ownerPassword, revision, keyLength);
			encryptionKey = this.prepareKeyData(fileIdBytes, decodedPassword, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata);
		}
		if (!encryptionKey) throw new Error("Password incorrect");
		this.encryptionKey = encryptionKey;
		if (algorithm >= 4) {
			const cf = dict.get(PDFName.of("CF"));
			if (cf instanceof PDFDict) cf.suppressEncryption = true;
			this.cf = cf;
			this.stmf = dict.get(PDFName.of("StmF")) || this.identityName;
			this.strf = dict.get(PDFName.of("StrF")) || this.identityName;
			this.eff = dict.get(PDFName.of("EFF")) || this.stmf;
		}
	}
	createCipherTransform(num, gen) {
		if (this.algorithm === 4 || this.algorithm === 5) return new CipherTransform(this.buildCipherConstructor(this.cf, this.strf, num, gen, this.encryptionKey), this.buildCipherConstructor(this.cf, this.stmf, num, gen, this.encryptionKey));
		const key = this.buildObjectKey(num, gen, this.encryptionKey, false);
		const cipherConstructor = function buildCipherCipherConstructor() {
			return new ARCFourCipher(key);
		};
		return new CipherTransform(cipherConstructor, cipherConstructor);
	}
	createEncryptionKey20(revision, password, ownerPassword, ownerValidationSalt, ownerKeySalt, uBytes, userPassword, userValidationSalt, userKeySalt, ownerEncryption, userEncryption, _perms) {
		if (password) {
			const passwordLength = Math.min(127, password.length);
			password = password.subarray(0, passwordLength);
		} else password = /* @__PURE__ */ new Uint8Array();
		let pdfAlgorithm;
		if (revision === 6) pdfAlgorithm = new PDF20();
		else pdfAlgorithm = new PDF17();
		if (pdfAlgorithm.checkUserPassword(password, userValidationSalt, userPassword)) return pdfAlgorithm.getUserKey(password, userKeySalt, userEncryption);
		else if (password.length && pdfAlgorithm.checkOwnerPassword(password, ownerValidationSalt, uBytes, ownerPassword)) return pdfAlgorithm.getOwnerKey(password, ownerKeySalt, uBytes, ownerEncryption);
		return null;
	}
	prepareKeyData(fileId, password, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata) {
		const hashDataSize = 40 + ownerPassword.length + fileId.length;
		const hashData = new Uint8Array(hashDataSize);
		let i = 0, j, n;
		if (password) {
			n = Math.min(32, password.length);
			for (; i < n; ++i) hashData[i] = password[i];
		}
		j = 0;
		while (i < 32) hashData[i++] = this.defaultPasswordBytes[j++];
		for (j = 0, n = ownerPassword.length; j < n; ++j) hashData[i++] = ownerPassword[j];
		hashData[i++] = flags & 255;
		hashData[i++] = flags >> 8 & 255;
		hashData[i++] = flags >> 16 & 255;
		hashData[i++] = flags >>> 24 & 255;
		for (j = 0, n = fileId.length; j < n; ++j) hashData[i++] = fileId[j];
		if (revision >= 4 && !encryptMetadata) {
			hashData[i++] = 255;
			hashData[i++] = 255;
			hashData[i++] = 255;
			hashData[i++] = 255;
		}
		let hash = calculateMD5(hashData, 0, i);
		const keyLengthInBytes = keyLength >> 3;
		if (revision >= 3) for (j = 0; j < 50; ++j) hash = calculateMD5(hash, 0, keyLengthInBytes);
		const encryptionKey = hash.subarray(0, keyLengthInBytes);
		let cipher, checkData;
		if (revision >= 3) {
			for (i = 0; i < 32; ++i) hashData[i] = this.defaultPasswordBytes[i];
			for (j = 0, n = fileId.length; j < n; ++j) hashData[i++] = fileId[j];
			cipher = new ARCFourCipher(encryptionKey);
			checkData = cipher.encryptBlock(calculateMD5(hashData, 0, i));
			n = encryptionKey.length;
			const derivedKey = new Uint8Array(n);
			for (j = 1; j <= 19; ++j) {
				for (let k = 0; k < n; ++k) derivedKey[k] = encryptionKey[k] ^ j;
				cipher = new ARCFourCipher(derivedKey);
				checkData = cipher.encryptBlock(checkData);
			}
			for (j = 0, n = checkData.length; j < n; ++j) if (userPassword[j] !== checkData[j]) return null;
		} else {
			cipher = new ARCFourCipher(encryptionKey);
			checkData = cipher.encryptBlock(this.defaultPasswordBytes);
			for (j = 0, n = checkData.length; j < n; ++j) if (userPassword[j] !== checkData[j]) return null;
		}
		return encryptionKey;
	}
	decodeUserPassword(password, ownerPassword, revision, keyLength) {
		const hashData = /* @__PURE__ */ new Uint8Array(32);
		let i = 0;
		const n = Math.min(32, password.length);
		for (; i < n; ++i) hashData[i] = password[i];
		let j = 0;
		while (i < 32) hashData[i++] = this.defaultPasswordBytes[j++];
		let hash = calculateMD5(hashData, 0, i);
		const keyLengthInBytes = keyLength >> 3;
		if (revision >= 3) for (j = 0; j < 50; ++j) hash = calculateMD5(hash, 0, hash.length);
		let cipher, userPassword;
		if (revision >= 3) {
			userPassword = ownerPassword;
			const derivedKey = new Uint8Array(keyLengthInBytes);
			for (j = 19; j >= 0; j--) {
				for (let k = 0; k < keyLengthInBytes; ++k) derivedKey[k] = hash[k] ^ j;
				cipher = new ARCFourCipher(derivedKey);
				userPassword = cipher.encryptBlock(userPassword);
			}
		} else {
			cipher = new ARCFourCipher(hash.subarray(0, keyLengthInBytes));
			userPassword = cipher.encryptBlock(ownerPassword);
		}
		return userPassword;
	}
	buildObjectKey(num, gen, encryptionKey, isAes = false) {
		const key = new Uint8Array(encryptionKey.length + 9);
		const n = encryptionKey.length;
		let i;
		for (i = 0; i < n; ++i) key[i] = encryptionKey[i];
		key[i++] = num & 255;
		key[i++] = num >> 8 & 255;
		key[i++] = num >> 16 & 255;
		key[i++] = gen & 255;
		key[i++] = gen >> 8 & 255;
		if (isAes) {
			key[i++] = 115;
			key[i++] = 65;
			key[i++] = 108;
			key[i++] = 84;
		}
		return calculateMD5(key, 0, i).subarray(0, Math.min(encryptionKey.length + 5, 16));
	}
	buildCipherConstructor(cf, name, num, gen, key) {
		if (!(name instanceof PDFName)) throw new Error("Invalid crypt filter name.");
		const cryptFilter = cf.get(PDFName.of(name.asString().replace("/", "")));
		let cfm;
		if (cryptFilter !== null && cryptFilter !== void 0) cfm = cryptFilter.get(PDFName.of("CFM"));
		if (!cfm || cfm.asString() === "/None") return function cipherTransformFactoryBuildCipherConstructorNone() {
			return new NullCipher();
		};
		if (cfm.asString() === "/V2") return () => new ARCFourCipher(this.buildObjectKey(num, gen, key, false));
		if (cfm.asString() === "/AESV2") return () => new AES128Cipher(this.buildObjectKey(num, gen, key, true));
		if (cfm.asString() === "/AESV3") return () => new AES256Cipher(key);
		throw new Error("Unknown crypto method");
	}
};
//#endregion
//#region src/api/PDFDocument.ts
/**
* Represents a PDF document.
*/
var PDFDocument = class PDFDocument {
	/**
	* Load an existing [[PDFDocument]]. The input data can be provided in
	* multiple formats:
	*
	* | Type          | Contents                                               |
	* | ------------- | ------------------------------------------------------ |
	* | `string`      | A base64 encoded string (or data URI) containing a PDF |
	* | `Uint8Array`  | The raw bytes of a PDF                                 |
	* | `ArrayBuffer` | The raw bytes of a PDF                                 |
	*
	* For example:
	* ```js
	* import { PDFDocument } from 'pdf-lib'
	*
	* // pdf=string
	* const base64 =
	*  'JVBERi0xLjcKJYGBgYEKCjUgMCBvYmoKPDwKL0ZpbHRlciAvRmxhdGVEZWNvZGUKL0xlbm' +
	*  'd0aCAxMDQKPj4Kc3RyZWFtCniccwrhMlAAwaJ0Ln2P1Jyy1JLM5ERdc0MjCwUjE4WQNC4Q' +
	*  '6cNlCFZkqGCqYGSqEJLLZWNuYGZiZmbkYuZsZmlmZGRgZmluDCQNzc3NTM2NzdzMXMxMjQ' +
	*  'ztFEKyuEK0uFxDuAAOERdVCmVuZHN0cmVhbQplbmRvYmoKCjYgMCBvYmoKPDwKL0ZpbHRl' +
	*  'ciAvRmxhdGVEZWNvZGUKL1R5cGUgL09ialN0bQovTiA0Ci9GaXJzdCAyMAovTGVuZ3RoID' +
	*  'IxNQo+PgpzdHJlYW0KeJxVj9GqwjAMhu/zFHkBzTo3nCCCiiKIHPEICuJF3cKoSCu2E8/b' +
	*  '20wPIr1p8v9/8kVhgilmGfawX2CGaVrgcAi0/bsy0lrX7IGWpvJ4iJYEN3gEmrrGBlQwGs' +
	*  'HHO9VBX1wNrxAqMX87RBD5xpJuddqwd82tjAHxzV1U5LPgy52DKXWnr1Lheg+j/c/pzGVr' +
	*  'iqV0VlwZPXGPCJjElw/ybkwUmeoWgxesDXGhHJC/D/iikp1Av80ptKU0FdBEe25pPihAM1' +
	*  'u6ytgaaWfs2Hrz35CJT1+EWmAKZW5kc3RyZWFtCmVuZG9iagoKNyAwIG9iago8PAovU2l6' +
	*  'ZSA4Ci9Sb290IDIgMCBSCi9GaWx0ZXIgL0ZsYXRlRGVjb2RlCi9UeXBlIC9YUmVmCi9MZW' +
	*  '5ndGggMzgKL1cgWyAxIDIgMiBdCi9JbmRleCBbIDAgOCBdCj4+CnN0cmVhbQp4nBXEwREA' +
	*  'EBAEsCwz3vrvRmOOyyOoGhZdutHN2MT55fIAVocD+AplbmRzdHJlYW0KZW5kb2JqCgpzdG' +
	*  'FydHhyZWYKNTEwCiUlRU9G'
	*
	* const dataUri = 'data:application/pdf;base64,' + base64
	*
	* const pdfDoc1 = await PDFDocument.load(base64)
	* const pdfDoc2 = await PDFDocument.load(dataUri)
	*
	* // pdf=Uint8Array
	* import fs from 'fs'
	* const uint8Array = fs.readFileSync('with_update_sections.pdf')
	* const pdfDoc3 = await PDFDocument.load(uint8Array)
	*
	* // pdf=ArrayBuffer
	* const url = 'https://pdf-lib.js.org/assets/with_update_sections.pdf'
	* const arrayBuffer = await fetch(url).then(res => res.arrayBuffer())
	* const pdfDoc4 = await PDFDocument.load(arrayBuffer)
	*
	* ```
	*
	* @param pdf The input data containing a PDF document.
	* @param options The options to be used when loading the document.
	* @returns Resolves with a document loaded from the input.
	*/
	static async load(pdf, options = {}) {
		const { ignoreEncryption = false, parseSpeed = ParseSpeeds.Slow, throwOnInvalidObject = false, updateMetadata = true, capNumbers = false, password } = options;
		assertIs(pdf, "pdf", [
			"string",
			Uint8Array,
			ArrayBuffer
		]);
		assertIs(ignoreEncryption, "ignoreEncryption", ["boolean"]);
		assertIs(parseSpeed, "parseSpeed", ["number"]);
		assertIs(throwOnInvalidObject, "throwOnInvalidObject", ["boolean"]);
		assertIs(password, "password", ["string", "undefined"]);
		const bytes = toUint8Array(pdf);
		const context = await PDFParser.forBytesWithOptions(bytes, parseSpeed, throwOnInvalidObject, capNumbers).parseDocument();
		if (!!context.lookup(context.trailerInfo.Encrypt) && password !== void 0) {
			const fileIds = context.lookup(context.trailerInfo.ID, PDFArray);
			const encryptDict = context.lookup(context.trailerInfo.Encrypt, PDFDict);
			return new PDFDocument(await PDFParser.forBytesWithOptions(bytes, parseSpeed, throwOnInvalidObject, capNumbers, new CipherTransformFactory(encryptDict, fileIds.get(0).asBytes(), password)).parseDocument(), true, updateMetadata);
		} else return new PDFDocument(context, ignoreEncryption, updateMetadata);
	}
	/**
	* Create a new [[PDFDocument]].
	* @returns Resolves with the newly created document.
	*/
	static async create(options = {}) {
		const { updateMetadata = true } = options;
		const context = PDFContext.create();
		const pageTree = PDFPageTree.withContext(context);
		const pageTreeRef = context.register(pageTree);
		const catalog = PDFCatalog.withContextAndPages(context, pageTreeRef);
		context.trailerInfo.Root = context.register(catalog);
		return new PDFDocument(context, false, updateMetadata);
	}
	constructor(context, ignoreEncryption, updateMetadata) {
		_defineProperty(
			this,
			/** The low-level context of this document. */
			"context",
			void 0
		);
		_defineProperty(
			this,
			/** The catalog of this document. */
			"catalog",
			void 0
		);
		_defineProperty(
			this,
			/** Whether or not this document is encrypted. */
			"isEncrypted",
			void 0
		);
		_defineProperty(
			this,
			/** The default word breaks used in PDFPage.drawText */
			"defaultWordBreaks",
			[" "]
		);
		_defineProperty(this, "fontkit", void 0);
		_defineProperty(this, "pageCount", void 0);
		_defineProperty(this, "pageCache", void 0);
		_defineProperty(this, "pageMap", void 0);
		_defineProperty(this, "formCache", void 0);
		_defineProperty(this, "fonts", void 0);
		_defineProperty(this, "images", void 0);
		_defineProperty(this, "embeddedPages", void 0);
		_defineProperty(this, "embeddedFiles", void 0);
		_defineProperty(this, "javaScripts", void 0);
		_defineProperty(this, "computePages", () => {
			const pages = [];
			this.catalog.Pages().traverse((node, ref) => {
				if (node instanceof PDFPageLeaf) {
					let page = this.pageMap.get(node);
					if (!page) {
						page = PDFPage.of(node, ref, this);
						this.pageMap.set(node, page);
					}
					pages.push(page);
				}
			});
			return pages;
		});
		_defineProperty(this, "getOrCreateForm", () => {
			const acroForm = this.catalog.getOrCreateAcroForm();
			return PDFForm.of(acroForm, this);
		});
		assertIs(context, "context", [[PDFContext, "PDFContext"]]);
		assertIs(ignoreEncryption, "ignoreEncryption", ["boolean"]);
		this.context = context;
		this.catalog = context.lookup(context.trailerInfo.Root);
		if (!!context.lookup(context.trailerInfo.Encrypt) && context.isDecrypted) delete context.trailerInfo.Encrypt;
		this.isEncrypted = !!context.lookup(context.trailerInfo.Encrypt);
		this.pageCache = Cache.populatedBy(this.computePages);
		this.pageMap = /* @__PURE__ */ new Map();
		this.formCache = Cache.populatedBy(this.getOrCreateForm);
		this.fonts = [];
		this.images = [];
		this.embeddedPages = [];
		this.embeddedFiles = [];
		this.javaScripts = [];
		if (!ignoreEncryption && this.isEncrypted) throw new EncryptedPDFError();
		if (updateMetadata) this.updateInfoDict();
	}
	/**
	* Register a fontkit instance. This must be done before custom fonts can
	* be embedded. See [here](https://github.com/Hopding/pdf-lib/tree/master#fontkit-installation)
	* for instructions on how to install and register a fontkit instance.
	*
	* > You do **not** need to call this method to embed standard fonts.
	*
	* For example:
	* ```js
	* import { PDFDocument } from 'pdf-lib'
	* import fontkit from '@pdf-lib/fontkit'
	*
	* const pdfDoc = await PDFDocument.create()
	* pdfDoc.registerFontkit(fontkit)
	* ```
	*
	* @param fontkit The fontkit instance to be registered.
	*/
	registerFontkit(fontkit) {
		this.fontkit = fontkit;
	}
	/**
	* Get the [[PDFForm]] containing all interactive fields for this document.
	* For example:
	* ```js
	* const form = pdfDoc.getForm()
	* const fields = form.getFields()
	* fields.forEach(field => {
	*   const type = field.constructor.name
	*   const name = field.getName()
	*   console.log(`${type}: ${name}`)
	* })
	* ```
	* @returns The form for this document.
	*/
	getForm() {
		const form = this.formCache.access();
		if (form.hasXFA()) {
			console.warn("Removing XFA form data as pdf-lib does not support reading or writing XFA");
			form.deleteXFA();
		}
		return form;
	}
	/**
	* Get this document's title metadata. The title appears in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const title = pdfDoc.getTitle()
	* ```
	* @returns A string containing the title of this document, if it has one.
	*/
	getTitle() {
		const title = this.getInfoDict().lookup(PDFName.Title);
		if (!title) return void 0;
		assertIsLiteralOrHexString(title);
		return title.decodeText();
	}
	/**
	* Get this document's author metadata. The author appears in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const author = pdfDoc.getAuthor()
	* ```
	* @returns A string containing the author of this document, if it has one.
	*/
	getAuthor() {
		const author = this.getInfoDict().lookup(PDFName.Author);
		if (!author) return void 0;
		assertIsLiteralOrHexString(author);
		return author.decodeText();
	}
	/**
	* Get this document's subject metadata. The subject appears in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const subject = pdfDoc.getSubject()
	* ```
	* @returns A string containing the subject of this document, if it has one.
	*/
	getSubject() {
		const subject = this.getInfoDict().lookup(PDFName.Subject);
		if (!subject) return void 0;
		assertIsLiteralOrHexString(subject);
		return subject.decodeText();
	}
	/**
	* Get this document's keywords metadata. The keywords appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const keywords = pdfDoc.getKeywords()
	* ```
	* @returns A string containing the keywords of this document, if it has any.
	*/
	getKeywords() {
		const keywords = this.getInfoDict().lookup(PDFName.Keywords);
		if (!keywords) return void 0;
		assertIsLiteralOrHexString(keywords);
		return keywords.decodeText();
	}
	/**
	* Get this document's creator metadata. The creator appears in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const creator = pdfDoc.getCreator()
	* ```
	* @returns A string containing the creator of this document, if it has one.
	*/
	getCreator() {
		const creator = this.getInfoDict().lookup(PDFName.Creator);
		if (!creator) return void 0;
		assertIsLiteralOrHexString(creator);
		return creator.decodeText();
	}
	/**
	* Get this document's producer metadata. The producer appears in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* const producer = pdfDoc.getProducer()
	* ```
	* @returns A string containing the producer of this document, if it has one.
	*/
	getProducer() {
		const producer = this.getInfoDict().lookup(PDFName.Producer);
		if (!producer) return void 0;
		assertIsLiteralOrHexString(producer);
		return producer.decodeText();
	}
	/**
	* Get this document's creation date metadata. The creation date appears in
	* the "Document Properties" section of most PDF readers. For example:
	* ```js
	* const creationDate = pdfDoc.getCreationDate()
	* ```
	* @returns A Date containing the creation date of this document,
	*          if it has one.
	*/
	getCreationDate() {
		const creationDate = this.getInfoDict().lookup(PDFName.CreationDate);
		if (!creationDate) return void 0;
		assertIsLiteralOrHexString(creationDate);
		return creationDate.decodeDate();
	}
	/**
	* Get this document's modification date metadata. The modification date
	* appears in the "Document Properties" section of most PDF readers.
	* For example:
	* ```js
	* const modification = pdfDoc.getModificationDate()
	* ```
	* @returns A Date containing the modification date of this document,
	*          if it has one.
	*/
	getModificationDate() {
		const modificationDate = this.getInfoDict().lookup(PDFName.ModDate);
		if (!modificationDate) return void 0;
		assertIsLiteralOrHexString(modificationDate);
		return modificationDate.decodeDate();
	}
	/**
	* Set this document's title metadata. The title will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setTitle('🥚 The Life of an Egg 🍳')
	* ```
	*
	* To display the title in the window's title bar, set the
	* `showInWindowTitleBar` option to `true` (works for _most_ PDF readers).
	* For example:
	* ```js
	* pdfDoc.setTitle('🥚 The Life of an Egg 🍳', { showInWindowTitleBar: true })
	* ```
	*
	* @param title The title of this document.
	* @param options The options to be used when setting the title.
	*/
	setTitle(title, options) {
		assertIs(title, "title", ["string"]);
		const key = PDFName.of("Title");
		this.getInfoDict().set(key, PDFHexString.fromText(title));
		if (options?.showInWindowTitleBar) this.catalog.getOrCreateViewerPreferences().setDisplayDocTitle(true);
	}
	/**
	* Set this document's author metadata. The author will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setAuthor('Humpty Dumpty')
	* ```
	* @param author The author of this document.
	*/
	setAuthor(author) {
		assertIs(author, "author", ["string"]);
		const key = PDFName.of("Author");
		this.getInfoDict().set(key, PDFHexString.fromText(author));
	}
	/**
	* Set this document's subject metadata. The subject will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setSubject('📘 An Epic Tale of Woe 📖')
	* ```
	* @param subject The subject of this document.
	*/
	setSubject(subject) {
		assertIs(subject, "author", ["string"]);
		const key = PDFName.of("Subject");
		this.getInfoDict().set(key, PDFHexString.fromText(subject));
	}
	/**
	* Set this document's keyword metadata. These keywords will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setKeywords(['eggs', 'wall', 'fall', 'king', 'horses', 'men'])
	* ```
	* @param keywords An array of keywords associated with this document.
	*/
	setKeywords(keywords) {
		assertIs(keywords, "keywords", [Array]);
		const key = PDFName.of("Keywords");
		this.getInfoDict().set(key, PDFHexString.fromText(keywords.join(" ")));
	}
	/**
	* Set this document's creator metadata. The creator will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setCreator('PDF App 9000 🤖')
	* ```
	* @param creator The creator of this document.
	*/
	setCreator(creator) {
		assertIs(creator, "creator", ["string"]);
		const key = PDFName.of("Creator");
		this.getInfoDict().set(key, PDFHexString.fromText(creator));
	}
	/**
	* Set this document's producer metadata. The producer will appear in the
	* "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setProducer('PDF App 9000 🤖')
	* ```
	* @param producer The producer of this document.
	*/
	setProducer(producer) {
		assertIs(producer, "creator", ["string"]);
		const key = PDFName.of("Producer");
		this.getInfoDict().set(key, PDFHexString.fromText(producer));
	}
	/**
	* Set this document's language metadata. The language will appear in the
	* "Document Properties" section of some PDF readers. For example:
	* ```js
	* pdfDoc.setLanguage('en-us')
	* ```
	*
	* @param language An RFC 3066 _Language-Tag_ denoting the language of this
	*                 document, or an empty string if the language is unknown.
	*/
	setLanguage(language) {
		assertIs(language, "language", ["string"]);
		const key = PDFName.of("Lang");
		this.catalog.set(key, PDFString.of(language));
	}
	/**
	* Set this document's creation date metadata. The creation date will appear
	* in the "Document Properties" section of most PDF readers. For example:
	* ```js
	* pdfDoc.setCreationDate(new Date())
	* ```
	* @param creationDate The date this document was created.
	*/
	setCreationDate(creationDate) {
		assertIs(creationDate, "creationDate", [[Date, "Date"]]);
		const key = PDFName.of("CreationDate");
		this.getInfoDict().set(key, PDFString.fromDate(creationDate));
	}
	/**
	* Set this document's modification date metadata. The modification date will
	* appear in the "Document Properties" section of most PDF readers. For
	* example:
	* ```js
	* pdfDoc.setModificationDate(new Date())
	* ```
	* @param modificationDate The date this document was last modified.
	*/
	setModificationDate(modificationDate) {
		assertIs(modificationDate, "modificationDate", [[Date, "Date"]]);
		const key = PDFName.of("ModDate");
		this.getInfoDict().set(key, PDFString.fromDate(modificationDate));
	}
	/**
	* Get the number of pages contained in this document. For example:
	* ```js
	* const totalPages = pdfDoc.getPageCount()
	* ```
	* @returns The number of pages in this document.
	*/
	getPageCount() {
		if (this.pageCount === void 0) this.pageCount = this.getPages().length;
		return this.pageCount;
	}
	/**
	* Get an array of all the pages contained in this document. The pages are
	* stored in the array in the same order that they are rendered in the
	* document. For example:
	* ```js
	* const pages = pdfDoc.getPages()
	* pages[0]   // The first page of the document
	* pages[2]   // The third page of the document
	* pages[197] // The 198th page of the document
	* ```
	* @returns An array of all the pages contained in this document.
	*/
	getPages() {
		return this.pageCache.access();
	}
	/**
	* Get the page rendered at a particular `index` of the document. For example:
	* ```js
	* pdfDoc.getPage(0)   // The first page of the document
	* pdfDoc.getPage(2)   // The third page of the document
	* pdfDoc.getPage(197) // The 198th page of the document
	* ```
	* @returns The [[PDFPage]] rendered at the given `index` of the document.
	*/
	getPage(index) {
		const pages = this.getPages();
		assertRange(index, "index", 0, pages.length - 1);
		return pages[index];
	}
	/**
	* Get an array of indices for all the pages contained in this document. The
	* array will contain a range of integers from
	* `0..pdfDoc.getPageCount() - 1`. For example:
	* ```js
	* const pdfDoc = await PDFDocument.create()
	* pdfDoc.addPage()
	* pdfDoc.addPage()
	* pdfDoc.addPage()
	*
	* const indices = pdfDoc.getPageIndices()
	* indices // => [0, 1, 2]
	* ```
	* @returns An array of indices for all pages contained in this document.
	*/
	getPageIndices() {
		return range(0, this.getPageCount());
	}
	/**
	* Remove the page at a given index from this document. For example:
	* ```js
	* pdfDoc.removePage(0)   // Remove the first page of the document
	* pdfDoc.removePage(2)   // Remove the third page of the document
	* pdfDoc.removePage(197) // Remove the 198th page of the document
	* ```
	* Once a page has been removed, it will no longer be rendered at that index
	* in the document.
	* @param index The index of the page to be removed.
	*/
	removePage(index) {
		const pageCount = this.getPageCount();
		if (this.pageCount === 0) throw new RemovePageFromEmptyDocumentError();
		assertRange(index, "index", 0, pageCount - 1);
		this.catalog.removeLeafNode(index);
		this.pageCount = pageCount - 1;
	}
	/**
	* Add a page to the end of this document. This method accepts three
	* different value types for the `page` parameter:
	*
	* | Type               | Behavior                                                                            |
	* | ------------------ | ----------------------------------------------------------------------------------- |
	* | `undefined`        | Create a new page and add it to the end of this document                            |
	* | `[number, number]` | Create a new page with the given dimensions and add it to the end of this document  |
	* | `PDFPage`          | Add the existing page to the end of this document                                   |
	*
	* For example:
	* ```js
	* // page=undefined
	* const newPage = pdfDoc.addPage()
	*
	* // page=[number, number]
	* import { PageSizes } from 'pdf-lib'
	* const newPage1 = pdfDoc.addPage(PageSizes.A7)
	* const newPage2 = pdfDoc.addPage(PageSizes.Letter)
	* const newPage3 = pdfDoc.addPage([500, 750])
	*
	* // page=PDFPage
	* const pdfDoc1 = await PDFDocument.create()
	* const pdfDoc2 = await PDFDocument.load(...)
	* const [existingPage] = await pdfDoc1.copyPages(pdfDoc2, [0])
	* pdfDoc1.addPage(existingPage)
	* ```
	*
	* @param page Optionally, the desired dimensions or existing page.
	* @returns The newly created (or existing) page.
	*/
	addPage(page) {
		assertIs(page, "page", [
			"undefined",
			[PDFPage, "PDFPage"],
			Array
		]);
		return this.insertPage(this.getPageCount(), page);
	}
	/**
	* Insert a page at a given index within this document. This method accepts
	* three different value types for the `page` parameter:
	*
	* | Type               | Behavior                                                                       |
	* | ------------------ | ------------------------------------------------------------------------------ |
	* | `undefined`        | Create a new page and insert it into this document                             |
	* | `[number, number]` | Create a new page with the given dimensions and insert it into this document   |
	* | `PDFPage`          | Insert the existing page into this document                                    |
	*
	* For example:
	* ```js
	* // page=undefined
	* const newPage = pdfDoc.insertPage(2)
	*
	* // page=[number, number]
	* import { PageSizes } from 'pdf-lib'
	* const newPage1 = pdfDoc.insertPage(2, PageSizes.A7)
	* const newPage2 = pdfDoc.insertPage(0, PageSizes.Letter)
	* const newPage3 = pdfDoc.insertPage(198, [500, 750])
	*
	* // page=PDFPage
	* const pdfDoc1 = await PDFDocument.create()
	* const pdfDoc2 = await PDFDocument.load(...)
	* const [existingPage] = await pdfDoc1.copyPages(pdfDoc2, [0])
	* pdfDoc1.insertPage(0, existingPage)
	* ```
	*
	* @param index The index at which the page should be inserted (zero-based).
	* @param page Optionally, the desired dimensions or existing page.
	* @returns The newly created (or existing) page.
	*/
	insertPage(index, page) {
		const pageCount = this.getPageCount();
		assertRange(index, "index", 0, pageCount);
		assertIs(page, "page", [
			"undefined",
			[PDFPage, "PDFPage"],
			Array
		]);
		if (!page || Array.isArray(page)) {
			const dims = Array.isArray(page) ? page : PageSizes.A4;
			page = PDFPage.create(this);
			page.setSize(...dims);
		} else if (page.doc !== this) throw new ForeignPageError();
		const parentRef = this.catalog.insertLeafNode(page.ref, index);
		page.node.setParent(parentRef);
		this.pageMap.set(page.node, page);
		this.pageCache.invalidate();
		this.pageCount = pageCount + 1;
		return page;
	}
	/**
	* Copy pages from a source document into this document. Allows pages to be
	* copied between different [[PDFDocument]] instances. For example:
	* ```js
	* const pdfDoc = await PDFDocument.create()
	* const srcDoc = await PDFDocument.load(...)
	*
	* const copiedPages = await pdfDoc.copyPages(srcDoc, [0, 3, 89])
	* const [firstPage, fourthPage, ninetiethPage] = copiedPages;
	*
	* pdfDoc.addPage(fourthPage)
	* pdfDoc.insertPage(0, ninetiethPage)
	* pdfDoc.addPage(firstPage)
	* ```
	* @param srcDoc The document from which pages should be copied.
	* @param indices The indices of the pages that should be copied.
	* @returns Resolves with an array of pages copied into this document.
	*/
	async copyPages(srcDoc, indices) {
		assertIs(srcDoc, "srcDoc", [[PDFDocument, "PDFDocument"]]);
		assertIs(indices, "indices", [Array]);
		await srcDoc.flush();
		const copier = PDFObjectCopier.for(srcDoc.context, this.context);
		const srcPages = srcDoc.getPages();
		const copiedPages = Array(indices.length);
		for (let idx = 0, len = indices.length; idx < len; idx++) {
			const srcPage = srcPages[indices[idx]];
			const copiedPage = copier.copy(srcPage.node);
			const ref = this.context.register(copiedPage);
			copiedPages[idx] = PDFPage.of(copiedPage, ref, this);
		}
		return copiedPages;
	}
	/**
	* Get a copy of this document.
	*
	* For example:
	* ```js
	* const srcDoc = await PDFDocument.load(...)
	* const pdfDoc = await srcDoc.copy()
	* ```
	*
	* > **NOTE:**  This method won't copy all information over to the new
	* > document (acroforms, outlines, etc...).
	*
	* @returns Resolves with a copy this document.
	*/
	async copy() {
		const pdfCopy = await PDFDocument.create();
		const contentPages = await pdfCopy.copyPages(this, this.getPageIndices());
		for (let idx = 0, len = contentPages.length; idx < len; idx++) pdfCopy.addPage(contentPages[idx]);
		if (this.getAuthor() !== void 0) pdfCopy.setAuthor(this.getAuthor());
		if (this.getCreationDate() !== void 0) pdfCopy.setCreationDate(this.getCreationDate());
		if (this.getCreator() !== void 0) pdfCopy.setCreator(this.getCreator());
		if (this.getModificationDate() !== void 0) pdfCopy.setModificationDate(this.getModificationDate());
		if (this.getProducer() !== void 0) pdfCopy.setProducer(this.getProducer());
		if (this.getSubject() !== void 0) pdfCopy.setSubject(this.getSubject());
		if (this.getTitle() !== void 0) pdfCopy.setTitle(this.getTitle());
		pdfCopy.defaultWordBreaks = this.defaultWordBreaks;
		return pdfCopy;
	}
	/**
	* Add JavaScript to this document. The supplied `script` is executed when the
	* document is opened. The `script` can be used to perform some operation
	* when the document is opened (e.g. logging to the console), or it can be
	* used to define a function that can be referenced later in a JavaScript
	* action. For example:
	* ```js
	* // Show "Hello World!" in the console when the PDF is opened
	* pdfDoc.addJavaScript(
	*   'main',
	*   'console.show(); console.println("Hello World!");'
	* );
	*
	* // Define a function named "foo" that can be called in JavaScript Actions
	* pdfDoc.addJavaScript(
	*   'foo',
	*   'function foo() { return "foo"; }'
	* );
	* ```
	* See the [JavaScript for Acrobat API Reference](https://www.adobe.com/content/dam/acom/en/devnet/acrobat/pdfs/js_api_reference.pdf)
	* for details.
	* @param name The name of the script. Must be unique per document.
	* @param script The JavaScript to execute.
	*/
	addJavaScript(name, script) {
		assertIs(name, "name", ["string"]);
		assertIs(script, "script", ["string"]);
		const embedder = JavaScriptEmbedder.for(script, name);
		const ref = this.context.nextRef();
		const javaScript = PDFJavaScript.of(ref, this, embedder);
		this.javaScripts.push(javaScript);
	}
	/**
	* Add an attachment to this document. Attachments are visible in the
	* "Attachments" panel of Adobe Acrobat and some other PDF readers. Any
	* type of file can be added as an attachment. This includes, but is not
	* limited to, `.png`, `.jpg`, `.pdf`, `.csv`, `.docx`, and `.xlsx` files.
	*
	* The input data can be provided in multiple formats:
	*
	* | Type          | Contents                                                       |
	* | ------------- | -------------------------------------------------------------- |
	* | `string`      | A base64 encoded string (or data URI) containing an attachment |
	* | `Uint8Array`  | The raw bytes of an attachment                                 |
	* | `ArrayBuffer` | The raw bytes of an attachment                                 |
	*
	* For example:
	* ```js
	* // attachment=string
	* await pdfDoc.attach('/9j/4AAQSkZJRgABAQAAAQABAAD/2wBD...', 'cat_riding_unicorn.jpg', {
	*   mimeType: 'image/jpeg',
	*   description: 'Cool cat riding a unicorn! 🦄🐈🕶️',
	*   creationDate: new Date('2019/12/01'),
	*   modificationDate: new Date('2020/04/19'),
	* })
	* await pdfDoc.attach('data:image/jpeg;base64,/9j/4AAQ...', 'cat_riding_unicorn.jpg', {
	*   mimeType: 'image/jpeg',
	*   description: 'Cool cat riding a unicorn! 🦄🐈🕶️',
	*   creationDate: new Date('2019/12/01'),
	*   modificationDate: new Date('2020/04/19'),
	* })
	*
	* // attachment=Uint8Array
	* import fs from 'fs'
	* const uint8Array = fs.readFileSync('cat_riding_unicorn.jpg')
	* await pdfDoc.attach(uint8Array, 'cat_riding_unicorn.jpg', {
	*   mimeType: 'image/jpeg',
	*   description: 'Cool cat riding a unicorn! 🦄🐈🕶️',
	*   creationDate: new Date('2019/12/01'),
	*   modificationDate: new Date('2020/04/19'),
	* })
	*
	* // attachment=ArrayBuffer
	* const url = 'https://pdf-lib.js.org/assets/cat_riding_unicorn.jpg'
	* const arrayBuffer = await fetch(url).then(res => res.arrayBuffer())
	* await pdfDoc.attach(arrayBuffer, 'cat_riding_unicorn.jpg', {
	*   mimeType: 'image/jpeg',
	*   description: 'Cool cat riding a unicorn! 🦄🐈🕶️',
	*   creationDate: new Date('2019/12/01'),
	*   modificationDate: new Date('2020/04/19'),
	* })
	* ```
	*
	* @param attachment The input data containing the file to be attached.
	* @param name The name of the file to be attached.
	* @returns Resolves when the attachment is complete.
	*/
	async attach(attachment, name, options = {}) {
		assertIs(attachment, "attachment", [
			"string",
			Uint8Array,
			ArrayBuffer
		]);
		assertIs(name, "name", ["string"]);
		assertOrUndefined(options.mimeType, "mimeType", ["string"]);
		assertOrUndefined(options.description, "description", ["string"]);
		assertOrUndefined(options.creationDate, "options.creationDate", [Date]);
		assertOrUndefined(options.modificationDate, "options.modificationDate", [Date]);
		assertIsOneOfOrUndefined(options.afRelationship, "options.afRelationship", AFRelationship);
		const bytes = toUint8Array(attachment);
		const embedder = FileEmbedder.for(bytes, name, options);
		const ref = this.context.nextRef();
		const embeddedFile = PDFEmbeddedFile.of(ref, this, embedder);
		this.embeddedFiles.push(embeddedFile);
	}
	/**
	* Embed a font into this document. The input data can be provided in multiple
	* formats:
	*
	* | Type            | Contents                                                |
	* | --------------- | ------------------------------------------------------- |
	* | `StandardFonts` | One of the standard 14 fonts                            |
	* | `string`        | A base64 encoded string (or data URI) containing a font |
	* | `Uint8Array`    | The raw bytes of a font                                 |
	* | `ArrayBuffer`   | The raw bytes of a font                                 |
	*
	* For example:
	* ```js
	* // font=StandardFonts
	* import { StandardFonts } from 'pdf-lib'
	* const font1 = await pdfDoc.embedFont(StandardFonts.Helvetica)
	*
	* // font=string
	* const font2 = await pdfDoc.embedFont('AAEAAAAVAQAABABQRFNJRx/upe...')
	* const font3 = await pdfDoc.embedFont('data:font/opentype;base64,AAEAAA...')
	*
	* // font=Uint8Array
	* import fs from 'fs'
	* const font4 = await pdfDoc.embedFont(fs.readFileSync('Ubuntu-R.ttf'))
	*
	* // font=ArrayBuffer
	* const url = 'https://pdf-lib.js.org/assets/ubuntu/Ubuntu-R.ttf'
	* const ubuntuBytes = await fetch(url).then(res => res.arrayBuffer())
	* const font5 = await pdfDoc.embedFont(ubuntuBytes)
	* ```
	* See also: [[registerFontkit]]
	* @param font The input data for a font.
	* @param options The options to be used when embedding the font.
	* @returns Resolves with the embedded font.
	*/
	async embedFont(font, options = {}) {
		const { subset = false, customName, features } = options;
		assertIs(font, "font", [
			"string",
			Uint8Array,
			ArrayBuffer
		]);
		assertIs(subset, "subset", ["boolean"]);
		let embedder;
		if (isStandardFont(font)) embedder = StandardFontEmbedder.for(font, customName);
		else if (canBeConvertedToUint8Array(font)) {
			const bytes = toUint8Array(font);
			const fontkit = this.assertFontkit();
			embedder = subset ? await CustomFontSubsetEmbedder.for(fontkit, bytes, customName, features) : await CustomFontEmbedder.for(fontkit, bytes, customName, features);
		} else throw new TypeError("`font` must be one of `StandardFonts | string | Uint8Array | ArrayBuffer`");
		const ref = this.context.nextRef();
		const pdfFont = PDFFont.of(ref, this, embedder);
		this.fonts.push(pdfFont);
		return pdfFont;
	}
	/**
	* Embed a standard font into this document.
	* For example:
	* ```js
	* import { StandardFonts } from 'pdf-lib'
	* const helveticaFont = pdfDoc.embedFont(StandardFonts.Helvetica)
	* ```
	* @param font The standard font to be embedded.
	* @param customName The name to be used when embedding the font.
	* @returns The embedded font.
	*/
	embedStandardFont(font, customName) {
		assertIs(font, "font", ["string"]);
		if (!isStandardFont(font)) throw new TypeError("`font` must be one of type `StandardFonts`");
		const embedder = StandardFontEmbedder.for(font, customName);
		const ref = this.context.nextRef();
		const pdfFont = PDFFont.of(ref, this, embedder);
		this.fonts.push(pdfFont);
		return pdfFont;
	}
	/**
	* Embed a JPEG image into this document. The input data can be provided in
	* multiple formats:
	*
	* | Type          | Contents                                                      |
	* | ------------- | ------------------------------------------------------------- |
	* | `string`      | A base64 encoded string (or data URI) containing a JPEG image |
	* | `Uint8Array`  | The raw bytes of a JPEG image                                 |
	* | `ArrayBuffer` | The raw bytes of a JPEG image                                 |
	*
	* For example:
	* ```js
	* // jpg=string
	* const image1 = await pdfDoc.embedJpg('/9j/4AAQSkZJRgABAQAAAQABAAD/2wBD...')
	* const image2 = await pdfDoc.embedJpg('data:image/jpeg;base64,/9j/4AAQ...')
	*
	* // jpg=Uint8Array
	* import fs from 'fs'
	* const uint8Array = fs.readFileSync('cat_riding_unicorn.jpg')
	* const image3 = await pdfDoc.embedJpg(uint8Array)
	*
	* // jpg=ArrayBuffer
	* const url = 'https://pdf-lib.js.org/assets/cat_riding_unicorn.jpg'
	* const arrayBuffer = await fetch(url).then(res => res.arrayBuffer())
	* const image4 = await pdfDoc.embedJpg(arrayBuffer)
	* ```
	*
	* @param jpg The input data for a JPEG image.
	* @returns Resolves with the embedded image.
	*/
	async embedJpg(jpg) {
		assertIs(jpg, "jpg", [
			"string",
			Uint8Array,
			ArrayBuffer
		]);
		const bytes = toUint8Array(jpg);
		const embedder = await JpegEmbedder.for(bytes);
		const ref = this.context.nextRef();
		const pdfImage = PDFImage.of(ref, this, embedder);
		this.images.push(pdfImage);
		return pdfImage;
	}
	/**
	* Embed a PNG image into this document. The input data can be provided in
	* multiple formats:
	*
	* | Type          | Contents                                                     |
	* | ------------- | ------------------------------------------------------------ |
	* | `string`      | A base64 encoded string (or data URI) containing a PNG image |
	* | `Uint8Array`  | The raw bytes of a PNG image                                 |
	* | `ArrayBuffer` | The raw bytes of a PNG image                                 |
	*
	* For example:
	* ```js
	* // png=string
	* const image1 = await pdfDoc.embedPng('iVBORw0KGgoAAAANSUhEUgAAAlgAAAF3...')
	* const image2 = await pdfDoc.embedPng('data:image/png;base64,iVBORw0KGg...')
	*
	* // png=Uint8Array
	* import fs from 'fs'
	* const uint8Array = fs.readFileSync('small_mario.png')
	* const image3 = await pdfDoc.embedPng(uint8Array)
	*
	* // png=ArrayBuffer
	* const url = 'https://pdf-lib.js.org/assets/small_mario.png'
	* const arrayBuffer = await fetch(url).then(res => res.arrayBuffer())
	* const image4 = await pdfDoc.embedPng(arrayBuffer)
	* ```
	*
	* @param png The input data for a PNG image.
	* @returns Resolves with the embedded image.
	*/
	async embedPng(png) {
		assertIs(png, "png", [
			"string",
			Uint8Array,
			ArrayBuffer
		]);
		const bytes = toUint8Array(png);
		const embedder = await PngEmbedder.for(bytes);
		const ref = this.context.nextRef();
		const pdfImage = PDFImage.of(ref, this, embedder);
		this.images.push(pdfImage);
		return pdfImage;
	}
	/**
	* Embed one or more PDF pages into this document.
	*
	* For example:
	* ```js
	* const pdfDoc = await PDFDocument.create()
	*
	* const sourcePdfUrl = 'https://pdf-lib.js.org/assets/with_large_page_count.pdf'
	* const sourcePdf = await fetch(sourcePdfUrl).then((res) => res.arrayBuffer())
	*
	* // Embed page 74 of `sourcePdf` into `pdfDoc`
	* const [embeddedPage] = await pdfDoc.embedPdf(sourcePdf, [73])
	* ```
	*
	* See [[PDFDocument.load]] for examples of the allowed input data formats.
	*
	* @param pdf The input data containing a PDF document.
	* @param indices The indices of the pages that should be embedded.
	* @returns Resolves with an array of the embedded pages.
	*/
	async embedPdf(pdf, indices = [0]) {
		assertIs(pdf, "pdf", [
			"string",
			Uint8Array,
			ArrayBuffer,
			[PDFDocument, "PDFDocument"]
		]);
		assertIs(indices, "indices", [Array]);
		const srcPages = pluckIndices((pdf instanceof PDFDocument ? pdf : await PDFDocument.load(pdf)).getPages(), indices);
		return this.embedPages(srcPages);
	}
	/**
	* Embed a single PDF page into this document.
	*
	* For example:
	* ```js
	* const pdfDoc = await PDFDocument.create()
	*
	* const sourcePdfUrl = 'https://pdf-lib.js.org/assets/with_large_page_count.pdf'
	* const sourceBuffer = await fetch(sourcePdfUrl).then((res) => res.arrayBuffer())
	* const sourcePdfDoc = await PDFDocument.load(sourceBuffer)
	* const sourcePdfPage = sourcePdfDoc.getPages()[73]
	*
	* const embeddedPage = await pdfDoc.embedPage(
	*   sourcePdfPage,
	*
	*   // Clip a section of the source page so that we only embed part of it
	*   { left: 100, right: 450, bottom: 330, top: 570 },
	*
	*   // Translate all drawings of the embedded page by (10, 200) units
	*   [1, 0, 0, 1, 10, 200],
	* )
	* ```
	*
	* @param page The page to be embedded.
	* @param boundingBox
	* Optionally, an area of the source page that should be embedded
	* (defaults to entire page).
	* @param transformationMatrix
	* Optionally, a transformation matrix that is always applied to the embedded
	* page anywhere it is drawn.
	* @returns Resolves with the embedded pdf page.
	*/
	async embedPage(page, boundingBox, transformationMatrix) {
		assertIs(page, "page", [[PDFPage, "PDFPage"]]);
		const [embeddedPage] = await this.embedPages([page], [boundingBox], [transformationMatrix]);
		return embeddedPage;
	}
	/**
	* Embed one or more PDF pages into this document.
	*
	* For example:
	* ```js
	* const pdfDoc = await PDFDocument.create()
	*
	* const sourcePdfUrl = 'https://pdf-lib.js.org/assets/with_large_page_count.pdf'
	* const sourceBuffer = await fetch(sourcePdfUrl).then((res) => res.arrayBuffer())
	* const sourcePdfDoc = await PDFDocument.load(sourceBuffer)
	*
	* const page1 = sourcePdfDoc.getPages()[0]
	* const page2 = sourcePdfDoc.getPages()[52]
	* const page3 = sourcePdfDoc.getPages()[73]
	*
	* const embeddedPages = await pdfDoc.embedPages([page1, page2, page3])
	* ```
	*
	* @param page
	* The pages to be embedded (they must all share the same context).
	* @param boundingBoxes
	* Optionally, an array of clipping boundaries - one for each page
	* (defaults to entirety of each page).
	* @param transformationMatrices
	* Optionally, an array of transformation matrices - one for each page
	* (each page's transformation will apply anywhere it is drawn).
	* @returns Resolves with an array of the embedded pdf pages.
	*/
	async embedPages(pages, boundingBoxes = [], transformationMatrices = []) {
		if (pages.length === 0) return [];
		for (let idx = 0, len = pages.length - 1; idx < len; idx++) {
			const currPage = pages[idx];
			const nextPage = pages[idx + 1];
			if (currPage.node.context !== nextPage.node.context) throw new PageEmbeddingMismatchedContextError();
		}
		const context = pages[0].node.context;
		const maybeCopyPage = context === this.context ? (p) => p : PDFObjectCopier.for(context, this.context).copy;
		const embeddedPages = Array(pages.length);
		for (let idx = 0, len = pages.length; idx < len; idx++) {
			const page = maybeCopyPage(pages[idx].node);
			const box = boundingBoxes[idx];
			const matrix = transformationMatrices[idx];
			const embedder = await PDFPageEmbedder.for(page, box, matrix);
			const ref = this.context.nextRef();
			embeddedPages[idx] = PDFEmbeddedPage.of(ref, this, embedder);
		}
		this.embeddedPages.push(...embeddedPages);
		return embeddedPages;
	}
	/**
	* > **NOTE:** You shouldn't need to call this method directly. The [[save]]
	* > and [[saveAsBase64]] methods will automatically ensure that all embedded
	* > assets are flushed before serializing the document.
	*
	* Flush all embedded fonts, PDF pages, and images to this document's
	* [[context]].
	*
	* @returns Resolves when the flush is complete.
	*/
	async flush() {
		await this.embedAll(this.fonts);
		await this.embedAll(this.images);
		await this.embedAll(this.embeddedPages);
		await this.embedAll(this.embeddedFiles);
		await this.embedAll(this.javaScripts);
	}
	/**
	* Serialize this document to an array of bytes making up a PDF file.
	* For example:
	* ```js
	* const pdfBytes = await pdfDoc.save()
	* ```
	*
	* There are a number of things you can do with the serialized document,
	* depending on the JavaScript environment you're running in:
	* * Write it to a file in Node or React Native
	* * Download it as a Blob in the browser
	* * Render it in an `iframe`
	*
	* @param options The options to be used when saving the document.
	* @returns Resolves with the bytes of the serialized document.
	*/
	async save(options = {}) {
		const { useObjectStreams = true, addDefaultPage = true, objectsPerTick = 50, updateFieldAppearances = true } = options;
		assertIs(useObjectStreams, "useObjectStreams", ["boolean"]);
		assertIs(addDefaultPage, "addDefaultPage", ["boolean"]);
		assertIs(objectsPerTick, "objectsPerTick", ["number"]);
		assertIs(updateFieldAppearances, "updateFieldAppearances", ["boolean"]);
		if (addDefaultPage && this.getPageCount() === 0) this.addPage();
		if (updateFieldAppearances) {
			const form = this.formCache.getValue();
			if (form) form.updateFieldAppearances();
		}
		await this.flush();
		return (useObjectStreams ? PDFStreamWriter : PDFWriter).forContext(this.context, objectsPerTick).serializeToBuffer();
	}
	/**
	* Serialize this document to a base64 encoded string or data URI making up a
	* PDF file. For example:
	* ```js
	* const base64String = await pdfDoc.saveAsBase64()
	* base64String // => 'JVBERi0xLjcKJYGBgYEKC...'
	*
	* const base64DataUri = await pdfDoc.saveAsBase64({ dataUri: true })
	* base64DataUri // => 'data:application/pdf;base64,JVBERi0xLjcKJYGBgYEKC...'
	* ```
	*
	* @param options The options to be used when saving the document.
	* @returns Resolves with a base64 encoded string or data URI of the
	*          serialized document.
	*/
	async saveAsBase64(options = {}) {
		const { dataUri = false, ...otherOptions } = options;
		assertIs(dataUri, "dataUri", ["boolean"]);
		const base64 = encodeToBase64(await this.save(otherOptions));
		return dataUri ? `data:application/pdf;base64,${base64}` : base64;
	}
	findPageForAnnotationRef(ref) {
		const pages = this.getPages();
		for (let idx = 0, len = pages.length; idx < len; idx++) {
			const page = pages[idx];
			if (page.node.Annots()?.indexOf(ref) !== void 0) return page;
		}
	}
	async embedAll(embeddables) {
		for (let idx = 0, len = embeddables.length; idx < len; idx++) await embeddables[idx].embed();
	}
	updateInfoDict() {
		const pdfLib = `pdf-lib (https://github.com/Hopding/pdf-lib)`;
		const now = /* @__PURE__ */ new Date();
		const info = this.getInfoDict();
		this.setProducer(pdfLib);
		this.setModificationDate(now);
		if (!info.get(PDFName.of("Creator"))) this.setCreator(pdfLib);
		if (!info.get(PDFName.of("CreationDate"))) this.setCreationDate(now);
	}
	getInfoDict() {
		const existingInfo = this.context.lookup(this.context.trailerInfo.Info);
		if (existingInfo instanceof PDFDict) return existingInfo;
		const newInfo = this.context.obj({});
		this.context.trailerInfo.Info = this.context.register(newInfo);
		return newInfo;
	}
	assertFontkit() {
		if (!this.fontkit) throw new FontkitNotRegisteredError();
		return this.fontkit;
	}
};
function assertIsLiteralOrHexString(pdfObject) {
	if (!(pdfObject instanceof PDFHexString) && !(pdfObject instanceof PDFString)) throw new UnexpectedObjectTypeError([PDFHexString, PDFString], pdfObject);
}
//#endregion
//#region src/api/PDFPageOptions.ts
var BlendMode = /* @__PURE__ */ function(BlendMode) {
	BlendMode["Normal"] = "Normal";
	BlendMode["Multiply"] = "Multiply";
	BlendMode["Screen"] = "Screen";
	BlendMode["Overlay"] = "Overlay";
	BlendMode["Darken"] = "Darken";
	BlendMode["Lighten"] = "Lighten";
	BlendMode["ColorDodge"] = "ColorDodge";
	BlendMode["ColorBurn"] = "ColorBurn";
	BlendMode["HardLight"] = "HardLight";
	BlendMode["SoftLight"] = "SoftLight";
	BlendMode["Difference"] = "Difference";
	BlendMode["Exclusion"] = "Exclusion";
	return BlendMode;
}({});
//#endregion
//#region src/types/matrix.ts
var identityMatrix = [
	1,
	0,
	0,
	1,
	0,
	0
];
//#endregion
//#region src/api/svg.ts
var combineMatrix = ([a, b, c, d, e, f], [a2, b2, c2, d2, e2, f2]) => [
	a * a2 + c * b2,
	b * a2 + d * b2,
	a * c2 + c * d2,
	b * c2 + d * d2,
	a * e2 + c * f2 + e,
	b * e2 + d * f2 + f
];
var applyTransformation = ([a, b, c, d, e, f], { x, y }) => ({
	x: a * x + c * y + e,
	y: b * x + d * y + f
});
var transformationToMatrix = (name, args) => {
	switch (name) {
		case "scale":
		case "scaleX":
		case "scaleY": {
			const [sx, sy = sx] = args;
			return [
				name === "scaleY" ? 1 : sx,
				0,
				0,
				name === "scaleX" ? 1 : sy,
				0,
				0
			];
		}
		case "translate":
		case "translateX":
		case "translateY": {
			const [tx, ty = tx] = args;
			return [
				1,
				0,
				0,
				1,
				name === "translateY" ? 0 : tx,
				name === "translateX" ? 0 : -ty
			];
		}
		case "rotate": {
			const [a, x = 0, y = 0] = args;
			const t1 = transformationToMatrix("translate", [x, y]);
			const t2 = transformationToMatrix("translate", [-x, -y]);
			const aRadians = degreesToRadians(-a);
			return combineMatrix(combineMatrix(t1, [
				Math.cos(aRadians),
				Math.sin(aRadians),
				-Math.sin(aRadians),
				Math.cos(aRadians),
				0,
				0
			]), t2);
		}
		case "skewY":
		case "skewX": {
			const a = degreesToRadians(-args[0]);
			const skew = Math.tan(a);
			return [
				1,
				name === "skewY" ? skew : 0,
				name === "skewX" ? skew : 0,
				1,
				0,
				0
			];
		}
		case "matrix": {
			const [a, b, c, d, e, f] = args;
			const r = transformationToMatrix("scale", [1, -1]);
			return combineMatrix(combineMatrix(r, [
				a,
				b,
				c,
				d,
				e,
				f
			]), r);
		}
		default: return identityMatrix;
	}
};
var combineTransformation = (matrix, name, args) => combineMatrix(matrix, transformationToMatrix(name, args));
var StrokeLineCapMap = {
	butt: LineCapStyle.Butt,
	round: LineCapStyle.Round,
	square: LineCapStyle.Projecting
};
var FillRuleMap = {
	evenodd: FillRule.EvenOdd,
	nonzero: FillRule.NonZero
};
var StrokeLineJoinMap = {
	bevel: LineJoinStyle.Bevel,
	miter: LineJoinStyle.Miter,
	round: LineJoinStyle.Round
};
/** methods to draw SVGElements onto a PDFPage */
var runnersToPage = (page, options) => ({
	async text(element) {
		const anchor = element.svgAttributes.textAnchor;
		const dominantBaseline = element.svgAttributes.dominantBaseline;
		const text = element.text.trim().replace(/\s/g, " ");
		const fontSize = element.svgAttributes.fontSize || 12;
		/** This will find the best font for the provided style in the list */
		function getBestFont(style, fonts) {
			const family = style.fontFamily;
			if (!family) return void 0;
			const isBold = style.fontWeight === "bold" || Number(style.fontWeight) >= 700;
			const isItalic = style.fontStyle === "italic";
			const getFont = (bold, italic, family) => fonts[family + (bold ? "_bold" : "") + (italic ? "_italic" : "")];
			return getFont(isBold, isItalic, family) || getFont(isBold, false, family) || getFont(false, isItalic, family) || getFont(false, false, family) || Object.keys(fonts).find((fontFamily) => fontFamily.startsWith(family));
		}
		const font = options.fonts && getBestFont(element.svgAttributes, options.fonts);
		const textWidth = (font || page.getFont()[0]).widthOfTextAtSize(text, fontSize);
		const textHeight = (font || page.getFont()[0]).heightAtSize(fontSize);
		const offsetX = anchor === "middle" ? textWidth / 2 : anchor === "end" ? textWidth : 0;
		const offsetY = dominantBaseline === "text-before-edge" ? textHeight : dominantBaseline === "text-after-edge" ? -textHeight : dominantBaseline === "middle" ? textHeight / 2 : 0;
		page.drawText(text, {
			x: -offsetX,
			y: -offsetY,
			font,
			size: fontSize,
			color: element.svgAttributes.fill,
			opacity: element.svgAttributes.fillOpacity,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async line(element) {
		page.drawLine({
			start: {
				x: element.svgAttributes.x1 || 0,
				y: -element.svgAttributes.y1 || 0
			},
			end: {
				x: element.svgAttributes.x2 || 0,
				y: -element.svgAttributes.y2 || 0
			},
			thickness: element.svgAttributes.strokeWidth,
			color: element.svgAttributes.stroke,
			opacity: element.svgAttributes.strokeOpacity,
			lineCap: element.svgAttributes.strokeLineCap,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async path(element) {
		if (!element.svgAttributes.d) return;
		page.drawSvgPath(element.svgAttributes.d, {
			x: 0,
			y: 0,
			borderColor: element.svgAttributes.stroke,
			borderWidth: element.svgAttributes.strokeWidth,
			borderOpacity: element.svgAttributes.strokeOpacity,
			borderLineCap: element.svgAttributes.strokeLineCap,
			color: element.svgAttributes.fill,
			opacity: element.svgAttributes.fillOpacity,
			fillRule: element.svgAttributes.fillRule,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async image(element) {
		const { src } = element.svgAttributes;
		if (!src) return;
		const img = src.match(/\.png(\?|$)|^data:image\/png;base64/gim) ? await page.doc.embedPng(src) : await page.doc.embedJpg(src);
		const { x, y, width, height } = getFittingRectangle(img.width, img.height, element.svgAttributes.width || img.width, element.svgAttributes.height || img.height, element.svgAttributes.preserveAspectRatio);
		page.drawImage(img, {
			x,
			y: -y - height,
			width,
			height,
			opacity: element.svgAttributes.fillOpacity,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async rect(element) {
		if (!element.svgAttributes.fill && !element.svgAttributes.stroke) return;
		page.drawRectangle({
			x: 0,
			y: 0,
			width: element.svgAttributes.width,
			height: element.svgAttributes.height * -1,
			borderColor: element.svgAttributes.stroke,
			borderWidth: element.svgAttributes.strokeWidth,
			borderOpacity: element.svgAttributes.strokeOpacity,
			borderLineCap: element.svgAttributes.strokeLineCap,
			color: element.svgAttributes.fill,
			opacity: element.svgAttributes.fillOpacity,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async ellipse(element) {
		page.drawEllipse({
			x: element.svgAttributes.cx || 0,
			y: -(element.svgAttributes.cy || 0),
			xScale: element.svgAttributes.rx,
			yScale: element.svgAttributes.ry,
			borderColor: element.svgAttributes.stroke,
			borderWidth: element.svgAttributes.strokeWidth,
			borderOpacity: element.svgAttributes.strokeOpacity,
			borderLineCap: element.svgAttributes.strokeLineCap,
			color: element.svgAttributes.fill,
			opacity: element.svgAttributes.fillOpacity,
			matrix: element.svgAttributes.matrix,
			clipSpaces: element.svgAttributes.clipSpaces
		});
	},
	async circle(element) {
		return runnersToPage(page, options).ellipse(element);
	}
});
var styleOrAttribute = (attributes, style, attribute, def) => {
	const value = style[attribute] || attributes[attribute];
	if (!value && typeof def !== "undefined") return def;
	return value;
};
var parseStyles = (style) => {
	const cssRegex = /([^:\s]+)\s*:\s*([^;]+)/g;
	const css = {};
	let match = cssRegex.exec(style);
	while (match != null) {
		css[match[1]] = match[2];
		match = cssRegex.exec(style);
	}
	return css;
};
var parseColor = (color, inherited) => {
	if (!color || color.length === 0) return void 0;
	if (["none", "transparent"].includes(color)) return void 0;
	if (color === "currentColor") return inherited || parseColor("#000000");
	const parsedColor = colorString(color);
	return {
		rgb: parsedColor.rgb,
		alpha: parsedColor.alpha ? parsedColor.alpha + "" : void 0
	};
};
var parseAttributes = (element, inherited, matrix) => {
	const attributes = element.attributes;
	const style = parseStyles(attributes.style);
	const widthRaw = styleOrAttribute(attributes, style, "width", "");
	const heightRaw = styleOrAttribute(attributes, style, "height", "");
	const fillRaw = parseColor(styleOrAttribute(attributes, style, "fill"));
	const fillOpacityRaw = styleOrAttribute(attributes, style, "fill-opacity");
	const opacityRaw = styleOrAttribute(attributes, style, "opacity");
	const strokeRaw = parseColor(styleOrAttribute(attributes, style, "stroke"));
	const strokeOpacityRaw = styleOrAttribute(attributes, style, "stroke-opacity");
	const strokeLineCapRaw = styleOrAttribute(attributes, style, "stroke-linecap");
	const strokeLineJoinRaw = styleOrAttribute(attributes, style, "stroke-linejoin");
	const fillRuleRaw = styleOrAttribute(attributes, style, "fill-rule");
	const strokeWidthRaw = styleOrAttribute(attributes, style, "stroke-width");
	const fontFamilyRaw = styleOrAttribute(attributes, style, "font-family");
	const fontStyleRaw = styleOrAttribute(attributes, style, "font-style");
	const fontWeightRaw = styleOrAttribute(attributes, style, "font-weight");
	const fontSizeRaw = styleOrAttribute(attributes, style, "font-size");
	const width = parseFloatValue(widthRaw, inherited.width);
	const height = parseFloatValue(heightRaw, inherited.height);
	const x = parseFloatValue(attributes.x, inherited.width);
	const y = parseFloatValue(attributes.y, inherited.height);
	const x1 = parseFloatValue(attributes.x1, inherited.width);
	const x2 = parseFloatValue(attributes.x2, inherited.width);
	const y1 = parseFloatValue(attributes.y1, inherited.height);
	const y2 = parseFloatValue(attributes.y2, inherited.height);
	const cx = parseFloatValue(attributes.cx, inherited.width);
	const cy = parseFloatValue(attributes.cy, inherited.height);
	const rx = parseFloatValue(attributes.rx || attributes.r, inherited.width);
	const ry = parseFloatValue(attributes.ry || attributes.r, inherited.height);
	const newInherited = {
		fontFamily: fontFamilyRaw || inherited.fontFamily,
		fontStyle: fontStyleRaw || inherited.fontStyle,
		fontWeight: fontWeightRaw || inherited.fontWeight,
		fontSize: parseFloatValue(fontSizeRaw) ?? inherited.fontSize,
		fill: fillRaw?.rgb || inherited.fill,
		fillOpacity: parseFloatValue(fillOpacityRaw || opacityRaw || fillRaw?.alpha) ?? inherited.fillOpacity,
		fillRule: FillRuleMap[fillRuleRaw] || inherited.fillRule,
		stroke: strokeRaw?.rgb || inherited.stroke,
		strokeWidth: parseFloatValue(strokeWidthRaw) ?? inherited.strokeWidth,
		strokeOpacity: parseFloatValue(strokeOpacityRaw || opacityRaw || strokeRaw?.alpha) ?? inherited.strokeOpacity,
		strokeLineCap: StrokeLineCapMap[strokeLineCapRaw] || inherited.strokeLineCap,
		strokeLineJoin: StrokeLineJoinMap[strokeLineJoinRaw] || inherited.strokeLineJoin,
		width: width || inherited.width,
		height: height || inherited.height,
		rotation: inherited.rotation,
		viewBox: element.tagName === "svg" && element.attributes.viewBox ? parseViewBox(element.attributes.viewBox) : inherited.viewBox
	};
	const svgAttributes = {
		src: attributes.src || attributes["xlink:href"],
		textAnchor: attributes["text-anchor"],
		dominantBaseline: attributes["dominant-baseline"],
		preserveAspectRatio: attributes.preserveAspectRatio
	};
	let transformList = attributes.transform || "";
	[
		"translate",
		"translateX",
		"translateY",
		"skewX",
		"skewY",
		"rotate",
		"scale",
		"scaleX",
		"scaleY",
		"matrix"
	].forEach((name) => {
		if (attributes[name]) transformList = attributes[name] + " " + transformList;
	});
	if (x || y) transformList = transformList + `translate(${x || 0} ${y || 0}) `;
	let newMatrix = matrix;
	if (transformList) {
		const regexTransform = /(\w+)\((.+?)\)/g;
		let parsed = regexTransform.exec(transformList);
		while (parsed !== null) {
			const [, name, rawArgs] = parsed;
			const args = (rawArgs || "").split(/\s*,\s*|\s+/).filter((value) => value.length > 0).map((value) => parseFloat(value));
			newMatrix = combineTransformation(newMatrix, name, args);
			parsed = regexTransform.exec(transformList);
		}
	}
	svgAttributes.x = x;
	svgAttributes.y = y;
	if (attributes.cx || attributes.cy) {
		svgAttributes.cx = cx;
		svgAttributes.cy = cy;
	}
	if (attributes.rx || attributes.ry || attributes.r) {
		svgAttributes.rx = rx;
		svgAttributes.ry = ry;
	}
	if (attributes.x1 || attributes.y1) {
		svgAttributes.x1 = x1;
		svgAttributes.y1 = y1;
	}
	if (attributes.x2 || attributes.y2) {
		svgAttributes.x2 = x2;
		svgAttributes.y2 = y2;
	}
	if (attributes.width || attributes.height) {
		svgAttributes.width = width ?? inherited.width;
		svgAttributes.height = height ?? inherited.height;
	}
	if (attributes.d) {
		newMatrix = combineTransformation(newMatrix, "scale", [1, -1]);
		svgAttributes.d = attributes.d;
	}
	if (newInherited.fontFamily) {
		const inner = newInherited.fontFamily.match(/^"(.*?)"|^'(.*?)'/);
		if (inner) newInherited.fontFamily = inner[1] || inner[2];
	}
	if (newInherited.strokeWidth) svgAttributes.strokeWidth = newInherited.strokeWidth;
	return {
		inherited: newInherited,
		svgAttributes,
		tagName: element.tagName,
		matrix: newMatrix
	};
};
var getFittingRectangle = (originalWidth, originalHeight, targetWidth, targetHeight, preserveAspectRatio) => {
	if (preserveAspectRatio === "none") return {
		x: 0,
		y: 0,
		width: targetWidth,
		height: targetHeight
	};
	const originalRatio = originalWidth / originalHeight;
	const targetRatio = targetWidth / targetHeight;
	const width = targetRatio > originalRatio ? originalRatio * targetHeight : targetWidth;
	const height = targetRatio >= originalRatio ? targetHeight : targetWidth / originalRatio;
	const dx = targetWidth - width;
	const dy = targetHeight - height;
	const [x, y] = (() => {
		switch (preserveAspectRatio) {
			case "xMinYMin": return [0, 0];
			case "xMidYMin": return [dx / 2, 0];
			case "xMaxYMin": return [dx, dy / 2];
			case "xMinYMid": return [0, dy];
			case "xMaxYMid": return [dx, dy / 2];
			case "xMinYMax": return [0, dy];
			case "xMidYMax": return [dx / 2, dy];
			case "xMaxYMax": return [dx, dy];
			default: return [dx / 2, dy / 2];
		}
	})();
	return {
		x,
		y,
		width,
		height
	};
};
var getAspectRatioTransformation = (matrix, originalWidth, originalHeight, targetWidth, targetHeight, preserveAspectRatio) => {
	const scaleX = targetWidth / originalWidth;
	const scaleY = targetHeight / originalHeight;
	const boxScale = combineTransformation(matrix, "scale", [scaleX, scaleY]);
	if (preserveAspectRatio === "none") return {
		clipBox: boxScale,
		content: boxScale
	};
	const scale = targetWidth > targetHeight ? scaleY : scaleX;
	const dx = targetWidth - originalWidth * scale;
	const dy = targetHeight - originalHeight * scale;
	const [x, y] = (() => {
		switch (preserveAspectRatio) {
			case "xMinYMin": return [0, 0];
			case "xMidYMin": return [dx / 2, 0];
			case "xMaxYMin": return [dx, dy / 2];
			case "xMinYMid": return [0, dy];
			case "xMaxYMid": return [dx, dy / 2];
			case "xMinYMax": return [0, dy];
			case "xMidYMax": return [dx / 2, dy];
			case "xMaxYMax": return [dx, dy];
			default: return [dx / 2, dy / 2];
		}
	})();
	return {
		clipBox: boxScale,
		content: combineTransformation(combineTransformation(matrix, "translate", [x, y]), "scale", [scale])
	};
};
var parseHTMLNode = (node, inherited, matrix, clipSpaces) => {
	if (node.nodeType === NodeType.COMMENT_NODE) return [];
	else if (node.nodeType === NodeType.TEXT_NODE) return [];
	else if (node.tagName === "g") return parseGroupNode(node, inherited, matrix, clipSpaces);
	else if (node.tagName === "svg") return parseSvgNode(node, inherited, matrix, clipSpaces);
	else {
		if (node.tagName === "polygon") {
			node.tagName = "path";
			node.attributes.d = `M${node.attributes.points}Z`;
			delete node.attributes.points;
		}
		const attributes = parseAttributes(node, inherited, matrix);
		const svgAttributes = {
			...attributes.inherited,
			...attributes.svgAttributes,
			matrix: attributes.matrix,
			clipSpaces
		};
		Object.assign(node, { svgAttributes });
		return [node];
	}
};
var parseSvgNode = (node, inherited, matrix, clipSpaces) => {
	if (!node.attributes.width) node.setAttribute("width", inherited.viewBox.width + "");
	if (!node.attributes.height) node.setAttribute("height", inherited.viewBox.height + "");
	const attributes = parseAttributes(node, inherited, matrix);
	const result = [];
	const viewBox = node.attributes.viewBox ? parseViewBox(node.attributes.viewBox) : node.attributes.width && node.attributes.height ? parseViewBox(`0 0 ${node.attributes.width} ${node.attributes.height}`) : inherited.viewBox;
	let newMatrix = combineTransformation(matrix, "translate", [parseFloat(node.attributes.x) || 0, parseFloat(node.attributes.y) || 0]);
	const { clipBox: clipBoxTransform, content: contentTransform } = getAspectRatioTransformation(newMatrix, viewBox.width, viewBox.height, parseFloat(node.attributes.width), parseFloat(node.attributes.height), node.attributes.preserveAspectRatio || "xMidYMid");
	const baseClipSpace = {
		topLeft: applyTransformation(clipBoxTransform, {
			x: 0,
			y: 0
		}),
		topRight: applyTransformation(clipBoxTransform, {
			x: viewBox.width,
			y: 0
		}),
		bottomRight: applyTransformation(clipBoxTransform, {
			x: viewBox.width,
			y: -viewBox.height
		}),
		bottomLeft: applyTransformation(clipBoxTransform, {
			x: 0,
			y: -viewBox.height
		})
	};
	newMatrix = combineTransformation(contentTransform, "translate", [-viewBox.x, -viewBox.y]);
	node.childNodes.forEach((child) => {
		const parsedNodes = parseHTMLNode(child, {
			...attributes.inherited,
			viewBox
		}, newMatrix, [...clipSpaces, baseClipSpace]);
		result.push(...parsedNodes);
	});
	return result;
};
var parseGroupNode = (node, inherited, matrix, clipSpaces) => {
	const attributes = parseAttributes(node, inherited, matrix);
	const result = [];
	node.childNodes.forEach((child) => {
		result.push(...parseHTMLNode(child, attributes.inherited, attributes.matrix, clipSpaces));
	});
	return result;
};
var parseFloatValue = (value, reference = 1) => {
	if (!value) return void 0;
	const v = parseFloat(value);
	if (isNaN(v)) return void 0;
	if (value.endsWith("%")) return v * reference / 100;
	return v;
};
var parseViewBox = (viewBox) => {
	if (!viewBox) return;
	const [xViewBox = 0, yViewBox = 0, widthViewBox = 1, heightViewBox = 1] = (viewBox || "").split(" ").map((val) => parseFloatValue(val));
	return {
		x: xViewBox,
		y: yViewBox,
		width: widthViewBox,
		height: heightViewBox
	};
};
var parse$1 = (svg, { width, height, fontSize }, size, matrix) => {
	const htmlElement = parse(svg).firstChild;
	if (width) htmlElement.setAttribute("width", width + "");
	if (height) htmlElement.setAttribute("height", height + "");
	if (fontSize) htmlElement.setAttribute("font-size", fontSize + "");
	return parseHTMLNode(htmlElement, {
		...size,
		viewBox: parseViewBox(htmlElement.attributes.viewBox || "0 0 1 1")
	}, matrix, []);
};
var drawSvg = async (page, svg, options) => {
	if (!svg) return;
	const size = page.getSize();
	const firstChild = parse(svg).firstChild;
	const attributes = firstChild.attributes;
	const style = parseStyles(attributes.style);
	const widthRaw = styleOrAttribute(attributes, style, "width", "");
	const heightRaw = styleOrAttribute(attributes, style, "height", "");
	const width = options.width !== void 0 ? options.width : parseFloat(widthRaw);
	const height = options.height !== void 0 ? options.height : parseFloat(heightRaw);
	if (!attributes.viewBox) firstChild.setAttribute("viewBox", `0 0 ${widthRaw || width} ${heightRaw || height}`);
	if (options.width || options.height) {
		if (width !== void 0) style.width = width + (isNaN(width) ? "" : "px");
		if (height !== void 0) style.height = height + (isNaN(height) ? "" : "px");
		firstChild.setAttribute("style", Object.entries(style).map(([key, val]) => `${key}:${val};`).join(""));
	}
	const baseTransformation = [
		1,
		0,
		0,
		1,
		options.x || 0,
		options.y || 0
	];
	const runners = runnersToPage(page, options);
	await parse$1(firstChild.outerHTML, options, size, baseTransformation).reduce(async (prev, elt) => {
		await prev;
		return runners[elt.tagName]?.(elt);
	}, Promise.resolve());
};
//#endregion
//#region src/api/PDFPage.ts
var _PDFPage;
/**
* Represents a single page of a [[PDFDocument]].
*/
var PDFPage = class {
	constructor(leafNode, ref, doc) {
		_defineProperty(
			this,
			/** The low-level PDFDictionary wrapped by this page. */
			"node",
			void 0
		);
		_defineProperty(
			this,
			/** The unique reference assigned to this page within the document. */
			"ref",
			void 0
		);
		_defineProperty(
			this,
			/** The document to which this page belongs. */
			"doc",
			void 0
		);
		_defineProperty(this, "fontKey", void 0);
		_defineProperty(this, "font", void 0);
		_defineProperty(this, "fontSize", 24);
		_defineProperty(this, "fontColor", rgb(0, 0, 0));
		_defineProperty(this, "lineHeight", 24);
		_defineProperty(this, "x", 0);
		_defineProperty(this, "y", 0);
		_defineProperty(this, "contentStream", void 0);
		_defineProperty(this, "contentStreamRef", void 0);
		assertIs(leafNode, "leafNode", [[PDFPageLeaf, "PDFPageLeaf"]]);
		assertIs(ref, "ref", [[PDFRef, "PDFRef"]]);
		assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
		this.node = leafNode;
		this.ref = ref;
		this.doc = doc;
	}
	/**
	* Rotate this page by a multiple of 90 degrees. For example:
	* ```js
	* import { degrees } from 'pdf-lib'
	*
	* page.setRotation(degrees(-90))
	* page.setRotation(degrees(0))
	* page.setRotation(degrees(90))
	* page.setRotation(degrees(180))
	* page.setRotation(degrees(270))
	* ```
	* @param angle The angle to rotate this page.
	*/
	setRotation(angle) {
		const degreesAngle = toDegrees(angle);
		assertMultiple(degreesAngle, "degreesAngle", 90);
		this.node.set(PDFName.of("Rotate"), this.doc.context.obj(degreesAngle));
	}
	/**
	* Get this page's rotation angle in degrees. For example:
	* ```js
	* const rotationAngle = page.getRotation().angle;
	* ```
	* @returns The rotation angle of the page in degrees (always a multiple of
	*          90 degrees).
	*/
	getRotation() {
		const Rotate = this.node.Rotate();
		return degrees(Rotate ? Rotate.asNumber() : 0);
	}
	/**
	* Resize this page by increasing or decreasing its width and height. For
	* example:
	* ```js
	* page.setSize(250, 500)
	* page.setSize(page.getWidth() + 50, page.getHeight() + 100)
	* page.setSize(page.getWidth() - 50, page.getHeight() - 100)
	* ```
	*
	* Note that the PDF specification does not allow for pages to have explicit
	* widths and heights. Instead it defines the "size" of a page in terms of
	* five rectangles: the MediaBox, CropBox, BleedBox, TrimBox, and ArtBox. As a
	* result, this method cannot directly change the width and height of a page.
	* Instead, it works by adjusting these five boxes.
	*
	* This method performs the following steps:
	*   1. Set width & height of MediaBox.
	*   2. Set width & height of CropBox, if it has same dimensions as MediaBox.
	*   3. Set width & height of BleedBox, if it has same dimensions as MediaBox.
	*   4. Set width & height of TrimBox, if it has same dimensions as MediaBox.
	*   5. Set width & height of ArtBox, if it has same dimensions as MediaBox.
	*
	* This approach works well for most PDF documents as all PDF pages must
	* have a MediaBox, but relatively few have a CropBox, BleedBox, TrimBox, or
	* ArtBox. And when they do have these additional boxes, they often have the
	* same dimensions as the MediaBox. However, if you find this method does not
	* work for your document, consider setting the boxes directly:
	*   * [[PDFPage.setMediaBox]]
	*   * [[PDFPage.setCropBox]]
	*   * [[PDFPage.setBleedBox]]
	*   * [[PDFPage.setTrimBox]]
	*   * [[PDFPage.setArtBox]]
	*
	* @param width The new width of the page.
	* @param height The new height of the page.
	*/
	setSize(width, height) {
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const mediaBox = this.getMediaBox();
		this.setMediaBox(mediaBox.x, mediaBox.y, width, height);
		const cropBox = this.getCropBox();
		const bleedBox = this.getBleedBox();
		const trimBox = this.getTrimBox();
		const artBox = this.getArtBox();
		const hasCropBox = Boolean(this.node.CropBox());
		const hasBleedBox = Boolean(this.node.BleedBox());
		const hasTrimBox = Boolean(this.node.TrimBox());
		const hasArtBox = Boolean(this.node.ArtBox());
		if (hasCropBox && rectanglesAreEqual(cropBox, mediaBox)) this.setCropBox(mediaBox.x, mediaBox.y, width, height);
		if (hasBleedBox && rectanglesAreEqual(bleedBox, mediaBox)) this.setBleedBox(mediaBox.x, mediaBox.y, width, height);
		if (hasTrimBox && rectanglesAreEqual(trimBox, mediaBox)) this.setTrimBox(mediaBox.x, mediaBox.y, width, height);
		if (hasArtBox && rectanglesAreEqual(artBox, mediaBox)) this.setArtBox(mediaBox.x, mediaBox.y, width, height);
	}
	/**
	* Resize this page by increasing or decreasing its width. For example:
	* ```js
	* page.setWidth(250)
	* page.setWidth(page.getWidth() + 50)
	* page.setWidth(page.getWidth() - 50)
	* ```
	*
	* This method uses [[PDFPage.setSize]] to set the page's width.
	*
	* @param width The new width of the page.
	*/
	setWidth(width) {
		assertIs(width, "width", ["number"]);
		this.setSize(width, this.getSize().height);
	}
	/**
	* Resize this page by increasing or decreasing its height. For example:
	* ```js
	* page.setHeight(500)
	* page.setHeight(page.getWidth() + 100)
	* page.setHeight(page.getWidth() - 100)
	* ```
	*
	* This method uses [[PDFPage.setSize]] to set the page's height.
	*
	* @param height The new height of the page.
	*/
	setHeight(height) {
		assertIs(height, "height", ["number"]);
		this.setSize(this.getSize().width, height);
	}
	/**
	* Set the MediaBox of this page. For example:
	* ```js
	* const mediaBox = page.getMediaBox()
	*
	* page.setMediaBox(0, 0, 250, 500)
	* page.setMediaBox(mediaBox.x, mediaBox.y, 50, 100)
	* page.setMediaBox(15, 5, mediaBox.width - 50, mediaBox.height - 100)
	* ```
	*
	* See [[PDFPage.getMediaBox]] for details about what the MediaBox represents.
	*
	* @param x The x coordinate of the lower left corner of the new MediaBox.
	* @param y The y coordinate of the lower left corner of the new MediaBox.
	* @param width The width of the new MediaBox.
	* @param height The height of the new MediaBox.
	*/
	setMediaBox(x, y, width, height) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const mediaBox = this.doc.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.node.set(PDFName.MediaBox, mediaBox);
	}
	/**
	* Set the CropBox of this page. For example:
	* ```js
	* const cropBox = page.getCropBox()
	*
	* page.setCropBox(0, 0, 250, 500)
	* page.setCropBox(cropBox.x, cropBox.y, 50, 100)
	* page.setCropBox(15, 5, cropBox.width - 50, cropBox.height - 100)
	* ```
	*
	* See [[PDFPage.getCropBox]] for details about what the CropBox represents.
	*
	* @param x The x coordinate of the lower left corner of the new CropBox.
	* @param y The y coordinate of the lower left corner of the new CropBox.
	* @param width The width of the new CropBox.
	* @param height The height of the new CropBox.
	*/
	setCropBox(x, y, width, height) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const cropBox = this.doc.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.node.set(PDFName.CropBox, cropBox);
	}
	/**
	* Set the BleedBox of this page. For example:
	* ```js
	* const bleedBox = page.getBleedBox()
	*
	* page.setBleedBox(0, 0, 250, 500)
	* page.setBleedBox(bleedBox.x, bleedBox.y, 50, 100)
	* page.setBleedBox(15, 5, bleedBox.width - 50, bleedBox.height - 100)
	* ```
	*
	* See [[PDFPage.getBleedBox]] for details about what the BleedBox represents.
	*
	* @param x The x coordinate of the lower left corner of the new BleedBox.
	* @param y The y coordinate of the lower left corner of the new BleedBox.
	* @param width The width of the new BleedBox.
	* @param height The height of the new BleedBox.
	*/
	setBleedBox(x, y, width, height) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const bleedBox = this.doc.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.node.set(PDFName.BleedBox, bleedBox);
	}
	/**
	* Set the TrimBox of this page. For example:
	* ```js
	* const trimBox = page.getTrimBox()
	*
	* page.setTrimBox(0, 0, 250, 500)
	* page.setTrimBox(trimBox.x, trimBox.y, 50, 100)
	* page.setTrimBox(15, 5, trimBox.width - 50, trimBox.height - 100)
	* ```
	*
	* See [[PDFPage.getTrimBox]] for details about what the TrimBox represents.
	*
	* @param x The x coordinate of the lower left corner of the new TrimBox.
	* @param y The y coordinate of the lower left corner of the new TrimBox.
	* @param width The width of the new TrimBox.
	* @param height The height of the new TrimBox.
	*/
	setTrimBox(x, y, width, height) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const trimBox = this.doc.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.node.set(PDFName.TrimBox, trimBox);
	}
	/**
	* Set the ArtBox of this page. For example:
	* ```js
	* const artBox = page.getArtBox()
	*
	* page.setArtBox(0, 0, 250, 500)
	* page.setArtBox(artBox.x, artBox.y, 50, 100)
	* page.setArtBox(15, 5, artBox.width - 50, artBox.height - 100)
	* ```
	*
	* See [[PDFPage.getArtBox]] for details about what the ArtBox represents.
	*
	* @param x The x coordinate of the lower left corner of the new ArtBox.
	* @param y The y coordinate of the lower left corner of the new ArtBox.
	* @param width The width of the new ArtBox.
	* @param height The height of the new ArtBox.
	*/
	setArtBox(x, y, width, height) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		assertIs(width, "width", ["number"]);
		assertIs(height, "height", ["number"]);
		const artBox = this.doc.context.obj([
			x,
			y,
			x + width,
			y + height
		]);
		this.node.set(PDFName.ArtBox, artBox);
	}
	/**
	* Get this page's width and height. For example:
	* ```js
	* const { width, height } = page.getSize()
	* ```
	*
	* This method uses [[PDFPage.getMediaBox]] to obtain the page's
	* width and height.
	*
	* @returns The width and height of the page.
	*/
	getSize() {
		const { width, height } = this.getMediaBox();
		return {
			width,
			height
		};
	}
	/**
	* Get this page's width. For example:
	* ```js
	* const width = page.getWidth()
	* ```
	*
	* This method uses [[PDFPage.getSize]] to obtain the page's size.
	*
	* @returns The width of the page.
	*/
	getWidth() {
		return this.getSize().width;
	}
	/**
	* Get this page's height. For example:
	* ```js
	* const height = page.getHeight()
	* ```
	*
	* This method uses [[PDFPage.getSize]] to obtain the page's size.
	*
	* @returns The height of the page.
	*/
	getHeight() {
		return this.getSize().height;
	}
	/**
	* Get the rectangle defining this page's MediaBox. For example:
	* ```js
	* const { x, y, width, height } = page.getMediaBox()
	* ```
	*
	* The MediaBox of a page defines the boundaries of the physical medium on
	* which the page is to be displayed/printed. It may include extended area
	* surrounding the page content for bleed marks, printing marks, etc...
	* It may also include areas close to the edges of the medium that cannot be
	* marked because of physical limitations of the output device. Content
	* falling outside this boundary may safely be discarded without affecting
	* the meaning of the PDF file.
	*
	* @returns An object defining the lower left corner of the MediaBox and its
	*          width & height.
	*/
	getMediaBox() {
		return this.node.MediaBox().asRectangle();
	}
	/**
	* Get the rectangle defining this page's CropBox. For example:
	* ```js
	* const { x, y, width, height } = page.getCropBox()
	* ```
	*
	* The CropBox of a page defines the region to which the contents of the page
	* shall be clipped when displayed or printed. Unlike the other boxes, the
	* CropBox does not necessarily represent the physical page geometry. It
	* merely imposes clipping on the page contents.
	*
	* The CropBox's default value is the page's MediaBox.
	*
	* @returns An object defining the lower left corner of the CropBox and its
	*          width & height.
	*/
	getCropBox() {
		return this.node.CropBox()?.asRectangle() ?? this.getMediaBox();
	}
	/**
	* Get the rectangle defining this page's BleedBox. For example:
	* ```js
	* const { x, y, width, height } = page.getBleedBox()
	* ```
	*
	* The BleedBox of a page defines the region to which the contents of the
	* page shall be clipped when output in a production environment. This may
	* include any extra bleed area needed to accommodate the physical
	* limitations of cutting, folding, and trimming equipment. The actual
	* printed page may include printing marks that fall outside the BleedBox.
	*
	* The BleedBox's default value is the page's CropBox.
	*
	* @returns An object defining the lower left corner of the BleedBox and its
	*          width & height.
	*/
	getBleedBox() {
		return this.node.BleedBox()?.asRectangle() ?? this.getCropBox();
	}
	/**
	* Get the rectangle defining this page's TrimBox. For example:
	* ```js
	* const { x, y, width, height } = page.getTrimBox()
	* ```
	*
	* The TrimBox of a page defines the intended dimensions of the finished
	* page after trimming. It may be smaller than the MediaBox to allow for
	* production-related content, such as printing instructions, cut marks, or
	* color bars.
	*
	* The TrimBox's default value is the page's CropBox.
	*
	* @returns An object defining the lower left corner of the TrimBox and its
	*          width & height.
	*/
	getTrimBox() {
		return this.node.TrimBox()?.asRectangle() ?? this.getCropBox();
	}
	/**
	* Get the rectangle defining this page's ArtBox. For example:
	* ```js
	* const { x, y, width, height } = page.getArtBox()
	* ```
	*
	* The ArtBox of a page defines the extent of the page's meaningful content
	* (including potential white space).
	*
	* The ArtBox's default value is the page's CropBox.
	*
	* @returns An object defining the lower left corner of the ArtBox and its
	*          width & height.
	*/
	getArtBox() {
		return this.node.ArtBox()?.asRectangle() ?? this.getCropBox();
	}
	/**
	* Translate this page's content to a new location on the page. This operation
	* is often useful after resizing the page with [[setSize]]. For example:
	* ```js
	* // Add 50 units of whitespace to the top and right of the page
	* page.setSize(page.getWidth() + 50, page.getHeight() + 50)
	*
	* // Move the page's content from the lower-left corner of the page
	* // to the top-right corner.
	* page.translateContent(50, 50)
	*
	* // Now there are 50 units of whitespace to the left and bottom of the page
	* ```
	* See also: [[resetPosition]]
	* @param x The new position on the x-axis for this page's content.
	* @param y The new position on the y-axis for this page's content.
	*/
	translateContent(x, y) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		this.node.normalize();
		this.getContentStream();
		const start = this.createContentStream(pushGraphicsState(), translate(x, y));
		const startRef = this.doc.context.register(start);
		const end = this.createContentStream(popGraphicsState());
		const endRef = this.doc.context.register(end);
		this.node.wrapContentStreams(startRef, endRef);
	}
	/**
	* Scale the size, content, and annotations of a page.
	*
	* For example:
	* ```js
	* page.scale(0.5, 0.5);
	* ```
	*
	* @param x The factor by which the width for the page should be scaled
	*          (e.g. `0.5` is 50%).
	* @param y The factor by which the height for the page should be scaled
	*          (e.g. `2.0` is 200%).
	*/
	scale(x, y) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		this.setSize(this.getWidth() * x, this.getHeight() * y);
		this.scaleContent(x, y);
		this.scaleAnnotations(x, y);
	}
	/**
	* Scale the content of a page. This is useful after resizing an existing
	* page. This scales only the content, not the annotations.
	*
	* For example:
	* ```js
	* // Bisect the size of the page
	* page.setSize(page.getWidth() / 2, page.getHeight() / 2);
	*
	* // Scale the content of the page down by 50% in x and y
	* page.scaleContent(0.5, 0.5);
	* ```
	* See also: [[scaleAnnotations]]
	* @param x The factor by which the x-axis for the content should be scaled
	*          (e.g. `0.5` is 50%).
	* @param y The factor by which the y-axis for the content should be scaled
	*          (e.g. `2.0` is 200%).
	*/
	scaleContent(x, y) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		this.node.normalize();
		this.getContentStream();
		const start = this.createContentStream(pushGraphicsState(), scale(x, y));
		const startRef = this.doc.context.register(start);
		const end = this.createContentStream(popGraphicsState());
		const endRef = this.doc.context.register(end);
		this.node.wrapContentStreams(startRef, endRef);
	}
	/**
	* Scale the annotations of a page. This is useful if you want to scale a
	* page with comments or other annotations.
	* ```js
	* // Scale the content of the page down by 50% in x and y
	* page.scaleContent(0.5, 0.5);
	*
	* // Scale the content of the page down by 50% in x and y
	* page.scaleAnnotations(0.5, 0.5);
	* ```
	* See also: [[scaleContent]]
	* @param x The factor by which the x-axis for the annotations should be
	*          scaled (e.g. `0.5` is 50%).
	* @param y The factor by which the y-axis for the annotations should be
	*          scaled (e.g. `2.0` is 200%).
	*/
	scaleAnnotations(x, y) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		const annots = this.node.Annots();
		if (!annots) return;
		for (let idx = 0; idx < annots.size(); idx++) {
			const annot = annots.lookup(idx);
			if (annot instanceof PDFDict) this.scaleAnnot(annot, x, y);
		}
	}
	/**
	* Reset the x and y coordinates of this page to `(0, 0)`. This operation is
	* often useful after calling [[translateContent]]. For example:
	* ```js
	* // Shift the page's contents up and to the right by 50 units
	* page.translateContent(50, 50)
	*
	* // This text will shifted - it will be drawn at (50, 50)
	* page.drawText('I am shifted')
	*
	* // Move back to (0, 0)
	* page.resetPosition()
	*
	* // This text will not be shifted - it will be drawn at (0, 0)
	* page.drawText('I am not shifted')
	* ```
	*/
	resetPosition() {
		this.getContentStream(false);
		this.x = 0;
		this.y = 0;
	}
	/**
	* Choose a default font for this page. The default font will be used whenever
	* text is drawn on this page and no font is specified. For example:
	* ```js
	* import { StandardFonts } from 'pdf-lib'
	*
	* const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman)
	* const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const courierFont = await pdfDoc.embedFont(StandardFonts.Courier)
	*
	* const page = pdfDoc.addPage()
	*
	* page.setFont(helveticaFont)
	* page.drawText('I will be drawn in Helvetica')
	*
	* page.setFont(timesRomanFont)
	* page.drawText('I will be drawn in Courier', { font: courierFont })
	* ```
	* @param font The default font to be used when drawing text on this page.
	*/
	setFont(font) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		this.font = font;
		this.fontKey = this.node.newFontDictionary(this.font.name, this.font.ref);
	}
	/**
	* Choose a default font size for this page. The default font size will be
	* used whenever text is drawn on this page and no font size is specified.
	* For example:
	* ```js
	* page.setFontSize(12)
	* page.drawText('I will be drawn in size 12')
	*
	* page.setFontSize(36)
	* page.drawText('I will be drawn in size 24', { fontSize: 24 })
	* ```
	* @param fontSize The default font size to be used when drawing text on this
	*                 page.
	*/
	setFontSize(fontSize) {
		assertIs(fontSize, "fontSize", ["number"]);
		this.fontSize = fontSize;
	}
	/**
	* Choose a default font color for this page. The default font color will be
	* used whenever text is drawn on this page and no font color is specified.
	* For example:
	* ```js
	* import { rgb, cmyk, grayscale } from 'pdf-lib'
	*
	* page.setFontColor(rgb(0.97, 0.02, 0.97))
	* page.drawText('I will be drawn in pink')
	*
	* page.setFontColor(cmyk(0.4, 0.7, 0.39, 0.15))
	* page.drawText('I will be drawn in gray', { color: grayscale(0.5) })
	* ```
	* @param fontColor The default font color to be used when drawing text on
	*                  this page.
	*/
	setFontColor(fontColor) {
		assertIs(fontColor, "fontColor", [[Object, "Color"]]);
		this.fontColor = fontColor;
	}
	/**
	* Choose a default line height for this page. The default line height will be
	* used whenever text is drawn on this page and no line height is specified.
	* For example:
	* ```js
	* page.setLineHeight(12);
	* page.drawText('These lines will be vertically \n separated by 12 units')
	*
	* page.setLineHeight(36);
	* page.drawText('These lines will be vertically \n separated by 24 units', {
	*   lineHeight: 24
	* })
	* ```
	* @param lineHeight The default line height to be used when drawing text on
	*                   this page.
	*/
	setLineHeight(lineHeight) {
		assertIs(lineHeight, "lineHeight", ["number"]);
		this.lineHeight = lineHeight;
	}
	/**
	* Get the default position of this page. For example:
	* ```js
	* const { x, y } = page.getPosition()
	* ```
	* @returns The default position of the page.
	*/
	getPosition() {
		return {
			x: this.x,
			y: this.y
		};
	}
	/**
	* Get the default x coordinate of this page. For example:
	* ```js
	* const x = page.getX()
	* ```
	* @returns The default x coordinate of the page.
	*/
	getX() {
		return this.x;
	}
	/**
	* Get the default y coordinate of this page. For example:
	* ```js
	* const y = page.getY()
	* ```
	* @returns The default y coordinate of the page.
	*/
	getY() {
		return this.y;
	}
	/**
	* Change the default position of this page. For example:
	* ```js
	* page.moveTo(0, 0)
	* page.drawText('I will be drawn at the origin')
	*
	* page.moveTo(0, 25)
	* page.drawText('I will be drawn 25 units up')
	*
	* page.moveTo(25, 25)
	* page.drawText('I will be drawn 25 units up and 25 units to the right')
	* ```
	* @param x The new default position on the x-axis for this page.
	* @param y The new default position on the y-axis for this page.
	*/
	moveTo(x, y) {
		assertIs(x, "x", ["number"]);
		assertIs(y, "y", ["number"]);
		this.x = x;
		this.y = y;
	}
	/**
	* Change the default position of this page to be further down the y-axis.
	* For example:
	* ```js
	* page.moveTo(50, 50)
	* page.drawText('I will be drawn at (50, 50)')
	*
	* page.moveDown(10)
	* page.drawText('I will be drawn at (50, 40)')
	* ```
	* @param yDecrease The amount by which the page's default position along the
	*                  y-axis should be decreased.
	*/
	moveDown(yDecrease) {
		assertIs(yDecrease, "yDecrease", ["number"]);
		this.y -= yDecrease;
	}
	/**
	* Change the default position of this page to be further up the y-axis.
	* For example:
	* ```js
	* page.moveTo(50, 50)
	* page.drawText('I will be drawn at (50, 50)')
	*
	* page.moveUp(10)
	* page.drawText('I will be drawn at (50, 60)')
	* ```
	* @param yIncrease The amount by which the page's default position along the
	*                  y-axis should be increased.
	*/
	moveUp(yIncrease) {
		assertIs(yIncrease, "yIncrease", ["number"]);
		this.y += yIncrease;
	}
	/**
	* Change the default position of this page to be further left on the x-axis.
	* For example:
	* ```js
	* page.moveTo(50, 50)
	* page.drawText('I will be drawn at (50, 50)')
	*
	* page.moveLeft(10)
	* page.drawText('I will be drawn at (40, 50)')
	* ```
	* @param xDecrease The amount by which the page's default position along the
	*                  x-axis should be decreased.
	*/
	moveLeft(xDecrease) {
		assertIs(xDecrease, "xDecrease", ["number"]);
		this.x -= xDecrease;
	}
	/**
	* Change the default position of this page to be further right on the y-axis.
	* For example:
	* ```js
	* page.moveTo(50, 50)
	* page.drawText('I will be drawn at (50, 50)')
	*
	* page.moveRight(10)
	* page.drawText('I will be drawn at (60, 50)')
	* ```
	* @param xIncrease The amount by which the page's default position along the
	*                  x-axis should be increased.
	*/
	moveRight(xIncrease) {
		assertIs(xIncrease, "xIncrease", ["number"]);
		this.x += xIncrease;
	}
	/**
	* Push one or more operators to the end of this page's current content
	* stream. For example:
	* ```js
	* import {
	*   pushGraphicsState,
	*   moveTo,
	*   lineTo,
	*   closePath,
	*   setFillingColor,
	*   rgb,
	*   fill,
	*   popGraphicsState,
	* } from 'pdf-lib'
	*
	* // Draw a green triangle in the lower-left corner of the page
	* page.pushOperators(
	*   pushGraphicsState(),
	*   moveTo(0, 0),
	*   lineTo(100, 0),
	*   lineTo(50, 100),
	*   closePath(),
	*   setFillingColor(rgb(0.0, 1.0, 0.0)),
	*   fill(),
	*   popGraphicsState(),
	* )
	* ```
	* @param operator The operators to be pushed.
	*/
	pushOperators(...operator) {
		assertEachIs(operator, "operator", [[PDFOperator, "PDFOperator"]]);
		this.getContentStream().push(...operator);
	}
	/**
	* Draw one or more lines of text on this page. For example:
	* ```js
	* import { StandardFonts, rgb } from 'pdf-lib'
	*
	* const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman)
	*
	* const page = pdfDoc.addPage()
	*
	* page.setFont(helveticaFont)
	*
	* page.moveTo(5, 200)
	* page.drawText('The Life of an Egg', { size: 36 })
	*
	* page.moveDown(36)
	* page.drawText('An Epic Tale of Woe', { size: 30 })
	*
	* page.drawText(
	*   `Humpty Dumpty sat on a wall \n` +
	*   `Humpty Dumpty had a great fall; \n` +
	*   `All the king's horses and all the king's men \n` +
	*   `Couldn't put Humpty together again. \n`,
	*   {
	*     x: 25,
	*     y: 100,
	*     font: timesRomanFont,
	*     size: 24,
	*     color: rgb(1, 0, 0),
	*     lineHeight: 24,
	*     opacity: 0.75,
	*   },
	* )
	* ```
	* @param text The text to be drawn.
	* @param options The options to be used when drawing the text.
	*/
	drawText(text, options = {}) {
		assertIs(text, "text", ["string"]);
		assertOrUndefined(options.color, "options.color", [[Object, "Color"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertOrUndefined(options.font, "options.font", [[PDFFont, "PDFFont"]]);
		assertOrUndefined(options.size, "options.size", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.xSkew, "options.xSkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.ySkew, "options.ySkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.lineHeight, "options.lineHeight", ["number"]);
		assertOrUndefined(options.maxWidth, "options.maxWidth", ["number"]);
		assertOrUndefined(options.wordBreaks, "options.wordBreaks", [Array]);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		const { oldFont, newFont, newFontKey } = this.setOrEmbedFont(options.font);
		const fontSize = options.size || this.fontSize;
		const wordBreaks = options.wordBreaks || this.doc.defaultWordBreaks;
		const textWidth = (t) => newFont.widthOfTextAtSize(t, fontSize);
		const lines = options.maxWidth === void 0 ? lineSplit(cleanText(text)) : breakTextIntoLines(text, wordBreaks, options.maxWidth, textWidth);
		const encodedLines = Array(lines.length);
		for (let idx = 0, len = lines.length; idx < len; idx++) encodedLines[idx] = newFont.encodeText(lines[idx]);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			blendMode: options.blendMode
		});
		this.getContentStream().push(...drawLinesOfText(encodedLines, {
			color: options.color ?? this.fontColor,
			font: newFontKey,
			size: fontSize,
			rotate: options.rotate ?? degrees(0),
			xSkew: options.xSkew ?? degrees(0),
			ySkew: options.ySkew ?? degrees(0),
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			lineHeight: options.lineHeight ?? this.lineHeight,
			graphicsState: graphicsStateKey,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces
		}));
		if (options.font) if (oldFont) this.setFont(oldFont);
		else this.resetFont();
	}
	/**
	* Draw an image on this page. For example:
	* ```js
	* import { degrees } from 'pdf-lib'
	*
	* const jpgUrl = 'https://pdf-lib.js.org/assets/cat_riding_unicorn.jpg'
	* const jpgImageBytes = await fetch(jpgUrl).then((res) => res.arrayBuffer())
	*
	* const jpgImage = await pdfDoc.embedJpg(jpgImageBytes)
	* const jpgDims = jpgImage.scale(0.5)
	*
	* const page = pdfDoc.addPage()
	*
	* page.drawImage(jpgImage, {
	*   x: 25,
	*   y: 25,
	*   width: jpgDims.width,
	*   height: jpgDims.height,
	*   rotate: degrees(30),
	*   opacity: 0.75,
	* })
	* ```
	* @param image The image to be drawn.
	* @param options The options to be used when drawing the image.
	*/
	drawImage(image, options = {}) {
		assertIs(image, "image", [[PDFImage, "PDFImage"]]);
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.width, "options.width", ["number"]);
		assertOrUndefined(options.height, "options.height", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.xSkew, "options.xSkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.ySkew, "options.ySkew", [[Object, "Rotation"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		const xObjectKey = this.node.newXObject("Image", image.ref);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			blendMode: options.blendMode
		});
		this.getContentStream().push(...drawImage(xObjectKey, {
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			width: options.width ?? image.size().width,
			height: options.height ?? image.size().height,
			rotate: options.rotate ?? degrees(0),
			xSkew: options.xSkew ?? degrees(0),
			ySkew: options.ySkew ?? degrees(0),
			graphicsState: graphicsStateKey,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces
		}));
	}
	/**
	* Draw an embedded PDF page on this page. For example:
	* ```js
	* import { degrees } from 'pdf-lib'
	*
	* const pdfDoc = await PDFDocument.create()
	* const page = pdfDoc.addPage()
	*
	* const sourcePdfUrl = 'https://pdf-lib.js.org/assets/with_large_page_count.pdf'
	* const sourcePdf = await fetch(sourcePdfUrl).then((res) => res.arrayBuffer())
	*
	* // Embed page 74 from the PDF
	* const [embeddedPage] = await pdfDoc.embedPdf(sourcePdf, 73)
	*
	* page.drawPage(embeddedPage, {
	*   x: 250,
	*   y: 200,
	*   xScale: 0.5,
	*   yScale: 0.5,
	*   rotate: degrees(30),
	*   opacity: 0.75,
	* })
	* ```
	*
	* The `options` argument accepts both `width`/`height` and `xScale`/`yScale`
	* as options. Since each of these options defines the size of the drawn page,
	* if both options are given, `width` and `height` take precedence and the
	* corresponding scale variants are ignored.
	*
	* @param embeddedPage The embedded page to be drawn.
	* @param options The options to be used when drawing the embedded page.
	*/
	drawPage(embeddedPage, options = {}) {
		assertIs(embeddedPage, "embeddedPage", [[PDFEmbeddedPage, "PDFEmbeddedPage"]]);
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.xScale, "options.xScale", ["number"]);
		assertOrUndefined(options.yScale, "options.yScale", ["number"]);
		assertOrUndefined(options.width, "options.width", ["number"]);
		assertOrUndefined(options.height, "options.height", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.xSkew, "options.xSkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.ySkew, "options.ySkew", [[Object, "Rotation"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		const xObjectKey = this.node.newXObject("EmbeddedPdfPage", embeddedPage.ref);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			blendMode: options.blendMode
		});
		const xScale = options.width !== void 0 ? options.width / embeddedPage.width : options.xScale !== void 0 ? options.xScale : 1;
		const yScale = options.height !== void 0 ? options.height / embeddedPage.height : options.yScale !== void 0 ? options.yScale : 1;
		this.getContentStream().push(...drawPage(xObjectKey, {
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			xScale,
			yScale,
			rotate: options.rotate ?? degrees(0),
			xSkew: options.xSkew ?? degrees(0),
			ySkew: options.ySkew ?? degrees(0),
			graphicsState: graphicsStateKey
		}));
	}
	/**
	* Draw an SVG path on this page. For example:
	* ```js
	* import { rgb } from 'pdf-lib'
	*
	* const svgPath = 'M 0,20 L 100,160 Q 130,200 150,120 C 190,-40 200,200 300,150 L 400,90'
	*
	* // Draw path as black line
	* page.drawSvgPath(svgPath, { x: 25, y: 75 })
	*
	* // Change border style and opacity
	* page.drawSvgPath(svgPath, {
	*   x: 25,
	*   y: 275,
	*   borderColor: rgb(0.5, 0.5, 0.5),
	*   borderWidth: 2,
	*   borderOpacity: 0.75,
	* })
	*
	* // Set fill color and opacity
	* page.drawSvgPath(svgPath, {
	*   x: 25,
	*   y: 475,
	*   color: rgb(1.0, 0, 0),
	*   opacity: 0.75,
	* })
	*
	* // Draw 50% of original size
	* page.drawSvgPath(svgPath, {
	*   x: 25,
	*   y: 675,
	*   scale: 0.5,
	* })
	* ```
	* @param path The SVG path to be drawn.
	* @param options The options to be used when drawing the SVG path.
	*/
	drawSvgPath(path, options = {}) {
		assertIs(path, "path", ["string"]);
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.scale, "options.scale", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.borderWidth, "options.borderWidth", ["number"]);
		assertOrUndefined(options.color, "options.color", [[Object, "Color"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertOrUndefined(options.borderColor, "options.borderColor", [[Object, "Color"]]);
		assertOrUndefined(options.borderDashArray, "options.borderDashArray", [Array]);
		assertOrUndefined(options.borderDashPhase, "options.borderDashPhase", ["number"]);
		assertIsOneOfOrUndefined(options.borderLineCap, "options.borderLineCap", LineCapStyle);
		assertRangeOrUndefined(options.borderOpacity, "options.borderOpacity", 0, 1);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		assertIsOneOfOrUndefined(options.fillRule, "options.fillRule", FillRule);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			borderOpacity: options.borderOpacity,
			blendMode: options.blendMode
		});
		if (!("color" in options) && !("borderColor" in options)) options.borderColor = rgb(0, 0, 0);
		this.getContentStream().push(...drawSvgPath(path, {
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			scale: options.scale,
			rotate: options.rotate ?? degrees(0),
			color: options.color ?? void 0,
			borderColor: options.borderColor ?? void 0,
			borderWidth: options.borderWidth ?? 0,
			borderDashArray: options.borderDashArray ?? void 0,
			borderDashPhase: options.borderDashPhase ?? void 0,
			borderLineCap: options.borderLineCap ?? void 0,
			graphicsState: graphicsStateKey,
			fillRule: options.fillRule,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces
		}));
	}
	/**
	* Draw a line on this page. For example:
	* ```js
	* import { rgb } from 'pdf-lib'
	*
	* page.drawLine({
	*   start: { x: 25, y: 75 },
	*   end: { x: 125, y: 175 },
	*   thickness: 2,
	*   color: rgb(0.75, 0.2, 0.2),
	*   opacity: 0.75,
	* })
	* ```
	* @param options The options to be used when drawing the line.
	*/
	drawLine(options) {
		assertIs(options.start, "options.start", [[Object, "{ x: number, y: number }"]]);
		assertIs(options.end, "options.end", [[Object, "{ x: number, y: number }"]]);
		assertIs(options.start.x, "options.start.x", ["number"]);
		assertIs(options.start.y, "options.start.y", ["number"]);
		assertIs(options.end.x, "options.end.x", ["number"]);
		assertIs(options.end.y, "options.end.y", ["number"]);
		assertOrUndefined(options.thickness, "options.thickness", ["number"]);
		assertOrUndefined(options.color, "options.color", [[Object, "Color"]]);
		assertOrUndefined(options.dashArray, "options.dashArray", [Array]);
		assertOrUndefined(options.dashPhase, "options.dashPhase", ["number"]);
		assertIsOneOfOrUndefined(options.lineCap, "options.lineCap", LineCapStyle);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			borderOpacity: options.opacity,
			blendMode: options.blendMode
		});
		if (!("color" in options)) options.color = rgb(0, 0, 0);
		this.getContentStream().push(...drawLine({
			start: options.start,
			end: options.end,
			thickness: options.thickness ?? 1,
			color: options.color ?? void 0,
			dashArray: options.dashArray ?? void 0,
			dashPhase: options.dashPhase ?? void 0,
			lineCap: options.lineCap ?? void 0,
			graphicsState: graphicsStateKey,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces
		}));
	}
	/**
	* Draw a rectangle on this page. For example:
	* ```js
	* import { degrees, grayscale, rgb } from 'pdf-lib'
	*
	* page.drawRectangle({
	*   x: 25,
	*   y: 75,
	*   width: 250,
	*   height: 75,
	*   rotate: degrees(-15),
	*   borderWidth: 5,
	*   borderColor: grayscale(0.5),
	*   color: rgb(0.75, 0.2, 0.2),
	*   opacity: 0.5,
	*   borderOpacity: 0.75,
	*   radius: 0.1,
	* })
	* ```
	* @param options The options to be used when drawing the rectangle.
	*/
	drawRectangle(options = {}) {
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.width, "options.width", ["number"]);
		assertOrUndefined(options.height, "options.height", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.xSkew, "options.xSkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.ySkew, "options.ySkew", [[Object, "Rotation"]]);
		assertOrUndefined(options.borderWidth, "options.borderWidth", ["number"]);
		assertOrUndefined(options.color, "options.color", [[Object, "Color"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertOrUndefined(options.borderColor, "options.borderColor", [[Object, "Color"]]);
		assertOrUndefined(options.borderDashArray, "options.borderDashArray", [Array]);
		assertOrUndefined(options.borderDashPhase, "options.borderDashPhase", ["number"]);
		assertIsOneOfOrUndefined(options.borderLineCap, "options.borderLineCap", LineCapStyle);
		assertRangeOrUndefined(options.borderOpacity, "options.borderOpacity", 0, 1);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		assertOrUndefined(options.radius, "options.radius", ["number"]);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			borderOpacity: options.borderOpacity,
			blendMode: options.blendMode
		});
		if (!("color" in options) && !("borderColor" in options)) options.color = rgb(0, 0, 0);
		this.getContentStream().push(...drawRectangle({
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			width: options.width ?? 150,
			height: options.height ?? 100,
			rotate: options.rotate ?? degrees(0),
			xSkew: options.xSkew ?? degrees(0),
			ySkew: options.ySkew ?? degrees(0),
			borderWidth: options.borderWidth ?? 0,
			color: options.color ?? void 0,
			borderColor: options.borderColor ?? void 0,
			borderDashArray: options.borderDashArray ?? void 0,
			borderDashPhase: options.borderDashPhase ?? void 0,
			graphicsState: graphicsStateKey,
			borderLineCap: options.borderLineCap ?? void 0,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces,
			radius: options.radius ?? 0
		}));
	}
	/**
	* Draw a square on this page. For example:
	* ```js
	* import { degrees, grayscale, rgb } from 'pdf-lib'
	*
	* page.drawSquare({
	*   x: 25,
	*   y: 75,
	*   size: 100,
	*   rotate: degrees(-15),
	*   borderWidth: 5,
	*   borderColor: grayscale(0.5),
	*   color: rgb(0.75, 0.2, 0.2),
	*   opacity: 0.5,
	*   borderOpacity: 0.75,
	* })
	* ```
	* @param options The options to be used when drawing the square.
	*/
	drawSquare(options = {}) {
		const { size } = options;
		assertOrUndefined(size, "size", ["number"]);
		this.drawRectangle({
			...options,
			width: size,
			height: size
		});
	}
	/**
	* Draw an ellipse on this page. For example:
	* ```js
	* import { grayscale, rgb } from 'pdf-lib'
	*
	* page.drawEllipse({
	*   x: 200,
	*   y: 75,
	*   xScale: 100,
	*   yScale: 50,
	*   borderWidth: 5,
	*   borderColor: grayscale(0.5),
	*   color: rgb(0.75, 0.2, 0.2),
	*   opacity: 0.5,
	*   borderOpacity: 0.75,
	* })
	* ```
	* @param options The options to be used when drawing the ellipse.
	*/
	drawEllipse(options = {}) {
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.xScale, "options.xScale", ["number"]);
		assertOrUndefined(options.yScale, "options.yScale", ["number"]);
		assertOrUndefined(options.rotate, "options.rotate", [[Object, "Rotation"]]);
		assertOrUndefined(options.color, "options.color", [[Object, "Color"]]);
		assertRangeOrUndefined(options.opacity, "opacity.opacity", 0, 1);
		assertOrUndefined(options.borderColor, "options.borderColor", [[Object, "Color"]]);
		assertRangeOrUndefined(options.borderOpacity, "options.borderOpacity", 0, 1);
		assertOrUndefined(options.borderWidth, "options.borderWidth", ["number"]);
		assertOrUndefined(options.borderDashArray, "options.borderDashArray", [Array]);
		assertOrUndefined(options.borderDashPhase, "options.borderDashPhase", ["number"]);
		assertIsOneOfOrUndefined(options.borderLineCap, "options.borderLineCap", LineCapStyle);
		assertIsOneOfOrUndefined(options.blendMode, "options.blendMode", BlendMode);
		const graphicsStateKey = this.maybeEmbedGraphicsState({
			opacity: options.opacity,
			borderOpacity: options.borderOpacity,
			blendMode: options.blendMode
		});
		if (!("color" in options) && !("borderColor" in options)) options.color = rgb(0, 0, 0);
		this.getContentStream().push(...drawEllipse({
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			xScale: options.xScale ?? 100,
			yScale: options.yScale ?? 100,
			rotate: options.rotate ?? void 0,
			color: options.color ?? void 0,
			borderColor: options.borderColor ?? void 0,
			borderWidth: options.borderWidth ?? 0,
			borderDashArray: options.borderDashArray ?? void 0,
			borderDashPhase: options.borderDashPhase ?? void 0,
			borderLineCap: options.borderLineCap ?? void 0,
			graphicsState: graphicsStateKey,
			matrix: options.matrix,
			clipSpaces: options.clipSpaces
		}));
	}
	/**
	* Draw a circle on this page. For example:
	* ```js
	* import { grayscale, rgb } from 'pdf-lib'
	*
	* page.drawCircle({
	*   x: 200,
	*   y: 150,
	*   size: 100,
	*   borderWidth: 5,
	*   borderColor: grayscale(0.5),
	*   color: rgb(0.75, 0.2, 0.2),
	*   opacity: 0.5,
	*   borderOpacity: 0.75,
	* })
	* ```
	* @param options The options to be used when drawing the ellipse.
	*/
	drawCircle(options = {}) {
		const { size = 100 } = options;
		assertOrUndefined(size, "size", ["number"]);
		this.drawEllipse({
			...options,
			xScale: size,
			yScale: size
		});
	}
	setOrEmbedFont(font) {
		const oldFont = this.font;
		const oldFontKey = this.fontKey;
		if (font) this.setFont(font);
		else this.getFont();
		return {
			oldFont,
			oldFontKey,
			newFont: this.font,
			newFontKey: this.fontKey
		};
	}
	/**
	* Draw an SVG on this page. For example:
	* ```js
	* const svg = '<svg><path d="M 0,20 L 100,160 Q 130,200 150,120 C 190,-40 200,200 300,150 L 400,90"></path></svg>'
	*
	* // Draw svg
	* page.drawSvg(svg, { x: 25, y: 75 })
	* ```
	* @param svg The SVG to be drawn.
	* @param options The options to be used when drawing the SVG.
	*/
	async drawSvg(svg, options = {}) {
		assertIs(svg, "svg", ["string"]);
		assertOrUndefined(options.x, "options.x", ["number"]);
		assertOrUndefined(options.y, "options.y", ["number"]);
		assertOrUndefined(options.width, "options.width", ["number"]);
		assertOrUndefined(options.height, "options.height", ["number"]);
		await drawSvg(this, svg, {
			x: options.x ?? this.x,
			y: options.y ?? this.y,
			fonts: options.fonts,
			width: options.width,
			height: options.height
		});
	}
	getFont() {
		if (!this.font || !this.fontKey) {
			const font = this.doc.embedStandardFont(StandardFonts.Helvetica);
			this.setFont(font);
		}
		return [this.font, this.fontKey];
	}
	resetFont() {
		this.font = void 0;
		this.fontKey = void 0;
	}
	getContentStream(useExisting = true) {
		if (useExisting && this.contentStream) return this.contentStream;
		this.contentStream = this.createContentStream();
		this.contentStreamRef = this.doc.context.register(this.contentStream);
		this.node.addContentStream(this.contentStreamRef);
		return this.contentStream;
	}
	createContentStream(...operators) {
		const dict = this.doc.context.obj({});
		return PDFContentStream.of(dict, operators);
	}
	maybeEmbedGraphicsState(options) {
		const { opacity, borderOpacity, blendMode } = options;
		if (opacity === void 0 && borderOpacity === void 0 && blendMode === void 0) return;
		const graphicsState = this.doc.context.obj({
			Type: "ExtGState",
			ca: opacity,
			CA: borderOpacity,
			BM: blendMode
		});
		return this.node.newExtGState("GS", graphicsState);
	}
	scaleAnnot(annot, x, y) {
		const selectors = [
			"RD",
			"CL",
			"Vertices",
			"QuadPoints",
			"L",
			"Rect"
		];
		for (let idx = 0, len = selectors.length; idx < len; idx++) {
			const list = annot.lookup(PDFName.of(selectors[idx]));
			if (list instanceof PDFArray) list.scalePDFNumbers(x, y);
		}
		const inkLists = annot.lookup(PDFName.of("InkList"));
		if (inkLists instanceof PDFArray) for (let idx = 0, len = inkLists.size(); idx < len; idx++) {
			const arr = inkLists.lookup(idx);
			if (arr instanceof PDFArray) arr.scalePDFNumbers(x, y);
		}
	}
};
_PDFPage = PDFPage;
_defineProperty(PDFPage, "of", (leafNode, ref, doc) => new _PDFPage(leafNode, ref, doc));
_defineProperty(PDFPage, "create", (doc) => {
	assertIs(doc, "doc", [[PDFDocument, "PDFDocument"]]);
	const dummyRef = PDFRef.of(-1);
	const pageLeaf = PDFPageLeaf.withContextAndParent(doc.context, dummyRef);
	const pageRef = doc.context.register(pageLeaf);
	return new _PDFPage(pageLeaf, pageRef, doc);
});
//#endregion
//#region src/api/form/PDFButton.ts
var _PDFButton;
/**
* Represents a button field of a [[PDFForm]].
*
* [[PDFButton]] fields are interactive controls that users can click with their
* mouse. This type of [[PDFField]] is not stateful. The purpose of a button
* is to perform an action when the user clicks on it, such as opening a print
* modal or resetting the form. Buttons are typically rectangular in shape and
* have a text label describing the action that they perform when clicked.
*/
var PDFButton = class extends PDFField {
	constructor(acroPushButton, ref, doc) {
		super(acroPushButton, ref, doc);
		_defineProperty(
			this,
			/** The low-level PDFAcroPushButton wrapped by this button. */
			"acroField",
			void 0
		);
		assertIs(acroPushButton, "acroButton", [[PDFAcroPushButton, "PDFAcroPushButton"]]);
		this.acroField = acroPushButton;
	}
	/**
	* Display an image inside the bounds of this button's widgets. For example:
	* ```js
	* const pngImage = await pdfDoc.embedPng(...)
	* const button = form.getButton('some.button.field')
	* button.setImage(pngImage, ImageAlignment.Center)
	* ```
	* This will update the appearances streams for each of this button's widgets.
	* @param image The image that should be displayed.
	* @param alignment The alignment of the image.
	*/
	setImage(image, alignment = ImageAlignment.Center) {
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			const streamRef = this.createImageAppearanceStream(widget, image, alignment);
			this.updateWidgetAppearances(widget, { normal: streamRef });
		}
		this.markAsClean();
	}
	/**
	* Set the font size for this field. Larger font sizes will result in larger
	* text being displayed when PDF readers render this button. Font sizes may
	* be integer or floating point numbers. Supplying a negative font size will
	* cause this method to throw an error.
	*
	* For example:
	* ```js
	* const button = form.getButton('some.button.field')
	* button.setFontSize(4)
	* button.setFontSize(15.7)
	* ```
	*
	* > This method depends upon the existence of a default appearance
	* > (`/DA`) string. If this field does not have a default appearance string,
	* > or that string does not contain a font size (via the `Tf` operator),
	* > then this method will throw an error.
	*
	* @param fontSize The font size to be used when rendering text in this field.
	*/
	setFontSize(fontSize) {
		assertPositive(fontSize, "fontSize");
		this.acroField.setFontSize(fontSize);
		this.markAsDirty();
	}
	/**
	* Show this button on the specified page with the given text. For example:
	* ```js
	* const ubuntuFont = await pdfDoc.embedFont(ubuntuFontBytes)
	* const page = pdfDoc.addPage()
	*
	* const form = pdfDoc.getForm()
	* const button = form.createButton('some.button.field')
	*
	* button.addToPage('Do Stuff', page, {
	*   x: 50,
	*   y: 75,
	*   width: 200,
	*   height: 100,
	*   textColor: rgb(1, 0, 0),
	*   backgroundColor: rgb(0, 1, 0),
	*   borderColor: rgb(0, 0, 1),
	*   borderWidth: 2,
	*   rotate: degrees(90),
	*   font: ubuntuFont,
	* })
	* ```
	* This will create a new widget for this button field.
	* @param text The text to be displayed for this button widget.
	* @param page The page to which this button widget should be added.
	* @param options The options to be used when adding this button widget.
	*/
	addToPage(text, page, options) {
		assertOrUndefined(text, "text", ["string"]);
		assertOrUndefined(page, "page", [[PDFPage, "PDFPage"]]);
		assertFieldAppearanceOptions(options);
		const widget = this.createWidget({
			x: (options?.x ?? 0) - (options?.borderWidth ?? 0) / 2,
			y: (options?.y ?? 0) - (options?.borderWidth ?? 0) / 2,
			width: options?.width ?? 100,
			height: options?.height ?? 50,
			textColor: options?.textColor ?? rgb(0, 0, 0),
			backgroundColor: options?.backgroundColor ?? rgb(.75, .75, .75),
			borderColor: options?.borderColor,
			borderWidth: options?.borderWidth ?? 0,
			rotate: options?.rotate ?? degrees(0),
			caption: text,
			hidden: options?.hidden,
			page: page.ref
		});
		const widgetRef = this.doc.context.register(widget.dict);
		this.acroField.addWidget(widgetRef);
		const font = options?.font ?? this.doc.getForm().getDefaultFont();
		this.updateWidgetAppearance(widget, font);
		page.node.addAnnot(widgetRef);
	}
	/**
	* Returns `true` if this button has been marked as dirty, or if any of this
	* button's widgets do not have an appearance stream. For example:
	* ```js
	* const button = form.getButton('some.button.field')
	* if (button.needsAppearancesUpdate()) console.log('Needs update')
	* ```
	* @returns Whether or not this button needs an appearance update.
	*/
	needsAppearancesUpdate() {
		if (this.isDirty()) return true;
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) if (!(widgets[idx].getAppearances()?.normal instanceof PDFStream)) return true;
		return false;
	}
	/**
	* Update the appearance streams for each of this button's widgets using
	* the default appearance provider for buttons. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const button = form.getButton('some.button.field')
	* button.defaultUpdateAppearances(helvetica)
	* ```
	* @param font The font to be used for creating the appearance streams.
	*/
	defaultUpdateAppearances(font) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		this.updateAppearances(font);
	}
	/**
	* Update the appearance streams for each of this button's widgets using
	* the given appearance provider. If no `provider` is passed, the default
	* appearance provider for buttons will be used. For example:
	* ```js
	* const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica)
	* const button = form.getButton('some.button.field')
	* button.updateAppearances(helvetica, (field, widget, font) => {
	*   ...
	*   return {
	*     normal: drawButton(...),
	*     down: drawButton(...),
	*   }
	* })
	* ```
	* @param font The font to be used for creating the appearance streams.
	* @param provider Optionally, the appearance provider to be used for
	*                 generating the contents of the appearance streams.
	*/
	updateAppearances(font, provider) {
		assertIs(font, "font", [[PDFFont, "PDFFont"]]);
		assertOrUndefined(provider, "provider", [Function]);
		const widgets = this.acroField.getWidgets();
		for (let idx = 0, len = widgets.length; idx < len; idx++) {
			const widget = widgets[idx];
			this.updateWidgetAppearance(widget, font, provider);
		}
	}
	updateWidgetAppearance(widget, font, provider) {
		const appearances = normalizeAppearance((provider ?? defaultButtonAppearanceProvider)(this, widget, font));
		this.updateWidgetAppearanceWithFont(widget, font, appearances);
	}
};
_PDFButton = PDFButton;
_defineProperty(PDFButton, "of", (acroPushButton, ref, doc) => new _PDFButton(acroPushButton, ref, doc));
//#endregion
export { AFRelationship, AcroButtonFlags, AcroChoiceFlags, AcroFieldFlags, AcroTextFlags, AnnotationFlags, AppearanceCharacteristics, BlendMode, Cache, CharCodes, ColorTypes, CombedTextLayoutError, CorruptPageTreeError, CustomFontEmbedder, CustomFontSubsetEmbedder, DecompressionBombError, Duplex, EncryptedPDFError, ExceededMaxLengthError, FieldAlreadyExistsError, FieldExistsAsNonTerminalError, FileEmbedder, FillRule, FontkitNotRegisteredError, ForeignPageError, ImageAlignment, IndexOutOfBoundsError, InvalidAcroFieldValueError, InvalidFieldNamePartError, InvalidMaxLengthError, InvalidPDFDateStringError, InvalidTargetIndexError, JpegEmbedder, LineCapStyle, LineJoinStyle, MethodNotImplementedError, MissingCatalogError, MissingDAEntryError, MissingKeywordError, MissingOnValueCheckError, MissingPDFHeaderError, MissingPageContentsEmbeddingError, MissingTfOperatorError, MultiSelectValueError, NextByteAssertionError, NoSuchFieldError, NonFullScreenPageMode, NumberParsingError, PDFAcroButton, PDFAcroCheckBox, PDFAcroChoice, PDFAcroComboBox, PDFAcroField, PDFAcroForm, PDFAcroListBox, PDFAcroNonTerminal, PDFAcroPushButton, PDFAcroRadioButton, PDFAcroSignature, PDFAcroTerminal, PDFAcroText, PDFAnnotation, PDFArray, PDFArrayIsNotRectangleError, PDFBool, PDFButton, PDFCatalog, PDFCheckBox, PDFContentStream, PDFContext, PDFCrossRefSection, PDFCrossRefStream, PDFDict, PDFDocument, PDFDropdown, PDFEmbeddedPage, PDFField, PDFFlateStream, PDFFont, PDFForm, PDFHeader, PDFHexString, PDFImage, PDFInvalidObject, PDFInvalidObjectParsingError, PDFJavaScript, PDFName, PDFNull_default as PDFNull, PDFNumber, PDFObject, PDFObjectCopier, PDFObjectParser, PDFObjectParsingError, PDFObjectStream, PDFObjectStreamParser, PDFOperator, PDFOperatorNames, PDFOptionList, PDFPage, PDFPageEmbedder, PDFPageLeaf, PDFPageTree, PDFParser, PDFParsingError, PDFRadioGroup, PDFRawStream, PDFRef, PDFSignature, PDFStream, PDFStreamParsingError, PDFStreamWriter, PDFString, PDFTextField, PDFTrailer, PDFTrailerDict, PDFWidgetAnnotation, PDFWriter, PDFXRefStreamParser, PageEmbeddingMismatchedContextError, PageSizes, ParseSpeeds, PngEmbedder, PrintScaling, PrivateConstructorError, ReadingDirection, RemovePageFromEmptyDocumentError, ReparseError, RichTextFieldReadError, RotationTypes, StalledParserError, StandardFontEmbedder, StandardFontValues, StandardFonts, TextAlignment, TextRenderingMode, UnbalancedParenthesisError, UnexpectedFieldTypeError, UnexpectedObjectTypeError, UnrecognizedStreamTypeError, UnsupportedEncodingError, ViewerPreferences, addRandomSuffix, adjustDimsForRotation, appendBezierCurve, appendQuadraticCurve, arrayAsString, asNumber, asPDFName, asPDFNumber, assertEachIs, assertInteger, assertIs, assertIsOneOf, assertIsOneOfOrUndefined, assertIsSubset, assertMultiple, assertOrUndefined, assertPositive, assertRange, assertRangeOrUndefined, backtick, beginMarkedContent, beginText, breakTextIntoLines, byAscendingId, bytesFor, canBeConvertedToUint8Array, charAtIndex, charFromCode, charFromHexCode, charSplit, cleanText, clip, clipEvenOdd, closePath, cmyk, colorString, colorToComponents, componentsToColor, concatTransformationMatrix, copyStringIntoBuffer, createPDFAcroField, createPDFAcroFields, createTypeErrorMsg, createValueErrorMsg, decodeFromBase64, decodeFromBase64DataUri, decodePDFRawStream, defaultButtonAppearanceProvider, defaultCheckBoxAppearanceProvider, defaultDropdownAppearanceProvider, defaultOptionListAppearanceProvider, defaultRadioGroupAppearanceProvider, defaultTextFieldAppearanceProvider, degrees, degreesToRadians, drawButton, drawCheckBox, drawCheckMark, drawEllipse, drawEllipsePath, drawImage, drawLine, drawLinesOfText, drawObject, drawOptionList, drawPage, drawRadioButton, drawRectangle, drawSvgPath, drawText, drawTextField, drawTextLines, encodeToBase64, endMarkedContent, endPath, endText, error, escapeRegExp, escapedNewlineChars, fill, fillAndStroke, fillEvenOdd, findLastMatch, getType, grayscale, hasSurrogates, hasUtf16BOM, highSurrogate, isArrayEqual, isNewlineChar, isStandardFont, isType, isWithinBMP, last, layoutCombedText, layoutMultilineText, layoutSinglelineText, lineSplit, lineTo, lowSurrogate, mergeIntoTypedArray, mergeLines, mergeUint8Arrays, moveText, moveTo, newlineChars, nextLine, normalizeAppearance, numberToString, padStart, parseDate, pdfDocEncodingDecode, pluckIndices, popGraphicsState, pushGraphicsState, radians, radiansToDegrees, range, rectangle, rectanglesAreEqual, reduceRotation, restoreDashPattern, reverseArray, rgb, rotateAndSkewTextDegreesAndTranslate, rotateAndSkewTextRadiansAndTranslate, rotateDegrees, rotateInPlace, rotateRadians, rotateRectangle, scale, setCharacterSpacing, setCharacterSqueeze, setDashPattern, setFillingCmykColor, setFillingColor, setFillingGrayscaleColor, setFillingRgbColor, setFontAndSize, setGraphicsState, setLineCap, setLineHeight, setLineJoin, setLineWidth, setStrokingCmykColor, setStrokingColor, setStrokingGrayscaleColor, setStrokingRgbColor, setTextMatrix, setTextRenderingMode, setTextRise, setWordSpacing, showText, singleQuote, sizeInBytes, skewDegrees, skewRadians, sortedUniq, square, stringAsByteArray, stroke, sum, toCharCode, toCodePoint, toDegrees, toHexString, toHexStringOfMinLength, toRadians, toUint8Array, translate, typedArrayFor, utf16Decode, utf16Encode, utf8Encode, values, waitForTick };

//# sourceMappingURL=index.js.map